import Konva from "konva";
import { Util } from 'konva/lib/Util.js'
import {
  categoryColors,
  categoryColors_pastel,
  categoryColors2,
  categoryColors_old,
  categoryColors_MUTED,
  categoryColors_STD,
  tagColors,
  uflTheme,
  xploreTheme,
  darkNavyTheme,
  defaultTheme,
  themes,
  themeColor as themeColorUtil,
  mixHex as mixHexUtil,
  mixHexWithWhite as mixHexWithWhiteUtil,
  renderPlainObject as renderPlainObjectUtil,
  getStructuredSummaryRenderers,
} from './renderers/plainObjectRenderer';
import CustomImage  from "./CustomImage";
import CustomText from "./CustomText";
import PrimitiveConfig, { flattenStructuredResponse } from "./PrimitiveConfig";
import CollectionUtils from "./CollectionHelper";
import moment from "moment";
import MainStore from "./MainStore";
import { renderToString } from "react-dom/server";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { compareTwoStrings, convertOrganizationFinancialData, extractHashtags, formatNumber, getBaseDomain, getRegisteredDomain, roundCurrency } from "./SharedTransforms";
import { prepareStructuredSummary, guessStructuredSectionStyle, resolveSummaryStructureTemplate } from "./utils/structuredSummary";
import { resolvePrimitiveImageUrl } from "./utils/primitiveImage";
import { HeroIcon } from "./HeroIcon";
import { cloneElement } from "react";
import WedgeRing from "./WedgeRing";
import { getConfig } from "@testing-library/react";
import { createProcessingPlaceholder, updateProcessingPlaceholder, configureProcessingPlaceholderAnimation, tickProcessingPlaceholder } from "./renderers/animations";
import { renderVizDslSlide } from "./VizDslRenderer";
import { renderDocDslDocument } from "./DocDslRenderer";
import { renderPieChart } from "./renderers/pie";
import { normalizeSectionLayoutSpec, buildLayoutPreset, applyLayoutPresetToSections } from "./sharedLayout";
export { renderPieChart };
const typeMaps = {}
const categoryMaps = {}

const resolveDisplayValue = (cell, table, options = {}) =>
  CollectionUtils.resolveCellValue(cell, table, { fallbackField: "count", ...options });
const isHeightDebugEnabled = () => (typeof window !== "undefined" && window.__DEBUG_TIMELINE_HEIGHT === true);
const isTimelineLayoutDebugEnabled = () => (typeof window !== "undefined" && window.__DEBUG_TIMELINE_LAYOUT === true);

const dynamicAccentStyleRegistry = new Map();
const MONGO_ID_PATTERN = /^[a-f\d]{24}$/i;

const safeColorKey = (color = "")=>
  color
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const normalizeHex = (hex = "")=>{
  let normalized = hex.trim().replace(/^#/, "");
  if(normalized.length === 3){
    normalized = normalized.split("").map((ch)=>ch + ch).join("");
  }
  if(normalized.length !== 6){
    return null;
  }
  return normalized.toLowerCase();
};

const hexToRgb = (hex)=>{
  const normalized = normalizeHex(hex);
  if(!normalized){
    return null;
  }
  const intVal = parseInt(normalized, 16);
  if(Number.isNaN(intVal)){
    return null;
  }
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255,
  };
};

const rgbToHex = ({ r, g, b })=>
  `#${[r, g, b]
    .map((value)=>Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0"))
    .join("")}`;

const mixRgb = (source, target, amount = 0.5)=>{
  const mixChannel = (s, t)=>Math.round(s * (1 - amount) + t * amount);
  return {
    r: mixChannel(source.r, target.r),
    g: mixChannel(source.g, target.g),
    b: mixChannel(source.b, target.b),
  };
};

const createRoundedRegularPolygonShape = (attrs = {})=>{
  const {
    sides = 3,
    radius = 0,
    cornerRadius = 0,
    angleOffset = -Math.PI / 2,
    ...shapeAttrs
  } = attrs;

  return new Konva.Shape({
    ...shapeAttrs,
    sides,
    radius,
    cornerRadius,
    angleOffset,
    sceneFunc(context, shape){
      const clamp = (value, min, max)=>Math.min(Math.max(value, min), max);
      const resolvedSides = Math.max(3, Math.round(shape.getAttr("sides") ?? sides ?? 3));
      const resolvedRadius = Math.max(0, Number(shape.getAttr("radius") ?? radius ?? 0));
      const desiredCornerRadius = Math.max(0, Number(shape.getAttr("cornerRadius") ?? cornerRadius ?? 0));
      const resolvedAngleOffset = typeof shape.getAttr("angleOffset") === "number" ? shape.getAttr("angleOffset") : angleOffset;

      context.beginPath();

      if(resolvedRadius <= 0 || resolvedSides < 3){
        context.fillStrokeShape(shape);
        return;
      }

      const angleStep = (Math.PI * 2) / resolvedSides;
      const points = Array.from({ length: resolvedSides }, (_, idx)=>{
        const angle = resolvedAngleOffset + idx * angleStep;
        return {
          x: Math.cos(angle) * resolvedRadius,
          y: Math.sin(angle) * resolvedRadius,
        };
      });

      if(desiredCornerRadius <= 0){
        context.moveTo(points[0].x, points[0].y);
        for(let i = 1; i < points.length; i++){
          context.lineTo(points[i].x, points[i].y);
        }
        context.closePath();
        context.fillStrokeShape(shape);
        return;
      }

      const corners = points.map((point, idx)=>{
        const prev = points[(idx + points.length - 1) % points.length];
        const next = points[(idx + 1) % points.length];
        const prevVec = { x: point.x - prev.x, y: point.y - prev.y };
        const nextVec = { x: next.x - point.x, y: next.y - point.y };
        const prevLen = Math.hypot(prevVec.x, prevVec.y);
        const nextLen = Math.hypot(nextVec.x, nextVec.y);

        if(prevLen === 0 || nextLen === 0){
          return {
            entry: point,
            vertex: point,
            exit: point,
            radius: 0,
          };
        }

        const maxAllowedRadius = Math.min(prevLen, nextLen) / 2;
        const effectiveRadius = clamp(desiredCornerRadius, 0, maxAllowedRadius);

        if(effectiveRadius <= 0){
          return {
            entry: point,
            vertex: point,
            exit: point,
            radius: 0,
          };
        }

        const prevDir = { x: prevVec.x / prevLen, y: prevVec.y / prevLen };
        const nextDir = { x: nextVec.x / nextLen, y: nextVec.y / nextLen };

        const entry = {
          x: point.x - prevDir.x * effectiveRadius,
          y: point.y - prevDir.y * effectiveRadius,
        };
        const exit = {
          x: point.x + nextDir.x * effectiveRadius,
          y: point.y + nextDir.y * effectiveRadius,
        };

        return {
          entry,
          vertex: point,
          exit,
          radius: effectiveRadius,
        };
      });

      const start = corners[0];
      context.moveTo(start.entry.x, start.entry.y);

      for(let i = 0; i < corners.length; i++){
        const corner = corners[i];
        if(corner.radius > 0){
          context.lineTo(corner.entry.x, corner.entry.y);
          context.quadraticCurveTo(corner.vertex.x, corner.vertex.y, corner.exit.x, corner.exit.y);
        }else{
          context.lineTo(corner.vertex.x, corner.vertex.y);
        }
      }

      context.closePath();
      context.fillStrokeShape(shape);
    },
  });
};

export const getLuminance = ({ r, g, b })=>{
  const toLinear = (value)=>{
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  };
  const [lr, lg, lb] = [r, g, b].map(toLinear);
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
};

const buildStylesForColor = (color, options = {})=>{
  const rgb = hexToRgb(color);
  if(!rgb){
    const fallback = { r: 59, g: 130, b: 246 };
    const alpha = typeof options.alpha === "number" ? options.alpha : 0.2;
    return {
      baseColor: rgbToHex(fallback),
      trackColor: options.fallbackTrackColor ?? `rgba(${fallback.r}, ${fallback.g}, ${fallback.b}, ${alpha})`,
      rgb: fallback,
    };
  }
  const alpha = typeof options.alpha === "number" ? options.alpha : 0.2;
  return {
    baseColor: rgbToHex(rgb),
    trackColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`,
    rgb,
  };
};

export const ensureDynamicAccentClasses = (color)=>{
  if(!color){
    return null;
  }
  const key = safeColorKey(color);
  if(!key){
    return null;
  }
  if(dynamicAccentStyleRegistry.has(key)){
    return dynamicAccentStyleRegistry.get(key);
  }

  const { baseColor, trackColor, rgb } = buildStylesForColor(color);
  const luminance = getLuminance(rgb);
  const onAccentColor = luminance > 0.6 ? "#0f172a" : "#ffffff";
  const softTextColor = luminance > 0.6 ? "rgba(30,41,59,0.75)" : "rgba(255,255,255,0.75)";
  const textOnLightRgb = luminance > 0.7 ? mixRgb(rgb, { r: 15, g: 23, b: 42 }, 0.4) : rgb;
  const textOnLight = rgbToHex(textOnLightRgb);
  const hoverTarget = luminance > 0.6 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
  const hoverColor = rgbToHex(mixRgb(rgb, hoverTarget, 0.12));
  const borderColor = rgbToHex(mixRgb(rgb, hoverTarget, 0.28));

  const baseClass = `tw-dynamic-bg-${key}`;
  const fadedClass = `${baseClass}__faded`;
  const borderClass = `${baseClass}__border`;
  const textClass = `${baseClass}__text`;
  const softTextClass = `${baseClass}__text-soft`;
  const ringClass = `${baseClass}__ring`;
  const trackClass = `${baseClass}__track`;
  const indicatorClass = `${baseClass}__indicator`;
  const contrastClass = `${baseClass}__contrast`;

  if(typeof document !== "undefined"){
    const styleId = `style-${baseClass}`;
    if(!document.getElementById(styleId)){
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
.${baseClass} {
  background-color: ${baseColor} !important;
  color: ${onAccentColor} !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.${fadedClass} {
  background-color: ${baseColor}30 !important;
  color: ${onAccentColor} !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.hover\\\:${fadedClass}:hover {
  background-color: ${baseColor}20 !important;
  color: ${onAccentColor} !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.after\\\:${baseClass}::after {
    content:"";
  background-color: ${baseColor} !important;
  color: ${onAccentColor} !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.${baseClass}:hover {
  background-color: ${hoverColor} !important;
  color: ${onAccentColor} !important;
}
.${baseClass} svg {
  color: inherit !important;
}
.${borderClass} {
  border-color: ${borderColor} !important;
}
.hover\\\:${borderClass}:hover {
  border-color: ${borderColor} !important;
}
.hover\\\:${textClass}:hover {
  color: ${textOnLight} !important;
}
.${textClass} {
  color: ${textOnLight} !important;
}
.${softTextClass} {
  color: ${softTextColor} !important;
}
.${ringClass} {
  box-shadow: 0 0 0 2px ${trackColor} !important;
}
.${trackClass} {
  background-color: ${trackColor} !important;
}
.${indicatorClass} {
  background-color: ${baseColor} !important;
  color: ${onAccentColor} !important;
}
.${contrastClass} {
  color: ${onAccentColor} !important;
}
      `.trim();
      document.head.appendChild(style);
    }
  }

  const record = {
    base: baseClass,
    border: borderClass,
    faded: fadedClass,
    text: textClass,
    softText: softTextClass,
    ring: ringClass,
    track: trackClass,
    indicator: indicatorClass,
    contrast: contrastClass,
    color: baseColor,
    textOnAccent: onAccentColor,
    textOnLight,
    hoverColor,
    borderColor,
    trackColor,
  };

  dynamicAccentStyleRegistry.set(key, record);
  return record;
};

export {
  categoryColors,
  categoryColors_pastel,
  categoryColors2,
  categoryColors_old,
  categoryColors_MUTED,
  categoryColors_STD,
  tagColors,
  uflTheme,
  xploreTheme,
  darkNavyTheme,
  defaultTheme,
  themes,
};

export const themeColor = themeColorUtil;
export const mixHex = mixHexUtil;
export const mixHexWithWhite = mixHexWithWhiteUtil;

const defaultWidthByCategory = {
    "29_overview": 500,
    "29_grid": 80,
    34: 256,
    63: 256,
    82: 600,
    109: 800,
    138: 256,
    109: 480,
    122: 320,
    123: 320,
    124: 480,
    173: 480,
    174: 480,
    125: 320,
    140: "1920_fixed",
    149: 280,
    152: 320,
    154: 640
}

export const heatMapPalette = PrimitiveConfig.heatMapPalette


  function interpolateHexColors(startHex, endHex, steps) {
    // strip “#”, handle shorthand
    const normalize = hex => {
      hex = hex.replace(/^#/, '');
      if (hex.length === 3) {
        hex = hex.split('').map(ch => ch+ch).join('');
      }
      return hex;
    };
  
    const hexToRgb = hex => {
      const h = normalize(hex);
      return {
        r: parseInt(h.slice(0,2), 16),
        g: parseInt(h.slice(2,4), 16),
        b: parseInt(h.slice(4,6), 16),
      };
    };
  
    const rgbToHex = ({r, g, b}) =>
      '#' +
      [r, g, b]
        .map(v => v.toString(16).padStart(2, '0'))
        .join('');
  
    const c1 = hexToRgb(startHex);
    const c2 = hexToRgb(endHex);
  
    if (steps < 2) {
      return [ normalize(startHex).startsWith('#') ? startHex : `#${normalize(startHex)}` ];
    }
  
    const out = [];
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const r = Math.round(c1.r + (c2.r - c1.r) * t);
      const g = Math.round(c1.g + (c2.g - c1.g) * t);
      const b = Math.round(c1.b + (c2.b - c1.b) * t);
      out.push(rgbToHex({ r, g, b }));
    }
    return out;
  }

function registerRenderer( mappings, callback){
    for(const d of [mappings].flat()){
        let obj = typeMaps
        if( d.type === "categoryId" ){
            obj = categoryMaps
        }
        const ids = [d.id ?? "default"].flat()
        for( const id of ids){
            if( !obj[id]){
                obj[id] = {}
            }
            const configs = d.configs ?? ["default"]
            for( const c of [configs].flat()){
                if( obj[ id ]?.[c] ){
                    console.log(`Overwriting renderer for ${id} / ${c}`)
                }
                obj[ id ][c] = callback
            }
        }
    }

}

function renderWithWidget( primitive, options, mainRender){
    if( options.widgetConfig && options.config !== "widget"){
        const g = new Konva.Group({
            name: "view",
            x:options.x ?? 0,
            y:options.y ?? 0
        })

        let w, h
        const widget = RenderPrimitiveAsKonva( primitive, {config: "widget", data: options.widgetConfig, imageCallback: options.imageCallback, theme: options.theme})
        g.add( widget )
        w = widget.width()
        h = widget.height()

        if( options.widgetConfig.showItems ){
            const px = Math.max(options.x ?? 5, 5)
            const py = Math.max(options.y ?? 5, 5)
            
            const content = mainRender({...options, x: px, y: py, widgetConfig: undefined})
            const contentScale = Math.min(1, (w - px - px) / content.width() )
            content.scale({x:contentScale, y:contentScale})
            content.y(h)
            g.add(content)
            
            h = h + py +(content.height() * contentScale)
        }

        g.width( w )
        g.height( h )
        
        return g
    }
    return mainRender(options)
}

export function RenderSetAsKonva( primitive, list, options = {} ){
    if( !list ){
        return
    }
    let config = "set_" + (options.config || "default")
    let source =  list?.[0]
    let referenceId =  options.referenceId ?? source?.referenceId ?? source?.primitive?.referenceId
    let renderer = categoryMaps[referenceId]?.[config] ?? typeMaps[ source?.type ]?.[config]
    if( !renderer ){
        renderer = typeMaps[ "default" ]?.[config]
    }

    if( !renderer ){
        console.warn(`Cant find renderer for ${primitive.id} ${primitive.type} ${primitive.referenceId} / ${config}`)
        return
    }
    const themedOptions = { ...options, theme: options.theme ?? defaultTheme };
    return renderer(primitive, {list:list, ...themedOptions, config: options.config} )
}
export function RenderPrimitiveAsKonva( primitive, options = {} ){
    if( !primitive){
        return
    }
    let config = options.config || "default"
    let renderer = categoryMaps[primitive.referenceId]?.[config] ?? typeMaps[ primitive.type ]?.[config]  ?? typeMaps[ primitive.type ]?.["default"]
    if( !renderer ){
        renderer = typeMaps[ "default" ]?.[config]
    }
    if( !renderer ){
        console.warn(`Cant find renderer for ${primitive.id} ${primitive.type} ${primitive.referenceId} / ${config}`)
        const g = new Konva.Group({
            width: 200,
            height: 300
        })
        g.add( new Konva.Rect({
            width: 200,
            height: 300,
            fill: "#DEADBE"
        }))
        return g
    }

    const themedOptions = { ...options, theme: options.theme ?? defaultTheme };
    return renderWithWidget( primitive, themedOptions, (options)=>renderer(primitive, options))

}
registerRenderer( {type: "default", configs: "set_dials"}, (primitive, options = {})=>{
    const theme = options.theme ?? defaultTheme;
    const config = {width: 60, height: 30, padding: [2,2,2,2], ...(options.renderConfig ?? {})}
    if( !options.list ){
        return undefined
    }
    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height
    })
        const items = options.list

        const pad = options.inTable ? config.height / 2 : 0
        
        const w = config.width - config.padding[3] - config.padding[1]
        const h = (config.height - pad - config.padding[0] - config.padding[2])
        const r = new Konva.Rect({
            x: config.padding[3],
            y: config.padding[0] + (pad / 2),
            width: w,
            height: h,
            fill: themeColor(theme, 'surface', '#f9fafb'),
            name: "background"
        })
        g.add(r)

        const colorscheme = primitive.renderConfig?.colors ?? "green"
        let colors = {
            green: [undefined, "#bbf7d0", "#86efac", "#4ade80"],
            blue: [undefined, "#bfdbfe", "#93c5fd", "#3b82f6"],
        }[colorscheme] ?? [undefined, "#bbf7d0", "#86efac", "#4ade80"]

        if( items.length > 0 ){
            let showTitle = !options.inTable
            if( options.checkMap ){
                const score = items.map(d=>d.parentPrimitiveIds.map(d=>options.checkMap[d] ?? 0)).flat().reduce((a,c)=>a > c ? a : c, 0)

                if( showTitle ){
                    const text = new CustomText({
                        fontSize: 7,
                        text: primitive.title,
                        align:"center",
                        wrap: false,
                        ellipsis: true,
                        verticalAlign:"middle",
                        bgFill: themeColor(theme, 'overlay', '#f3f4f6'),
                        x: 0,
                        y: config.height - 9,
                        width: config.width,
                        height: 12,
                        refreshCallback: options.imageCallback
                    })
                    g.add(text)
                }
                const ah = h - (showTitle ? 10 : 0)
                    var arc1 = new Konva.Rect({
                        x: config.padding[3],
                        y: config.padding[0] + (pad / 2),
                        width: w,
                        height: ah,
                        fill: themeColor(theme, 'overlay', '#eee'),
                        stroke: themeColor(theme, 'border', '#ccc'),
                        strokeWidth: 0.5,
                    });
                  g.add(arc1)
                const p = w / 3
                const dx = p * 0.1
                for(let i = 0; i < score; i++){
                    var arc = new Konva.Rect({
                        x: config.padding[3] + (p * i) + dx,
                        y: config.padding[0] + dx + (pad / 2),
                        width: p - dx - dx,
                        height: ah - dx - dx,
                        fill: colors[i + 1],
                        stroke: '#ccc',
                        strokeWidth: 0.5,
                    });
                    g.add(arc)
                }
            }

        }

    if( options.getConfig){
        config.cachedNodes = g
        return config
    }else{
        if( options.cachedNodes ){
            options.cachedNodes.destroy()
        }
    }

    return g
})
registerRenderer( {type: "default", configs: "datatable_checktable"}, ({table, cell, renderOptions, ...options})=>{
    const config = {width: 60, height: 60, padding: [2,2,2,2]}
    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height
    })
        
        const w = config.width - config.padding[3] - config.padding[1]
        const h = config.height - config.padding[0] - config.padding[2]
        const r = new Konva.Rect({
            x: config.padding[3],
            y: config.padding[0],
            width: w,
            height: h,
            fill: '#f9fafb',
            name: "background"
        })
        g.add(r)

        const displayValue = resolveDisplayValue(cell, table)
        if( displayValue > 0 ){

                const cScale = Math.min(w,h * 0.75) / 500 
                const dim = 500 * cScale
                const points = [
                    100, 300, 200, 400, 400, 100
                ].map(d=>d * cScale)
                
                const polyline = new Konva.Line({
                    points: points,
                    x: config.padding[3] + ((w - dim) / 2),
                    y: config.padding[0] + ((h - dim) / 2),
                    stroke: "black",
                    strokeWidth: 2,
                    lineJoin: 'round',
                    lineCap: 'round',
                    closed: false
                });
                g.add(polyline)

        }

    if( options.getConfig){
        return config
    }

    return g
})
registerRenderer( {type: "default", configs: "set_totalValue"}, (primitive, options = {})=>{
    const config = {width: 130, height: 60, padding: [5,5,5,5], ...(options.renderConfig ?? {})}

    const renderWidth = (config.width - config.padding[3] - config.padding[1]) 
    const renderHeight = config.height - config.padding[0] - config.padding[2]

    if( options.getConfig){
        const years = parseInt(primitive.renderConfig?.range ?? "1") 
        const endDate = primitive.renderConfig?.end ?? new Date()
        const startDate = moment(endDate).subtract(years, "year")

        let sourceData = options.list.map(d=>(d.referenceParameters.allFundingRoundInfo ?? []).map(d=>({date: d.annouced, amount: d.amount}))).flat()
        console.log(`Got ${sourceData.length} total`)
        sourceData = sourceData.filter(d=>d.date <= endDate && d.date >= startDate)
        console.log(`Got ${sourceData.length} filtered`)
        const total = sourceData.reduce((a,d)=>a+ (d.amount ?? 0), 0)
        

        config.data = total
        return config
    }
    const total = options.data ?? 0
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height
    })
    const r = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: config.width - config.padding[3] - config.padding[1],
        height: renderHeight,
        fill: "white",
        name: "background"
    })
    const t = new CustomText({
        x: config.padding[3],
        y: config.padding[0],
        fontSize: 24,
        lineHeight: 1.5,
        text: roundCurrency( total ),
        align:"center",
        verticalAlign:"middle",
        fill: '#334155',
        wrap: false,
        width: renderWidth,
        refreshCallback: options.imageCallback
    })
    t.y (config.padding[0] + (renderHeight - t.height())/2 )
    g.add(r)
    g.add(t)

    return g
})

function getTextColor(hexColor) {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16) / 255;
    const g = parseInt(hexColor.slice(3, 5), 16) / 255;
    const b = parseInt(hexColor.slice(5, 7), 16) / 255;

    // Calculate luminance
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Return black or white text color based on luminance
    return luminance > 0.5 ? 'black' : 'white';
}

registerRenderer( {type: "categoryId", id: 29, configs: "set_investment_landscape"}, (primitive, options = {})=>{
    const config = {width: 320, height: 160, fontSize: 20, padding: [15,15,15,15], ...(options.renderOptions ?? {}), ...(options.renderConfig ?? {})}
    if( options.getConfig){
        return config
    }
    if( !options.list ){
        return undefined
    }
    const g = new Konva.Group({
        id: options.id,
        name:"inf_track primitive inf_keep",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        id: primitive.id
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        shadowBlur: 10,
        shadowOffset: { x: 3, y: 3 },
        shadowOpacity: 0.5,

        fill: "white",
        cornerRadius: 10,
        name: "background"
    })

    const pe_stages = ["Private Equity"]
    const vc_stages = ["Angel Round","Seed Round", "Series A", "Series B", "Series C", "Series D", "Series E", "Series F", "Series G", "Series H", "Series I", "Venture Round", "Pre-Seed"]
    const vcBacked = options.list.filter(d=>d.referenceParameters?.fundingRounds && d.referenceParameters?.fundingRounds.find(d=>vc_stages.includes(d)))
    const peBacked = options.list.filter(d=>d.referenceParameters?.fundingRounds && d.referenceParameters?.fundingRounds.find(d=>pe_stages.includes(d)))
    const sections = [
        {
            title: "Total",
            number: options.list.length,
            line: true
        },
        {
            title: "Raised <$1m",
            number: vcBacked.filter(d=>d.referenceParameters?.funding && d.referenceParameters?.funding < 1000000).length            
        },
        {
            title: "Raised $1m-$50m",
            number: vcBacked.filter(d=>d.referenceParameters?.funding >= 1000000 && d.referenceParameters?.funding < 50000000).length            
        },
        {
            title: "Raised >$50m",
            number: vcBacked.filter(d=>d.referenceParameters?.funding >= 5000000).length            
        },
        {
            title: "PE Backed",
            number: peBacked.length            
        }
    ]

    const sectionWidth = (config.width - config.padding[3] - config.padding[1]) / sections.length
    const sPadding = sectionWidth * 0.05

    let x = config.padding[3]
    for( const section of sections){
        const sg = new Konva.Group({
            name:"inf_track",
            x: x,
            y: config.padding[0],
            width: sectionWidth,
            height: config.height - config.padding[2] - config.padding[0]
        })
        g.add(sg)
        const t = new CustomText({
            x: sPadding,
            y: 10,
            width: sectionWidth - 2 * sPadding,
            height: config.fontSize * 2.5,
            align:"center",
            fontSize: config.fontSize * 2.5,
            text: `**${section.number}**`,
            fontFamily: "poppins",
            fontStyle: "bold",
            withMarkdown: true,
            fill: "black",
            refreshCallback: options.imageCallback
        })
        sg.add(t)
        const t2 = new CustomText({
            x: sPadding,
            y: 10 + (t.height() * 1.2),
            width: sectionWidth - 2 * sPadding,
            align:"center",
            fontSize: config.fontSize,
            text: section.title,
            fontFamily: "poppins",
            fontStyle: "bold",
            withMarkdown: true,
            fill: "black",
            wrap: true,
            refreshCallback: options.imageCallback
        })
        sg.add(t2)
        if( section.line){
            sg.add(new Konva.Line({
                points: [sectionWidth, 10, sectionWidth, sg.height() - 10],
                strokeWidth: 1,
                stroke: '#999'
            }))
        }
        x += sectionWidth
        

    }

    g.add(r)
    return g

})

registerRenderer( {type: "default", configs: "set_timeseries"}, (primitive, options = {})=>{
    const config = {width: 128, height: 80, padding: [5,5,5,5], fontSize: 8, ...(options.renderConfig ?? {}), ...(options.renderOptions ?? {})}
    if( !options.list ){
        return undefined
    }
    const alignScale = primitive.renderConfig?.align_scale ??"no"
    const doBoth = alignScale === "both"
    if( doBoth && options.getConfig){
        if( !options.renderConfig.width){
            options.config = options.config * 2
        }
    }

    let locateThreshold //= 0.15
    let location
    let showPercChange = false
    let showEndValue = true

    const renderWidth = (config.width - config.padding[3] - config.padding[1]) * (doBoth ? 0.45 : 1)
    const tHeight = config.height - config.padding[0] - config.padding[2]
    const renderHeight = tHeight - (config.show_x_label ? config.fontSize * 1.2 : 0) - (showEndValue || showPercChange ? config.fontSize * 2.2 : 0)
    let period = "month"

    if( options.getConfig){
        const years = parseInt(primitive.renderConfig?.range ?? "1") 
        const endDate = moment(primitive.renderConfig?.end ? new Date(primitive.renderConfig?.end) : new Date())
        const startDate = moment(endDate).subtract(years, "year")
        //const startDate = moment(endDate).subtract(1, "Q").toDate()
        config.data = {
            startDate: startDate.format("YYYY"),
            endDate: endDate.format("YYYY"),
            series: CollectionUtils.convertToTimesSeries( 
                                        options.list, 
                                        {
                                            dataset: primitive.renderConfig?.set ?? options.renderConfig?.viewConfig?.set,
                                            dateField: primitive.renderConfig?.dateField ?? options.renderConfig?.viewConfig?.dateField,
                                            field: primitive.renderConfig?.field ?? options.renderConfig?.viewConfig?.field,
                                            cumulative: false,
                                            startDate: startDate,
                                            endDate: endDate,
                                            period: period
                                        })}
        return config
    }
    const series = options.data.series
    

    if( locateThreshold ){
        let maximum = Math.max(...series)
        location = series.findIndex(d=>d >= (locateThreshold * maximum))
    }

    
    const g = new Konva.Group({
        id: options.id,
        name:"inf_track inf_keep primitive",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        id: primitive.id
    })
    const r = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: config.width - config.padding[3] - config.padding[1],
        height: tHeight,
        fill: "transparent",
        name: "background"
    })


    const palette = options.theme?.palette?.categoryColors ?? categoryColors
    let color = "#00bc7d" //primitive.renderConfig?.color ? palette[primitive.renderConfig?.color] : "#0ea5e9"

    if( options.renderConfig?.viewConfig?.scheme === "under_over" ){
        const cleaned = series.filter(d=>d)
        const first = cleaned[0]
        const last = cleaned[ cleaned.length - 1 ]
        color = "#000000"
        if( last > first ){
            color = "#22c55e"
        }else if( last < first ){
            color = "#dc2626"
        }
    }

    let fadeColor = mixHexWithWhite(color, 0.5)

    let offsetX = 0
    const len = series.length
    g.add(r)
    function renderPlot(alignScale){
        const maxValue = (alignScale ? (options.globalData?.maximumValue ?? 0) : series.reduce((a,c)=>c > a ? c : a, -Infinity)) + 1
        const minValue = (alignScale ? (options.globalData?.minimumValue ?? 0): series.reduce((a,c)=>c < a ? c : a, Infinity))- 1

        const range = maxValue - minValue
        
        const dx = renderWidth / (len - 1)
        const lineMargin = 0//renderHeight * 0.1
        const scale = (renderHeight - lineMargin - lineMargin) / range
        const points = series.flatMap((d,i)=>[offsetX + (i * dx), renderHeight - lineMargin - (scale * (d - minValue))] )

        const plotY = config.padding[0] + (showEndValue || showPercChange ? config.fontSize * 2.2 : 0)

        const l = new Konva.Line({
            x: config.padding[0],
            y: plotY,
            points: points,
            strokeWidth: 1,
            strokeScaleEnabled: false,
            stroke: color
        })
        g.add(l)

        if( location ){
            let locationX = (location * dx)

            const r = new Konva.Rect({
                x: offsetX + config.padding[1] + locationX,
                y: config.padding[0],
                width: renderWidth - locationX,
                height: renderHeight,
                strokeWidth: 1,
                strokeScaleEnabled: true,
                stroke: fadeColor
            })
            g.add(r)
            const r2 = new Konva.Rect({
                x: offsetX + config.padding[0] + locationX - (dx / 2),
                y: config.padding[1],
                width: dx,
                height: renderHeight,
                strokeWidth: 1,
                strokeScaleEnabled: true,
                fill: color
            })
            g.add(r2)
            const count = len -location
            const px = offsetX + config.padding[1] + locationX - dx
            const t = new CustomText({
                x: px,
                y: config.padding[0] + (renderHeight / 2),
                fontSize: config.fontSize,
                lineHeight: 1,
                text: `${count} ${period}${count === 1 ? "" : "s"}`,
                verticalAlign:"middle",
                fill: color,
                refreshCallback: options.imageCallback
            })
            t.x( px - t.width())
            g.add(t)

        }
        if( showPercChange ){
            const withValues = series.filter(d=>d)
            const first = withValues[0]
            const last = withValues[withValues.length - 1]
            const perc = (first && last) ? ((last - first) / first * 100).toFixed(2) : "-"

            const t = new CustomText({
                x: 0,
                y: config.padding[0],
                fontSize: config.fontSize,
                lineHeight: 1,
                text: `${perc}%`,
                verticalAlign:"top",
                fill: color,
                refreshCallback: options.imageCallback
            })
            t.x( renderWidth - t.width())
            g.add(t)

        }
        if( showEndValue ){
            const withValues = series.filter(d=>d)
            const last = withValues[withValues.length - 1]

            {

                const t = new CustomText({
                    x: 0,
                    y: config.padding[0],
                    fontSize: config.fontSize * 2,
                    lineHeight: 1,
                    text: roundCurrency(last),
                    verticalAlign:"top",
                    fill: color,
                    refreshCallback: options.imageCallback
                })
                t.x( config.padding[3] + renderWidth - t.width())
                g.add(t)
            }
            {
                const t = new CustomText({
                    x: config.padding[3],
                    y: config.padding[0],
                    fontSize: config.fontSize * 2,
                    lineHeight: 1,
                    text: roundCurrency(withValues[0]),
                    verticalAlign:"top",
                    fill: color,
                    refreshCallback: options.imageCallback
                })
                g.add(t)
            }
                
        }
        if( config.show_x_label){
            const axisY = renderHeight + plotY + config.fontSize * 0.3
            const st = new CustomText({
                x: config.padding[3],
                y: axisY,
                fontSize: config.fontSize,
                lineHeight: 1,
                text: options.data.startDate,
                verticalAlign:"top",
                fill: "#999",
                refreshCallback: options.imageCallback
            })
            g.add(st)

            const et = new CustomText({
                x: 0,
                y: axisY,
                fontSize: config.fontSize,
                lineHeight: 1,
                text: options.data.endDate,
                verticalAlign:"top",
                fill: '#999',
                refreshCallback: options.imageCallback
            })
            et.x( config.padding[3] + renderWidth - et.width())
            g.add(et)/*
            g.add(new Konva.Line({
                points: [
                    config.padding[3], renderHeight + plotY + config.fontSize * 0.15,
                    config.padding[3] + renderWidth, renderHeight + plotY + config.fontSize * 0.15,
                ],
                stroke: "#999",
                strokeWidth: 1
            }))*/

        }
        offsetX += renderWidth + (renderWidth / 4.5)

    }
    
    if( series.length > 0){
        if(doBoth || alignScale === "yes"){
            renderPlot(true)
        }
        if(doBoth || alignScale === "no"){
            renderPlot(false)
        }
    }



    return g
})

registerRenderer( {type: "default", configs: "datatable_grid"}, function renderFunc({table, cell, renderOptions, childRenderOptions, stageOptions, ...options}){
    const items = cell.items
    const config = {columns: 1, padding: [0, 0, 0, 0], spacing: [10,10], ...options}
    const defaultWidth = defaultWidthByCategory[items[0]?.referenceId + "_" + options.config] ?? defaultWidthByCategory[items[0]?.referenceId] ?? 200
    const itemWidth = typeof(defaultWidth) == "string" && defaultWidth.endsWith("_fixed") ? parseInt(defaultWidth.slice(0,-6)) : renderOptions.width ?? config.itemSize ?? defaultWidth
    const itemHeight = renderOptions.height
    const maxHeight = renderOptions.maxHeight
    const theme = options.theme
    let itemCount = items.length

    const referenceIds = items.map(d=>d.referenceId).filter((d,i,a)=>a.indexOf(d) === i)
    if( referenceIds.length > 1){
        console.log(`Multiple types in list, selecting first`)
    }
    
    let cellContentLimit = {
            "result": 50,
            "evidence": 150
        }[items[0]?.type] ?? 150
        
        cellContentLimit = {
            29: 63
        }[referenceIds[0]] ?? cellContentLimit

    let cellShowExtra
    if( cellContentLimit ){
        if( items.length > cellContentLimit){
            //if( options.expand && options.expand.includes([column.idx, row.idx].filter(d=>d).join("-"))){
            if( options.expand && options.expand.includes(cell.id)){
                cellShowExtra = -1
            }else{
                cellShowExtra = items.length - cellContentLimit
                itemCount = cellContentLimit
            }
        }
    }
    
    config.columns = Math.max( renderOptions.columns, options.minColumns ?? 1, 1)
    if( !renderOptions.columns ){
        if( options.width ){
            config.columns = (options.width -  config.padding[1] - config.padding[3] + config.spacing[0]) / (itemWidth + config.spacing[0]) 
        }else{
            config.columns = Math.max(1, Math.floor( Math.sqrt( itemCount ) ))
        }
    }

    let width = (config.columns * itemWidth) + ((config.columns - 1) * config.spacing[0])
    config.width =  width + config.padding[1] + config.padding[3]

    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width
    })
    const bgRect = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: width,
        name: "background",
        fill: options.includeBackground ? themeColor(theme, "cell") : undefined,
        hoverFill: themeColor(theme, "hoverFill"),
        hoverStroke: themeColor(theme, "hoverStroke")
    });
    g.add(bgRect);
    let x = config.padding[3];
    let y = config.padding[0];
    let idx = 0;
    let rows = 0;
    let thisRow = [];

    const columnYs = new Array(config.columns).fill(y);
    const skipForOverflow = new Array(config.columns).fill(false);

    for (let dIdx = 0; dIdx < itemCount; dIdx++) {
        if (idx === 0){
            rows++;
            if( dIdx > 0){
                columnYs.forEach((d, i)=>columnYs[i] = d +  config.spacing[0])
            }
        }

        if (config.maxHeight && skipForOverflow[idx]) continue;

        const data = items[dIdx];
        let node;

        if (data) {
            const rConfig = (options.config === "grid" ? "default" : options.config) ?? "default";
            node = RenderPrimitiveAsKonva(data, {
                config: rConfig,
                x: x,
                y: columnYs[idx],
                itemIdx: dIdx,
                onClick: options.primitiveClick,
                width: itemWidth,
                height: itemHeight,
                placeholder: stageOptions.placeholder !== false,
                imageCallback: stageOptions.imageCallback,
                utils: options.utils,
                data: options.data,
                sectionConfig: config.sectionConfig,
                theme: options.theme,
                renderOptions: childRenderOptions,
                ...options.extras
            });
        } else {
            node = addExtraNode(config, options, x, y, itemWidth);
        }

        g.add(node);
        thisRow.push(node);

        const nodeHeight = node.attrs.height;
        const nextY = columnYs[idx] + nodeHeight;

        if (config.maxHeight && nextY > config.maxHeight) {
            skipForOverflow[idx] = true;
            //node.destroy();
            //thisRow.pop();
            node.clipHeight( config.maxHeight - columnYs[idx] )
            columnYs[idx] = config.maxHeight
        } else {
            columnYs[idx] = nextY;
        }

        x += itemWidth + config.spacing[1];
        idx++;

        if (idx === config.columns) {
            idx = 0;
            x = config.padding[3] //+ config.spacing[1];

            if (config.alignHeight && thisRow.length) {
                const maxY = Math.max(...columnYs);
                const maxHeight = thisRow
                                .map(d => d.find('.item_background')[0]?.height() ?? 0)
                                .reduce((a, c) => (a > c ? a : c), 0);

                columnYs.fill(maxY);
                for (const d of thisRow) {
                    d.height(maxHeight);
                    const bg = d.find('.item_background')[0];
                    if (bg) bg.height(maxHeight);
                }
            }

            y = columnYs[idx];
            thisRow = [];
        }
    }
    if( cellShowExtra  ){
        idx = config.columns - 1
        x = (itemWidth + config.spacing[1]) * idx
        columnYs[idx] += config.spacing[0]
        const node = addExtraNode({showExtra: cellShowExtra}, options, x, columnYs[idx], itemWidth);
        columnYs[idx] += node.height() + config.spacing[0]
        g.add(node);
    }

    const maxY = Math.max(...columnYs);
    let newHeight = maxY + config.padding[2];

    if( options.cellHeight ){
        newHeight = options.cellHeight
    }
    
    bgRect.height(newHeight - config.padding[0] - config.padding[2]);
    g.height(newHeight);
    config.height = newHeight;

    g.attrs.resizeInfo = {
        //padding: config.padding,
        //spacing: config.spacing,
        widthPadding: config.padding[1] + config.padding[3] + ((config.columns - 1) * config.spacing[1]),
        columns: config.columns,
        rows,
    };

    Object.assign(config, config);
    if (options.getConfig) return config;
    return g
})
registerRenderer( {type: "default", configs: "set_grid"}, (primitive, options = {})=>{
    console.warn("DEPRECATED SET_GRID")
    const config = {itemSize: 256, columns: 5, spacing: [8,12], itemPadding: [10,12,10,8], padding: [5,5,5,5], ...(options.renderConfig ?? {}), ...(options.renderOptions ?? {})}
    if( config.minWidth ){
        config.itemSize = config.minWidth
    }
    if( !options.list ){
        return undefined
    }
    let items = options.list
    let itemCount = items.length + (config.showExtra ? 1 : 0)

    if( config.minColumns) {
        config.columns = Math.max(config.minColumns, config.columns)
    }
    const fullWidth = config.itemSize + config.itemPadding[1] + config.itemPadding[3]

    const calcWidth = ((Math.max(1, config.columns)  + 1) * config.spacing[1]) + (Math.max(1, config.columns) * fullWidth) + config.padding[1] + config.padding[3]

    if( calcWidth > config.width && !config.columns){
        config.columns = Math.ceil(Math.max(1, ((config.width - config.padding[1] + config.padding[3]) - config.spacing[1]) / (config.itemSize + config.spacing[1])))
    }else{
        config.width = calcWidth
    }

    config.rows = Math.ceil( itemCount / config.columns )

    const width = config.width 
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: width
    })
    let x = config.padding[3] + config.spacing[1]

    const r = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: config.width - config.padding[3] - config.padding[1],
        height: 0,//config.height - config.padding[0] - config.padding[2],
        fill: '#f9fafb',
        name: "background"
    })
    g.add(r)

    let col = 0
    
    let ypos = new Array( config.columns ?? 1).fill(config.padding[0] + config.spacing[0])
    const {columns, count, ...relayOptions} = options.renderOptions ?? {}

    let skipCol = new Array(ypos.length).fill(false)
    for( let dIdx = 0; dIdx < itemCount; dIdx++){
        const d = items[dIdx]
        let y = ypos[col]
        let node
        let lastNode

        const skip = skipCol[col] || (options.renderOptions.height &&  y >= options.renderOptions.height )
        if( !skip ){
            if( d ){
                if( options.cachedNodes ){
                    node = options.cachedNodes.children.find(d2=>d2.attrs.id === d.id)
                    if( node ){
                        node.remove()
                        node.attrs.placeholder = options.placeholder !== false
                        if(node.children){
                            node.children.forEach(d=>{
                                if(d.className === "CustomImage"|| d.className === "CustomText"){
                                    d.attrs.refreshCallback = options.imageCallback
                                }
                                
                            })
                        }
                    }
                }
                if( !node ){
                    node = RenderPrimitiveAsKonva( d, {
                        config: "default", 
                        x: x, 
                        y: y, 
                        onClick: options.primitiveClick,
                        width: fullWidth, 
                        padding: config.itemPadding, 
                        placeholder: options.placeholder !== false,
                        renderOptions: relayOptions,
                        imageCallback: options.imageCallback
                    })
                }
                if( options.renderOptions.height && (y + node.height()) > options.renderOptions.height ){
                    const delta = options.renderOptions.height - node.y()
                    node.height( delta)
                    node.clipHeight(delta)
                    skipCol[col] = true
                }
            }else{
                col = config.columns - 1
                x = (config.padding[3] + config.spacing[1]) + ((fullWidth + config.spacing[1]) * col)
                y = ypos[col]
                
                node = addExtraNode( config, options, x, y, fullWidth)
            }
        
            if( node ){
                g.add(node)
                ypos[col] += config.spacing[0] + (node.height() ?? 0)
            }
        }
        

        x += fullWidth + config.spacing[1]
        col++
        if( col === config.columns){
            col = 0
            x = config.padding[3] + config.spacing[1]
        }
        lastNode = node
    }
    
    const height = Math.max(ypos.reduce((a,c)=>c > a ? c : a, 0) + config.padding[0] + config.padding[2], config.minHeight ?? 0)
    config.height = height + config.spacing[0]

    if( options.getConfig){
        return config
    }else{
        if( options.cachedNodes ){
            options.cachedNodes.destroy()
        }
    }


    r.height( height )
    g.height( config.height)

    return g
})
registerRenderer( {type: "categoryId", id: 118, configs: "default"}, (primitive, options = {})=>{
    const config = {showId: true, idSize: 14, width: 256, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 
    const theme = options.theme ?? defaultTheme;

    const g = new Konva.Group({
        name: "view",
        x:options.x ?? 0,
        y:options.y ?? 0
    })
    if (options.renderOptions?.renderContextId) {
        g.attrs.renderContextId = options.renderOptions.renderContextId;
    }
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: 1,
        height: 1,
        fill: '#f9fafb',
        name: "background"
    })
    g.add(r)
    
    let partials = options.partials ?? []
    const rowUnion = partials.map(d=>d.rowExtents).flat()
    let rowLabels = rowUnion.map(d=>d.label ?? "").filter((d,i,a)=>a.indexOf(d)===i).sort()
    let mainPartial = partials[0]

    let primColumns = []
    let skipMainPrim = false
    if(primitive.referenceParameters?.fields ){
        let fieldList = primitive.referenceParameters?.fields?.split(",").map(d=>d.trim())
        if( fieldList.includes("SKIP")){
            fieldList = fieldList.filter(d=>d!=="SKIP")
            skipMainPrim = true
            partials = partials.slice(1)
        }
        
        const row = mainPartial ? mainPartial.rowExtents?.find(d=>(d.label ?? "") === rowLabels[0]) : undefined
        let subList = row ? mainPartial.list.filter((item)=>(Array.isArray(item.row) ? item.row.includes( row.idx ) : item.row === row.idx)).map(d=>d.primitive) : []
        if( subList[0]){
            const category = subList[0].metadata

            primColumns = fieldList.map(src=>{
                let [d,w] = src.split("|")

                d = d.trim()
                if(d==="title"){
                    return {
                        label: "Name",
                        field: d,
                        width: w ? parseInt(w) : 200,
                        type: "string"
                    }
                }else if(d==="icon"){
                    return {
                        label: "",
                        field: d,
                        width: w ? parseInt(w) : 60,
                        type: "icon"
                    }
                }
                const param = category.parameters[d]
                    let width = w ? parseInt(w) : (param?.type === "string" || param?.type === "long_string" ? 200 : 100)
                    let height
                    if( d === "description"){
                        width = 600
                        height = 80
                    }
                    return {
                        label: param?.title ?? d,
                        field: d,
                        width,
                        height,
                        type: param?.type
                    }
            }).filter(d=>d)
            console.log(`FIELDS`, primColumns)
        }
    }
    
    const baseRenderConfig = {
                placeholder: options.placeholder !== false, 
                imageCallback: options.imageCallback,
    }

    const cells = []
    let rIdx = 0
    const columns = []
    for(const thisLabel of rowLabels){
        let cIdx = 0
        const row = mainPartial.rowExtents.find(d=>(d.label ?? "") === thisLabel)
        let subList = row ? mainPartial.list.filter((item)=>(Array.isArray(item.row) ? item.row.includes( row.idx ) : item.row === row.idx)).map(d=>d.primitive) : []
        for( const d of primColumns){
            const cell = {
                cIdx,
                rIdx,
                orIdx: rIdx,
                primitive: subList[0],
                present: row !== undefined,
            }
            cells.push(cell)
            cIdx++
        }
        for(const partial of partials){
            const row = partial.rowExtents.find(d=>(d.label ?? "") === thisLabel)
            let subList = row ? partial.list.filter((item)=>(Array.isArray(item.row) ? item.row.includes( row.idx ) : item.row === row.idx)).map(d=>d.primitive) : []
            const cell = {
                cIdx,
                rIdx,
                orIdx: rIdx,
                list: subList,
                listLength: subList.length,
                present: row !== undefined,
            }
            cells.push(cell)
            cIdx++
        }
        rIdx++
        cIdx = 0
    }
    rIdx = 0
    const mainstore = MainStore()
    const partialConfig = []
    const pcLength = primColumns.length
    const pScores = []

    let cIdx = pcLength
    for(const partial of partials){
        let configName = partial.viewConfig?.renderType ?? "grid" 
        partialConfig[cIdx - pcLength] = {}
        if( configName === "checktable"){
            const checkFor = [
                partial.axis.column,
                partial.axis.row,
            ].filter(d=>d.type == "category").map(d=>mainstore.primitive(d.primitiveId)).filter(d=>d.referenceId === PrimitiveConfig.Constants.EVALUATOR)                
            const scoreMap = checkFor.reduce((a,d)=>{
                d.primitives.allItems.forEach(d=>{
                    if( d.title === "clearly"){
                        a[d.id] = 3
                    }else if( d.title === "likely"){
                        a[d.id] = 2
                    }else if( d.title === "possibly"){
                        a[d.id] = 1
                    }
                })
                return a
            }, {})
            partialConfig[cIdx - pcLength].checkMap = scoreMap
            rIdx = 0
            for(const thisLabel of rowLabels){
                const cell = cells.find(d=>d.cIdx === cIdx && d.rIdx === rIdx)
                let score = cell.list.map(d=>d.parentPrimitiveIds.map(d=>partialConfig[cIdx - pcLength].checkMap[d] ?? 0)).flat().reduce((a,c)=>a > c ? a : c, 0)
                pScores[rIdx] = (pScores[rIdx] ?? 0) + (score * score)
                rIdx++
            }
        }else if( configName === "dials"){
            const checkFor = [
                partial.axis.column,
                partial.axis.row,
            ].filter(d=>d.type == "category").map(d=>mainstore.primitive(d.primitiveId)).filter(d=>d.referenceId === PrimitiveConfig.Constants.SCORE)                
            const scoreMap = checkFor.reduce((a,d)=>{
                d.primitives.allItems.forEach(d=>{
                    if( d.title === "high"){
                        a[d.id] = 3
                    }else if( d.title === "medium"){
                        a[d.id] = 2
                    }else if( d.title === "low"){
                        a[d.id] = 1
                    }
                })
                return a
            }, {})
            partialConfig[cIdx - pcLength].checkMap = scoreMap
            rIdx = 0
        }
        cIdx++
    }

    if( pScores.length > 0){
        const newOrder = rowLabels.map((d,i)=>[d,i]).sort((a,b)=>pScores[b[1]]-pScores[a[1]])
        newOrder.forEach((d,idx)=>{
            cells.filter(c=>c.orIdx === d[1]).forEach(c=>c.rIdx = idx)
            rowLabels[idx] = d[0]
        })
    }

    rIdx = 0
    for(const thisLabel of rowLabels){
        let cIdx = 0
        for( const d of primColumns){
            const config ={
                    ...baseRenderConfig,
                    config: "field",
                    field: d.field,
                    type: d.type,
                    width: d.width,
                    showId: false,  
                    inTable: true,
                    padding:[5,8,5,8],
                    renderConfig:{
                    }                
            }
            const cell = cells.find(d=>d.cIdx === cIdx && d.rIdx === rIdx)
            const updateConfig = RenderPrimitiveAsKonva( cell.primitive, {...config, getConfig: true})    
            cell.config = {
                ...config,
                ...updateConfig,
                getConfig: false
            }
            cIdx++
        }
        for(const partial of partials){
            const range = rowLabels.map((_,i)=>cells.filter(d=>d.cIdx === cIdx && d.rIdx === i).map(d=>d.listLength))
            const cell = cells.find(d=>d.cIdx === cIdx && d.rIdx === rIdx)
            let minWidth =  defaultWidthByCategory[cell.list[0]?.referenceId] ?? 100
            let preferredWith = primitive.columns?.[cIdx]?.width
            
            let configName = partial.viewConfig?.renderType ?? "grid" 
            const asCounts = partial.viewConfig?.showAsCounts
            const asChecks = partial.viewConfig?.parameters?.showAsCheck ?? configName == "checktable"
            if( asCounts ){
                configName = "heatmap"
            }
            if( asChecks ){
                configName = "checktable"
            }
            
            
            const config ={
                    ...baseRenderConfig,
                    partial: partial.primitive,
                    config: configName,
                    range: [Math.min(...range), Math.max(...range)],
                    ...partialConfig[cIdx - pcLength],
                    renderConfig:{
                        showId: false,  
                        inTable: true,
                        padding:[5,8,5,8],
                        asChecks,
                        minWidth: minWidth,
                        columns: 1,
                    }                
            }
            if( preferredWith ){
                config.renderConfig.width = preferredWith 
            }
            if(primitive.widths?.[cIdx] ){
                config.renderConfig.width = 80//primitive.widths[cIdx]
            }
            let updateConfig 
            if( cell.list.length === 1 && cell.list[0].type === "summary"){
                   const configForSummary = configName === "grid" ? "default" : configName 
                   updateConfig = RenderPrimitiveAsKonva( cell.list[0], {
                       ...config,
                       config: configForSummary,
                       ...config.renderConfig,
                       getConfig: true
                    })
                    updateConfig.config = configForSummary
            }else{
                updateConfig = RenderSetAsKonva( partial.primitive, cell.list, {...config, getConfig: true})    
            }
            cell.config = {
                ...config,
                ...updateConfig
            }
                
            cIdx++
        }
        rIdx++
    }
    console.log(`Got ${cells.length} cells`)
    const columnWidths = [primColumns,partials].flat().map((_,i)=>Math.max(0,...cells.filter(d=>d.cIdx === i).map(d=>d.config.width)))
    const rowHeights = rowLabels.map((_,i)=>Math.max(0,...cells.filter(d=>d.rIdx === i).map(d=>d.config.height ?? 0)))
    
    for(const cell of cells){
        cell.config.renderConfig.width = columnWidths[cell.cIdx]
        cell.config.renderConfig.height = rowHeights[cell.rIdx]
    }
    let x = 0, y = 80, h = 0 

    rowLabels.forEach((_,rIdx)=>{
        primColumns.forEach((column,cIdx)=>{
            const cell = cells.find(d=>d.cIdx === cIdx && d.rIdx === rIdx)
            if( rIdx === 0){
                g.add( addHeader(column.label, {x: x, y: 0, width: cell.config.renderConfig.width, height: 80, imageCallback: options.imageCallback, class: "column_header"}))
            }
            if( cell.primitive ){

                const renderedCell = RenderPrimitiveAsKonva( cell.primitive, 
                    {
                        ...cell.config, 
                        ...cell.config.renderConfig,
                        id: `${cell.cIdx}-${cell.rIdx}`, 
                    })    
                    renderedCell.x(x)
                    renderedCell.y(y)
                    g.add(renderedCell)
            }
            x += columnWidths[cIdx]
        })
        partials.forEach((partial,pIdx)=>{
            let cIdx = pIdx + primColumns.length
            const cell = cells.find(d=>d.cIdx === cIdx && d.rIdx === rIdx)
            if( rIdx === 0){
                g.add( addHeader(partial.primitive.title, {x: x, y: 0, width: cell.config.renderConfig.width, height: 80, imageCallback: options.imageCallback, class: "column_header"}))
            }
            let renderedCell
            if( cell.list.length === 1 && cell.list[0].type === "summary"){
                renderedCell = RenderPrimitiveAsKonva( cell.list[0], {
                        ...cell.config,
                        ...config.renderConfig,
                        ...cell.config.renderConfig,
                        getConfig: false
                    })
            }else{
                renderedCell = RenderSetAsKonva( partial.primitive, cell.list, 
                    {
                        ...cell.config, 
                        id: `${cell.cIdx}-${cell.rIdx}`, 
                    })    
            }
            renderedCell.x(x)
            renderedCell.y(y)
            g.add(renderedCell)
            x += columnWidths[cIdx]
        })
        x = 0
        y += rowHeights[rIdx]
        h += rowHeights[rIdx]
    })

    
    const tw = g.find(()=>true).map(d=>d.x() + d.width()).reduce((a,c)=>c > a ? c : a, 0)
    const th = g.find(()=>true).map(d=>d.y() + d.height()).reduce((a,c)=>c > a ? c : a, 0)
    g.width( tw)
    g.height( th)
    r.width( tw)
    r.height( th)

    return g
})
function addHeader( title, options ={}){
    let config = {x: 0, y: 0, width: 100, fontSize: 12, height:25, padding: [2,2,2,2], textPadding:[2,2,2,2], ...options}
    const group = new Konva.Group({
        name: `inf_track ${options.class ?? "row_header"}`,
        x: config.x,
        y: config.y,
        width: config.width,
        height: config.height
    }) 
    const bg = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: config.width - config.padding[3] - config.padding[1],
        height: config.height - config.padding[0] - config.padding[2] ,
        fill:'#f3f4f6'
    })
    group.add(bg)
    const t = new CustomText({
        //fontFamily: "system-ui",
        fontSize: config.fontSize,
        text: title,
        wrap: true,
        align:"center",
        bgFill:"#f3f4f6",
        verticalAlign:"middle",
        x: config.padding[3] + config.textPadding[3],
        y: config.padding[0] + config.textPadding[0],
        width: config.width - (config.padding[3] + config.textPadding[3] + config.padding[1] + config.textPadding[1]),
        refreshCallback: options.imageCallback
    })
    t.y( (config.height - t.height()) / 2)
    group.add(t)
    return group

}

registerRenderer( {type: "categoryId", id: 138, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemWidth: 600, minColumns: 1, spacing: [2,2], itemPadding: [10,10,10,10], ...(options.renderConfig ?? {})}
    return baseGridRender(options, config)
})
registerRenderer( {type: "categoryId", id: 109, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemWidth: 600, minColumns: 1, spacing: [2,2], itemPadding: [10,10,10,10], ...(options.renderConfig ?? {})}
    return baseGridRender(options, config)
})

// dsl_transformer summary primitive (referenceId 182): the crystallized DSL
// output. We pull the DSL JSON off the parent query's merged config (it lives
// on the flow-template in the flow case, so getConfig walks the chain) and
// pair it with the structured_summary that the crystallizer wrote. The slide
// is rendered at a fixed 16:9 ratio derived from the requested width.
//
// Registered for both "default" (direct render) and "full" (the config the
// board sets when this primitive is rendered inside an element/page wrapper).
registerRenderer( {type: "categoryId", id: 182, configs: ["default", "full"]}, (primitive, options = {})=>{
    // Render the slide at a fixed 1920x1080 reference resolution and scale
    // the resulting group to fit the requested width/height. Scaling rather
    // than re-laying-out at the target width keeps the slide visually
    // consistent at any frame size — small frames just show a scaled-down
    // version of the same composition, instead of cramped fonts.
    const SLIDE_W = 1920
    const SLIDE_H = 1080
    const config = {
        width: SLIDE_W,
        padding: [0, 0, 0, 0],
        ...options
    }
    if( options.getConfig ){
        const height = options.height ?? Math.round(config.width * 9 / 16)
        return { ...config, height }
    }

    const targetWidth = options.width ?? SLIDE_W
    const targetHeight = options.height ?? Math.round(targetWidth * 9 / 16)
    // Fit the 16:9 reference slide inside the target box; uniform scale so
    // the aspect ratio is preserved even if the frame isn't 16:9.
    const scale = Math.min(targetWidth / SLIDE_W, targetHeight / SLIDE_H)
    const theme = options.theme ?? defaultTheme

    const g = new Konva.Group({
        name: "viz_dsl",
        x: options.x ?? 0,
        y: options.y ?? 0,
        width: targetWidth,
        height: targetHeight
    })
    if (options.renderOptions?.renderContextId) {
        g.attrs.renderContextId = options.renderOptions.renderContextId
    }
    // Attach source primitive IDs and summary primitive ID to the outer group
    // so that the viz_block click handler can resolve provenance without
    // needing a BoardViewer renderContext entry.
    const sourceMap = primitive?.referenceParameters?.structured_summary_source_map ?? {};
    const allSourceIds = [...new Set(Object.values(sourceMap).flat().map(id => `${id}`))];
    g.setAttr("_vizSourceIds", allSourceIds);
    g.setAttr("_vizSummaryId", primitive?.id);

    // Crystallized data was written by the dsl_transformer rebuild_summary
    // branch onto the summary primitive itself.
    const crystallizedData = primitive?.referenceParameters?.structured_summary ?? {}

    // DSL JSON lives on the parent query's merged config (flow-template
    // configParent in flow scenarios). Fall back to direct referenceParameters
    // for non-flow cases.
    const parent = primitive?.origin
    const rawPrompt = parent?.getConfig?.prompt ?? parent?.referenceParameters?.prompt
    let dsl = null
    if (rawPrompt) {
        try {
            dsl = typeof rawPrompt === "string" ? JSON.parse(rawPrompt) : rawPrompt
        } catch (err) {
            console.warn(`[render 182] DSL JSON parse failed for primitive ${primitive?.id}: ${err.message}`)
        }
    }

    if (!dsl || typeof dsl !== "object") {
        // Nothing to render — leave a small placeholder rect so the empty slot
        // is still visible/clickable rather than a zero-sized group.
        g.add(new Konva.Rect({
            x: 0, y: 0, width: targetWidth, height: targetHeight,
            fill: themeColor(theme, "surface", "#fafafa"),
            stroke: "#ddd", strokeWidth: 1
        }))
        g.add(new CustomText({
            x: 16, y: 16, width: targetWidth - 32,
            text: "Missing DSL on parent query",
            fontSize: 14, fill: "#888"
        }))
        return g
    }

    // Route to the appropriate renderer based on DSL kind.
    // "document" → DocDslRenderer (multi-page); "slide" or default → VizDslRenderer.
    const isDocument = dsl.kind === "document";
    const renderContextId = options.renderOptions?.renderContextId;
    const provenance = primitive?.referenceParameters?.structured_summary_provenance ?? {};

    let rendered;
    if (isDocument) {
      // Document renderer produces multiple pages; select the page
      // specified in renderConfig._documentPage (defaults to first page).
      const result = renderDocDslDocument(dsl, crystallizedData, {
        width: SLIDE_W,
        height: SLIDE_H,
        imageCallback: options.imageCallback,
        theme,
        debug: options.debug ?? false,
        renderContextId,
        provenance
      });
      const pages = result.pages || [];
      if (pages.length > 0) {
        const pageIndex = Math.max(0, Math.min(pages.length - 1, (options.renderOptions?._documentPage ?? 1) - 1));
        rendered = pages[pageIndex];
        rendered.scale({ x: scale, y: scale });
        rendered.x(Math.max(0, (targetWidth - SLIDE_W * scale) / 2));
        rendered.y(Math.max(0, (targetHeight - SLIDE_H * scale) / 2));
        g.add(rendered);
        // Store all pages for export
        g.setAttr("_docPages", pages);
        g.setAttr("_dsl", dsl);
      }
    } else {
      const slide = renderVizDslSlide(dsl, crystallizedData, {
        width: SLIDE_W,
        height: SLIDE_H,
        imageCallback: options.imageCallback,
        theme,
        debug: options.debug ?? false,
        renderContextId,
        provenance,
        _vizSourceIds: allSourceIds,
        _vizSummaryId: primitive?.id
      });
      slide.scale({ x: scale, y: scale });
      slide.x(Math.max(0, (targetWidth - SLIDE_W * scale) / 2));
      slide.y(Math.max(0, (targetHeight - SLIDE_H * scale) / 2));
      g.add(slide);
    }
    return g
})
registerRenderer( {type: "categoryId", id: 100, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemWidth: 600, itemHeight: 1800, spacing: [2,2], itemPadding: [10,10,10,10], ...(options.renderConfig ?? {}), columns: 3}
    return baseGridRender(options, config)
})
registerRenderer( {type: "categoryId", id: 29, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemSize: 60, columns: 3, minColumns: options.renderOptions?.int_columns ?? 3, spacing: [8,8], itemPadding: [2,2,2,2], padding: [2,2,2,2], ...(options.renderConfig ?? {})}
    return baseGridRender(options, config)
})
registerRenderer( {type: "categoryId", id: 101, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemWidth: 360, spacing: [2,2], itemPadding: [2,2,2,2], ...(options.renderConfig ?? {})}
    return baseGridRender(options, config)
})
registerRenderer( {type: "categoryId", id: 95, configs: "set_grid"},(primitive, options = {})=>{
    const config = {itemWidth:555, spacing: [20,20], itemPadding: [2,2,2,2], ...(options.renderConfig ?? {}), height: undefined}

    return baseGridRender({...options}, config)
})
function baseGridRender(options, config) {
  if (!options?.list) return undefined;

  // normalize (inline, no helpers)
  const cfg = {
    ...config,
    padding: (Array.isArray(config?.padding) && config.padding.length === 4) ? config.padding
             : (Array.isArray(options?.padding) && options.padding.length === 4) ? options.padding
             : [0, 0, 0, 0],
    spacing: (Array.isArray(config?.spacing) && config.spacing.length === 2) ? config.spacing : [0, 0],
    sectionConfig: config?.sectionConfig ?? {},
  };

  const items = options.list;
  const itemCount = items.length + (cfg.showExtra ? 1 : 0);

  const heightDefined = Boolean(cfg.itemHeight || cfg.itemSize);
  const fullHeight = heightDefined ? (cfg.itemHeight ?? cfg.itemSize) : undefined;
  const fullWidth  = (cfg.columns === 1 ? (cfg.minWidth ?? cfg.itemWidth) : (cfg.itemWidth ?? cfg.itemSize));

  // min columns if minWidth present
  if (cfg.minWidth) {
    const inner = (cfg.minWidth - cfg.padding[1] - cfg.padding[3]) - cfg.spacing[1];
    const denom = (fullWidth + cfg.spacing[1]);
    const byMinWidth = Math.floor(inner / denom);
    const minColumns = Math.max(cfg.minColumns ?? 1, 1, byMinWidth);
    cfg.columns = Math.max(minColumns, cfg.columns ?? 1);
  } else if (cfg.minColumns) {
    cfg.columns = Math.max(cfg.minColumns, cfg.columns ?? 1);
  }

  // columns/width coherence
  if (!cfg.columns) {
    if (!cfg.width) cfg.columns = 1;
    const inner = (cfg.width - cfg.padding[1] - cfg.padding[3]) - cfg.spacing[1];
    cfg.columns = Math.max(1, Math.floor(inner / (fullWidth + cfg.spacing[1])));
  }

  const calcWidth = ((cfg.columns - 1) * cfg.spacing[1]) + (cfg.columns * fullWidth) + cfg.padding[1] + cfg.padding[3];
  if (!cfg.width || calcWidth > cfg.width) {
    cfg.width = calcWidth;
    const inner = (cfg.width - cfg.padding[1] - cfg.padding[3]) - cfg.spacing[1];
    cfg.columns = Math.max(1, Math.floor(inner / (fullWidth + cfg.spacing[1])));
  }

  cfg.rows = Math.ceil(itemCount / cfg.columns);

  if (heightDefined) {
    cfg.height ||= ((cfg.rows + 1) * cfg.spacing[0]) + (cfg.rows * fullHeight) + cfg.padding[0] + cfg.padding[2];
  }

  if (options.getConfig && cfg.height){
    return cfg
  }

  // ----- render -----
  const g = new Konva.Group({
    id: options.id,
    name: "cell inf_track",
    x: (options.x ?? 0),
    y: (options.y ?? 0),
    width: cfg.width,
    height: cfg.height,
  });

  const bgRect = new Konva.Rect({
    x: cfg.padding[3],
    y: cfg.padding[0],
    width: cfg.width - cfg.padding[3] - cfg.padding[1],
    height: cfg.height ? (cfg.height - cfg.padding[0] - cfg.padding[2]) : undefined,
    name: "background",
    fill: '#f9fafb'
  });
  g.add(bgRect);

  let x = cfg.padding[3];
  let y = cfg.padding[0];
  let idx = 0;
  let rows = 0;
  let thisRow = [];

  const columnYs = new Array(cfg.columns).fill(y);
  const skipForOverflow = new Array(cfg.columns).fill(false);

  for (let dIdx = 0; dIdx < itemCount; dIdx++) {
    if (idx === 0) rows++;

    if (cfg.maxHeight && skipForOverflow[idx]) continue;

    const data = items[dIdx];
    let node;

    if (data) {
      const rConfig = (options.config === "grid" ? "default" : options.config) ?? "default";
      node = RenderPrimitiveAsKonva(data, {
        config: rConfig,
        x: x,
        y: columnYs[idx],
        onClick: options.primitiveClick,
        height: fullHeight,
        width: fullWidth,
        placeholder: options.placeholder !== false,
        imageCallback: options.imageCallback,
        utils: options.utils,
        data: options.data,
        sectionConfig: cfg.sectionConfig,
        ...options.extras
      });
    } else {
      node = addExtraNode(cfg, options, x, y, fullWidth);
    }

    g.add(node);
    thisRow.push(node);

    const nodeHeight = fullHeight ?? node.attrs.height;
    const nextY = columnYs[idx] + nodeHeight + cfg.spacing[0];

    if (cfg.maxHeight && nextY > cfg.maxHeight) {
      skipForOverflow[idx] = true;
      node.destroy();
      thisRow.pop();
    } else {
      columnYs[idx] = nextY;
    }

    x += fullWidth + cfg.spacing[1];
    idx++;

    if (idx === cfg.columns) {
      idx = 0;
      x = cfg.padding[3] + cfg.spacing[1];

      if (cfg.alignHeight && thisRow.length) {
        const maxY = Math.max(...columnYs);
        const maxHeight = thisRow
          .map(d => d.find('.item_background')[0]?.height() ?? 0)
          .reduce((a, c) => (a > c ? a : c), 0);

        columnYs.fill(maxY);
        for (const d of thisRow) {
          d.height(maxHeight);
          const bg = d.find('.item_background')[0];
          if (bg) bg.height(maxHeight);
        }
      }

      y = columnYs[idx];
      if (heightDefined && (y + fullHeight) > (cfg.height ?? Infinity)) break;

      thisRow = [];
    }
  }

  const maxY = Math.max(...columnYs);
  const newHeight = maxY + cfg.padding[2];
  bgRect.height(newHeight - cfg.padding[0] - cfg.padding[2]);
  g.height(newHeight);
  cfg.height = newHeight;

  g.attrs.resizeInfo = {
    padding: cfg.padding,
    spacing: [cfg.spacing[1] + cfg.spacing[1], cfg.spacing[0] + cfg.spacing[0]],
    columns: cfg.columns,
    rows,
  };

  Object.assign(config, cfg);
  if (options.getConfig) return cfg;
  return g;
}
function addExtraNode(config, options, x,y, fullWidth){
    const number = `${config.showExtra}`
    const check = config.showExtra >= 0 ? number.length > 4 ? number : "more" : `Show less`
    const fullLabel = config.showExtra >= 0 ? `**${number}**\nmore`: `Show less`
    let size = fullWidth * 5
    let minSize = fullWidth * 0.2
    const t = new CustomText({
        x: 0,
        y: 10,
        fontSize: size,
        lineHeight: config.showExtra >= 0 ? 0.7 : 1.2,
        text: fullLabel,
        align:"center",
        withMarkdown: true,
        fill: '#334155',
        showPlaceHolder: false,
        wrap: true,
        bgFill: 'transparent',
        width: fullWidth,
        refreshCallback: options.imageCallback
    })
    while( (t.measureSize(check).width > fullWidth * 0.3) && size > minSize){
        size = size > 20 ? size -= (size * 0.1) : size - 0.25
        t.fontSize( size )
    }

    const thisHeight = t.height() + 20
    let node = new Konva.Group({
        id: options.id,
        x: x,
        y: y,
        width: fullWidth,
        height: thisHeight,
        onClick: options.onClick,
        name:"inf_track widget show_extra"
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: fullWidth,
        height: thisHeight,
        cornerRadius: 4,
        fill: '#d2d2d2',
        hoverFill: '#a2a2a2',
    })
    node.add(r)
    node.add(t)

    return node
}
registerRenderer( {type: "default", configs: "set_export_finances"}, (primitive, options = {})=>{
    const config = {width: 612, spacing: 10,/*height: 1400,count: 4,*/ itemPadding: [2,2,2,2], padding: [10,10,10,10], ...(options.renderConfig ?? {})}
    if( primitive.renderConfig?.count){
        config.count = primitive.renderConfig?.count
    }
    const width = config.width 
    const height = config.count ? undefined : config.height 
    
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: width,
        height: height,
    })
    const r = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: width - config.padding[3] - config.padding[1],
        height: height - config.padding[0] - config.padding[2],
        fill: '#f9fafb',
        name: "background"
    })
    g.add(r)
    let x = config.padding[3]
    let y = config.padding[0]

    let items = options.list
    const maxScale = items.map(d=>d.referenceParameters.funding).reduce((a,c)=>c > a ? c : a, 0)

    let rWidth = 0
    let renderList = config.count ? items.slice(0, config.count) : items
    for( const d of renderList ){
        const node = RenderPrimitiveAsKonva( d, {
            config: "export_finances", 
            x: x, 
            y: y, 
            width: width - config.itemPadding[1] - config.itemPadding[3], 
            imageCallback: options.imageCallback,
            renderConfig: primitive.renderConfig,
            padding: config.itemPadding, imageCallback: options.imageCallback})
        
        g.add(node)
        rWidth = Math.max(rWidth, node.width())
        y += node.height() + config.spacing
    }
    g.width( rWidth + config.padding[1])
    g.height( y + config.padding[2])

    if( options.getConfig){
        config.width = g.width()
        config.height = g.height()
        return config
    }

    return g
})
registerRenderer( {type: "default", configs: "set_overview"}, (primitive, options = {})=>{
    return renderBaselineOverviewSet( primitive, options)
})
registerRenderer( {type: "categoryId", id: 34, configs: "set_overview"}, (primitive, options = {})=>{
    return renderBaselineOverviewSet( primitive, {...options, renderConfig: {...(options.renderConfig ?? {}), width: 600}})
})
function renderBaselineOverviewSet(primitive, options){
    const config = {width: 300, spacing: 10, itemPadding: [2,2,2,2], padding: [10,10,10,10], ...(options.renderConfig ?? {}), ...(options.renderOptions ?? {})}
    if( primitive.renderConfig?.count){
        config.count = primitive.renderConfig?.count
    }
    const width = config.width 
    const height = config.count ? undefined : config.height 
    
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: width,
        height: height,
    })
    const r = new Konva.Rect({
        x: config.padding[3],
        y: config.padding[0],
        width: width - config.padding[3] - config.padding[1],
        height: height ? height - config.padding[0] - config.padding[2] : 0,
        fill: 'transparent',
        name: "background"
    })
    g.add(r)
    let x = 0
    let y = config.padding[0]

    let rankBy = primitive.renderConfig?.rank 
    let items = options.list
    if( rankBy){
        if( rankBy === "title"){
            items = items.sort((a,b)=>(a.title ?? "").localeCompare(b.title ?? ""))
        }else{
            items = items.filter(d=>d.referenceParameters?.[rankBy]).sort((a,b)=>b.referenceParameters[rankBy] - a.referenceParameters[rankBy])
        }

    }

    let renderList = config.count ? items.slice(0, config.count) : items
    for( const d of renderList ){
        const node = RenderPrimitiveAsKonva( d, {
            config: "overview", 
            x: x, 
            y: y, 
            width: width - config.itemPadding[1] - config.itemPadding[3], 
            imageCallback: options.imageCallback,
            renderOptions: options.renderOptions,
            theme: options.renderOptions?.theme,
            padding: config.itemPadding, imageCallback: options.imageCallback})
        
        g.add(node)
        y += node.height() + config.spacing
        if( y >= config.height ){
            break
        }
    }
    g.height( y + config.padding[2])

    if( options.getConfig){
        config.height = g.height()
        return config
    }

    return g
}
registerRenderer( {type: "default",  configs: "overview"}, (primitive, options = {})=>{
    
    const config = {width: 300, padding: [10,10,10,10], fontSize: 10, leftSize: 150, maxScale: 100, parameter: "funding", ...options}
    if( options.getConfig){
        return config
    }

    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.height ? (config.height - config.padding[0] - config.padding[2]) :undefined
    let ox =  config.padding[3]
    let oy =  config.padding[0]


    const g = new Konva.Group({
        id: primitive.id,
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        name:"inf_track primitive "
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            height: config.height,
            cornerRadius: 2,
            fill: 'white',
        })
        g.add(r)

        let tx = ox 
        const title = new CustomText({
            fontSize: config.fontSize - 2,
            fontStyle:"bold",
            text: primitive.title,
            y: oy + config.padding[0],
            x: tx,
            width: availableWidth - tx,
            height: 16,
            wrap: false,
            ellipsis: true,
            refreshCallback: options.imageCallback
        })
        g.add(title);
        
        const t = new CustomText({
            x: tx,
            y: config.padding[0] + oy + 16,
            fontSize: config.fontSize,
            lineHeight: 1.5,
            withMarkdown: true,
            text: primitive.referenceParameters?.description?.slice(0,150) ?? primitive.referenceParameters?.summary ?? "",
            fill: '#334155',
            wrap: true,
            refreshCallback: options.imageCallback,
            ellipsis: true,
            width: availableWidth - tx,
            height: availableHeight ? availableHeight - (config.padding[0] +oy+16) : undefined,
        })
        g.add(t)
        //const h = Math.max( t.height() + t.y(), (config.itemSize ?? 0) + oy) + config.padding[2]
        const h = t.height() + t.y()
        r.height(h)
        g.height(h)


    }
    return g

})
registerRenderer( {type: "categoryId", id: [58], configs: "default"}, (primitive, options = {})=>{
    const config = {
        ...options,
        width: options?.width ?? 600,
        height: options?.height ?? 80,
        padding: options?.padding ?? [2, 2, 2, 2],
        fontSize: options?.fontSize ?? 16
    };
    if (options.getConfig) {
        return config
    }

    const g = new Konva.Group({
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        name:"inf_track primitive",
        id: primitive.id
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: 'white',
        name:"background"
    })
    g.add(r)

    let ox =  config.padding[3]
    let oy =  config.padding[0]

    

    const logo = imageHelper( primitive.referenceParameters?.imageUrl, {
        x: ox,
        y: oy,
        size: config.height - config.padding[0] - config.padding[2],
        center: true,
        imageCallback: options.imageCallback,
        placeholder: options.placeholder !== false,
        maxScale: 1,
        scaleRatio: 2
    })
    g.add( logo )

    const roleInCompany = primitive.referenceParameters.experience?.filter(d=>d.company_name === primitive.referenceParameters.company).sort((a,b)=>b.date_from_year - a.date_from_year).at(0)?.position_title
    const titleText = primitive.title + (roleInCompany ? ` - ${roleInCompany}` : "")
    const headline = primitive.referenceParameters?.headline

    let tx = ox + logo.width() + config.padding[1] + config.padding[3]
    let y = oy + config.padding[0] + (config.fontSize / 2)
    const title = new CustomText({
        fontSize: config.fontSize,
        fontStyle:"bold",
        text: titleText,
        y,
        x: tx,
        linkUrl: primitive.referenceParameters?.url,
        width: config.width - tx - config.padding[1],
        height: config.fontSize,
        wrap: false,
        ellipsis: true,
        refreshCallback: options.imageCallback
    })
    g.add(title);
    y += title.height() * 1.2
    if( headline ){
        const sub = new CustomText({
            fontSize: config.fontSize - 2,
            fontStyle:"bold",
            text: headline,
            y,
            x: tx,
            withMarkdown: true,
            width: config.width - tx - config.padding[1],
            url: primitive.referenceParameters?.url,
            linkUrl: primitive.referenceParameters?.url,
            height: config.fontSize,
            wrap: false,
            ellipsis: true,
            refreshCallback: options.imageCallback
        })
        g.add(sub);
        y += sub.height()
    }
    const remaining = config.height - y
    if( remaining > 0){

        const sub = new CustomText({
            fontSize: config.fontSize - 2,
            fontStyle:"light",
            withMarkdown: true,
            text: primitive.referenceParameters?.summary,
            y: y,
            x: tx,
            width: config.width - tx - config.padding[1],
            url: primitive.referenceParameters?.url,
            linkUrl: primitive.referenceParameters?.url,
            height: remaining,
            wrap: true,
            ellipsis: true,
            refreshCallback: options.imageCallback
        })
        g.add(sub);
    }

    return g

})
registerRenderer( {type: "categoryId", id: [34, 78], configs: "overview"}, (primitive, options = {})=>{
    const config = {
        ...options,
        width: options?.width ?? 600,
        height: options?.height ?? 40,
        padding: options?.padding ?? [2, 2, 2, 2],
        fontSize: options?.fontSize ?? 12
    };
    if (options.getConfig) {
        return config
    }

    const g = new Konva.Group({
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        name:"inf_track primitive",
        id: primitive.id
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: 'white',
        name:"background"
    })
    g.add(r)

    let ox =  config.padding[3]
    let oy =  config.padding[0]

    

    const imgUrl = primitive.referenceParameters?.hasImg ? `/api/image/${primitive.id}` + (primitive.imageCount ? `?${primitive.imageCount}` : "") : `/api/preview?url=${encodeURIComponent(primitive.referenceParameters?.url)}`
    const logo = imageHelper( imgUrl, {
        x: ox,
        y: oy,
        size: config.height - config.padding[0] - config.padding[2],
        center: true,
        imageCallback: options.imageCallback,
        placeholder: options.placeholder !== false,
        maxScale: 1,
        scaleRatio: 2
    })
    g.add( logo )

    let tx = ox + config.height
    const title = new CustomText({
        fontSize: config.fontSize,
        fontStyle:"bold",
        text: primitive.title,
        y: oy + config.padding[0] + (config.fontSize / 2),
        x: tx,
        linkUrl: primitive.referenceParameters?.url,
        width: config.width - tx - config.padding[1],
        height: config.fontSize,
        wrap: false,
        ellipsis: true,
        refreshCallback: options.imageCallback
    })
    const sub = new CustomText({
        fontSize: config.fontSize - 2,
        fontStyle:"light",
        text: primitive.referenceParameters?.url,
        y: oy + config.padding[0] + (config.fontSize / 2) + title.height(),
        x: tx,
        width: config.width - tx - config.padding[1],
        url: primitive.referenceParameters?.url,
        linkUrl: primitive.referenceParameters?.url,
        height: config.fontSize,
        wrap: false,
        ellipsis: true,
        refreshCallback: options.imageCallback
    })

    g.add(title);
    g.add(sub);
    return g

})
registerRenderer( {type: "categoryId", id: 44, configs: "overview"}, (primitive, options = {})=>{
    const config = {width: 300, itemSize: 60, padding: [10,10,10,10], fontSize: 10, leftSize: 150, maxScale: 100, ...options}
    if( options.getConfig){
        return config
    }

    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.height ? (config.height - config.padding[0] - config.padding[2]) :undefined
    let ox =  config.padding[3]
    let oy =  config.padding[0]


    const g = new Konva.Group({
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        name:"inf_track primitive"
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            height: config.height,
            cornerRadius: 2,
            fill: 'white',
        })
        g.add(r)

        const logo = imageHelper( `/api/image/${primitive.id}` + (primitive.imageCount ? `?${primitive.imageCount}` : ""), {
            x: ox,
            y: oy,
            size: config.itemSize,
            center: true,
            imageCallback: options.imageCallback,
            placeholder: options.placeholder !== false,
            maxScale: 1,
            scaleRatio: 2
        })
        g.add( logo )


        let tx = ox + config.itemSize + (config.itemSize / 5)
        const title = new CustomText({
            fontSize: config.fontSize - 2,
            fontStyle:"bold",
            text: primitive.title,
            y: oy + config.padding[0],
            x: tx,
            width: availableWidth - tx,
            height: 16,
            wrap: false,
            ellipsis: true,
            refreshCallback: options.imageCallback
        })
        g.add(title);

        let overview = primitive.referenceParameters?.description ?? ""

        if(overview.length > 200){
            let lines = overview.split(/(?<!\d)\.(?!\d)|\n/).map(d=>d.trim())
            overview = ""
            do{
                overview += lines.shift() + " "

            }while(lines[0] && ((overview.length + lines[0].length) < 200))
        }
        
        const t = new CustomText({
            x: tx,
            y: config.padding[0] + oy + 16,
            fontSize: config.fontSize,
            lineHeight: 1.5,
            text: overview.trim(),
            fill: '#334155',
            wrap: true,
            refreshCallback: options.imageCallback,
            ellipsis: true,
            width: availableWidth - tx,
            height: availableHeight ? availableHeight - (config.padding[0] +oy+16) : undefined,
        })
        g.add(t)
        if( !availableHeight){
            const h = Math.max( t.height() + t.y(), config.itemSize + oy) + config.padding[2]
            r.height(h)
            g.height(h)
        }


    }
    return g

})
registerRenderer( {type: "categoryId", id: 29, configs: "overview"}, (primitive, options = {})=>{
    let showDescription = options.renderOptions?.showDescription
    const config = {width: showDescription ? 300 : 500, itemSize: 60, padding: [10,10,10,10], fontSize: showDescription ? 10 : 28, leftSize: 150, maxScale: 100, parameter: "funding", ...options}
    if( options.getConfig){
        return config
    }

    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.height ? (config.height - config.padding[0] - config.padding[2]) :undefined
    let ox =  config.padding[3]
    let oy =  config.padding[0]

    const theme = options.theme ?? defaultTheme;
    const resolvedFontFamily = options.fontFamily ?? theme?.fontFamily ?? "Arial";
    const resolvedFontColor = themeColor(theme, 'primary', '#000000', options.text_color)
    const resovledBackground = themeColor(theme, 'background', '#ffffff')
    const placeholderFill = mixHex( resolvedFontColor, resovledBackground)

    const g = new Konva.Group({
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
        name:"inf_track primitive",
        id: primitive.id
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            height: config.height,
            fill: themeColor(theme, "surface"),
        })
        g.add(r)

        const logo = imageHelper( `/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`, {
            x: ox,
            y: oy,
            size: config.itemSize,
            center: true,
            imageCallback: options.imageCallback,
            placeholder: options.placeholder !== false,
            maxScale: 1,
            scaleRatio: 2
        })
        g.add( logo )


        let tx = ox + config.itemSize + (config.itemSize / 5)
        const title = new CustomText({
            fontSize: config.fontSize - 2,
            fontStyle:"bold",
            //text: (!config.parameter || config.parameter === "none") ? primitive.title : `${primitive.title} (${roundCurrency(primitive?.referenceParameters[config.parameter] ?? 0)})`,
            text: primitive.title ,
            y: oy + config.padding[0],
            x: tx,
            fontFamily: resolvedFontFamily,
            fill: resolvedFontColor,
            placeholderFill,
            width: availableWidth - tx,
            height: 16,
            wrap: false,
            ellipsis: true,
            refreshCallback: options.imageCallback
        })
        g.add(title);
        if( !availableHeight){
            const h = Math.max( title.height() + title.y(), config.itemSize + oy) + config.padding[2]
            r.height(h)
            g.height(h)
        }
        if( showDescription ){
            let overview = primitive.referenceParameters?.description ?? ""
            
            if(overview.length > 200){
                let lines = overview.split(/(?<!\d)\.(?!\d)|\n/).map(d=>d.trim())
                overview = ""
                do{
                    overview += lines.shift() + " "
                    
                }while(lines[0] && ((overview.length + lines[0].length) < 200))
                }
                
                const t = new CustomText({
                            x: tx,
                            y: config.padding[0] + oy + 16,
                            fontSize: config.fontSize,
                            lineHeight: 1.5,
                    text: overview.trim(),
                    fontFamily: resolvedFontFamily,
                    fill: resolvedFontColor,
                    placeholderFill,
                    wrap: true,
                    refreshCallback: options.imageCallback,
                    ellipsis: true,
                    width: availableWidth - tx,
                    height: availableHeight ? availableHeight - (config.padding[0] +oy+16) : undefined,
                })
            g.add(t)
            if( !availableHeight){
                const h = Math.max( t.height() + t.y(), config.itemSize + oy) + config.padding[2]
                r.height(h)
                g.height(h)
            }
        }else{
            title.y( (r.height() - title.height() )/ 2)
        }
    }
    return g

})
registerRenderer( {type: "default", configs: "datatable_ranking"}, ({table, cell, renderOptions, ...options})=>{
    const config = {width: 200, height: 200, itemSize: 30, itemPadding: [2,2,2,2], padding: [10,10,10,10], ...(options.renderConfig ?? {})}

    if( options.cellHeight ){
        config.height = options.cellHeight
    }else if( options.maxHeight ){
        config.height = options.maxHeight
    }

    let items = cell.items

    const defaultWidth = defaultWidthByCategory[items[0]?.referenceId + "_" + options.config] ?? defaultWidthByCategory[items[0]?.referenceId] ?? 200
    config.width = typeof(defaultWidth) == "string" && defaultWidth.endsWith("_fixed") ? parseInt(defaultWidth.slice(0,-6)) : renderOptions.width ?? defaultWidth
    
    if( options.getConfig){
        return config
    }
    const width = config.width 
    const height = config.height 
    const theme = options.theme
    
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: width,
        height: height,
    })
    const bgRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: width,
        name: "background",
        fill: themeColor(theme, "surface"),
        hoverFill: themeColor(theme, "hoverFill"),
        hoverStroke: themeColor(theme, "hoverStroke")
    });
    g.add(bgRect)

    let x = 0
    let y = config.padding[0]
    const fullHeight = config.itemSize + config.itemPadding[0] + config.itemPadding[2]

    
    let rankBy = renderOptions?.rank ?? "title" 
    const rankKey = rankBy === "raised" ? "funding" : rankBy
    if( rankBy){
        if( rankBy === "title"){
            items = items.sort((a,b)=>(a.title ?? "").localeCompare(b.title ?? ""))
        }else{
            items = items.filter(d=>d.referenceParameters?.[rankKey]).sort((a,b)=>b.referenceParameters[rankKey] - a.referenceParameters[rankKey])
        }

    }
    const maxScale = items.map(d=>d.referenceParameters?.[rankKey]).reduce((a,c)=>c > a ? c : a, 0)

    

    for( const d of items ){
        const node = RenderPrimitiveAsKonva( d, {
            config: "ranking", 
            maxScale,
            x: x, 
            y: y, 
            height: fullHeight, 
            width: width - config.itemPadding[1] - config.itemPadding[3], 
            placeholder: options.placeholder !== false,
            parameter: rankKey,
            currency: rankKey === "funding" ? "$" : false,
            padding: config.itemPadding, 
            imageCallback: options.stageOptions.imageCallback,
            theme: options.theme
            })
        if( node ){
            g.add(node)
        }
        y += fullHeight
        if( (y + fullHeight) > height){
            break
        }
    }
    bgRect.height( config.height )

    return g
})
registerRenderer( {type: "categoryId", id: [29,78], configs: "set_ranking"}, (primitive, options = {})=>{
    const config = {width: 200, height: 200, itemSize: 30, itemPadding: [2,2,2,2], padding: [10,10,10,10], ...(options.renderConfig ?? {})}
    if( options.getConfig){
        return config
    }
    const width = config.width 
    const height = config.height 
    
    
    const g = new Konva.Group({
        id: primitive.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: width,
        height: height,
    })
    let x = 0
    let y = config.padding[0]
    const fullHeight = config.itemSize + config.itemPadding[0] + config.itemPadding[2]

    let items = options.list
    
    let rankBy = options.renderOptions?.rank ?? "title" 
    const rankKey = rankBy === "raised" ? "funding" : rankBy
    if( rankBy){
        if( rankBy === "title"){
            items = items.sort((a,b)=>(a.title ?? "").localeCompare(b.title ?? ""))
        }else{
            items = items.filter(d=>d.referenceParameters?.[rankKey]).sort((a,b)=>b.referenceParameters[rankKey] - a.referenceParameters[rankKey])
        }

    }
    const maxScale = items.map(d=>d.referenceParameters?.[rankKey]).reduce((a,c)=>c > a ? c : a, 0)

    

    for( const d of items ){
        const node = RenderPrimitiveAsKonva( d, {
            config: "ranking", 
            maxScale,
            x: x, 
            y: y, 
            height: fullHeight, 
            width: width - config.itemPadding[1] - config.itemPadding[3], 
            placeholder: options.placeholder !== false,
            parameter: rankKey,
            currency: rankKey === "funding" ? "$" : false,
            padding: config.itemPadding, imageCallback: options.imageCallback})
        if( node ){
            g.add(node)
        }
        y += fullHeight
        if( (y + fullHeight) > height){
            break
        }
    }

    return g
})

registerRenderer( {type: "categoryId", id: [29,78], configs: "ranking"}, (primitive, options = {})=>{
    const config = {width: 300, height: 30, itemSize: 25, padding: [10,10,10,10], fontSize: 12, leftSize: 150, maxScale: 100, parameter: "funding", ...options}
    if( options.getConfig){
        return config
    }

    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.height - config.padding[0] - config.padding[2]
    let ox = 0
    let oy = 0

    const theme = options.theme ?? defaultTheme
    const resolvedFontFamily = theme?.fontFamily 
    const resovledBackground = themeColor(theme, 'background', '#ffffff')
    const mainColor = themeColor(theme, 'primary', '#000000', options.text_color)
    const dataColor = themeColor(theme, 'secondary', '#000000', options.text_color)


    const g = new Konva.Group({
        id: primitive.id,
        width: config.width,
        height: config.height,
        x: (options.x ?? 0) + config.padding[3],
        y: (options.y ?? 0) + config.padding[0],
        name: 'primitive clickable'
    })
    if( g ){

        let url
        if( primitive.referenceParameters?.hasImg ){
            url = `/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`
        }else{
            if( primitive.referenceParameters?.url ){
                let domain = getRegisteredDomain( primitive.referenceParameters?.url) 
                if(domain){
                    url = `/api/companyLogo?name=${primitive.title}&domain=${domain}` 
                }else{
                    url = `/api/companyLogo?name=${primitive.title}` 
                }

            }
        }

        if( url ){
            const logo = imageHelper( url, {
                x: ox,
                y: oy,
                size: config.itemSize,
                center: true,
                name: 'primitive',
                imageCallback: options.imageCallback,
                placeholder: options.placeholder !== false
            })
            g.add( logo )
        }

        let tx = url ? ox + config.itemSize + (config.itemSize / 5) : ox
        const title = new Konva.Text({
            fontSize: config.fontSize,
            text: primitive.title,
            fontFamily: resolvedFontFamily,
            fill: mainColor,
            placeholderFill: mixHex( mainColor, resovledBackground, 0.7),
            y: oy + config.padding[0] + (config.itemSize - config.fontSize) / 2,
            x: tx,
            width: config.leftSize - tx,
            height: 12,
            wrap: false,
            ellipsis: true
        })
        g.add(title);
        
        const rhs = config.leftSize
        const rightSize = availableWidth - rhs
        const amountSize = 80
        const barSize = rightSize - amountSize

        const scale = (primitive?.referenceParameters[config.parameter] ??0 ) / config.maxScale 
        const thisBar = Math.min(Math.max(0, scale), 1) * barSize

        const bar = new Konva.Rect({
            y: oy + config.padding[0],
            x: rhs,
            width: thisBar,
            height: availableHeight,
            fill: "#00bc7d"            
        })
        g.add(bar);
        const value = primitive?.referenceParameters[config.parameter] ?? 0
        const amount = new Konva.Text({
            fontSize: config.fontSize * 0.8,
            fontFamily: resolvedFontFamily,
            fill: dataColor,
            placeholderFill: mixHex( dataColor, resovledBackground, 0.7),
            text: config.currency ? roundCurrency(value) : formatNumber(value),
            y: oy + config.padding[0] + (config.itemSize - config.fontSize) / 2,
            x: rhs + thisBar + 5,
            width: amountSize - 10,
            height: 12,
            wrap: false,
            ellipsis: true
        })
        g.add(amount);


    }
    return g

})

registerRenderer( {type: "categoryId", id: 109, configs: "dials"}, function renderFunc(primitive, options = {}){
    return typeMaps[ "default" ]["set_dials"](options.partial, {...options, list:[primitive]})
})
registerRenderer( {type: "categoryId", id: 109, configs: "checktable"}, function renderFunc(primitive, options = {}){
    return typeMaps[ "default" ]["set_checktable"](options.partial, {...options, list:[primitive]})
})

function parseTimelineDateValue(rawValue){
    if( !rawValue ){
        return null
    }
    if( rawValue instanceof Date ){
        const time = rawValue.getTime()
        return Number.isFinite(time) ? time : null
    }
    if( typeof rawValue === "number" ){
        return Number.isFinite(rawValue) ? rawValue : null
    }
    if( typeof rawValue !== "string" ){
        return null
    }
    const trimmed = rawValue.trim()
    if( trimmed.length === 0 ){
        return null
    }
    const parsed = Date.parse(trimmed)
    if( !Number.isNaN(parsed) ){
        return parsed
    }
    const yearOnly = trimmed.match(/^(\d{4})$/)
    if( yearOnly ){
        return Date.UTC(Number.parseInt(yearOnly[1], 10), 0, 1)
    }
    const yearMonth = trimmed.match(/^(\d{4})-(\d{2})$/)
    if( yearMonth ){
        return Date.UTC(Number.parseInt(yearMonth[1], 10), Math.max(0, Number.parseInt(yearMonth[2], 10) - 1), 1)
    }
    return null
}

function normalizeTimelineEventKeyPart(value){
    return String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .slice(0, 240)
}

function buildTimelineEventKey(node = {}){
    const datePart = normalizeTimelineEventKeyPart(node.date ?? node.timestamp ?? node.time)
    const eventPart = normalizeTimelineEventKeyPart(node.event ?? node.name ?? node.title)
    const detailPart = normalizeTimelineEventKeyPart(node.overview ?? node.rationale ?? node.deal ?? node.source)
    return [datePart, eventPart, detailPart].join("|")
}

function extractTimelineEventsFromStructuredSummary(structuredSummary){
    const events = []
    const walk = (node)=>{
        if( !node ){
            return
        }
        if( Array.isArray(node) ){
            node.forEach(walk)
            return
        }
        if( typeof node !== "object" ){
            return
        }
        const ts = parseTimelineDateValue(node.date ?? node.timestamp ?? node.time)
        const eventText = [node.event, node.name, node.title].find((entry)=>typeof entry === "string" && entry.trim().length > 0)
        if( ts !== null && eventText ){
            events.push({
                key: buildTimelineEventKey(node),
                ts,
                date: node.date,
                event: eventText.trim(),
                detail: ""
            })
        }
        Object.values(node).forEach((value)=>{
            if( value && typeof value === "object" ){
                walk(value)
            }
        })
    }
    walk(structuredSummary)
    const dedupe = new Set()
    return events
        .sort((a,b)=>a.ts - b.ts)
        .filter((entry)=>{
            const key = entry.key || `${entry.ts}:${entry.event.toLowerCase()}`
            if( dedupe.has(key) ){
                return false
            }
            dedupe.add(key)
            return true
        })
}

function formatTimelineTick(ts){
    if( !Number.isFinite(ts) ){
        return ""
    }
    return moment(ts).format("YYYY-MM-DD")
}

function formatTimelineLabelDate(entry){
    const raw = typeof entry?.date === "string" ? entry.date.trim() : ""
    if( raw ){
        return raw
    }
    return Number.isFinite(entry?.ts) ? formatTimelineTick(entry.ts) : ""
}

function resolveTimelineMarkerGranularity(option, startTs, endTs){
    const raw = typeof option === "string" ? option.toLowerCase() : "auto"
    if( raw && raw !== "auto" ){
        return raw
    }
    const spanDays = Math.max(1, (endTs - startTs) / (1000 * 60 * 60 * 24))
    if( spanDays >= (365 * 4) ){
        return "year"
    }
    if( spanDays >= 365 ){
        return "quarter"
    }
    return "month"
}

function buildTimelineMarkers({startTs, endTs, mode}){
    if( !Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs || mode === "none" ){
        return []
    }
    let cursor = moment.utc(startTs)
    if( mode === "year" ){
        cursor = cursor.startOf("year")
    }else if( mode === "quarter" ){
        cursor = cursor.startOf("quarter")
    }else{
        cursor = cursor.startOf("month")
    }
    while( cursor.valueOf() < startTs ){
        if( mode === "year" ){
            cursor.add(1, "year")
        }else if( mode === "quarter" ){
            cursor.add(1, "quarter")
        }else{
            cursor.add(1, "month")
        }
    }
    const markers = []
    while( cursor.valueOf() <= endTs ){
        const ts = cursor.valueOf()
        let label = cursor.format("YYYY-MM")
        if( mode === "year" ){
            label = cursor.format("YYYY")
        }else if( mode === "quarter" ){
            label = `Q${cursor.quarter()} ${cursor.format("YYYY")}`
        }
        markers.push({ ts, label })
        if( mode === "year" ){
            cursor.add(1, "year")
        }else if( mode === "quarter" ){
            cursor.add(1, "quarter")
        }else{
            cursor.add(1, "month")
        }
    }
    const maxMarkers = 20
    if( markers.length <= maxMarkers ){
        return markers
    }
    const stride = Math.ceil(markers.length / maxMarkers)
    return markers.filter((_, idx)=>idx % stride === 0)
}

function resolveTimelinePalette({ theme, option, targetLength = 0, reverse = false } = {}){
    const fallbackPalette = theme?.palette?.categoryColors ?? categoryColors
    const ensureList = (source)=>{
        if(!Array.isArray(source)){
            return null
        }
        const filtered = source.filter((value)=>typeof value === "string" && value.trim().length > 0)
        return filtered.length ? filtered : null
    }

    let palette = null
    if(Array.isArray(option) || (typeof option === "object" && option !== null)){
        palette = ensureList(Array.isArray(option) ? option : Object.values(option))
    }else if(typeof option === "string"){
        if(option === "theme"){
            palette = ensureList(fallbackPalette)
        }
        if(!palette){
            const heatPalette = heatMapPalette.find((entry)=>entry.name === option)
            if(heatPalette){
                if(heatPalette.dynamic && heatPalette.colors?.length >= 2){
                    palette = interpolateHexColors(heatPalette.colors[0], heatPalette.colors.at(-1), Math.max(targetLength, 2))
                }else if(Array.isArray(heatPalette.category_colors) && heatPalette.category_colors.length){
                    palette = heatPalette.category_colors.slice()
                }else if(Array.isArray(heatPalette.colors) && heatPalette.colors.length){
                    palette = heatPalette.colors.slice()
                }
            }
        }
        if(!palette){
            const themeCollections = theme?.palette?.collections
            const fromTheme = theme?.palette?.[option] ?? themeCollections?.[option]
            palette = ensureList(Array.isArray(fromTheme) ? fromTheme : null)
        }
        if(!palette){
            const builtin = {
                default: categoryColors,
                pastel: categoryColors_pastel,
                muted: categoryColors_MUTED,
                std: categoryColors_STD,
                classic: categoryColors_old,
                vibrant: categoryColors2
            }
            if(builtin[option]){
                palette = builtin[option].slice()
            }
        }
    }
    if(!palette || palette.length === 0){
        palette = fallbackPalette.slice()
    }
    if(reverse){
        palette = [...palette].reverse()
    }
    if(targetLength > 0){
        if(palette.length < targetLength){
            const extended = []
            for(let i = 0; i < targetLength; i++){
                extended.push(palette[i % palette.length])
            }
            palette = extended
        }else if(palette.length > targetLength){
            palette = palette.slice(0, targetLength)
        }
    }
    return palette
}

registerRenderer( {type: "categoryId", id: 178, configs: "default"}, function renderTimelineSummary(primitive, options = {}){
    const renderOptions = {
        width: 800,
        height: 900,
        padding: [16, 18, 16, 18],
        orientation: "vertical",
        layers: 2,
        fontSize: 10,
        start: undefined,
        end: undefined,
        ...(options.renderConfig ?? {}),
        ...(options.renderOptions ?? {}),
    }

    const safeNumber = (value, fallback)=>{
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : fallback
    }
    const resolveBoolean = (value, fallback)=>{
        if( value === undefined || value === null ){
            return fallback
        }
        if( typeof value === "string" ){
            const normalized = value.trim().toLowerCase()
            if( ["false", "0", "no", "off"].includes(normalized) ){
                return false
            }
            if( ["true", "1", "yes", "on"].includes(normalized) ){
                return true
            }
        }
        return Boolean(value)
    }
    const width = Math.max(260, safeNumber(renderOptions.width ?? options.width, 800))
    const height = Math.max(160, safeNumber(renderOptions.height ?? options.height, 200))
    const padding = Array.isArray(renderOptions.padding) && renderOptions.padding.length === 4
        ? renderOptions.padding
        : [16, 18, 16, 18]
    const requestedOrientation = String(
        renderOptions.timeline_orientation ??
        renderOptions.orientation ??
        renderOptions.variant ??
        "vertical"
    ).toLowerCase()
    const orientation = requestedOrientation.startsWith("v") ? "vertical" : "horizontal"
    const labelLayers = Math.max(1, Math.min(5, Number.parseInt(
        renderOptions.timeline_layers ??
        renderOptions.layers ??
        renderOptions.labelLayers ??
        2, 10) || 2))
    const fontSize = Math.max(9, Math.min(28, safeNumber(
        renderOptions.timeline_font_size ??
        renderOptions.fontSize,
        12
    )))
    const markerRadius = Math.max(3, safeNumber(renderOptions.markerRadius, 4))
    const lineWidth = Math.max(2, Math.min(10, safeNumber(
        renderOptions.timeline_line_width ??
        renderOptions.lineWidth,
        3
    )))
    const firstLayerOffset = Math.max(0, Math.min(96, safeNumber(
        renderOptions.timeline_first_layer_offset ??
        renderOptions.firstLayerOffset,
        22
    )))
    const showLabelDates = Boolean(
        renderOptions.timeline_show_label_dates ??
        renderOptions.showLabelDates
    )
    const timelineDebug = Boolean(renderOptions.timeline_debug) || isTimelineLayoutDebugEnabled()
    const fastMode = resolveBoolean(
        renderOptions.timeline_fast ??
        renderOptions.fastMode,
        true
    )

    const theme = options.theme ?? renderOptions.theme ?? defaultTheme
    const themeStyle = renderOptions.themeStyle
    const backgroundColor = themeColor(theme, themeStyle ?? "background", themeColor(theme, "background", "#ffffff"))
    const surfaceColor = themeColor(theme, "surface", backgroundColor)
    const textColor = themeColor(theme, "primary", "#0f172a")
    const accentColor = themeColor(theme, "active", themeColor(theme, "cta", themeColor(theme, "selection", "#06b6d4")))
    const subtleBorder = mixHex(accentColor, surfaceColor, 0.7)
    const connectorColor = mixHex(accentColor, backgroundColor, 0.2)
    const lineColor = mixHex(accentColor, backgroundColor, 0.1)
    const surfaceTop = mixHex(backgroundColor, "#ffffff", 0.04)
    const surfaceBottom = mixHex(backgroundColor, "#000000", 0.06)
    const sectionConfig = options.sectionConfig ?? options.renderOptions?.sectionConfig ?? primitive?.getConfig?.sections ?? {}
    const timelineEventConfig = sectionConfig?.timeline_events ?? {}
    const hiddenEventKeys = new Set(
        [timelineEventConfig.hiddenEventKeys ?? timelineEventConfig.hiddenEventIds ?? timelineEventConfig.hidden ?? []]
            .flat()
            .filter(Boolean)
            .map((entry)=>String(entry))
    )
    const importantEventKeys = new Set(
        [timelineEventConfig.importantEventKeys ?? timelineEventConfig.importantEventIds ?? timelineEventConfig.important ?? []]
            .flat()
            .filter(Boolean)
            .map((entry)=>String(entry))
    )
    const events = extractTimelineEventsFromStructuredSummary(primitive.referenceParameters?.structured_summary)
        .filter((entry)=>!hiddenEventKeys.has(String(entry.key)))
        .map((entry)=>({
            ...entry,
            important: importantEventKeys.has(String(entry.key))
        }))
    const detailColor = mixHex(textColor, surfaceColor, 0.45)
    const palette = resolveTimelinePalette({
        theme,
        option: renderOptions.timeline_palette ?? renderOptions.colors ?? "theme",
        targetLength: Math.max(events.length, 2),
        reverse: Boolean(renderOptions.timeline_reverse_palette ?? renderOptions.reverse_palette)
    })
    const dataStart = events.length > 0 ? events[0].ts : Date.now() - (1000 * 60 * 60 * 24 * 30)
    const dataEnd = events.length > 0 ? events[events.length - 1].ts : Date.now()
    const requestedStart = parseTimelineDateValue(
        renderOptions.timeline_start ??
        renderOptions.start ??
        renderOptions.startDate ??
        renderOptions.start_date
    )
    const requestedEnd = parseTimelineDateValue(
        renderOptions.timeline_end ??
        renderOptions.end ??
        renderOptions.endDate ??
        renderOptions.end_date
    )
    let startTs = requestedStart ?? dataStart
    let endTs = requestedEnd ?? dataEnd
    if( !Number.isFinite(startTs) ){
        startTs = dataStart
    }
    if( !Number.isFinite(endTs) ){
        endTs = dataEnd
    }
    if( endTs < startTs ){
        const tmp = startTs
        startTs = endTs
        endTs = tmp
    }
    if( endTs === startTs ){
        endTs = startTs + (1000 * 60 * 60 * 24)
    }
    const markerMode = resolveTimelineMarkerGranularity(renderOptions.timeline_markers, startTs, endTs)
    const timelineMarkers = buildTimelineMarkers({ startTs, endTs, mode: markerMode })

    const availableWidth = Math.max(80, width - padding[1] - padding[3])
    const availableHeight = Math.max(80, height - padding[0] - padding[2])
    const group = new Konva.Group({
        id: primitive.id,
        x: options.x ?? 0,
        y: options.y ?? 0,
        width,
        height,
        onClick: options.onClick,
        name: "inf_track primitive timeline_summary"
    })

    const backdrop = new Konva.Rect({
        x: 0,
        y: 0,
        width,
        height,
        cornerRadius: 14,
        fill: backgroundColor,
        stroke: subtleBorder,
        strokeWidth: 1.2,
        ...(fastMode ? {} : {
            shadowColor: mixHex(accentColor, "#000000", 0.6),
            shadowBlur: 12,
            shadowOpacity: 0.12
        })
    })
    group.add(backdrop)

    const axisStartX = padding[3]
    const axisEndX = width - padding[1]
    const axisStartY = padding[0]
    const axisEndY = height - padding[2]
    const axisY = padding[0] + availableHeight / 2
    const axisX = padding[3] + availableWidth / 2
    const range = Math.max(1, endTs - startTs)
    const eventColors = palette

    const toAxisPoint = (ts)=>{
        const ratio = Math.max(0, Math.min(1, (ts - startTs) / range))
        if( orientation === "vertical" ){
            return {
                x: axisX,
                y: axisStartY + ratio * (axisEndY - axisStartY),
            }
        }
        return {
            x: axisStartX + ratio * (axisEndX - axisStartX),
            y: axisY,
        }
    }

    group.add(new Konva.Line({
        points: orientation === "vertical" ? [axisX, axisStartY, axisX, axisEndY] : [axisStartX, axisY, axisEndX, axisY],
        stroke: lineColor,
        strokeWidth: lineWidth,
        lineCap: "round",
        ...(fastMode ? {} : {
            shadowColor: lineColor,
            shadowBlur: 6,
            shadowOpacity: 0.35
        })
    }))

    if( timelineMarkers.length > 0 ){
        const markerTick = Math.max(4, Math.round(lineWidth * 1.8))
        const markerFontSize = Math.max(8, fontSize - 3)
        const startMarkerHideThreshold = Math.max(10, fontSize + markerTick + 2)
        const endMarkerHideThreshold = Math.max(10, fontSize + markerTick + 2)
        timelineMarkers.forEach((marker)=>{
            const p = toAxisPoint(marker.ts)
            const axisPos = orientation === "vertical" ? p.y : p.x
            const axisStartPos = orientation === "vertical" ? axisStartY : axisStartX
            const axisEndPos = orientation === "vertical" ? axisEndY : axisEndX
            if( Math.abs(axisPos - axisStartPos) < startMarkerHideThreshold || Math.abs(axisPos - axisEndPos) < endMarkerHideThreshold ){
                return
            }
            if( orientation === "vertical" ){
                group.add(new Konva.Line({
                    points: [axisX - markerTick, p.y, axisX + markerTick, p.y],
                    stroke: mixHex(lineColor, textColor, 0.35),
                    strokeWidth: 1,
                    ...(fastMode ? {} : { opacity: 0.7 })
                }))
                group.add(new CustomText({
                    x: axisX + markerTick + 6,
                    y: p.y - (markerFontSize / 2),
                    width: Math.max(72, Math.min(140, availableWidth * 0.24)),
                    fontSize: markerFontSize,
                    text: marker.label,
                    fontFamily: "Poppins",
                    fill: detailColor,
                    wrap: false,
                    ellipsis: true,
                    align: "left"
                }))
            }else{
                group.add(new Konva.Line({
                    points: [p.x, axisY - markerTick, p.x, axisY + markerTick],
                    stroke: mixHex(lineColor, textColor, 0.35),
                    strokeWidth: 1,
                    ...(fastMode ? {} : { opacity: 0.7 })
                }))
                group.add(new CustomText({
                    x: p.x - 34,
                    y: axisY + markerTick + 3,
                    width: 68,
                    fontSize: markerFontSize,
                    text: marker.label,
                    fontFamily: "Poppins",
                    fill: detailColor,
                    wrap: false,
                    ellipsis: true,
                    align: "center"
                }))
            }
        })
    }

    const tickWidth = Math.max(110, Math.min(180, Math.round(availableWidth * 0.22)))
    group.add(new CustomText({
        x: orientation === "vertical" ? axisX + 10 : axisStartX,
        y: orientation === "vertical" ? axisStartY - fontSize / 2 : axisY + markerRadius + 8,
        width: orientation === "vertical" ? Math.max(80, availableWidth / 2) : tickWidth,
        fontSize: Math.max(9, fontSize - 2),
        text: formatTimelineTick(startTs),
        fontFamily: "Poppins",
        fill: detailColor,
        wrap: false,
        ellipsis: true,
        align: "left"
    }))
    group.add(new CustomText({
        x: orientation === "vertical" ? axisX + 10 : Math.max(axisStartX, axisEndX - tickWidth),
        y: orientation === "vertical" ? axisEndY - fontSize / 2 : axisY + markerRadius + 8,
        width: tickWidth,
        fontSize: Math.max(9, fontSize - 2),
        text: formatTimelineTick(endTs),
        fontFamily: "Poppins",
        fill: detailColor,
        wrap: false,
        ellipsis: true,
        align: orientation === "vertical" ? "left" : "right"
    }))

    const laneOccupancy = Array.from({length: labelLayers}, ()=>[])
    const minGap = Math.max(10, Math.min(48, safeNumber(
        renderOptions.timeline_layer_gap ??
        renderOptions.layerGap,
        16
    )))
    const routeClearance = Math.max(2, Math.ceil(minGap * 0.15))
    const renderedLabelCount = { count: 0 }
    const laneGap = 10
    const connectorGap = Math.max(8, Math.min(64, safeNumber(
        renderOptions.timeline_gap ??
        renderOptions.connectorGap,
        18
    )))
    const maxJoinDistanceMultiple = Math.max(1, Math.min(6, safeNumber(
        renderOptions.timeline_max_join_distance_multiple ??
        renderOptions.maxJoinDistanceMultiple,
        3
    )))
    const connectorInset = markerRadius + connectorGap + firstLayerOffset
    const maxDepth = Math.max(0, Math.floor((labelLayers - 1) / 2))
    const upperSpace = Math.max(24, axisY - padding[0] - connectorInset)
    const lowerSpace = Math.max(24, (height - padding[2]) - axisY - connectorInset)
    const leftSpace = Math.max(80, axisX - padding[3] - connectorInset)
    const rightSpace = Math.max(80, (width - padding[1]) - axisX - connectorInset)
    const rowHeight = orientation === "horizontal"
        ? Math.max(fontSize + 12, Math.min(72, (Math.min(upperSpace, lowerSpace) - laneGap * maxDepth) / Math.max(1, maxDepth + 1)))
        : 0
    const columnWidth = orientation === "vertical"
        ? Math.max(130, Math.min(420, (Math.min(leftSpace, rightSpace) - laneGap * maxDepth) / Math.max(1, maxDepth + 1)))
        : 0

    const measureLabel = ({text, maxWidth, maxHeight, preferredWidth, dateText})=>{
        const resolvedMaxWidth = Math.max(120, maxWidth)
        const targetWidth = Math.max(120, Math.min(resolvedMaxWidth, preferredWidth ?? resolvedMaxWidth))
        const node = new Konva.Text({
            text,
            fontSize,
            fontFamily: "Poppins",
            lineHeight: 1.22,
            wrap: "word",
            width: targetWidth
        })
        const naturalHeight = node.height()
        const minHeight = Math.max(fontSize + 10, fontSize * 1.8)
        const dateReserve = dateText ? (Math.max(9, fontSize - 3) + 8) : 0
        const boundedHeight = Math.max(minHeight + dateReserve, Math.min(maxHeight, naturalHeight + 8 + dateReserve))
        return {
            width: targetWidth,
            height: boundedHeight,
            dateReserve
        }
    }
    const buildLabelPayload = (entry)=>{
        const primary = entry?.event ?? ""
        const secondary = entry?.detail ? `\n${entry.detail}` : ""
        const labelText = `${primary}${secondary}`
        const labelDateText = showLabelDates ? formatTimelineLabelDate(entry) : ""
        return { labelText, labelDateText }
    }

    const addInterval = (intervals, start, end)=>{
        intervals.push([start, end])
        intervals.sort((a,b)=>a[0] - b[0])
    }

    const findFreeSlot = ({intervals, desiredStart, span, minStart, maxStart})=>{
        if( !Number.isFinite(minStart) || !Number.isFinite(maxStart) || maxStart < minStart ){
            return null
        }
        const clampedDesired = Math.max(minStart, Math.min(maxStart, desiredStart))
        const collides = (candidateStart)=>{
            const candidateEnd = candidateStart + span
            return intervals.some(([a,b])=>!(candidateEnd < a || candidateStart > b))
        }
        if( !collides(clampedDesired) ){
            return clampedDesired
        }
        for( const [,b] of intervals ){
            const candidateStart = b + minGap
            if( candidateStart >= minStart && candidateStart <= maxStart && !collides(candidateStart) ){
                return candidateStart
            }
        }
        for( let i = intervals.length - 1; i >= 0; i-- ){
            const [a] = intervals[i]
            const candidateStart = a - minGap - span
            if( candidateStart >= minStart && candidateStart <= maxStart && !collides(candidateStart) ){
                return candidateStart
            }
        }
        return null
    }

    const inRangeEvents = events.filter((entry)=>entry.ts >= startTs && entry.ts <= endTs)
    const pointsWithEvents = inRangeEvents.map((entry, idx)=>({
        idx,
        entry,
        point: toAxisPoint(entry.ts)
    }))

    if( isHeightDebugEnabled() || renderOptions.debugHeight === true ){
        console.debug("[height-debug][RenderHelpers][timeline-178]", {
            primitiveId: primitive?.id,
            width,
            height,
            orientation,
            layers: labelLayers,
            start: formatTimelineTick(startTs),
            end: formatTimelineTick(endTs),
            eventsTotal: events.length,
            eventsInRange: inRangeEvents.length,
            rowHeight,
            columnWidth,
            renderOptions
        })
    }

    const placementsByIdx = new Map()
    const unplacedIdx = new Set(pointsWithEvents.map((d)=>d.idx))
    const railOccupancy = orientation === "vertical"
        ? { left: [], right: [] }
        : { top: [], bottom: [] }
    const mergeIntervals = (intervals = [])=>{
        if( !Array.isArray(intervals) || intervals.length === 0 ){
            return []
        }
        const sorted = intervals
            .filter((d)=>Array.isArray(d) && Number.isFinite(d[0]) && Number.isFinite(d[1]))
            .map(([a,b])=>a <= b ? [a,b] : [b,a])
            .sort((a,b)=>a[0] - b[0])
        const merged = []
        for(const [start, end] of sorted){
            const last = merged.at(-1)
            if( !last || start > last[1] ){
                merged.push([start, end])
            }else if( end > last[1] ){
                last[1] = end
            }
        }
        return merged
    }
    const boxesOverlap = (a, b, gap = 0)=>{
        const ax1 = a.x - gap
        const ay1 = a.y - gap
        const ax2 = a.x + a.width + gap
        const ay2 = a.y + a.height + gap
        const bx1 = b.x - gap
        const by1 = b.y - gap
        const bx2 = b.x + b.width + gap
        const by2 = b.y + b.height + gap
        return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1
    }

    const tryPlaceInLayer = ({eventNode, layer, requireStraight, minChronoAxis = -Infinity, minGlobalChronoAxis = -Infinity, commit = true, packOnly = false})=>{
        const { point, entry, idx } = eventNode
        const side = layer % 2 === 0 ? -1 : 1
        const depth = Math.floor(layer / 2)
        const { labelText, labelDateText } = buildLabelPayload(entry)
        const laneIntervals = laneOccupancy[layer]

        let labelX
        let labelY
        let intervalStart
        let intervalEnd
        let measured
        let entryX
        let entryY
        let railIntervals
        let railReserveStart
        let railReserveEnd
        const railOffset = Math.max(6, Math.min(22, Math.round(connectorGap * 0.55)))

        if( orientation === "vertical" ){
            const railKey = side < 0 ? "left" : "right"
            railIntervals = railOccupancy[railKey]
            const blockerIntervals = (depth > 0 && !packOnly)
                ? mergeIntervals(
                    Array.from(placementsByIdx.values())
                        .filter((p)=>p.side === side && p.depth < depth)
                        .map((p)=>[p.labelY - routeClearance, p.labelY + p.measured.height + routeClearance])
                )
                : []
            const sideSpace = side < 0 ? leftSpace : rightSpace
            const laneWidth = Math.max(120, Math.min(columnWidth, sideSpace))
            const maxWidthForLane = Math.max(120, sideSpace - (depth * (laneWidth + laneGap)))
            if( maxWidthForLane < 120 ){
                return null
            }
            measured = measureLabel({
                text: labelText,
                maxWidth: maxWidthForLane,
                maxHeight: Math.max(36, availableHeight * 0.38),
                preferredWidth: laneWidth,
                dateText: labelDateText
            })
            const laneOffset = depth * (columnWidth + laneGap)
            if( side < 0 ){
                labelX = axisX - connectorInset - laneOffset - measured.width
            }else{
                labelX = axisX + connectorInset + laneOffset
            }
            if( labelX < padding[3] || (labelX + measured.width) > (width - padding[1]) ){
                return null
            }

            const yMin = padding[0]
            const yMax = (height - padding[2] - measured.height)
            const globalMinStart = Number.isFinite(minGlobalChronoAxis)
                ? (minGlobalChronoAxis - (measured.height / 2))
                : yMin
            const chronologyMinStart = Number.isFinite(minChronoAxis)
                ? (minChronoAxis - (measured.height / 2))
                : yMin
            if( requireStraight ){
                const containMin = point.y - measured.height + 6
                const containMax = point.y - 6
                const straightMin = Math.max(yMin, containMin, chronologyMinStart, globalMinStart)
                const straightMax = Math.min(yMax, containMax)
                if( straightMax < straightMin ){
                    return null
                }
                const desiredY = point.y - measured.height / 2
                const placedY = findFreeSlot({
                    intervals: laneIntervals,
                    desiredStart: desiredY,
                    span: measured.height,
                    minStart: straightMin,
                    maxStart: straightMax
                })
                if( !Number.isFinite(placedY) ){
                    return null
                }
                labelY = placedY
            }else{
                const desiredY = point.y - measured.height / 2
                const placedY = findFreeSlot({
                    intervals: laneIntervals,
                    desiredStart: desiredY,
                    span: measured.height,
                    minStart: Math.max(yMin, chronologyMinStart, globalMinStart),
                    maxStart: yMax
                })
                if( !Number.isFinite(placedY) ){
                    return null
                }
                labelY = placedY
            }
            entryX = side < 0 ? (labelX + measured.width) : labelX
            const boxTop = labelY
            const boxBottom = labelY + measured.height
            const entryMin = boxTop + 6
            const entryMax = boxBottom - 6
            const centerY = labelY + (measured.height / 2)
            const centerBlocked = blockerIntervals.some(([a,b])=>centerY >= a && centerY <= b)
            if( centerBlocked ){
                return null
            }
            if( requireStraight && !packOnly ){
                const straightY = point.y
                if( straightY < entryMin || straightY > entryMax ){
                    return null
                }
                if( blockerIntervals.some(([a,b])=>straightY >= a && straightY <= b) ){
                    return null
                }
                const reservedY = findFreeSlot({
                    intervals: railIntervals,
                    desiredStart: straightY,
                    span: 0,
                    minStart: straightY,
                    maxStart: straightY
                })
                if( !Number.isFinite(reservedY) ){
                    return null
                }
                entryY = reservedY
            }else{
                const desiredEntryY = Math.max(entryMin, Math.min(entryMax, point.y))
                const reservedY = findFreeSlot({
                    intervals: mergeIntervals([...railIntervals, ...blockerIntervals]),
                    desiredStart: desiredEntryY,
                    span: 0,
                    minStart: entryMin,
                    maxStart: entryMax
                })
                if( !Number.isFinite(reservedY) ){
                    return null
                }
                entryY = reservedY
            }
            railReserveStart = entryY - Math.max(2, minGap * 0.75)
            railReserveEnd = entryY + Math.max(2, minGap * 0.75)
            intervalStart = labelY - minGap
            intervalEnd = labelY + measured.height + minGap

            const railX = axisX + (side < 0 ? -railOffset : railOffset)
            const centerX = labelX + (measured.width / 2)
            const segMinX = Math.min(centerX, railX)
            const segMaxX = Math.max(centerX, railX)
            const segY = centerY
            const crossesPlacedBox = !packOnly && Array.from(placementsByIdx.values()).some((p)=>{
                const px1 = p.labelX - routeClearance
                const px2 = p.labelX + p.measured.width + routeClearance
                const py1 = p.labelY - routeClearance
                const py2 = p.labelY + p.measured.height + routeClearance
                const xOverlap = segMaxX >= px1 && segMinX <= px2
                const yInside = segY >= py1 && segY <= py2
                return xOverlap && yInside
            })
            if( crossesPlacedBox ){
                return null
            }
        }else{
            const railKey = side < 0 ? "top" : "bottom"
            railIntervals = railOccupancy[railKey]
            const blockerIntervals = (depth > 0 && !packOnly)
                ? mergeIntervals(
                    Array.from(placementsByIdx.values())
                        .filter((p)=>p.side === side && p.depth < depth)
                        .map((p)=>[p.labelX - routeClearance, p.labelX + p.measured.width + routeClearance])
                )
                : []
            const prevPoint = pointsWithEvents[idx - 1]?.point
            const nextPoint = pointsWithEvents[idx + 1]?.point
            const leftBound = prevPoint ? (prevPoint.x + point.x) / 2 : axisStartX
            const rightBound = nextPoint ? (nextPoint.x + point.x) / 2 : axisEndX
            const localSpan = Math.max(120, rightBound - leftBound - 10)
            const preferredWidth = Math.max(140, Math.min(460, localSpan * 1.35))
            const laneSpace = side < 0 ? upperSpace : lowerSpace
            const maxHeightForLane = Math.max(36, laneSpace - (depth * (rowHeight + laneGap)))
            if( maxHeightForLane < Math.max(fontSize + 8, 30) ){
                return null
            }
            measured = measureLabel({
                text: labelText,
                maxWidth: Math.max(130, Math.min(520, localSpan * 1.8)),
                maxHeight: maxHeightForLane,
                preferredWidth,
                dateText: labelDateText
            })

            const xMin = padding[3]
            const xMax = width - padding[1] - measured.width
            const globalMinStart = Number.isFinite(minGlobalChronoAxis)
                ? (minGlobalChronoAxis - (measured.width / 2))
                : xMin
            const chronologyMinStart = Number.isFinite(minChronoAxis)
                ? (minChronoAxis - (measured.width / 2))
                : xMin
            if( requireStraight ){
                const containMin = point.x - measured.width + 6
                const containMax = point.x - 6
                const straightMin = Math.max(xMin, containMin, chronologyMinStart, globalMinStart)
                const straightMax = Math.min(xMax, containMax)
                if( straightMax < straightMin ){
                    return null
                }
                const desiredX = point.x - measured.width / 2
                const placedX = findFreeSlot({
                    intervals: laneIntervals,
                    desiredStart: desiredX,
                    span: measured.width,
                    minStart: straightMin,
                    maxStart: straightMax
                })
                if( !Number.isFinite(placedX) ){
                    return null
                }
                labelX = placedX
            }else{
                const desiredX = point.x - measured.width / 2
                const placedX = findFreeSlot({
                    intervals: laneIntervals,
                    desiredStart: desiredX,
                    span: measured.width,
                    minStart: Math.max(xMin, chronologyMinStart, globalMinStart),
                    maxStart: xMax
                })
                if( !Number.isFinite(placedX) ){
                    return null
                }
                labelX = placedX
            }
            const laneOffset = depth * (rowHeight + laneGap)
            if( side < 0 ){
                labelY = axisY - connectorInset - laneOffset - measured.height
            }else{
                labelY = axisY + connectorInset + laneOffset
            }
            if( labelY < padding[0] || (labelY + measured.height) > (height - padding[2]) ){
                return null
            }
            const boxLeft = labelX
            const boxRight = labelX + measured.width
            const entryMin = boxLeft + 6
            const entryMax = boxRight - 6
            const centerX = labelX + (measured.width / 2)
            const centerBlocked = blockerIntervals.some(([a,b])=>centerX >= a && centerX <= b)
            if( centerBlocked ){
                return null
            }
            if( requireStraight && !packOnly ){
                const straightX = point.x
                if( straightX < entryMin || straightX > entryMax ){
                    return null
                }
                if( blockerIntervals.some(([a,b])=>straightX >= a && straightX <= b) ){
                    return null
                }
                const reservedX = findFreeSlot({
                    intervals: railIntervals,
                    desiredStart: straightX,
                    span: 0,
                    minStart: straightX,
                    maxStart: straightX
                })
                if( !Number.isFinite(reservedX) ){
                    return null
                }
                entryX = reservedX
            }else{
                let desiredEntryX = point.x
                if( desiredEntryX <= boxLeft ){
                    desiredEntryX = boxLeft
                }else if( desiredEntryX >= boxRight ){
                    desiredEntryX = boxRight
                }else{
                    const leftDist = Math.abs(point.x - boxLeft)
                    const rightDist = Math.abs(boxRight - point.x)
                    desiredEntryX = leftDist <= rightDist ? boxLeft : boxRight
                }
                desiredEntryX = Math.max(entryMin, Math.min(entryMax, desiredEntryX))
                const reservedX = findFreeSlot({
                    intervals: mergeIntervals([...railIntervals, ...blockerIntervals]),
                    desiredStart: desiredEntryX,
                    span: 0,
                    minStart: entryMin,
                    maxStart: entryMax
                })
                if( !Number.isFinite(reservedX) ){
                    return null
                }
                entryX = reservedX
            }
            railReserveStart = entryX - Math.max(2, minGap * 0.75)
            railReserveEnd = entryX + Math.max(2, minGap * 0.75)
            entryY = side < 0 ? (labelY + measured.height) : labelY
            intervalStart = labelX - minGap
            intervalEnd = labelX + measured.width + minGap

            const railY = axisY + (side < 0 ? -railOffset : railOffset)
            const centerY = labelY + (measured.height / 2)
            const segMinY = Math.min(centerY, railY)
            const segMaxY = Math.max(centerY, railY)
            const segX = centerX
            const crossesPlacedBox = !packOnly && Array.from(placementsByIdx.values()).some((p)=>{
                const px1 = p.labelX - routeClearance
                const px2 = p.labelX + p.measured.width + routeClearance
                const py1 = p.labelY - routeClearance
                const py2 = p.labelY + p.measured.height + routeClearance
                const yOverlap = segMaxY >= py1 && segMinY <= py2
                const xInside = segX >= px1 && segX <= px2
                return yOverlap && xInside
            })
            if( crossesPlacedBox ){
                return null
            }
        }

        const candidateBox = { x: labelX, y: labelY, width: measured.width, height: measured.height }
        const labelCollisionGap = Math.max(4, Math.round(minGap * 0.5))
        const collidesExistingLabel = Array.from(placementsByIdx.values()).some((p)=>
            boxesOverlap(
                candidateBox,
                { x: p.labelX, y: p.labelY, width: p.measured.width, height: p.measured.height },
                labelCollisionGap
            )
        )
        if( collidesExistingLabel ){
            return null
        }

        const boxCenterX = labelX + (measured.width / 2)
        const boxCenterY = labelY + (measured.height / 2)
        const importantDistanceBoost = Boolean(entry?.important)
            ? Math.max(1, Math.min(2.5, safeNumber(
                renderOptions.timeline_important_distance_multiplier ??
                renderOptions.importantDistanceMultiplier,
                1.35
            )))
            : 1
        const maxJoinDistance = Math.max(24, measured.height * maxJoinDistanceMultiple)
        const maxAxisOffset = Math.max(60, Math.min(320, safeNumber(
            renderOptions.timeline_max_axis_offset ??
            renderOptions.maxAxisOffset,
            200
        )))
        const effectiveMaxJoinDistance = Math.min(maxJoinDistance, maxAxisOffset) * importantDistanceBoost
        const joinDistance = orientation === "vertical"
            ? Math.abs(boxCenterY - point.y)
            : Math.abs(boxCenterX - point.x)
        if( !packOnly && joinDistance > effectiveMaxJoinDistance ){
            return null
        }

        if( commit ){
            if( railIntervals && Number.isFinite(railReserveStart) && Number.isFinite(railReserveEnd) ){
                addInterval(railIntervals, railReserveStart, railReserveEnd)
            }
            addInterval(laneIntervals, intervalStart, intervalEnd)
        }
        return {
            layer,
            side,
            depth,
            important: Boolean(entry?.important),
            centerAxis: orientation === "vertical"
                ? (labelY + (measured.height / 2))
                : (labelX + (measured.width / 2)),
            eventAxis: orientation === "vertical" ? point.y : point.x,
            joinDistance,
            labelX,
            labelY,
            measured,
            labelText,
            labelDateText,
            entryX,
            entryY,
            connectorMode: (packOnly || !requireStraight) ? "elbow" : "straight"
        }
    }

    const eventsInChronologicalOrder = pointsWithEvents
        .slice()
        .sort((a,b)=>{
            if( orientation === "vertical" ){
                return a.point.y - b.point.y
            }
            return a.point.x - b.point.x
        })
    const layerMinChronoAxis = Array.from({length: labelLayers}, ()=>-Infinity)
    const centerAxisFor = (p)=>orientation === "vertical" ? p.point.y : p.point.x
    const crowdBand = Math.max(72, Math.round((orientation === "vertical" ? rowHeight : columnWidth) || (fontSize * 6)))
    const getLayerOrderForEvent = (eventNode)=>{
        const axisTarget = centerAxisFor(eventNode)
        const placed = Array.from(placementsByIdx.values())
        return Array.from({length: labelLayers}, (_, layer)=>{
            const side = layer % 2 === 0 ? -1 : 1
            const depth = Math.floor(layer / 2)
            const localLayerCrowd = placed.filter((p)=>p.layer === layer && Math.abs(p.centerAxis - axisTarget) <= crowdBand).length
            const localSideCrowd = placed.filter((p)=>p.side === side && Math.abs(p.centerAxis - axisTarget) <= crowdBand).length
            const totalLayer = placed.filter((p)=>p.layer === layer).length
            const totalSide = placed.filter((p)=>p.side === side).length
            const score =
                (depth * 1.1) +
                (localLayerCrowd * 3.0) +
                (localSideCrowd * 1.2) +
                (totalLayer * 0.12) +
                (totalSide * 0.05)
            return { layer, score }
        })
            .sort((a,b)=>a.score === b.score ? a.layer - b.layer : a.score - b.score)
            .map((d)=>d.layer)
    }

    const evaluatePackedPlacement = (eventNode)=>{
        const layerOrder = getLayerOrderForEvent(eventNode)
        let best = null
        for(const layer of layerOrder){
            const simulated = tryPlaceInLayer({
                eventNode,
                layer,
                requireStraight: false,
                minChronoAxis: layerMinChronoAxis[layer],
                minGlobalChronoAxis: -Infinity,
                commit: false,
                packOnly: true
            })
            if( !simulated ){
                continue
            }
            const score =
                (simulated.depth * 8) +
                (simulated.layer * 2) +
                (eventNode.entry?.important ? -16 : 0) +
                Math.abs((orientation === "vertical" ? eventNode.point.y : eventNode.point.x) - simulated.centerAxis) * 0.05
            if( !best || score < best.score ){
                best = { score, layer, requireStraight: false, simulated }
            }
        }
        return best
    }

    // Pass 1: pack labels first (slot placement only, no hard connector constraints).
    while( unplacedIdx.size > 0 ){
        const remaining = eventsInChronologicalOrder.filter((eventNode)=>unplacedIdx.has(eventNode.idx))
        if( remaining.length === 0 ){
            break
        }
        const root = remaining.find((eventNode)=>eventNode.entry?.important) ?? remaining[0]
        const bestPacked = evaluatePackedPlacement(root)
        const bestCandidate = bestPacked ? { ...bestPacked, eventNode: root } : null
        if( !bestCandidate ){
            unplacedIdx.delete(root.idx) // skip when no slot fits
            continue
        }

        const placed = tryPlaceInLayer({
            eventNode: bestCandidate.eventNode,
            layer: bestCandidate.layer,
            requireStraight: bestCandidate.requireStraight,
            minChronoAxis: layerMinChronoAxis[bestCandidate.layer],
            minGlobalChronoAxis: -Infinity,
            commit: true,
            packOnly: true
        })
        if( placed ){
            placementsByIdx.set(bestCandidate.eventNode.idx, placed)
            unplacedIdx.delete(bestCandidate.eventNode.idx)
            layerMinChronoAxis[placed.layer] = placed.eventAxis
        }else{
            unplacedIdx.delete(root.idx)
        }
    }

    const getPlacementDistanceCaps = (placement)=>{
        const slotHeight = placement?.measured?.height ?? fontSize
        const cappedByHeight = Math.max(24, slotHeight * maxJoinDistanceMultiple)
        const cappedByAxis = Math.max(60, Math.min(320, safeNumber(
            renderOptions.timeline_max_axis_offset ??
            renderOptions.maxAxisOffset,
            200
        )))
        const importantDistanceBoost = placement?.important
            ? Math.max(1, Math.min(2.5, safeNumber(
                renderOptions.timeline_important_distance_multiplier ??
                renderOptions.importantDistanceMultiplier,
                1.35
            )))
            : 1
        const maxAllowedDistance = Math.min(cappedByHeight, cappedByAxis) * importantDistanceBoost
        const maxRemapWindow = Math.max(40, Math.min(480, safeNumber(
            renderOptions.timeline_remap_window ??
            renderOptions.remapWindow,
            140
        )))
        return { maxAllowedDistance, maxRemapWindow }
    }

    // Second pass: compact placements toward their event dots while preserving ordering and gaps.
    const refinePlacements = ()=>{
        const placements = Array.from(placementsByIdx.entries()).map(([idx, placement])=>({ idx, placement }))
        const axisMin = orientation === "vertical" ? padding[0] : padding[3]
        const axisMax = orientation === "vertical" ? (height - padding[2]) : (width - padding[1])

        for(let layer = 0; layer < labelLayers; layer++){
            const layerItems = placements
                .filter((entry)=>entry.placement.layer === layer)
                .sort((a,b)=>(a.placement.eventAxis ?? 0) - (b.placement.eventAxis ?? 0))
            if( layerItems.length <= 1 ){
                continue
            }
            const n = layerItems.length
            const sizes = layerItems.map((entry)=>orientation === "vertical" ? entry.placement.measured.height : entry.placement.measured.width)
            const targets = layerItems.map((entry)=>entry.placement.eventAxis ?? 0)
            const maxAllowed = layerItems.map((entry)=>getPlacementDistanceCaps(entry?.placement).maxAllowedDistance)
            const centers = new Array(n).fill(0)

            for(let i = 0; i < n; i++){
                const half = sizes[i] / 2
                centers[i] = Math.max(axisMin + half, Math.min(axisMax - half, targets[i]))
            }

            // Iterate forward/backward projection to satisfy non-overlap + ordering.
            for(let pass = 0; pass < 5; pass++){
                for(let i = 1; i < n; i++){
                    const minCenter = centers[i - 1] + (sizes[i - 1] / 2) + (sizes[i] / 2) + minGap
                    if( centers[i] < minCenter ){
                        centers[i] = minCenter
                    }
                }
                for(let i = n - 2; i >= 0; i--){
                    const maxCenter = centers[i + 1] - (sizes[i + 1] / 2) - (sizes[i] / 2) - minGap
                    if( centers[i] > maxCenter ){
                        centers[i] = maxCenter
                    }
                }
                for(let i = 0; i < n; i++){
                    const half = sizes[i] / 2
                    centers[i] = Math.max(axisMin + half, Math.min(axisMax - half, centers[i]))
                    centers[i] = Math.max(targets[i] - maxAllowed[i], Math.min(targets[i] + maxAllowed[i], centers[i]))
                }
            }

            for(let i = 0; i < n; i++){
                const { placement } = layerItems[i]
                if( orientation === "vertical" ){
                    placement.labelY = centers[i] - (placement.measured.height / 2)
                    placement.centerAxis = centers[i]
                }else{
                    placement.labelX = centers[i] - (placement.measured.width / 2)
                    placement.centerAxis = centers[i]
                }
            }
        }
    }

    const remapPlacementsToBestEvents = ()=>{
        const slotEntries = Array.from(placementsByIdx.entries())
            .map(([idx, placement])=>({ idx, placement }))
            .sort((a,b)=>a.placement.centerAxis - b.placement.centerAxis)
        const slotCount = slotEntries.length
        if( slotCount === 0 ){
            return
        }
        const sortedEvents = pointsWithEvents
            .slice()
            .sort((a,b)=>(orientation === "vertical" ? a.point.y - b.point.y : a.point.x - b.point.x))
        const eventCount = sortedEvents.length
        if( eventCount < slotCount ){
            return
        }
        const eventPositionByIdx = new Map()
        sortedEvents.forEach((e, i)=>eventPositionByIdx.set(e.idx, i))
        const remapDebug = []
        const selected = new Array(slotCount).fill(-1)
        let eventCursor = 0
        for(let slotIdx = 0; slotIdx < slotCount; slotIdx++){
            const slot = slotEntries[slotIdx].placement
            const { maxAllowedDistance, maxRemapWindow } = getPlacementDistanceCaps(slot)
            const sourceEventPos = eventPositionByIdx.get(slotEntries[slotIdx].idx)
            let best = null
            for(let j = eventCursor; j < eventCount; j++){
                const candidateEvent = sortedEvents[j]
                if( slot?.important && !candidateEvent?.entry?.important ){
                    continue
                }
                if( !slot?.important && candidateEvent?.entry?.important ){
                    continue
                }
                const eventAxis = orientation === "vertical" ? sortedEvents[j].point.y : sortedEvents[j].point.x
                const distanceCost = Math.abs(slot.centerAxis - eventAxis)
                const sourceEventAxis = Number.isFinite(slot.eventAxis) ? slot.eventAxis : eventAxis
                const remapDrift = Math.abs(eventAxis - sourceEventAxis)
                if( distanceCost > maxAllowedDistance || remapDrift > maxRemapWindow ){
                    if( timelineDebug ){
                        remapDebug.push({
                            slot: slotIdx,
                            eventIdx: j,
                            reason: distanceCost > maxAllowedDistance ? "distance_cap" : "remap_window",
                            distanceCost,
                            maxAllowedDistance,
                            remapDrift,
                            maxRemapWindow
                        })
                    }
                    continue
                }
                const indexDrift = Number.isFinite(sourceEventPos) ? Math.abs(j - sourceEventPos) : 0
                if(
                    !best ||
                    distanceCost < best.distanceCost ||
                    (distanceCost === best.distanceCost && indexDrift < best.indexDrift)
                ){
                    best = { j, distanceCost, indexDrift }
                }
            }
            if( !best ){
                if( timelineDebug ){
                    remapDebug.push({
                        slot: slotIdx,
                        reason: "no_feasible_candidate",
                        eventCursor,
                        sourceEventPos
                    })
                }
                continue
            }
            selected[slotIdx] = best.j
            eventCursor = best.j + 1
        }
        const newMap = new Map()
        const assignmentDebug = []
        for(let slotIdx = 0; slotIdx < slotCount; slotIdx++){
            const selectedPos = selected[slotIdx]
            if( selectedPos < 0 ){
                continue
            }
            const selectedEvent = sortedEvents[selectedPos]
            if( !selectedEvent ){
                continue
            }
            const nextPlacement = { ...slotEntries[slotIdx].placement }
            const oldAxis = Number.isFinite(nextPlacement.eventAxis) ? nextPlacement.eventAxis : null
            const newAxis = orientation === "vertical" ? selectedEvent.point.y : selectedEvent.point.x
            const { labelText, labelDateText } = buildLabelPayload(selectedEvent.entry)
            if( orientation === "vertical" ){
                const sideSpace = nextPlacement.side < 0 ? leftSpace : rightSpace
                const laneWidth = Math.max(120, Math.min(columnWidth, sideSpace))
                const maxWidthForLane = Math.max(120, sideSpace - (nextPlacement.depth * (laneWidth + laneGap)))
                const maxHeightForLane = Math.max(36, availableHeight * 0.38)
                nextPlacement.measured = measureLabel({
                    text: labelText,
                    maxWidth: maxWidthForLane,
                    maxHeight: maxHeightForLane,
                    preferredWidth: Math.min(nextPlacement.measured?.width ?? laneWidth, maxWidthForLane),
                    dateText: labelDateText
                })
            }else{
                const laneSpace = nextPlacement.side < 0 ? upperSpace : lowerSpace
                const maxHeightForLane = Math.max(36, laneSpace - (nextPlacement.depth * (rowHeight + laneGap)))
                const preferredWidth = Math.max(140, nextPlacement.measured?.width ?? 220)
                nextPlacement.measured = measureLabel({
                    text: labelText,
                    maxWidth: Math.max(130, Math.min(520, preferredWidth * 1.3)),
                    maxHeight: maxHeightForLane,
                    preferredWidth,
                    dateText: labelDateText
                })
            }
            nextPlacement.labelText = labelText
            nextPlacement.labelDateText = labelDateText
            nextPlacement.eventAxis = orientation === "vertical" ? selectedEvent.point.y : selectedEvent.point.x
            newMap.set(selectedEvent.idx, nextPlacement)
            if( timelineDebug ){
                assignmentDebug.push({
                    slot: slotIdx,
                    oldAxis,
                    newAxis,
                    delta: (oldAxis === null) ? null : (newAxis - oldAxis),
                    eventIdx: selectedEvent.idx
                })
            }
        }
        placementsByIdx.clear()
        for(const [k,v] of newMap.entries()){
            placementsByIdx.set(k, v)
        }
        if( timelineDebug ){
            console.debug("[timeline-debug][remap-summary]", {
                primitiveId: primitive?.id,
                orientation,
                slotCount,
                eventCount,
                assignments: assignmentDebug.slice(0, 80),
                rejected: remapDebug.slice(0, 200)
            })
        }
    }

    const pruneInvalidPlacements = ()=>{
        const labelCollisionGap = Math.max(4, Math.round(minGap * 0.5))
        const sorted = Array.from(placementsByIdx.entries())
            .map(([eventIdx, placement])=>({ eventIdx, placement }))
            .sort((a,b)=>{
                const aImportant = a.placement?.important ? 1 : 0
                const bImportant = b.placement?.important ? 1 : 0
                if( aImportant !== bImportant ){
                    return bImportant - aImportant
                }
                return (a.placement.eventAxis ?? 0) - (b.placement.eventAxis ?? 0)
            })
        const keep = []
        const dropped = []
        for(const entry of sorted){
            const { placement, eventIdx } = entry
            placement.centerAxis = orientation === "vertical"
                ? (placement.labelY + ((placement.measured?.height ?? 0) / 2))
                : (placement.labelX + ((placement.measured?.width ?? 0) / 2))
            const { maxAllowedDistance } = getPlacementDistanceCaps(placement)
            const joinDistance = Math.abs((placement.centerAxis ?? 0) - (placement.eventAxis ?? 0))
            if( joinDistance > maxAllowedDistance ){
                dropped.push({ eventIdx, reason: "join_distance", joinDistance, maxAllowedDistance })
                continue
            }
            const candidateBox = {
                x: placement.labelX,
                y: placement.labelY,
                width: placement.measured?.width ?? 0,
                height: placement.measured?.height ?? 0
            }
            const collidingKeep = keep.filter((kept)=>boxesOverlap(
                candidateBox,
                {
                    x: kept.placement.labelX,
                    y: kept.placement.labelY,
                    width: kept.placement.measured?.width ?? 0,
                    height: kept.placement.measured?.height ?? 0
                },
                labelCollisionGap
            ))
            if( collidingKeep.length > 0 ){
                const hasImportantConflict = collidingKeep.some((kept)=>Boolean(kept?.placement?.important))
                if( hasImportantConflict && !placement.important ){
                    dropped.push({ eventIdx, reason: "overlap_important_kept" })
                    continue
                }
                if( placement.important ){
                    for(const collided of collidingKeep){
                        if( !collided?.placement?.important ){
                            const keepIdx = keep.findIndex((candidate)=>candidate.eventIdx === collided.eventIdx)
                            if( keepIdx >= 0 ){
                                const [removed] = keep.splice(keepIdx, 1)
                                dropped.push({ eventIdx: removed.eventIdx, reason: "replaced_by_important" })
                            }
                        }
                    }
                }
            }
            const stillCollides = keep.some((kept)=>boxesOverlap(
                candidateBox,
                {
                    x: kept.placement.labelX,
                    y: kept.placement.labelY,
                    width: kept.placement.measured?.width ?? 0,
                    height: kept.placement.measured?.height ?? 0
                },
                labelCollisionGap
            ))
            if( stillCollides ){
                dropped.push({ eventIdx, reason: "overlap" })
                continue
            }
            keep.push(entry)
        }
        placementsByIdx.clear()
        keep.forEach((entry)=>placementsByIdx.set(entry.eventIdx, entry.placement))
        if( timelineDebug && dropped.length ){
            console.debug("[timeline-debug][prune-invalid]", {
                primitiveId: primitive?.id,
                orientation,
                dropped: dropped.slice(0, 200)
            })
        }
    }
    const rebuildOccupancyFromPlacements = ()=>{
        laneOccupancy.forEach((intervals, idx)=>{
            laneOccupancy[idx] = []
        })
        if( orientation === "vertical" ){
            railOccupancy.left = []
            railOccupancy.right = []
        }else{
            railOccupancy.top = []
            railOccupancy.bottom = []
        }
        const railPadding = Math.max(2, minGap * 0.75)
        Array.from(placementsByIdx.values()).forEach((placement)=>{
            if( !placement ){
                return
            }
            const layer = Number.isFinite(placement.layer) ? placement.layer : 0
            if( !laneOccupancy[layer] ){
                laneOccupancy[layer] = []
            }
            if( orientation === "vertical" ){
                const y = Number.isFinite(placement.labelY) ? placement.labelY : 0
                const h = Number.isFinite(placement?.measured?.height) ? placement.measured.height : 0
                addInterval(laneOccupancy[layer], y - minGap, y + h + minGap)
                const railKey = placement.side < 0 ? "left" : "right"
                const entryY = Number.isFinite(placement.entryY) ? placement.entryY : (y + (h / 2))
                addInterval(railOccupancy[railKey], entryY - railPadding, entryY + railPadding)
            }else{
                const x = Number.isFinite(placement.labelX) ? placement.labelX : 0
                const w = Number.isFinite(placement?.measured?.width) ? placement.measured.width : 0
                addInterval(laneOccupancy[layer], x - minGap, x + w + minGap)
                const railKey = placement.side < 0 ? "top" : "bottom"
                const entryX = Number.isFinite(placement.entryX) ? placement.entryX : (x + (w / 2))
                addInterval(railOccupancy[railKey], entryX - railPadding, entryX + railPadding)
            }
        })
    }
    const backfillUnplacedEvents = ()=>{
        rebuildOccupancyFromPlacements()
        const unassigned = eventsInChronologicalOrder.filter((eventNode)=>!placementsByIdx.has(eventNode.idx))
        if( unassigned.length === 0 ){
            return 0
        }
        let added = 0
        for(const eventNode of unassigned){
            const layerOrder = getLayerOrderForEvent(eventNode)
            let placed = null
            for(const layer of layerOrder){
                placed = tryPlaceInLayer({
                    eventNode,
                    layer,
                    requireStraight: false,
                    minChronoAxis: -Infinity,
                    minGlobalChronoAxis: -Infinity,
                    commit: true,
                    packOnly: false
                })
                if( placed ){
                    break
                }
            }
            if( placed ){
                placementsByIdx.set(eventNode.idx, placed)
                added += 1
            }
        }
        if( timelineDebug ){
            console.debug("[timeline-debug][backfill]", {
                primitiveId: primitive?.id,
                orientation,
                requested: unassigned.length,
                added
            })
        }
        return added
    }
    remapPlacementsToBestEvents()
    refinePlacements()
    pruneInvalidPlacements()
    // Re-run assignment after pruning so surviving slots can claim closer dots
    // that may have been temporarily consumed by dropped placements.
    remapPlacementsToBestEvents()
    refinePlacements()
    pruneInvalidPlacements()
    backfillUnplacedEvents()
    refinePlacements()
    pruneInvalidPlacements()

    if( timelineDebug ){
        const sortedByAxis = Array.from(placementsByIdx.entries())
            .map(([eventIdx, p])=>({
                eventIdx,
                eventAxis: p.eventAxis,
                centerAxis: p.centerAxis,
                joinDistance: Math.abs((p.centerAxis ?? 0) - (p.eventAxis ?? 0)),
                layer: p.layer,
                depth: p.depth,
                side: p.side
            }))
            .sort((a,b)=>(a.eventAxis ?? 0) - (b.eventAxis ?? 0))
        console.debug("[timeline-debug][placements]", {
            primitiveId: primitive?.id,
            orientation,
            count: sortedByAxis.length,
            placements: sortedByAxis.slice(0, 120)
        })
    }

    pointsWithEvents.forEach(({entry, point, idx})=>{
        const color = eventColors[idx % eventColors.length]
        group.add(new Konva.Circle({
            x: point.x,
            y: point.y,
            radius: markerRadius,
            fill: color,
            stroke: mixHex(backgroundColor, "#ffffff", 0.2),
            strokeWidth: 1.2,
            ...(fastMode ? {} : {
                shadowColor: color,
                shadowBlur: 8,
                shadowOpacity: 0.5
            })
        }))

        const placement = placementsByIdx.get(idx)
        if( !placement ){
            return
        }
        renderedLabelCount.count += 1
        const { labelX, labelY, measured, side, entryX, entryY, connectorMode, labelDateText, labelText } = placement
        const boxCenterX = labelX + (measured.width / 2)
        const boxCenterY = labelY + (measured.height / 2)

        let connector
        if( connectorMode === "straight" ){
            connector = [boxCenterX, boxCenterY, point.x, point.y]
        }else if( orientation === "vertical" ){
            const railOffset = Math.max(6, Math.min(22, Math.round(connectorGap * 0.55)))
            const railX = axisX + (side < 0 ? -railOffset : railOffset)
            connector = [boxCenterX, boxCenterY, railX, boxCenterY, point.x, point.y]
        }else{
            const railOffset = Math.max(6, Math.min(22, Math.round(connectorGap * 0.55)))
            const railY = axisY + (side < 0 ? -railOffset : railOffset)
            connector = [boxCenterX, boxCenterY, boxCenterX, railY, point.x, point.y]
        }

            group.add(new Konva.Line({
                points: connector,
                stroke: mixHex(color, connectorColor, 0.2),
                strokeWidth: 1.5,
                ...(fastMode ? {} : { opacity: 0.95 })
            }))
            group.add(new Konva.Rect({
                x: labelX,
                y: labelY,
                width: measured.width,
                height: measured.height,
                cornerRadius: 8,
                fill: mixHex(surfaceBottom, backgroundColor, 0.2),
                stroke: mixHex(color, backgroundColor, 0.35),
                strokeWidth: 1.2,
                ...(fastMode ? {} : {
                    shadowColor: mixHex(color, "#000000", 0.65),
                    shadowBlur: 6,
                    shadowOpacity: 0.22
                })
            }))
            group.add(new CustomText({
                x: labelX + 6,
                y: labelY + 4,
                width: measured.width - 12,
                height: Math.max(20, measured.height - 8 - (measured.dateReserve ?? 0)),
                fontSize,
                lineHeight: 1.2,
                text: labelText,
                fontFamily: "Poppins",
                fill: textColor,
                wrap: "word",
                ellipsis: true
            }))
            if( labelDateText ){
                group.add(new CustomText({
                    x: labelX + 6,
                    y: labelY + measured.height - (measured.dateReserve ?? 0) + 2,
                    width: measured.width - 12,
                    height: Math.max(10, (measured.dateReserve ?? 0) - 4),
                    fontSize: Math.max(9, fontSize - 3),
                    lineHeight: 1.0,
                    text: labelDateText,
                    fontFamily: "Poppins",
                    fill: detailColor,
                    wrap: false,
                    ellipsis: true,
                    align: "right"
                }))
            }
    })

    if( inRangeEvents.length === 0 ){
        group.add(new CustomText({
            x: padding[3],
            y: padding[0] + 24,
            width: availableWidth,
            fontSize,
            text: "No timeline events in selected range",
            fontFamily: "Poppins",
            fill: detailColor,
            wrap: false,
            ellipsis: true
        }))
    }else if( renderedLabelCount.count < inRangeEvents.length ){
        group.add(new CustomText({
            x: padding[3],
            y: height - padding[2] - (fontSize + 4),
            width: availableWidth,
            fontSize: Math.max(9, fontSize - 2),
            text: `${renderedLabelCount.count}/${inRangeEvents.length} labels shown`,
            fontFamily: "Poppins",
            fill: detailColor,
            align: "right",
            wrap: false,
            ellipsis: true
        }))
    }

    if( options.getConfig ){
        return {
            ...renderOptions,
            width,
            height
        }
    }
    return group
})

registerRenderer( {type: "categoryId", id: 178, configs: "set_grid"}, (primitive, options = {})=>{
    const config = {itemWidth: 800, minColumns: 1, spacing: [8,8], itemPadding: [8,8,8,8], ...(options.renderConfig ?? {})}
    return typeMaps.default.set_grid(primitive, {...options, renderConfig: config})
})
registerRenderer( {type: "categoryId", id: 178, configs: "full"}, function renderTimelineSummaryFull(primitive, options = {}){
    return categoryMaps[178]?.default?.(primitive, options)
})

registerRenderer( {type: "categoryId", id: 82, configs: "default"}, function renderFunc(primitive, options = {}){
    return categoryMaps[109]["default"](primitive, {...options, field:"description", fallback: "summary"})
})
registerRenderer( {type: "categoryId", id: 109, configs: "set_summary_section"}, function renderFunc(primitive, options = {}){
    const config = {alignParts: "section", itemWidth: options.renderOptions?.width ?? 600, minColumns: 1, spacing: options.items?.length > 1 ? [40,40] : [0,0], alignHeight: true, itemPadding: [10,10,10,10], padding: [5,5,5,5], ...(options.renderConfig ?? {})}
    const sectionConfig = primitive.getConfig.sections ?? {}
    return baseGridRender(options, {...config, sectionConfig})
})
registerRenderer({ type: "categoryId", id: 109, configs: "summary_section" }, function renderFunc(primitive, options = {}) {
  // ---------- base config ----------
  const config = {
    field: "summary",
    showId: true,
    idSize: 14,
    fontSize: 16,
    width: 1600,
    maxHeight: 3000,
    padding: [10, 10, 10, 10],
    ...options
  };

  const baseSectionConfig = options.sectionConfig ?? primitive.getConfig.sections ?? {};
  const { template: primaryStructureTemplate } = resolveSummaryStructureTemplate(primitive);
  const { sections: structuredSections, sectionConfig } = prepareStructuredSummary({
    structuredSummary: primitive.referenceParameters?.structured_summary,
    sectionConfig: baseSectionConfig,
    segmentTitle: primitive.filterDescription,
    structureTemplate: primaryStructureTemplate,
  });

  if (config.minWidth) config.width = Math.max(config.width ?? 0, config.minWidth);

  const themeStyle = options.renderOptions.themeStyle

  const theme = options.theme
  const resolvedFontColor = themeColor(theme, themeStyle ?  `${themeStyle}Text` : "primary", themeColor( theme, "primary", "#334155"))
  const resovledBackground = themeColor(theme, themeStyle ??  "surface", themeColor( theme, "background"))
  const placeholderFill =  mixHex(resolvedFontColor, resovledBackground, 0.7)
  const resovledTableHeaderBackground = themeColor(theme, "header", "#999999")
  const resovledTableFontColor = themeColor(theme, "headerText", "#000000")
  const resovledTablePlaceholderFill = mixHex( resovledTableFontColor, resovledTableHeaderBackground, 0.7)
  const verticalLayout = sectionConfig?.vertical_sections

  if( theme.padding?.[themeStyle] ){
    config.padding = theme.padding[themeStyle]
  }
  
  let availableWidth = config.width - config.padding[1] - config.padding[3];
  const availableHeight = config.maxHeight != null ? config.maxHeight - config.padding[0] - config.padding[2] : undefined;

  const ox = options.x ?? 0;
  const oy = options.y ?? 0;

  const g = new Konva.Group({
    id: primitive.id,
    x: ox,
    y: oy,
    width: config.width,
    onClick: options.onClick,

    name: "inf_track primitive"
  });
  const r = new Konva.Rect({
    x: 0,
    y: 0,
    width: config.width,
    cornerRadius: 10,
    fill: resovledBackground,
    name: "item_background"
  });
  g.add(r);

  // Render helpers return the height they consume (and mutate y)
  const mkText = (attrs) => {
        return new CustomText({
            fontFamily: "Poppins",
            lineHeight: 1.3,
            fill: resolvedFontColor,
            placeholderFill: attrs.placeholderFill ?? placeholderFill,
            tableHeaderFill: resovledTableHeaderBackground,
            tableHeaderColor: resovledTableFontColor,
            tableHeaderPlaceholderFill: resovledTablePlaceholderFill,
            wrap: true,
            withMarkdown: true,
            ...attrs
        });
    }

  // ----------------------------------------------------------------
  // Renderers: add more by pushing into RENDERERS
  // Each renderer: {name, match(heading,node)=>score, render(ctx)}
  // ctx: {group, x, y, config, availableWidth, clampHeight, options, primitive}
  // node: {heading, content, subsections?}
  // render MUST return {heightConsumed, heightWithPaddingConsumed?, skipTraversal?: boolean}
  // ----------------------------------------------------------------
  const rendererDefs = getStructuredSummaryRenderers({
    mkText,
    theme,
    themeColor,
    resolvedFontColor,
    placeholderFill,
    tableHeaderBackground: resovledTableHeaderBackground,
    tableHeaderFontColor: resovledTableFontColor,
    tableHeaderPlaceholderFill: resovledTablePlaceholderFill,
    compareFn: compareTwoStrings,
    imageHelper,
    resolvePrimitiveForHeader: ensurePlainObjectPrimitiveResolver(),
    resolvePrimitiveImageUrl,
  });

  const RENDERERS = rendererDefs.map((def) => ({
    name: def.name,
    match: (heading, node, ctx) => {
      if ((node?.sectionStyle ?? node?.autoSectionStyle) === def.name) return 1;
      return typeof def.match === 'function' ? def.match(heading, node, ctx) ?? 0 : 0;
    },
    render: def.render,
  }));
  const RENDERER_MAP = RENDERERS.reduce((acc, entry) => {
    acc[entry.name] = entry;
    return acc;
  }, {});

  // ===========================================================
  // 2) TREE WALK
  // ===========================================================

  const data = structuredSections;
  const spaceYBase = config.fontSize * 1;
  let y = config.padding[0];
  let x = config.padding[3];

  // availableHeight clamping helper
  const clampHeight = (h) => {
    if (availableHeight == null) return h;
    const used = y - config.padding[0]; // already used
    const left = Math.max(availableHeight - used, 0);
    return Math.min(h, left);
  };

  const ctxBase = {
    group: g,
    config,
    availableWidth,
    options,
    primitive,
    spaceY: spaceYBase,
    clampHeight
  };

  const chooseRenderer = (node) => {
    const style = node?.sectionStyle
      ?? node?.autoSectionStyle
      ?? guessStructuredSectionStyle(node, { compareFn: compareTwoStrings });
    if (style && RENDERER_MAP[style]) {
      return RENDERER_MAP[style];
    }
    let best = { score: -1, r: null };
    for (const r of RENDERERS) {
      const s = r.match(node?.heading ?? node?.originalHeading ?? "", node, ctxBase) ?? 0;
      if (s > best.score) best = { score: s, r };
    }
    return best.r;
  };

  const titleRenderer = RENDERERS.find(d=>d.name === "title")
  let first = true
  let rowTrack = []
  let rowStart = y

  const visit = (node, start = false) => {
    if (!node) return;
    const configKey = node?.configKey ?? node?.heading ?? node?.originalHeading;
    const thisSectionConfig = sectionConfig?.[configKey] ?? {}
    if( thisSectionConfig.show == false){
        return
    }
    const explicitStyle = thisSectionConfig.sectionStyle ?? node?.sectionStyle;
    let renderer = explicitStyle ? RENDERER_MAP[explicitStyle] : undefined;
    if (!renderer && node?.autoSectionStyle) {
        renderer = RENDERER_MAP[node.autoSectionStyle];
    }
    if (!renderer) {
        renderer = chooseRenderer(node);
    }

    if (renderer) {
        if( verticalLayout ){
            y = rowStart
            first = true
        }
        if( thisSectionConfig.largeSpacing && !first ){
            y += ctxBase.spaceY
        }
        if( thisSectionConfig.heading ){
            const headingText = node.heading ?? node.originalHeading;
            const headingResult =
              titleRenderer.render(
                { ...ctxBase, config: { ...ctxBase.config, ...thisSectionConfig }, x, y },
                { content: headingText }
              ) || {};
            const headingHeight = clampHeight(headingResult.heightConsumed || 0);
            if (availableHeight != null && headingHeight <= 0) return;
            y += headingHeight;
        }
        
        const res =
          renderer.render(
            { ...ctxBase, config: { ...ctxBase.config, ...thisSectionConfig }, x, y },
            node
          ) || {};
        const bodyHeight = clampHeight(res.heightConsumed || 0);
        if (availableHeight != null && bodyHeight <= 0) return;
        const paddedHeight = clampHeight(
          res.heightWithPaddingConsumed ?? (res.heightConsumed ?? 0) + ctxBase.spaceY
        );

        if( verticalLayout ){
            x += ctxBase.availableWidth + config.padding[3] * 2
            rowTrack.push(paddedHeight)
        }else{
            y += paddedHeight;
        }
        first = false

        // if renderer says "don't traverse children", stop here
        if (res.skipTraversal) return;
    }

    // traverse children
    if (Array.isArray(node.subsections)) {
      for (const child of node.subsections) {
        if (availableHeight != null && (y - config.padding[0]) >= availableHeight) break;
        visit(child);
      }
    }
  };

  if (Array.isArray(data) && data.length) {

    if( sectionConfig?.segment_title?.show && primitive.filterDescription){
        const res =
          titleRenderer.render(
            { ...ctxBase, config: { ...ctxBase.config, fontSize: (ctxBase.config.fontSize ?? 16) * 1.2 }, y },
            { content: primitive.filterDescription }
          ) || {};
        const consume = clampHeight(res.heightConsumed || 0);
        if (availableHeight != null && consume <= 0) return;
        y += consume;
    }
    
    for (const node of data) {
      if (availableHeight != null && (y - config.padding[0]) >= availableHeight) break;
        if( verticalLayout ){
            const activeSections = data.map(node=>{
                const configKey = node?.configKey ?? node?.heading ?? node?.originalHeading;
                const thisSectionConfig = sectionConfig?.[configKey] ?? {}
                if( thisSectionConfig.show == false){
                    return false;
                }
                return !!chooseRenderer(node);
            }).filter(Boolean)
            ctxBase.availableWidth = availableWidth / activeSections.length 
        }
        
        visit(node, true);
    }
    y += Math.max( ...rowTrack, 0 )
  } else {
    // --------- legacy single-field path (unchanged) ----------
    let text = primitive.referenceParameters[config.field];
    text = text?.replaceAll("\\n", "\n");
    const t = mkText({
      x: config.padding[3],
      y: config.padding[0],
      fontSize: config.fontSize,
      text,
      width: availableWidth
    });
    t.attrs.refreshCallback = options.imageCallback;
    if (options.inTable && options.height){
        t.y((options.height - t.height()) / 2);
    }

    let h = t.height();
    if (availableHeight && h > availableHeight) {
      t.ellipsis(true);
      t.height(availableHeight);
      h = availableHeight;
    }
    g.add(t);
    y = Math.max(h + config.padding[0], options.height ?? 0);
  }

  const totalheight = Math.max(y + config.padding[2] - ctxBase.spaceY, config.padding[0] + config.padding[2])

  if (options.getConfig) {
    return { ...config, height: totalheight };
  }

  g.setAttrs({ width: config.width, height: totalheight });
  r.height(totalheight);
  return g;
});

const MARKET_MAP_AXIS_KEYS = ["axis_1", "axis_2", "axis_3"];

const normalizeMarketMapKey = (value = "")=>
    `${value}`
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "");

const normalizeMarketMapName = (value = "")=>
    `${value}`
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/\s+/g, " ");

function resolveMarketMapAxisKey(value, fallback = "axis_1"){
    const raw = `${value ?? ""}`.trim().toLowerCase();
    const match = raw.match(/axis[_\s-]*(\d)/) ?? raw.match(/^(\d)$/);
    if( match ){
        const key = `axis_${match[1]}`;
        if( MARKET_MAP_AXIS_KEYS.includes(key) ){
            return key;
        }
    }
    return fallback;
}

function parseMarketMapMarkdownEntries(content = ""){
    if( typeof content !== "string" ){
        return [];
    }
    return content
        .replaceAll("\\n", "\n")
        .split("\n")
        .map((line)=>line.trim())
        .map((line)=>line.replace(/^[-*]\s*/, ""))
        .map((line)=>{
            const match = line.match(/^([^:]+):\s*(.*)$/);
            return match ? {key: match[1].trim(), value: match[2].trim(), raw: line} : null;
        })
        .filter(Boolean);
}

function marketMapEntryValue(entries, candidates = []){
    const candidateSet = new Set(candidates.map(normalizeMarketMapKey));
    const found = entries.find((entry)=>candidateSet.has(normalizeMarketMapKey(entry.key)));
    return found?.value;
}

function cleanMarketMapEndpoint(value, fallback){
    const cleaned = `${value ?? ""}`
        .replace(/\([^)]*\)/g, "")
        .replace(/\b(extreme|moderately|moderate|highly)\b/gi, "")
        .replace(/\s+/g, " ")
        .replace(/[.;:,]+$/g, "")
        .trim();
    return cleaned || fallback;
}

function cleanMarketMapBucketDescription(value, fallback){
    const cleaned = `${value ?? ""}`
        .replace(/\s+/g, " ")
        .replace(/[.;:,]+$/g, "")
        .trim();
    return cleaned || fallback;
}

function extractMarketMapScaleLabels(content = {}, axisTitle = ""){
    const titleParts = `${axisTitle ?? ""}`
        .split(/\s+vs\.?\s+|\s+versus\s+/i)
        .map((part)=>part.trim())
        .filter(Boolean);
    const fallbackLow = titleParts[1] ?? "Low";
    const fallbackHigh = titleParts[0] ?? "High";
    const labels = {};

    if( content && typeof content === "object" && !Array.isArray(content) ){
        const scaleValue = Object.entries(content).find(([key])=>normalizeMarketMapKey(key).includes("scale"))?.[1];
        if( scaleValue && typeof scaleValue === "object" ){
            Object.entries(scaleValue).forEach(([key, value])=>{
                const score = Number.parseFloat(key);
                if( Number.isFinite(score) ){
                    labels[score] = cleanMarketMapBucketDescription(value, `${score}`);
                }
            });
        }
    }

    if( typeof content === "string" ){
        content.replaceAll("\\n", "\n").split("\n").forEach((line)=>{
            const match = line.trim().match(/^[-*]?\s*([1-5](?:\.\d+)?)\s*:\s*(.+)$/);
            if( match ){
                labels[Number.parseFloat(match[1])] = cleanMarketMapBucketDescription(match[2], match[1]);
            }
        });
    }

    return {
        low: cleanMarketMapEndpoint(labels[1], fallbackLow),
        high: cleanMarketMapEndpoint(labels[5], fallbackHigh),
        buckets: [1, 2, 3, 4, 5].reduce((acc, score)=>{
            acc[score] = labels[score] ?? `${score}`;
            return acc;
        }, {})
    };
}

function extractMarketMapCompanies(content = {}, axisNumber){
    const companies = [];
    const addCompany = (name, score, reason)=>{
        const numericScore = Number.parseFloat(score);
        const label = `${name ?? ""}`.trim();
        if( !label || !Number.isFinite(numericScore) ){
            return;
        }
        if( /^\d+(\.\d+)?$/.test(label) ){
            return;
        }
        const normalized = normalizeMarketMapKey(label);
        if( normalized.includes("axis") || normalized.includes("scale") || normalized.includes("description") || normalized.includes("title") ){
            return;
        }
        companies.push({name: label, score: Math.max(1, Math.min(5, numericScore)), reason: `${reason ?? ""}`.trim()});
    };

    if( content && typeof content === "object" && !Array.isArray(content) ){
        const companyValue = Object.entries(content).find(([key])=>{
            const normalized = normalizeMarketMapKey(key);
            return normalized === `axis${axisNumber}companies` || normalized === "companies" || normalized.endsWith("companies");
        })?.[1];
        if( Array.isArray(companyValue) ){
            companyValue.forEach((entry)=>{
                if( typeof entry === "string" ){
                    const match = entry.match(/^(.+?):\s*([1-5](?:\.\d+)?)\s*(?:[|:-]\s*(.*))?$/);
                    if( match ){
                        addCompany(match[1], match[2], match[3]);
                    }
                }else if( entry && typeof entry === "object" ){
                    addCompany(entry.name ?? entry.company ?? entry.title, entry.score ?? entry.value ?? entry.rating, entry.reason ?? entry.description);
                }
            });
        }else if( companyValue && typeof companyValue === "object" ){
            Object.entries(companyValue).forEach(([name, value])=>{
                if( value && typeof value === "object" ){
                    addCompany(name, value.score ?? value.value ?? value.rating, value.reason ?? value.description);
                }else{
                    addCompany(name, value);
                }
            });
        }
    }

    if( typeof content === "string" ){
        content.replaceAll("\\n", "\n").split("\n").forEach((line)=>{
            const match = line.trim().match(/^[-*]\s*(.+?):\s*([1-5](?:\.\d+)?)\s*(?:\|\s*(.*)|-\s*(.*))?$/);
            if( match ){
                addCompany(match[1], match[2], match[3] ?? match[4]);
            }
        });
    }

    return companies;
}

function parseMarketMapAxisSection(section){
    const heading = `${section?.heading ?? section?.title ?? section?.key ?? ""}`;
    const axisMatch = heading.match(/axis[_\s-]*(\d)/i);
    const content = section?.content ?? section?.markdown ?? section?.text ?? "";
    const entries = parseMarketMapMarkdownEntries(content);
    const titleFromEntries = axisMatch
        ? marketMapEntryValue(entries, [`axis${axisMatch[1]}Title`, `axis_${axisMatch[1]}_title`, "axisTitle", "title"])
        : marketMapEntryValue(entries, ["axisTitle", "title"]);
    const axisNumber = axisMatch?.[1] ?? `${MARKET_MAP_AXIS_KEYS.length + 1}`;
    const title = titleFromEntries
        ?? (content && typeof content === "object" ? Object.entries(content).find(([key])=>normalizeMarketMapKey(key).endsWith("title"))?.[1] : undefined)
        ?? heading.replace(/_/g, " ");
    const companies = extractMarketMapCompanies(content, axisNumber);
    return {
        key: `axis_${axisNumber}`,
        title: `${title ?? heading}`.trim(),
        labels: extractMarketMapScaleLabels(content, title),
        companies
    };
}

function parseMarketMapBestAxisHints(sections = []){
    const bestSection = sections.find((section)=>{
        const heading = normalizeMarketMapKey(section?.heading ?? section?.title ?? "");
        return heading.includes("best2d") || heading.includes("bestmap") || heading.includes("map");
    });
    if( !bestSection ){
        return {};
    }
    const entries = parseMarketMapMarkdownEntries(bestSection.content ?? "");
    return {
        x: marketMapEntryValue(entries, ["x_axis", "xAxis", "x axis"]),
        y: marketMapEntryValue(entries, ["y_axis", "yAxis", "y axis"])
    };
}

function buildMarketMapData(primitive, renderOptions = {}){
    const sections = Array.isArray(primitive.referenceParameters?.structured_summary)
        ? primitive.referenceParameters.structured_summary
        : [];
    const axes = sections
        .filter((section)=>/axis[_\s-]*[123]/i.test(`${section?.heading ?? section?.title ?? ""}`))
        .map(parseMarketMapAxisSection)
        .filter((axis)=>MARKET_MAP_AXIS_KEYS.includes(axis.key));
    const axisByKey = axes.reduce((acc, axis)=>{
        acc[axis.key] = axis;
        return acc;
    }, {});
    const bestHints = parseMarketMapBestAxisHints(sections);
    const axisKeyByTitle = axes.reduce((acc, axis)=>{
        acc[normalizeMarketMapName(axis.title)] = axis.key;
        return acc;
    }, {});
    const resolveFromTitle = (title, fallback)=>{
        const normalized = normalizeMarketMapName(title);
        return axisKeyByTitle[normalized] ?? axes.find((axis)=>normalized && normalizeMarketMapName(axis.title).includes(normalized))?.key ?? fallback;
    };
    const xKey = resolveMarketMapAxisKey(
        renderOptions.market_map_x_axis ?? renderOptions.x_axis,
        bestHints.x ? resolveFromTitle(bestHints.x, "axis_1") : "axis_1"
    );
    const yKey = resolveMarketMapAxisKey(
        renderOptions.market_map_y_axis ?? renderOptions.y_axis,
        bestHints.y ? resolveFromTitle(bestHints.y, "axis_2") : "axis_2"
    );
    const xAxis = axisByKey[xKey] ?? axes[0];
    const yAxis = axisByKey[yKey] ?? axes.find((axis)=>axis.key !== xAxis?.key) ?? axes[1] ?? axes[0];

    if( !xAxis || !yAxis ){
        return {axes, points: []};
    }

    const companyByName = new Map();
    const collect = (axis, axisName)=>{
        axis.companies.forEach((company)=>{
            const key = normalizeMarketMapName(company.name);
            if( !key ){
                return;
            }
            const current = companyByName.get(key) ?? {name: company.name, scores: {}};
            current.name = current.name || company.name;
            current.scores[axisName] = company.score;
            companyByName.set(key, current);
        });
    };
    collect(xAxis, "x");
    collect(yAxis, "y");

    const points = Array.from(companyByName.values())
        .filter((company)=>Number.isFinite(company.scores.x) && Number.isFinite(company.scores.y))
        .map((company)=>{
            const primitiveMatch = resolveMarketMapPrimitive(primitive, company.name);
            return {
                ...company,
                name: primitiveMatch?.title ?? company.name,
                primitive: primitiveMatch
            };
        });

    return {axes, xAxis, yAxis, points};
}

function resolveMarketMapPrimitive(primitive, name, id){
    const normalizedName = normalizeMarketMapName(name);
    const mainstore = MainStore();
    if( id && mainstore?.primitive ){
        const byId = mainstore.primitive(id);
        if( byId ){
            return byId;
        }
    }
    const candidates = [
        ...(primitive.primitives?.source?.allItems ?? []),
        ...(primitive.primitives?.imports?.allItems ?? []),
        ...(primitive.primitives?.origin?.allItems ?? []),
        ...(primitive.primitives?.ref?.allItems ?? []),
    ].filter(Boolean);
    const byCandidateId = id ? candidates.find((candidate)=>candidate.id === id) : undefined;
    if( byCandidateId ){
        return byCandidateId;
    }
    const direct = candidates.find((candidate)=>normalizeMarketMapName(candidate.title) === normalizedName);
    if( direct ){
        return direct;
    }
    const loose = candidates.find((candidate)=>{
        const candidateName = normalizeMarketMapName(candidate.title);
        return candidateName && (candidateName.includes(normalizedName) || normalizedName.includes(candidateName));
    });
    if( loose ){
        return loose;
    }
    if( mainstore?.primitive ){
        return candidates.map((candidate)=>candidate.id).map((id)=>mainstore.primitive(id)).find((candidate)=>normalizeMarketMapName(candidate?.title) === normalizedName);
    }
    return undefined;
}

function marketMapRectsOverlap(a, b, gap = 0){
    return a.x < b.x + b.width + gap
        && a.x + a.width + gap > b.x
        && a.y < b.y + b.height + gap
        && a.y + a.height + gap > b.y;
}

function clampMarketMapRect(rect, bounds){
    const width = Math.min(rect.width, bounds.right - bounds.left);
    const height = Math.min(rect.height, bounds.bottom - bounds.top);
    return {
        ...rect,
        width,
        height,
        x: Math.max(bounds.left, Math.min(bounds.right - width, rect.x)),
        y: Math.max(bounds.top, Math.min(bounds.bottom - height, rect.y))
    };
}

function placeMarketMapBox({anchorX, anchorY, width, height, bounds, occupied = [], markerRadius = 0, preferred = "right"}){
    const gap = 8;
    const markerBox = {
        x: anchorX - markerRadius - gap,
        y: anchorY - markerRadius - gap,
        width: (markerRadius + gap) * 2,
        height: (markerRadius + gap) * 2
    };
    const directions = [
        {name: "right", x: anchorX + markerRadius + gap, y: anchorY - height / 2},
        {name: "left", x: anchorX - markerRadius - gap - width, y: anchorY - height / 2},
        {name: "above", x: anchorX - width / 2, y: anchorY - markerRadius - gap - height},
        {name: "below", x: anchorX - width / 2, y: anchorY + markerRadius + gap},
        {name: "aboveRight", x: anchorX + markerRadius + gap, y: anchorY - markerRadius - gap - height},
        {name: "belowRight", x: anchorX + markerRadius + gap, y: anchorY + markerRadius + gap},
        {name: "aboveLeft", x: anchorX - markerRadius - gap - width, y: anchorY - markerRadius - gap - height},
        {name: "belowLeft", x: anchorX - markerRadius - gap - width, y: anchorY + markerRadius + gap},
    ].sort((a, b)=>(a.name === preferred ? -1 : 0) - (b.name === preferred ? -1 : 0));

    const candidates = [];
    for(const direction of directions){
        for(const offset of [0, 12, -12, 26, -26, 44, -44]){
            const horizontal = direction.name === "above" || direction.name === "below";
            candidates.push(clampMarketMapRect({
                x: direction.x + (horizontal ? offset : 0),
                y: direction.y + (horizontal ? 0 : offset),
                width,
                height
            }, bounds));
        }
    }
    for(let radius = 24; radius <= 160; radius += 24){
        for(let angle = 0; angle < Math.PI * 2; angle += Math.PI / 6){
            candidates.push(clampMarketMapRect({
                x: anchorX + Math.cos(angle) * radius - width / 2,
                y: anchorY + Math.sin(angle) * radius - height / 2,
                width,
                height
            }, bounds));
        }
    }

    const scored = candidates
        .map((rect)=>{
            const overlaps = occupied.filter((existing)=>marketMapRectsOverlap(rect, existing, 6)).length
                + (markerRadius > 0 && marketMapRectsOverlap(rect, markerBox, 0) ? 1 : 0);
            const centerX = rect.x + rect.width / 2;
            const centerY = rect.y + rect.height / 2;
            const distance = Math.hypot(centerX - anchorX, centerY - anchorY);
            return {rect, overlaps, distance};
        })
        .sort((a, b)=>a.overlaps - b.overlaps || a.distance - b.distance);
    return scored[0]?.rect ?? clampMarketMapRect({x: anchorX - width / 2, y: anchorY - height / 2, width, height}, bounds);
}

function resolveMarketMapImageUrl(point){
    return resolvePrimitiveImageUrl(point.primitive, point.name)
        ?? `/api/companyLogo?name=${encodeURIComponent(point.name ?? "")}`;
}

registerRenderer({ type: "categoryId", id: 109, configs: "market_map" }, function renderMarketMapSummary(primitive, options = {}) {
    const renderOptions = options.renderOptions ?? {};
    const config = {
        width: Math.max(700, Number(renderOptions.width ?? options.width ?? 1600)),
        height: Math.max(520, Number(renderOptions.height ?? options.height ?? 900)),
        padding: [120, 190, 110, 230],
        axisColor: renderOptions.market_map_axis_color ?? "#93c5fd",
        fontFamily: options.theme?.fontFamily ?? "Poppins",
        background: themeColor(options.theme, "background", "#ffffff"),
        textColor: themeColor(options.theme, "primary", "#0f172a"),
        ...options.renderConfig
    };

    const {xAxis, yAxis, points} = buildMarketMapData(primitive, renderOptions);
    const g = new Konva.Group({
        id: primitive.id,
        x: options.x ?? 0,
        y: options.y ?? 0,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name: "inf_track primitive market_map"
    });

    if( options.getConfig ){
        return config;
    }

    g.add(new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: config.background,
        cornerRadius: 2,
        name: "background"
    }));

    if( !xAxis || !yAxis || points.length === 0 ){
        const placeholder = new CustomText({
            x: 40,
            y: 40,
            width: config.width - 80,
            fontSize: 24,
            fontFamily: config.fontFamily,
            fill: config.textColor,
            text: "No market map data found",
            wrap: true
        });
        g.add(placeholder);
        return g;
    }

    const plotLeft = config.padding[3];
    const plotRight = config.width - config.padding[1];
    const plotTop = config.padding[0];
    const plotBottom = config.height - config.padding[2];
    const plotWidth = plotRight - plotLeft;
    const plotHeight = plotBottom - plotTop;
    const centerX = plotLeft + plotWidth / 2;
    const centerY = plotTop + plotHeight / 2;
    const toX = (score)=>plotLeft + ((score - 1) / 4) * plotWidth;
    const toY = (score)=>plotBottom - ((score - 1) / 4) * plotHeight;
    const axisExtremeFontSize = Math.max(8, Number(renderOptions.market_map_axis_extreme_font_size ?? 30));
    const bucketFontSize = Math.max(6, Math.min(24, Number(renderOptions.market_map_bucket_font_size ?? 10)));
    const companyPalette = resolveTimelinePalette({
        theme: options.theme,
        option: renderOptions.market_map_palette ?? renderOptions.colors ?? "default",
        targetLength: points.length,
        reverse: Boolean(renderOptions.market_map_reverse_palette ?? renderOptions.reverse_palette)
    });
    const displayMode = renderOptions.market_map_display_mode ?? "markers";
    const placementBounds = {
        left: plotLeft + 8,
        top: plotTop + 8,
        right: plotRight - 8,
        bottom: plotBottom - 8
    };

    if( renderOptions.market_map_show_grid === true ){
        [1,2,3,4,5].forEach((score)=>{
            const x = toX(score);
            const y = toY(score);
            if( score !== 3 ){
                g.add(new Konva.Line({
                    points: [x, plotTop, x, plotBottom],
                    stroke: mixHex(config.axisColor, config.background, 0.45),
                    strokeWidth: 1,
                    dash: [5, 7],
                    listening: false
                }));
                g.add(new Konva.Line({
                    points: [plotLeft, y, plotRight, y],
                    stroke: mixHex(config.axisColor, config.background, 0.45),
                    strokeWidth: 1,
                    dash: [5, 7],
                    listening: false
                }));
            }
        });
    }

    g.add(new Konva.Line({
        points: [plotLeft, centerY, plotRight, centerY],
        stroke: config.axisColor,
        strokeWidth: 3
    }));
    g.add(new Konva.Line({
        points: [centerX, plotTop, centerX, plotBottom],
        stroke: config.axisColor,
        strokeWidth: 3
    }));

    if( renderOptions.market_map_show_bucket_values === true ){
        const xBucketWidth = Math.min(210, Math.max(95, plotWidth / 5 - 10));
        const yBucketWidth = Math.min(230, Math.max(110, centerX - plotLeft - 18));
        const bucketFill = mixHex(config.textColor, config.background, 0.35);
        [1,2,3,4,5].forEach((score)=>{
            const x = toX(score);
            const y = toY(score);
            const xText = xAxis.labels.buckets?.[score] ?? `${score}`;
            const yText = yAxis.labels.buckets?.[score] ?? `${score}`;
            g.add(new Konva.Line({
                points: [x, centerY - 5, x, centerY + 5],
                stroke: config.axisColor,
                strokeWidth: 1,
                listening: false
            }));
            g.add(new Konva.Line({
                points: [centerX - 5, y, centerX + 5, y],
                stroke: config.axisColor,
                strokeWidth: 1,
                listening: false
            }));
            g.add(new CustomText({
                x: Math.max(plotLeft, Math.min(plotRight - xBucketWidth, x - xBucketWidth / 2)),
                y: centerY + 8,
                width: xBucketWidth,
                height: bucketFontSize * 4.4,
                text: xText,
                fontSize: bucketFontSize,
                fontFamily: config.fontFamily,
                fill: bucketFill,
                align: "center",
                wrap: true,
                ellipsis: true,
                lineHeight: 1.15,
                listening: false
            }));
            g.add(new CustomText({
                x: centerX + 10,
                y: Math.max(plotTop, Math.min(plotBottom - bucketFontSize * 4.4, y - bucketFontSize * 2.2)),
                width: Math.min(yBucketWidth, plotRight - centerX - 14),
                height: bucketFontSize * 4.4,
                text: yText,
                fontSize: bucketFontSize,
                fontFamily: config.fontFamily,
                fill: bucketFill,
                align: "left",
                wrap: true,
                ellipsis: true,
                lineHeight: 1.15,
                listening: false
            }));
        });
    }

    const addAxisLabel = ({text, x, y, width, fontSize = 28, align = "center", verticalAlign = "middle"})=>{
        const label = new CustomText({
            x,
            y,
            width,
            height: fontSize * 3.2,
            text,
            fontSize,
            fontFamily: config.fontFamily,
            fontStyle: "bold",
            fill: config.textColor,
            align,
            verticalAlign,
            wrap: true,
            lineHeight: 1.2
        });
        g.add(label);
        return label;
    };

    addAxisLabel({
        text: `${yAxis.labels.high ?? "High"}`.toUpperCase(),
        x: centerX - 280,
        y: 18,
        width: 560,
        fontSize: axisExtremeFontSize
    });
    addAxisLabel({
        text: `${yAxis.labels.low ?? "Low"}`.toUpperCase(),
        x: centerX - 280,
        y: config.height - 58,
        width: 560,
        fontSize: axisExtremeFontSize
    });
    addAxisLabel({
        text: `${xAxis.labels.low ?? "Low"}`.toUpperCase(),
        x: 20,
        y: centerY - 58,
        width: Math.max(150, plotLeft - 45),
        fontSize: axisExtremeFontSize
    });
    addAxisLabel({
        text: `${xAxis.labels.high ?? "High"}`.toUpperCase(),
        x: plotRight + 45,
        y: centerY - 58,
        width: Math.max(150, config.width - plotRight - 60),
        fontSize: axisExtremeFontSize,
        align: "left"
    });

    if( renderOptions.market_map_show_axis_titles !== false ){
        g.add(new CustomText({
            x: plotLeft,
            y: plotBottom + 32,
            width: plotWidth,
            text: xAxis.title,
            fontSize: 14,
            fontFamily: config.fontFamily,
            fill: mixHex(config.textColor, config.background, 0.35),
            align: "center",
            wrap: true
        }));
        const yTitle = new CustomText({
            x: 22,
            y: plotTop,
            width: plotHeight,
            text: yAxis.title,
            fontSize: 14,
            fontFamily: config.fontFamily,
            fill: mixHex(config.textColor, config.background, 0.35),
            align: "center",
            wrap: true,
            rotation: -90
        });
        yTitle.offsetX(plotHeight);
        g.add(yTitle);
    }

    const occupied = [];
    const markerRadius = Math.max(5, Number(renderOptions.market_map_marker_size ?? 8));
    const labelFontSize = Number(renderOptions.market_map_label_size ?? 22);
    const labelWidth = Math.max(120, Number(renderOptions.market_map_label_width ?? 210));
    const logoSize = Math.max(28, Number(renderOptions.market_map_image_size ?? 56));

    points.forEach((point, idx)=>{
        const baseX = toX(point.scores.x);
        const baseY = toY(point.scores.y);
        const color = companyPalette[idx % companyPalette.length] ?? config.textColor;
        if( displayMode === "images" ){
            const imageRect = placeMarketMapBox({
                anchorX: baseX,
                anchorY: baseY,
                width: logoSize,
                height: logoSize,
                bounds: placementBounds,
                occupied,
                markerRadius: 0,
                preferred: "above"
            });
            const anchorVisible = Math.hypot(imageRect.x + imageRect.width / 2 - baseX, imageRect.y + imageRect.height / 2 - baseY) > logoSize * 0.45;
            if( anchorVisible ){
                g.add(new Konva.Circle({
                    x: baseX,
                    y: baseY,
                    radius: 3,
                    fill: color,
                    opacity: 0.55
                }));
                g.add(new Konva.Line({
                    points: [baseX, baseY, imageRect.x + imageRect.width / 2, imageRect.y + imageRect.height / 2],
                    stroke: color,
                    strokeWidth: 1,
                    opacity: 0.45
                }));
            }
            const backing = new Konva.Rect({
                x: imageRect.x - 3,
                y: imageRect.y - 3,
                width: imageRect.width + 6,
                height: imageRect.height + 6,
                fill: config.background,
                stroke: color,
                strokeWidth: 2,
                cornerRadius: 8,
                shadowColor: "#000000",
                shadowOpacity: 0.08,
                shadowBlur: 6,
                shadowOffset: {x: 0, y: 2}
            });
            g.add(backing);
            const image = imageHelper(resolveMarketMapImageUrl(point), {
                x: imageRect.x,
                y: imageRect.y,
                width: imageRect.width,
                height: imageRect.height,
                imageCallback: options.imageCallback,
                placeholder: point.name?.slice(0, 2)?.toUpperCase()
            });
            image.attrs.primitiveId = point.primitive?.id;
            g.add(image);
            occupied.push({x: imageRect.x - 4, y: imageRect.y - 4, width: imageRect.width + 8, height: imageRect.height + 8});
            return;
        }

        g.add(new Konva.Circle({
            x: baseX,
            y: baseY,
            radius: markerRadius,
            fill: color,
            stroke: mixHex(color, config.background, 0.25),
            strokeWidth: 2,
            opacity: 0.95
        }));
        occupied.push({
            x: baseX - markerRadius - 4,
            y: baseY - markerRadius - 4,
            width: (markerRadius + 4) * 2,
            height: (markerRadius + 4) * 2
        });

        const labelProbe = new CustomText({
            x: 0,
            y: 0,
            width: labelWidth,
            text: point.name,
            fontSize: labelFontSize,
            fontFamily: config.fontFamily,
            fontStyle: "bold",
            wrap: true,
            lineHeight: 1.15
        });
        const labelHeight = Math.min(Math.max(labelProbe.height(), labelFontSize * 1.25), labelFontSize * 3.8);
        const labelRect = placeMarketMapBox({
            anchorX: baseX,
            anchorY: baseY,
            width: labelWidth,
            height: labelHeight,
            bounds: placementBounds,
            occupied,
            markerRadius,
            preferred: baseX < centerX ? "right" : "left"
        });
        const label = new CustomText({
            x: labelRect.x,
            y: labelRect.y,
            width: labelRect.width,
            height: labelRect.height,
            text: point.name,
            fontSize: labelFontSize,
            fontFamily: config.fontFamily,
            fontStyle: "bold",
            fill: color,
            align: labelRect.x > baseX ? "left" : "right",
            wrap: true,
            lineHeight: 1.15,
            ellipsis: true
        });
        label.attrs.primitiveId = point.primitive?.id;
        label.attrs.refreshCallback = options.imageCallback;
        g.add(label);
        occupied.push({...labelRect});
    });

    return g;
});

registerRenderer( {type: "categoryId", id: 109, configs: "default"}, function renderFunc(primitive, options = {}){

    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 1200, padding: [10,10,10,10], ...options}
    if( config.minWidth){
        config.width = Math.max(config.width ?? 0, config.minWidth)
    }

    let idHeight = config.showId ?  20 : 0
    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.maxHeight !== undefined ? config.maxHeight - config.padding[0] - config.padding[2] - idHeight: undefined
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            cornerRadius: 2,
            fill: 'white',
            name:"background"
        })
        g.add(r)

        let text = primitive.referenceParameters[config.field] ?? primitive.referenceParameters[config.fallback]
        /*if( primitive.origin.plainId === 435057){
            text = text.replace(/^.+MVTR.+\n/,"")
        }*/
       if( typeof(text) === "object"){
        text = Object.keys(text).map(d=>`${d}: ${text[d]}`).join("\n")
       }
       text = text?.replaceAll('\\n','\n')

        const t = new CustomText({
            x: config.padding[3],
            y: config.padding[0],
            fontSize: config.fontSize,
            //fontFamily: "Poppins",
            lineHeight: 1.3,
            text: text,
            withMarkdown: true,
            fill: '#334155',
            wrap: true,
            width: availableWidth,
        })
        t.attrs.refreshCallback = options.imageCallback
        if( options.inTable && options.height ){
            t.y((options.height - t.height()) / 2)
        }

        let h = t.height()
        if( availableHeight ){
            if( h > availableHeight ){
                t.ellipsis(true)
                t.height( availableHeight )
                h = availableHeight
            }
        }
        //t.height(h)
        g.add(t)

        let totalheight = Math.max(h + config.padding[0] + config.padding[2] + (config.showId ? idHeight : 0), options.height ?? 0)

        if( options.getConfig){
            config.height = totalheight
            return config
        }

        if( config.showId ){
            const idText = new CustomText({
                name:"plainId",
                x: config.padding[3],
                y: totalheight - config.padding[2] - config.idSize ,
                fontSize: config.idSize,
                text: `${primitive.displayType} #${primitive.plainId}`,
                fill: '#94a3b8',
                wrap: true,
                width: availableWidth,
            })
            idText.attrs.refreshCallback = options.imageCallback
            g.add(idText)
        }

        g.setAttrs({
            width: config.width,
            height: totalheight
        })
        r.height( totalheight )
    }
    return g
})
registerRenderer( {type: "categoryId", id: 124, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseSocialMedia(primitive, {
            ...options, 
            width: defaultWidthByCategory[124],
            account_name: primitive.referenceParameters?.account,
            name: primitive.referenceParameters?.full_name,
            type: primitive.referenceParameters?.category_name,
            bioline1: primitive.referenceParameters?.biography,
            bioline2: primitive.referenceParameters?.bio_hastags,
            followers: primitive.referenceParameters?.followers,
            post_count: primitive.referenceParameters?.posts_count,
            image_url: primitive.referenceParameters?.profile_image_link ?? primitive.referenceParameters?.imageUrl
        })
})

registerRenderer( {type: "categoryId", id: 173, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseSocialMedia(primitive, {
            ...options, 
            width: defaultWidthByCategory[173],
            account_name: primitive.referenceParameters?.handle ?? primitive.referenceParameters?.account,
            name: primitive.referenceParameters?.full_name ?? primitive.referenceParameters?.profile_nickname,
            type: primitive.referenceParameters?.category_name ?? "TikTok Profile",
            bioline1: primitive.referenceParameters?.biography ?? primitive.referenceParameters?.signature,
            bioline2: primitive.referenceParameters?.language ?? primitive.referenceParameters?.predicted_lang,
            followers: primitive.referenceParameters?.followers,
            post_count: primitive.referenceParameters?.posts_count ?? primitive.referenceParameters?.videos_count,
            image_url: primitive.referenceParameters?.profile_image_link ?? primitive.referenceParameters?.imageUrl
        })
})

registerRenderer( {type: "categoryId", id: 174, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseSocialMedia(primitive, {
            ...options, 
            width: defaultWidthByCategory[174],
            account_name: primitive.referenceParameters?.handle ?? primitive.referenceParameters?.account,
            name: primitive.referenceParameters?.full_name,
            type: primitive.referenceParameters?.category_name ?? "YouTube Channel",
            bioline1: primitive.referenceParameters?.biography,
            bioline2: primitive.referenceParameters?.location,
            followers: primitive.referenceParameters?.subscribers ?? primitive.referenceParameters?.followers,
            post_count: primitive.referenceParameters?.videos_count ?? primitive.referenceParameters?.posts_count,
            image_url: primitive.referenceParameters?.profile_image_link ?? primitive.referenceParameters?.imageUrl
        })
})

function baseSocialMedia(primitive, options){
    const config = {showId: true, idSize: 14, width: 480, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }

    let idHeight = config.showId ?  20 : 0
    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.maxHeight !== undefined ? config.maxHeight - config.padding[0] - config.padding[2] - idHeight: undefined
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            cornerRadius: 2,
            fill: 'white',
        })
        g.add(r)

        let imageWidth = config.width
        let imageHeight = 0
        const providedImageUrl = options.image_url ?? primitive.referenceParameters?.profile_image_link ?? primitive.referenceParameters?.imageUrl
        const hasStoredImage = primitive.referenceParameters?.hasImg
        let resolvedImageUrl
        if( hasStoredImage ){
            resolvedImageUrl = `/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`
        }else if( providedImageUrl ){
            try{
                resolvedImageUrl = `/api/remoteImage?url=${encodeURIComponent(providedImageUrl)}`
            }catch(error){
                resolvedImageUrl = undefined
            }
        }

        if( resolvedImageUrl ){
            imageHeight = (imageWidth / 16 * 9) + 10
            const img = imageHelper( resolvedImageUrl, {
                x: 0,
                y: 0,
                padding: config.padding,
                width: imageWidth,
                height: imageHeight,
                center: true,
                fit:"cover",
                imageCallback: options.imageCallback,
                placeholder: options.placeholder !== false,
                maxScale: 1,
                scaleRatio: 2
            })
            g.add( img )
        }

        const textToShow = [
            options.account_name,
            options.name && `**${options.name}**`,
            options.bioline1,
            options.bioline2,
            " ",
            options.followers && `Followers: ${formatNumber(options.followers)}`,
            options.post_count && `Posts: ${formatNumber(options.post_count)}`
        ].filter(d=>d).join("\n")

        const t = new CustomText({
            x: config.padding[3],
            y: config.padding[0] + imageHeight,
            fontSize: 16,
            lineHeight: 1.15,
            withMarkdown: true,
            text: textToShow,
            fill: '#334155',
            wrap: true,
            imageCallback: options.imageCallback,
            ellipsis: true,
            width: availableWidth,
        })
        t.attrs.refreshCallback = options.imageCallback

        let h = t.height()
        if( availableHeight ){
            if( h > availableHeight ){
                t.ellipsis(true)
                t.height( availableHeight )
                h = availableHeight
            }
        }
        //t.height(h)
        g.add(t)

        let fy = 0

        let totalheight = fy + h + config.padding[0] + config.padding[2] + idHeight + imageHeight

        if( config.showId ){
            const idText = new CustomText({
                x: config.padding[3],
                y: totalheight - config.padding[2] - config.idSize ,
                fontSize: config.idSize,
                text: `${primitive.displayType} #${primitive.plainId}`,
                fill: '#94a3b8',
                wrap: true,
                width: availableWidth,
            })
            idText.attrs.refreshCallback = options.imageCallback
            g.add(idText)
        }

        g.setAttrs({
            width: config.width,
            height: totalheight
        })
        r.height( totalheight )
    }
    return g
}

export function renderPlaceholder(renderOptions = {}){
    let { 
        id, 
        x = 0, 
        y = 0, 
        width = 128, 
        height = 128, 
        type, 
        text, 
        scale = 1,
        fontFamily,
        ...options
    } = {
        ...renderOptions
    };

    const theme = renderOptions.theme ?? defaultTheme;
    const themeFont = theme.fontFamily ?? "Poppins";
    fontFamily = fontFamily ?? themeFont;

    width = Math.abs(width)
    height = Math.abs(height)
    

    const og = new Konva.Group({
        id: id,
        x,
        y,
        width,
        height,
        opacity: 0.6,
        id: options.ids?.[0],
        name:"inf_track primitive shape_element"
    })
    const g = new Konva.Group({
        id: id,
        x: 0,
        y: 0,
        width,
        height,
        opacity: 0.6,
        id: options.ids?.[0],
        name:"inf_track primitive shape_element"
    })
    const padding = Math.min(width, height) * 0.05 

    const usableHeight = height - (padding * 2)
    const usableWidth = width - (padding * 2)
    
    const r = new Konva.Rect({
        x: padding,
        y: padding,
        width: usableWidth,
        height: usableHeight,
        stroke: themeColor(theme, 'placeholder', '#e2e2e2'),
        strokeWidth: 10 * scale,
        cornerRadius: padding
    })
    g.add(r)
    const isErrorStatus = options.status?.error === true
    
    const resolvedFontColor = themeColor(theme, 'primary', '#000000', options.text_color)
    const resovledBackground = themeColor(theme, 'background', '#ffffff')
    const placeholderFill = mixHex( resolvedFontColor, resovledBackground)
    const dangerColor = themeColor(theme, 'danger', '#f97316', options.status?.color)
    
    const baseLineFill = themeColor(theme, 'placeholder', '#e2e2e2');
    const lowLineFill = mixHex(baseLineFill, resovledBackground, 0.35);
    const highlightLineFill = mixHex(baseLineFill, resovledBackground, 0.65);
    const skeletonLines = [];
    const shouldAnimateSkeleton = !isErrorStatus && (options.animate === true || options.animateSkeleton === true);

    if (isErrorStatus) {
        r.stroke(dangerColor);
        r.strokeWidth(Math.max(6 * scale, r.strokeWidth()));
        r.fill(mixHex(dangerColor, resovledBackground, 0.9));
    }
    
    if (!isErrorStatus) {
        switch(options.style){
            case "text":
                {
                    const h = usableHeight < 120 ? 10 * scale : 30 * scale
                    const widths = [0.6, 0.8, 0.4, 0.7, 0.2]
                    const lenW = widths.length
                    const cornerRadius = h * 0.3
                    for( let y = h + (padding * 2), i = 0 ; (y + h) < usableHeight; i++, y += ( h * 2)){
                        const thisWidth = (usableWidth - (2 * padding)) * widths[i % lenW]
                        const lineRect = new Konva.Rect({
                            x: padding * 2,
                            y,
                            width: thisWidth,
                            height: h,
                            cornerRadius,
                            fill: baseLineFill
                        });
                        g.add(lineRect);
                        skeletonLines.push(lineRect);
                    }
                }
                break
            case "pie_chart":
                {
                    const r = (Math.min(usableHeight, usableWidth) / 2) * 0.9
                    g.add(new Konva.Circle({
                            x: padding + r + (usableWidth - r * 2) / 2,
                            y: padding + r + (usableHeight - r * 2) / 2,
                            radius: r,
                            fill: themeColor(theme, 'overlay', '#f2f2f2'),
                        }))
                    g.add(new Konva.Arc({
                            x: padding + r + (usableWidth - r * 2) / 2,
                            y: padding + r + (usableHeight - r * 2) / 2,
                            angle: 50,
                            rotation: 270,
                            outerRadius: r,
                            fill: themeColor(theme, 'border', '#d2d2d2'),
                        }))
                      g.add(new Konva.Arc({
                              x: padding + r + (usableWidth - r * 2) / 2,
                              y: padding + r + (usableHeight - r * 2) / 2,
                              angle: 90,
                              rotation: 320,
                              outerRadius: r,
                              fill: themeColor(theme, 'placeholder', '#e2e2e2'),
                          }))

                }
                break
        }
    } else {
        const iconRadius = Math.max(Math.min(usableWidth, usableHeight) * 0.2, 16 * scale);
        const triangleCenterX = padding + usableWidth / 2;
        const triangleCenterY = padding + usableHeight / 2 - iconRadius * 0.6;
        const triangle = createRoundedRegularPolygonShape({
            x: triangleCenterX,
            y: triangleCenterY,
            sides: 3,
            radius: iconRadius,
            cornerRadius: Math.min(padding, iconRadius * 0.65),
            fill: mixHex(dangerColor, resovledBackground, 0.25),
            stroke: dangerColor,
            strokeWidth: Math.max(12 * scale, 2),
        });
        g.add(triangle);

        const exclamation = new Konva.Text({
            text: '!',
            fontSize: iconRadius * 1.35,
            fontStyle: 'bold',
            fill: dangerColor,
            width: iconRadius * 1.7,
            align: 'center',
        });
        exclamation.x(triangle.x() - exclamation.width() / 2);
        exclamation.y(triangle.y() - exclamation.height() / 2);
        g.add(exclamation);

        const messagePadding = usableWidth * 0.05
        const messageText = new CustomText({
            text: options.status.message,
            fontSize: Math.max(14 * scale, Math.min(usableWidth * 0.12, 28 * scale)),
            lineHeight: 1.2,
            fill: dangerColor,
            placeholderFill,
            align: 'center',
            width: usableWidth - (messagePadding * 2),
            maxHeight: usableHeight - (triangle.y() + iconRadius) + padding,
            wrap: true,
            fontStyle:"bold",
            x: padding + messagePadding,
            fontFamily,
        });
        messageText.y(triangle.y() + iconRadius + padding);
        g.add(messageText);
    }
    
    if (shouldAnimateSkeleton && options.amimCallback && skeletonLines.length > 0) {
        const shimmerStops = [0, lowLineFill, 0.25, baseLineFill, 0.5, highlightLineFill, 0.75, baseLineFill, 1, lowLineFill];
        skeletonLines.forEach((rect, index) => {
            const width = rect.width() ?? rect.attrs.width ?? 0;
            const paddingWidth = Math.min(Math.max(12 * scale, width * 0.3), width + usableWidth * 0.25);
            const baseStart = -paddingWidth;
            const baseEnd = width + paddingWidth;
            rect.fillPriority('linear-gradient');
            rect.fillLinearGradientColorStops(shimmerStops);
            rect.fillLinearGradientStartPoint({ x: baseStart, y: 0 });
            rect.fillLinearGradientEndPoint({ x: baseEnd, y: 0 });
            rect.__skeletonGradient = {
                baseStart,
                baseEnd,
                span: baseEnd - baseStart,
                // stagger phases across lines for subtle variation
                phase: (index / skeletonLines.length) * 0.6,
            };
        });

        const animationNode = g;
        og.attrs.hasAnimationNode = animationNode._id;
        const cycleDuration = options.placeholderCycleMs ?? 1600;
        options.amimCallback(animationNode, (tick) => {
            if (!Number.isFinite(tick)) {
                return false;
            }
            let shouldDraw = false;
            for (const rect of skeletonLines) {
                const meta = rect.__skeletonGradient;
                if (!meta || meta.span <= 0) {
                    continue;
                }
                const phasedTick = tick + meta.phase * cycleDuration;
                const progress = ((phasedTick % cycleDuration) + cycleDuration) % cycleDuration / cycleDuration;
                const offset = meta.span * progress;
                const start = meta.baseStart + offset;
                const end = meta.baseEnd + offset;
                rect.fillLinearGradientStartPoint({ x: start, y: 0 });
                rect.fillLinearGradientEndPoint({ x: end, y: 0 });
                shouldDraw = true;
            }
            return shouldDraw;
        });
    }
    
    og.add(g)
    return og

}

registerRenderer( {type: "categoryId", id: 154, configs: "default"}, function renderFunc(primitive, options = {}){
     const config = {width: 640, height: 640, ...options}
     config.width = config.width ?? 640
     config.height = config.height ?? 640

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    if( g ){

        let imageFit = "cover"
        const url = `/api/image/${primitive.id}` + (primitive.imageCount ? `?${primitive.imageCount}` : "")
        if( url ){
            g.add( imageHelper( url , {
                x: 0,
                y: 0,
                padding: config.padding,
                width: config.width,
                height: config.height,
                clearImg: false,
                center: true,
                maxScale: 1,
                scaleRatio: 2,
                fit: imageFit,
                imageCallback: options.imageCallback,
                placeholder: options.placeholder !== false
            }))
        }

        g.setAttrs({
            width: config.width,
            height: config.height
        })
    }
    if( options.getConfig){
        return config
    }
    return g
})

registerRenderer( {type: "categoryId", id: 138, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.summary})
})
registerRenderer( {type: "categoryId", id: 149, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {itemSize: options?.width ?? 280, ...options, textField: primitive.referenceParameters?.overview, padding: [10,10,10,10], imageUrl: primitive.referenceParameters?.imageUrl})
})
registerRenderer( {type: "categoryId", id: 123, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.description, width: options.width ?? 320, padding: [10,10,10,10], imageUrl: primitive.referenceParameters?.imageUrl})
})
registerRenderer( {type: "categoryId", id: 122, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.overview, width: options.width ?? 320, imageUrl: primitive.referenceParameters?.imageUrl})
})
registerRenderer( {type: "categoryId", id: 152, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.overview, width: options.width ?? 320, imageUrl: primitive.referenceParameters?.imageUrl})
})
registerRenderer( {type: "categoryId", id: 125, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: `**${primitive.title}**\n${primitive.referenceParameters?.description ?? ""}`, width: options.width ?? 320, brandName: "reddit"})
})
registerRenderer( {type: "categoryId", id: 63, configs: "default"}, function renderFunc(primitive, options = {}){
    return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.snippet ?? primitive.text})
})
registerRenderer( {type: "categoryId", id: 34, configs: "default"}, function renderFunc(primitive, options = {}){
    //return baseImageWithText(primitive, {...options, textField: primitive.referenceParameters?.description.replace(/[\*-]+/g, '')?.replace(/\n+/g, '\n')?.replace(/\s+/g, ' ').trim() ?? primitive.snippet})
    return baseImageWithText(primitive, {...options, textField: primitive.snippet ??  primitive.referenceParameters?.description?.replace(/[\*-]+/g, '')?.replace(/\n+/g, '\n')?.replace(/\s+/g, ' ')?.trim()?.slice(0,200) ?? ""})
})
function baseImageWithText(primitive, options){
    const config = {showId: true, idSize: 14, fontSize: 16, width: 256, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }
    const theme = options.theme
    const resolvedFontFamily = options.fontFamily ?? theme?.fontFamily ?? "Arial";
    const resolvedFontColor = themeColor(theme, 'primary', '#000000', options.text_color)
    const resovledBackground = themeColor(theme, 'surface', '#ffffff')
    const resovledPlaceholderFill = mixHex( resolvedFontColor, resovledBackground, 0.7)
    let idHeight = config.showId ?  20 : 0
    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.maxHeight !== undefined ? config.maxHeight - config.padding[0] - config.padding[2] - idHeight: undefined
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            cornerRadius: 2,
            fill: themeColor(theme, "surface"),
        })
        g.add(r)

        let imageHeight = 0
        let url
        let imageFit = "cover"
        imageHeight = (config.width / 16 * 9) + 10
        if( primitive.referenceParameters?.hasImg){
            url = `/api/image/${primitive.id}` + (primitive.imageCount ? `?${primitive.imageCount}` : "")
        }else if( primitive.referenceParameters.source === "Instagram" ){
            const shortcode = primitive.referenceParameters.url.split("/").at(-1)
            url = "/api/remoteImage?url=" + encodeURIComponent(`https://www.instagram.com/p/${shortcode}/media/?size=m`)
        }else if( options.imageUrl ){
            url = "/api/remoteImage?url=" + encodeURIComponent(options.imageUrl)
        }else if( options.brandName ){
            imageFit = "comtain"
            url = "/api/companyLogo?name=" + options.brandName
        }
        if( url ){
            g.add( imageHelper( url , {
                x: 0,
                y: 0,
                padding: config.padding,
                width: config.width,
                height: imageHeight,
                center: true,
                maxScale: 1,
                scaleRatio: 2,
                fit: imageFit,
                imageCallback: options.imageCallback,
                placeholder: options.placeholder !== false
            }))

        }

        let textToShow = options.textField?.trim()
        let hashtags
        if( options.renderOptions?.extract_hashtags){
            const {text: cleaned, hashtags: _hashtags} = extractHashtags( textToShow)
            hashtags = _hashtags
            textToShow = cleaned
            if( !isNaN(options.renderOptions.extract_hashtags)){
                hashtags = hashtags.slice(0, options.renderOptions.extract_hashtags)
                const delta = _hashtags.length - options.renderOptions.extract_hashtags
                if( delta > 0){
                    hashtags.push(`+${delta} more`)
                }

            }
        }

        if( options.renderOptions?.text_length){
            const parts = textToShow.split(/\s+/)
            textToShow = parts.slice(0, options.renderOptions.text_length).join(" ")
            if( parts.length > options.renderOptions.text_length ){
                textToShow += "..."
            }
        }else{
            textToShow = textToShow?.slice(0,150)
        }
        let y = config.padding[0] + imageHeight
        let totalheight = y + idHeight + config.padding[2] 
        let hashtagText 
        if( hashtags ){
            hashtagText = new CustomText({
                x: config.padding[3],
                y,
                fontSize: config.fontSize * 0.75,
                lineHeight: 1.5,
                text: hashtags.join(" "),
                fontStyle: "bold",
                fill: themeColor(theme, "muted"),
                placeholderFill: resovledPlaceholderFill,
                wrap: true,
                imageCallback: options.imageCallback,
                width: availableWidth,
            })
            g.add( hashtagText )
            y += hashtagText.height() + (config.fontSize * 0.5)
            totalheight = y + (config.fontSize * 0.5) + idHeight + config.padding[2] 
        }

        if( availableHeight ){
            if( y > availableHeight ){
                hashtagText.ellipsis(true)
                hashtagText.height( availableHeight )
            }
        }
        if( !availableHeight || y < availableHeight ){
            const t = new CustomText({
                x: config.padding[3],
                y: y,
                fontSize: config.fontSize,
                fontFamily: resolvedFontFamily,
                lineHeight: 1.5,
                text: textToShow,
                fill: resolvedFontColor,
                placeholderFill: resovledPlaceholderFill,
                withMarkdown: true,
                wrap: true,
                    imageCallback: options.imageCallback,
                ellipsis: true,
                width: availableWidth,
            })
            t.attrs.refreshCallback = options.imageCallback

            totalheight = t.y() + t.height() 

            if( availableHeight ){
                if( totalheight > availableHeight ){
                    t.ellipsis(true)
                    t.height( availableHeight - t.y() )
                }
            }
            g.add(t)
            totalheight = t.y() + t.height() + (config.fontSize * 0.5) + idHeight + config.padding[2] 
        }


        if( config.showId ){
            const idText = new CustomText({
                x: config.padding[3],
                y: totalheight - config.padding[2] - config.idSize ,
                fontSize: config.idSize,
                text: `${primitive.displayType} #${primitive.plainId}`,
                fill: themeColor(theme, "secondary"),
                placeholderFill: resovledPlaceholderFill,
                wrap: true,
                width: availableWidth,
            })
            idText.attrs.refreshCallback = options.imageCallback
            g.add(idText)
        }

        g.setAttrs({
            width: config.width,
            height: totalheight
        })
        r.height( totalheight )
    }
    return g
}
registerRenderer( {type: "default", configs: "ai_processing"}, function renderFunc(primitive, options = {}){
    const config = {showId: true, idSize: 14, width: 256, height: 212, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }

    let ox = (options.x ?? 0)
    let oy = (options.y ?? 0)

    const theme = options.theme ?? defaultTheme;

    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        cornerRadius: 2,
        fill: 'white',
    })
    g.add(r)
    const spinner = createSpinner( 10, 60, config.width / 2, config.height / 2)
    g.add( spinner)

    var angularSpeed = 360 / 4000 / 33;
    let lastTick, startTick
    let count = 0
    if( options.amimCallback ){
        g.attrs.hasAnimationNode = spinner._id
        options.amimCallback(spinner, (tick)=>{
            if( !lastTick ){
                lastTick = tick
                startTick = tick
            }
            if( (tick - lastTick) < 33){
                return false
            }
            lastTick = tick
            const duration = tick - startTick
            const rotation = (duration / 2000) * 360
            //spinner.rotation(rotation);
            count++
            spinner.rotation(count * (250/9));
            return true
        })

    }
    
    return g
})

function createSpinner(n, radius, centerX, centerY) {
    const spinnerGroup = new Konva.Group({
        x: centerX,
        y: centerY,
    });

    const angleIncrement = 250 / (n - 1); // 240 degrees divided by (n-1) intervals
    const colorIncrement = 255 / (n - 1); // Color increment for shading
    
    const circle = new Konva.Circle({
        x: 0,
        y: 0,
        radius: radius * 1.4,
        fill: "white"
    })
        spinnerGroup.add(circle);

    for (let i = 0; i < n; i++) {
        const angle = (angleIncrement * i) * (Math.PI / 180); // Convert degrees to radians
        const x = radius * Math.cos(angle);
        const y = -radius * Math.sin(angle); // Y coordinate inverted for canvas

        const redValue = Math.round((i / (n - 1)) * 230); // From 0 to 230
        const greenValue = 255; // Constant green
        const blueValue = Math.round((i / (n - 1)) * 230); // From 0 to 230
        const color = `rgb(${redValue}, ${greenValue}, ${blueValue})`; // Interpolated color

        //const colorValue = Math.floor(255 - colorIncrement * Math.abs((n/2) - i)); // Darker in the middle, lighter on edges
        //const color = `rgb(0, ${colorValue}, 0)`;

        const circle = new Konva.Circle({
            x: x,
            y: y,
            radius: radius * 0.15, // Adjust the size of the small circles
            fill: color
        });

        spinnerGroup.add(circle);
    }
    spinnerGroup.offsetX(spinnerGroup.width() / 2);
    spinnerGroup.offsetY(spinnerGroup.height() / 2);

    return spinnerGroup;
}

registerRenderer( {type: "default", configs: "default"}, function renderFunc(primitive, options = {}){
    const config = {showId: true, idSize: 14, width: 256, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }

    let idHeight = config.showId ?  20 : 0
    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.maxHeight !== undefined ? config.maxHeight - config.padding[0] - config.padding[2] - idHeight: undefined
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    if( g ){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            cornerRadius: 2,
            fill: 'white',
        })
        g.add(r)
        const t = new CustomText({
            x: config.padding[3],
            y: config.padding[0],
            fontSize: 16,
            lineHeight: 1.5,
            text: primitive.title,
            verticalAlign: options.inTable ? "middle" : undefined,
            fill: '#334155',
            wrap: true,
            width: availableWidth,
        })
        t.attrs.refreshCallback = options.imageCallback

        let h = t.height()
        if( availableHeight ){
            if( h > availableHeight ){
                t.ellipsis(true)
                t.height( availableHeight )
            }
        }
        //t.height(h)
        g.add(t)

        let fy = 0
        if( primitive.referenceId === 101 ){
            const fields = [[["Description", "Description: " + primitive.referenceParameters?.description], ["Pricing", "Pricing: $" +primitive.referenceParameters?.price_month + "/mo"]],Object.keys(primitive.referenceParameters?.features ?? {}).map(d=>[d,`${primitive.referenceParameters.features[d]}`])].flat()
            const showFieldName = false
            fy = h + config.padding[0] + config.padding[2] + 8
            for(const d of fields){
                if(d[1] === "FALSE"){
                    continue
                }
                let h = 0
                if( showFieldName ){
                    const t1 = new CustomText({
                        x: config.padding[3],
                        y: fy,
                        fontSize: 12,
                        lineHeight: 1.5,
                        text: d[0],
                        fill: '#334155',
                        wrap: true,
                        width: availableWidth * 0.25,
                    })
                    t1.attrs.refreshCallback = options.imageCallback
                    g.add(t1)
                    h = t1.height()
                }
                const t2 = new CustomText({
                    x: config.padding[3] + (showFieldName ? (availableWidth * 0.25) + 8 : 0),
                    y: fy,
                    fontSize: 12,
                    lineHeight: 1.5,
                    text: d[1],
                    fill: '#334155',
                    wrap: true,
                    width: (availableWidth * 0.75) - 8,
                })
                t2.attrs.refreshCallback = options.imageCallback
                g.add(t2)
                fy += Math.max(h, t2.height()) + 4
            }

        }

        let totalheight = fy + h + config.padding[0] + config.padding[2] + idHeight

        if( config.showId ){
            const idText = new CustomText({
                x: config.padding[3],
                y: totalheight - config.padding[2] - config.idSize ,
                fontSize: config.idSize,
                text: `${primitive.displayType} #${primitive.plainId}`,
                fill: '#94a3b8',
                wrap: true,
                width: availableWidth,
            })
            idText.attrs.refreshCallback = options.imageCallback
            g.add(idText)
        }

        g.setAttrs({
            width: config.width,
            height: totalheight
        })
        r.height( totalheight )
    }
    return g
})

registerRenderer( {type: "categoryId", id: 95, configs: "default"}, (primitive, options = {})=>{
    const config = {width: 555, height: 800, padding: [10,10,10,10], ...options}
    const theme = options.theme ?? defaultTheme;
    if( options.getConfig){
        return config
    }

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track primitive inf_keep"
    })
    const bg = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: themeColor(theme, "background", "#ffffff")

    })
    g.add( bg )
    if( options.concept_info?.[primitive.id]){

        /*const logo = imageHelper( `/published/image/${options.concept_info[primitive.id]?.id}`, {
            size: 48,
            x: config.padding[3] + 12,
            y: config.padding[0] + 6,
            center: true,
            imageCallback: options.imageCallback,
            placeholder: options.placeholder !== false
        })
        g.add( logo )*/
        const conceptText = new Konva.Text({
            x: config.padding[3] + 68,
            y: config.padding[0] + 8,
            fontSize:14,
            fontStyle:"bold",
            width: config.width - config.padding[3] - config.padding[1] - 24,
            text: options.concept_info[primitive.id]?.title
        })
        g.add( conceptText )
    }
    const concept2Text = new Konva.Text({
        x: config.padding[3] + 68,
        y: config.padding[0] + 8 + 16,
        fontSize:12,
        lineHeight:1.2,
        width: config.width - config.padding[3] - config.padding[1] - 24,
        fill: themeColor(theme, 'accent', '#7e8184'),
        text: "1,264 followers\nPromoted"
    })
    g.add( concept2Text )
    const subheading = new Konva.Text({
        x: config.padding[3] + 12,
        y: 64,
        fontSize:14,
        width: config.width - config.padding[3] - config.padding[1],
        wrap: true,
        text: primitive.referenceParameters?.subheadline
    })
    g.add( subheading )
    const image = imageHelper( `/published/image/${primitive.id}`, {
        x: 0,
        y: subheading.attrs.y + subheading.height() + 8,
        padding: [0,0,0,0],
        width: 555,
        height: 300,
        center: true,
        imageCallback: options.imageCallback,
        fit: "cover",
        placeholder: options.placeholder !== false,
        maxScale: 1,
        scaleRatio: 4

    })
    g.add( image )
    const headingText = new Konva.Text({
        x: config.padding[3] + 12,
        y: 380,
        y: image.attrs.y + image.height() + 12,
        fontSize:14,
        fontStyle:"bold",
        width: config.width - config.padding[3] - config.padding[1] - 24,
        wrap: true,
        text: primitive.referenceParameters?.headline
    })
    const urlText = new Konva.Text({
        x: config.padding[3] + 12,
        y: 380,
        y: image.attrs.y + image.height() + 12 + headingText.height() +8 ,
        fontSize:12,
        width: config.width - config.padding[3] - config.padding[1] - 24,
        fill: themeColor(theme, "accent", "#7e8184"),
        wrap: true,
        text: primitive.referenceParameters?.url ?? "www.homepage.io"
    })
    const textBg = new Konva.Rect({
        x: 0,
        y: image.attrs.y + image.height(),
        width: config.width,
        height: headingText.height() + 36 + urlText.height(),
        fill: themeColor(theme, "overlay", "#edf3f8")

    })
    let footerHeight = 0

    g.add( textBg )
    g.add( headingText )
    g.add( urlText )
    g.height( textBg.attrs.y + textBg.attrs.height + footerHeight)
    bg.height( textBg.attrs.y + textBg.attrs.height + footerHeight)

    return g

})

registerRenderer( {type: "categoryId", id: 100, configs: "default"}, (primitive, options = {})=>{
    const config = {width: 600, height: 1800, padding: [10,10,10,10], ...options}
    if( options.getConfig){
        return config
    }

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track primitive inf_keep"
    })
    if( g ){

        const logo = imageHelper( `/api/image/${primitive.id}` + (primitive.imageCount ? `?${primitive.imageCount}` : ""), {
            x: 0,
            y: 0,
            padding: config.padding,
            width: config.width,
            height: config.height,
            center: true,
            imageCallback: options.imageCallback,
            placeholder: options.placeholder !== false,
            maxScale: 1,
            scaleRatio: 4

        })
        g.add( logo )

    }
    return g

})
registerRenderer( {type: "type", id: "flow", configs: "default"}, (primitive, options = {})=>{
    const config = {width: 800, height: 600, ...options}
    const g = new Konva.Group({
        id: primitive.id,
        x: config.x,
        y: config.y,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        minRenderSize : 0,
        name:"inf_track inf_keep"
        //name:"inf_track action_primitive inf_keep"
    })
    return g
})
function arrowButton({x = 0, y = 0, size = 60, dir = "right", callback, theme = defaultTheme}){

    const g = new Konva.Group({
        x, y, onClick: callback,
        width: size,
        height: size,
        name: "clickable"
    })
    const color = themeColor(theme, 'primary', 'black');
    if( dir === "left"){
        g.add( new Konva.Line({
            name: "hover_target",
            fill: color,
            points: [size,0,
                0, size / 2,
                size,size
            ],
            closed: true
        }))
    }else{
        g.add( new Konva.Line({
            name: "hover_target",
            fill: color,
            points: [0,0,
                size, size / 2,
                0,size
            ],
            closed: true
        }))

    }
    return g
}

function createActionButton({
    x = 0,
    y = 0,
    width = 160,
    height = 40,
    text = "Run",
    onClick,
    theme = defaultTheme,
    imageCallback,
}) {
    const button = new Konva.Group({
        x,
        y,
        width,
        height,
        onClick,
        name: "clickable"
    });

    const background = new Konva.Rect({
        width,
        height,
        cornerRadius: height / 2,
        fill: themeColor(theme, "accent", "#1f2937"),
        opacity: 0.92
    });

    const label = new CustomText({
        text,
        align: "center",
        fontSize: Math.min(20, height - 10),
        fontStyle: "bold",
        fill: themeColor(theme, "background", "#ffffff"),
        x: 0,
        y: 0,
        width,
        wrap: false,
        refreshCallback: imageCallback,
        fontFamily: theme.fontFamily
    });

    label.y((height - label.height()) / 2);

    button.add(background);
    button.add(label);

    button.on("mouseenter", () => {
        const stage = button.getStage();
        if (stage && stage.container()) {
            stage.container().style.cursor = "pointer";
        }
    });
    button.on("mouseleave", () => {
        const stage = button.getStage();
        if (stage && stage.container()) {
            stage.container().style.cursor = "default";
        }
    });

    return button;
}

function renderBaseThemeElements( page, theme, config, options){
    return renderThemeElements( page, theme, config, options, false)
}
function renderTopThemeElements( page, theme, config, options){
    return renderThemeElements( page, theme, config, options, true)
}
function renderThemeElements( page, theme, config, options, top){
    const elements = (theme.elements ?? []).filter(d=>top ? d.top === top : !d.top)
    const eg = new Konva.Group({
        width: config.width,
        height: config.height,
        id: "elements",
        //name:"inf_track primitive shape_element"
        name:"inf_track primitive shape_element no_hover no_select no_drag"
    })
    for(const d of elements ){
        const [width, height] = d.size ?? [100, 100]
        const [offsetX, offsetY] = d.offset ?? [0, 0]
        let position = {
            "top-left": [offsetX, offsetY],
            "top-right": [config.width - width - offsetX, offsetY],
            "bottom-left": [offsetX, config.height - height - offsetY],
            "bottom-right": [config.width - width - offsetX, config.height - height - offsetY],
        }[d.position] ?? [0,0]
        let element
        switch( d.type ){
            case "image":
                let url = `/api/companyLogo?domain=co-created.com`
                switch(d.source){
                    case "workspace":
                        url = MainStore().workspace(page.workspaceId).logoUrl 
                        break
                    case "url":
                        url = d.url ?? url
                        break
                }
                element = imageHelper( url, {
                    x: position[0],
                    y: position[1],
                    width, 
                    height,
                    center: true,
                    imageCallback: options.imageCallback,
                    colorKey: true,
                    maxScale: 1,
                    scaleRatio: 2
                })
                break
            case "text":
                const color = themeColor(theme, d.color)
                element = new CustomText({
                    text: d.text,
                    align: d.align ?? "left",
                    fontStyle: d.fontStyle,
                    fill: color,
                    placeholderFill: mixHex( color, themeColor(theme, "background"), 0.7),
                    x: position[0],
                    y: position[1],
                    width, 
                    height,
                    wrap: true,
                    lineHeight: 1.05,
                    fontSize: d.fontSize,
                    refreshCallback: options.imageCallback,
                    fontFamily: theme.fontFamily
                })
                break
        }
        if( element ){
            eg.add(element)
        }
    }
    return eg

}

const LAYOUT_ROW_ORDER = [
    "top",
    "upper",
    "upper_middle",
    "middle",
    "center",
    "lower",
    "bottom"
];

const LAYOUT_COLUMN_ORDER = ["left", "center", "right", "sidebar_left", "sidebar_right"];

function buildOrderResolver(baseOrder) {
    const cache = new Map();
    let extraIndex = baseOrder.length;
    return (key) => {
        if (!cache.has(key)) {
            const idx = baseOrder.indexOf(key);
            cache.set(key, idx >= 0 ? idx : extraIndex++);
        }
        return cache.get(key);
    };
}

const resolveRowOrder = buildOrderResolver(LAYOUT_ROW_ORDER);
const resolveColumnOrder = buildOrderResolver(LAYOUT_COLUMN_ORDER);

function resolveRowKey(slot) {
    if (!slot) return null;
    if (slot.startsWith("top") || slot.startsWith("upper")) return "top";
    if (slot.startsWith("bottom") || slot === "bottom_full" || slot === "footer") return "bottom";
    if (slot === "center" || slot.startsWith("middle") || slot === "callout" || slot === "sidebar_left" || slot === "sidebar_right" || slot === "full_width" || slot === "full_page") {
        return "middle";
    }
    return null;
}

function resolveColumnKey(slot) {
    if (!slot) return null;
    if (slot.endsWith("left") || slot === "sidebar_left") return "left";
    if (slot.endsWith("right") || slot === "sidebar_right" || slot === "callout") return "right";
    if (slot.includes("center") || slot === "center") return "center";
    if (slot === "full_width" || slot === "full_page" || slot === "bottom_full") return "center";
    return null;
}

function computeStructuredSectionFrames(sections, layoutSpec, bounds) {
    const { width, height, startX, startY, spacerX, spacerY } = bounds;
    const hintsArray = Array.isArray(layoutSpec?.sections) ? layoutSpec.sections : null;
    const preset = buildLayoutPreset(layoutSpec, sections.length);

    const layoutInfos = sections.map((section, index) => {
        let layoutHint = section?.layout;
        if (!layoutHint && Array.isArray(hintsArray)) {
            const hintMatch = hintsArray.find((entry, hintIndex) => {
                if (!entry) return false;
                if (entry.section_id != null) {
                    return entry.section_id === section?.id;
                }
                if (entry.id != null) {
                    return entry.id === section?.id;
                }
                if (entry.index != null) {
                    return entry.index === index;
                }
                return hintIndex === index;
            });
            if (hintMatch) {
                layoutHint = hintMatch.layout ?? hintMatch;
            }
        }
        if (!layoutHint && preset?.sectionLayouts) {
            const presetHint = preset.sectionLayouts.find((entry) => entry.index === index);
            if (presetHint) {
                layoutHint = presetHint.layout;
            }
        }

        const normalized = normalizeSectionLayoutSpec(layoutHint);
        const slot = normalized?.slot;
        let rowKey = resolveRowKey(slot);
        let columnKey = resolveColumnKey(slot);

        if (!columnKey && (normalized?.size === "full" || slot === "full_width" || slot === "full_page" || slot === "bottom_full")) {
            columnKey = "center";
        }
        if (!rowKey && normalized?.size === "full") {
            rowKey = "middle";
        }

        return {
            index,
            normalized,
            slot,
            rowKey,
            columnKey,
            size: normalized?.size,
        };
    });

    const hasStructured = layoutInfos.some((info) => info.rowKey || info.columnKey || info.size === "full");
    if (!hasStructured) {
        return null;
    }

    layoutInfos.forEach((info, index) => {
        if (!info.rowKey) {
            if (info.slot?.startsWith("top")) {
                info.rowKey = "top";
            } else if (info.slot?.startsWith("bottom")) {
                info.rowKey = "bottom";
            } else {
                info.rowKey = `auto_${index}`;
            }
        }
        if (!info.columnKey) {
            info.columnKey = "center";
        }
    });

    let columnsOrder = Array.from(new Set(layoutInfos.map((info) => info.columnKey)));
    if (columnsOrder.length === 1 && (columnsOrder[0] === "left" || columnsOrder[0] === "right")) {
        const replacement = "center";
        layoutInfos.forEach((info) => {
            if (info.columnKey === columnsOrder[0]) {
                info.columnKey = replacement;
            }
        });
        columnsOrder = [replacement];
    }
    columnsOrder.sort((a, b) => resolveColumnOrder(a) - resolveColumnOrder(b));

    const rowsOrder = Array.from(new Set(layoutInfos.map((info) => info.rowKey)));
    rowsOrder.sort((a, b) => resolveRowOrder(a) - resolveRowOrder(b));

    const totalCols = Math.max(columnsOrder.length, 1);
    let totalRows = Math.max(rowsOrder.length, 1);

    const columnIndexMap = new Map();
    columnsOrder.forEach((col, idx) => columnIndexMap.set(col, idx));
    const rowIndexMap = new Map();
    rowsOrder.forEach((row, idx) => rowIndexMap.set(row, idx));

    layoutInfos.forEach((info) => {
        info.columnIndex = columnIndexMap.get(info.columnKey) ?? 0;
        info.rowIndex = rowIndexMap.get(info.rowKey) ?? 0;
    });

    const columnNormalizationMap = new Map();
    layoutInfos.forEach((info) => {
        const list = columnNormalizationMap.get(info.columnIndex) ?? [];
        list.push(info);
        columnNormalizationMap.set(info.columnIndex, list);
    });
    columnNormalizationMap.forEach((list) => {
        const minRowIndex = Math.min(...list.map((entry) => entry.rowIndex ?? 0));
        if (Number.isFinite(minRowIndex) && minRowIndex > 0) {
            list.forEach((entry) => {
                entry.rowIndex -= minRowIndex;
            });
        }
    });

    const uniqueRowIndices = Array.from(new Set(layoutInfos.map((entry) => entry.rowIndex ?? 0))).sort((a, b) => a - b);
    if (uniqueRowIndices.length > 0) {
        const compressionMap = new Map();
        uniqueRowIndices.forEach((value, idx) => compressionMap.set(value, idx));
        layoutInfos.forEach((info) => {
            const compressed = compressionMap.get(info.rowIndex);
            if (compressed != null) {
                info.rowIndex = compressed;
            }
        });
        totalRows = Math.max(uniqueRowIndices.length, 1);
    }

    const byColumn = new Map();
    layoutInfos.forEach((info) => {
        const list = byColumn.get(info.columnIndex) ?? [];
        list.push(info);
        byColumn.set(info.columnIndex, list);
    });
    byColumn.forEach((list) => list.sort((a, b) => a.rowIndex - b.rowIndex));

    layoutInfos.forEach((info) => {
        const columnEntries = byColumn.get(info.columnIndex) ?? [];
        const selfIndex = columnEntries.indexOf(info);
        let nextRowIndex = totalRows;
        if (selfIndex !== -1 && selfIndex + 1 < columnEntries.length) {
            nextRowIndex = columnEntries[selfIndex + 1].rowIndex;
        }
        info.rowSpan = Math.max(1, nextRowIndex - info.rowIndex);
    });

    layoutInfos.forEach((info) => {
        let colSpan = 1;
        const slot = info.slot;
        const isCentralFullSlot = slot === "full_width" || slot === "full_page" || slot === "bottom_full" || slot === "top_center" || slot === "bottom_center" || slot === "center";
        const isEdgeSlot = slot?.endsWith("left") || slot?.endsWith("right") || slot === "sidebar_left" || slot === "sidebar_right";

        if (info.size === "full" && isCentralFullSlot && totalCols > 1) {
            colSpan = totalCols;
            info.columnIndex = 0;
        } else if (info.size === "half" && totalCols > 1 && !isEdgeSlot) {
            colSpan = Math.min(2, totalCols);
            if (info.columnIndex + colSpan > totalCols) {
                info.columnIndex = totalCols - colSpan;
            }
        } else if (info.size === "full" && !isEdgeSlot && totalCols > 1 && columnIndexMap.size === 1) {
            colSpan = Math.min(totalCols, 2);
        }
        info.colSpan = colSpan;
        info.initialColumnIndex = info.columnIndex ?? 0;
    });

    const occupancy = Array.from({ length: totalRows }, () => Array(totalCols).fill(null));

    const fillOccupancy = (info) => {
        const rowSpan = Math.max(1, info.rowSpan ?? 1);
        const colSpan = Math.max(1, info.colSpan ?? 1);
        for (let r = info.rowIndex; r < info.rowIndex + rowSpan; r += 1) {
            for (let c = info.columnIndex; c < info.columnIndex + colSpan; c += 1) {
                if (r >= 0 && r < totalRows && c >= 0 && c < totalCols) {
                    occupancy[r][c] = info;
                }
            }
        }
    };

    layoutInfos.forEach(fillOccupancy);
    const baseOccupancy = occupancy.map((row) => [...row]);

    const hasBaseOccupantToLeft = (info) => {
        const startColumn = info.initialColumnIndex ?? info.columnIndex ?? 0;
        if (startColumn === 0) {
            return false;
        }
        const rowSpan = Math.max(1, info.rowSpan ?? 1);
        for (let r = info.rowIndex; r < info.rowIndex + rowSpan; r += 1) {
            const row = baseOccupancy[r];
            if (!row) continue;
            for (let c = 0; c < startColumn; c += 1) {
                const occupant = row[c];
                if (occupant && occupant !== info) {
                    return true;
                }
            }
        }
        return false;
    };

    const columnAvailable = (info, column) => {
        const rowSpan = Math.max(1, info.rowSpan ?? 1);
        for (let r = info.rowIndex; r < info.rowIndex + rowSpan; r += 1) {
            const occupant = occupancy[r]?.[column];
            if (occupant && occupant !== info) {
                return false;
            }
        }
        return true;
    };

    const sortedInfos = [...layoutInfos].sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) return a.rowIndex - b.rowIndex;
        return a.columnIndex - b.columnIndex;
    });

    sortedInfos.forEach((info) => {
        const rowSpan = Math.max(1, info.rowSpan ?? 1);
        // expand right
        while (info.columnIndex + info.colSpan < totalCols) {
            const targetCol = info.columnIndex + info.colSpan;
            if (!columnAvailable(info, targetCol)) {
                break;
            }
            for (let r = info.rowIndex; r < info.rowIndex + rowSpan; r += 1) {
                occupancy[r][targetCol] = info;
            }
            info.colSpan += 1;
        }

        // expand left
        if (!hasBaseOccupantToLeft(info)) {
            while (info.columnIndex > 0) {
                const targetCol = info.columnIndex - 1;
                if (!columnAvailable(info, targetCol)) {
                    break;
                }
                info.columnIndex -= 1;
                info.colSpan += 1;
                for (let r = info.rowIndex; r < info.rowIndex + rowSpan; r += 1) {
                    occupancy[r][targetCol] = info;
                }
            }
        }
    });

    const columnWidth = (width - spacerX * (totalCols - 1)) / totalCols;
    const rowHeight = (height - spacerY * (totalRows - 1)) / totalRows;

    return sections.map((section, index) => {
        const info = layoutInfos.find((entry) => entry.index === index);
        if (!info) {
            return null;
        }
        const colIndex = info.columnIndex ?? 0;
        const rowIndex = info.rowIndex ?? 0;
        const colSpan = Math.max(1, info.colSpan ?? 1);
        const rowSpan = Math.max(1, info.rowSpan ?? 1);

        const x = startX + colIndex * (columnWidth + spacerX);
        const y = startY + rowIndex * (rowHeight + spacerY);
        const frameWidth = columnWidth * colSpan + spacerX * (colSpan - 1);
        const frameHeight = rowHeight * rowSpan + spacerY * (rowSpan - 1);

        return {
            x,
            y,
            width: frameWidth,
            height: frameHeight,
        };
    });
}

function computeSequentialSectionFrames(count, bounds) {
    const { width, height, startX, startY, spacerX } = bounds;
    if (count === 0) {
        return [];
    }
    const sectionWidth = (width - spacerX * (count - 1)) / count;
    return Array.from({ length: count }, (_, index) => ({
        x: startX + index * (sectionWidth + spacerX),
        y: startY,
        width: sectionWidth,
        height: height,
    }));
}

function renderProcessingState({ group, x, y, width, height, spacerY, theme, id, options, messageText = "Working..." }) {
    const messageWidth = Math.min(width * 0.85, 520);
    const messageX = x + (width - messageWidth) / 2;
    const messageY = y + spacerY;
    const message = new CustomText({
        x: messageX,
        y: messageY,
        width: messageWidth,
        text: messageText,
        align: "center",
        wrap: true,
        fontSize: 24,
        fill: themeColor(theme, "muted"),
        refreshCallback: options.imageCallback
    });
    group.add(message);

    const messageBottom = message.y() + message.height();
    const availableHeight = height - (messageBottom - y) - spacerY;
    let animationSize = Math.min(width * 0.6, availableHeight > 0 ? availableHeight : width * 0.6);
    animationSize = Math.max(64, Math.min(animationSize, 280));
    const animationTop = messageBottom + spacerY;

    const placeholder = createProcessingPlaceholder(animationSize, {
        backgroundFill: themeColor(theme, "background"),
        animationId: id,
    });
    const animationGroup = placeholder.group;
    animationGroup.x(x + (width - animationSize) / 2);
    animationGroup.y(animationTop);
    group.add(animationGroup);

    const chartOrder = ["bars", "area", "table", "bars", "textbox", "pie"];
    configureProcessingPlaceholderAnimation(placeholder, {
        chartOrder,
        transitionDuration: 600,
        pauseDuration: 800 * (1 + Math.floor(Math.random() * 4) / 4),
        reset: true,
    });

    updateProcessingPlaceholder(placeholder, {
        chartKey: chartOrder[0],
        stage: "build",
        stageProgress: 0,
        shimmerProgress: 0,
        hue: 0,
    });

    if (options.amimCallback) {
        const animationNode = options.animationNode ?? group;
        group.attrs.hasAnimationNode = animationNode._id;
        options.amimCallback(animationNode, (tick) => tickProcessingPlaceholder(placeholder, tick));
    }
}

export function renderProcessingPlaceholder(stageOptions = {}, options = {}) {
    const width = Number.isFinite(options.width) ? options.width : Number.isFinite(stageOptions.width) ? stageOptions.width : 960;
    const height = Number.isFinite(options.height) ? options.height : Number.isFinite(stageOptions.height) ? stageOptions.height : 540;
    const spacerY = Number.isFinite(options.spacerY) ? options.spacerY : Math.min(Math.max(Math.round(height * 0.05), 24), 80);
    const theme = options.theme ?? stageOptions.theme ?? defaultTheme;
    const animationId =
        options.animationId ??
        stageOptions.animationId ??
        options.id ??
        stageOptions.id ??
        `processing_placeholder_${Math.random().toString(36).slice(2)}`;
    const messageText = options.messageText ?? stageOptions.messageText ?? "Working...";

    const group = new Konva.Group({
        id: stageOptions.id ?? options.id,
        x: stageOptions.x ?? options.x ?? 0,
        y: stageOptions.y ?? options.y ?? 0,
        width,
        height,
        name: "inf_track primitive inf_keep processing_placeholder",
    });

    renderProcessingState({
        group,
        x: 0,
        y: 0,
        width,
        height,
        spacerY,
        theme,
        id: animationId,
        options: stageOptions,
        messageText,
    });

    return group;
}

 function renderSlideSuggestions(primitive, g, theme, options){
            let margin = [60, 60 ,60, 60], spacer = [20,20]
            let mainWidth = 1920  - margin[3] - margin[1]
            let headerFontSize = 48
            const skeleton = primitive.slide_state.data?.slideSpec
            const t = new CustomText({
                text: skeleton.title,
                align:"left",
                fontStyle: "bold",
                fill: themeColor(theme, 'muted'),
                x: margin[3],
                y: margin[0],
                wrap: true,
                width: mainWidth,
                lineHeight: 1.05,
                fontSize: headerFontSize ,
                refreshCallback: options.imageCallback,
                fontFamily: theme.fontFamily
            })
            g.add(t)

            const sectionsInput = skeleton.sections ?? [];
            const appliedSkeletonLayout = applyLayoutPresetToSections(skeleton.layout, sectionsInput);
            const sections = appliedSkeletonLayout.sections ?? sectionsInput;
            const layoutForFrames = appliedSkeletonLayout.layout ?? skeleton.layout;
            const mainstore = MainStore();

            const contentTop = margin[0] + (headerFontSize * 2 * 1.05) + spacer[1];
            const contentHeight = Math.max(0, 1080 - margin[2] - contentTop);

            const layoutFrames = computeStructuredSectionFrames(sections, layoutForFrames, {
                width: mainWidth,
                height: contentHeight,
                startX: margin[3],
                startY: contentTop,
                spacerX: spacer[1],
                spacerY: spacer[0]
            }) ?? computeSequentialSectionFrames(sections.length, {
                width: mainWidth,
                height: contentHeight,
                startX: margin[3],
                startY: contentTop,
                spacerX: spacer[1]
            });

            sections.forEach((d, i) => {
                const frame = layoutFrames[i];
                if (!frame) {
                    return;
                }

                const { x, y, width: sectionWidth, height: sectionHeight } = frame;
                const sectionData = options.data?.sections?.[i];
                const summaryPayload = d.materialized ?? {};
                const legacyPayload = primitive.referenceParameters?.materializedSummaries;
                const legacySummary = typeof legacyPayload?.summaryText === "string" ? legacyPayload.summaryText : "";
                const rawSummary = typeof summaryPayload.plain === "string" ? summaryPayload.plain : legacySummary;
                const summaryText = rawSummary.replace(/\s*\[\[id:[^\]]+\]\]/g, "").trim();
                const isPending = d?.runtime_status === "pending" || d?.status === "pending";

                const outline = new Konva.Rect({
                    x,
                    y,
                    width: sectionWidth,
                    height: sectionHeight,
                    strokeWidth: 1,
                    stroke: themeColor(theme, "border", "#a2a2a2"),
                    cornerRadius: 10,
                    dashEnabled: true,
                    dash: [5, 3],
                });
                g.add(outline);

                if (isPending) {
                    renderProcessingState({
                        group: g,
                        x,
                        y,
                        width: sectionWidth,
                        height: sectionHeight,
                        spacerY: spacer[0],
                        theme,
                        id: primitive.id + (i << 3),
                        options,
                    });
                    return;
                }

                const displayTitle = typeof d.title === "string" && d.title.trim().length > 0
                    ? d.title.trim()
                    : "Preview not available";

                const textNode = new Konva.CustomText({
                    text: displayTitle,
                    fontSize: 26,
                    align: "center",
                    x: x + 32,
                    width: Math.max(sectionWidth - 64, 120),
                    wrap: true,
                    fontFamily: theme.fontFamily,
                    fill: themeColor(theme, "primary"),
                });

                if (d.type === "summary") {
                    const contentPadding = 24;
                    const buttonHeight = 44;
                    const buttonWidth = Math.min(220, sectionWidth - contentPadding * 2);
                    const buttonX = x + (sectionWidth - buttonWidth) / 2;
                    const buttonY = y + sectionHeight - buttonHeight - spacer[0];

                    const summaryAreaX = x + contentPadding;
                    const summaryAreaY = y + contentPadding;
                    const summaryAreaWidth = sectionWidth - contentPadding * 2;
                    const summaryAreaHeight = Math.max(buttonY - summaryAreaY - spacer[0], 80);

                    const hasSummary = summaryText.length > 0;

                    const summaryNode = new CustomText({
                        x: summaryAreaX,
                        y: summaryAreaY,
                        width: summaryAreaWidth,
                        height: summaryAreaHeight,
                        text: hasSummary ? summaryText : "Summary not generated yet.",
                        align: "left",
                        wrap: true,
                        lineHeight: 1.35,
                        fontSize: hasSummary ? 22 : 20,
                        fill: themeColor(theme, hasSummary ? "primary" : "muted"),
                        fontFamily: theme.fontFamily,
                        withMarkdown: true,
                        refreshCallback: options.imageCallback,
                    });
                    g.add(summaryNode);

                    const buttonLabel = hasSummary ? "Refresh summary" : "Generate summary";
                    const button = createActionButton({
                        x: buttonX,
                        y: buttonY,
                        width: buttonWidth,
                        height: buttonHeight,
                        text: buttonLabel,
                        theme,
                        imageCallback: options.imageCallback,
                        onClick: async () => {
                            await mainstore.doPrimitiveAction(primitive, "run_agent_materialize_summary", {
                                sectionId: d.id,
                            });
                        },
                    });
                    g.add(button);

                    if (displayTitle) {
                        textNode.y(Math.max(y + spacer[0], buttonY - textNode.height() - spacer[0]));
                        g.add(textNode);
                    }
                    return;
                }

                const preview = sectionData?.visualization;
                if (preview) {
                    const previewWidth = Math.min(sectionWidth * 0.75, sectionWidth - 64);
                    const previewHeight = Math.min(sectionHeight * 0.6, sectionHeight - textNode.height() - spacer[0] * 3);
                    const previewX = x + (sectionWidth - previewWidth) / 2;
                    const previewY = y + spacer[0];

                    const renderOptions = preview.renderOptions ?? {};
                    if (renderOptions.size === "size") {
                        renderOptions.tableWidth = previewWidth;
                        renderOptions.tableHeight = previewHeight;
                    }

                    const chart = renderDatatable({
                        data: preview.data,
                        viewConfig: preview.viewConfig,
                        renderOptions: {
                            ...renderOptions,
                            headerBackground: false,
                            headerFontSize: (renderOptions.legend_size ?? 8) * 1.25,
                        },
                        id: primitive.id,
                        stageOptions: {
                            x: previewX,
                            y: previewY,
                            imageCallback: options.imageCallback,
                        },
                        theme: options.theme,
                    });
                    g.add(chart);
                    if (preview.renderOptions.size === "scale") {
                        const scale = Math.min(previewWidth / chart.width(), previewHeight / chart.height());
                        chart.scale({ x: scale, y: scale });
                    }

                    if (displayTitle) {
                        textNode.y(Math.min(y + sectionHeight - textNode.height() - spacer[0], previewY + previewHeight + spacer[0]));
                        g.add(textNode);
                    }
                    return;
                }

                renderProcessingState({
                    group: g,
                    x,
                    y,
                    width: sectionWidth,
                    height: sectionHeight,
                    spacerY: spacer[0],
                    id: primitive.id + "-" + i,
                    theme,
                    options,
                    messageText: "Awaiting generation...",
                });
                if (displayTitle) {
                    textNode.y(y + sectionHeight - textNode.height() - spacer[0]);
                    g.add(textNode);
                }
            });
            const suggestions = primitive.slide_state.suggestions
            const suggestionCount = primitive.slide_state.suggestions.length
            const currentSuggestion = (primitive.slide_state.data?.selection ?? 1) 

            function switchSuggestion(suggestion){
                console.log(`DEPRECATED - DO NOT SET IN RENDERER`)
                const newData = {
                    ...primitive.slide_state,
                    data: {
                        selection: suggestion,
                        slideSpec: suggestions[suggestion - 1]
                    }
                }
                

                primitive.setField("slide_state", newData)
            }

            if( currentSuggestion > 1){
                g.add( arrowButton( {x: 1840, y: 10, size: 30, dir: "left", callback: ()=>switchSuggestion( currentSuggestion - 1), theme} ))
            }
            if( currentSuggestion < suggestionCount ){
                g.add( arrowButton( {x: 1880, y: 10, size: 30, dir: "right", callback: ()=>switchSuggestion( currentSuggestion + 1), theme} ))
            }
}

registerRenderer( {type: "type", id: "page", configs: "default"}, (primitive, options = {})=>{
    const config = {
        ...options,
        width: 1920 ?? options.width, 
        height: 1080 ?? options.height, 
        pageColumns: 6 ?? options.pageColumns 
    }
    const pfc = primitive.configParent ?? primitive
    const themeKey = pfc.renderConfig?.theme
    let theme = themes[themeKey] ?? options.theme ?? themes.default;
    const g = new Konva.Group({
        id: primitive.id,
        x: config.x,
        y: config.y,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        minRenderSize : 0,
        name:"inf_track",
        clipX:0,
        clipY:0,
        clipWidth:config.width,
        clipHeight:config.height
    })


    let startX = 0
    let startY = 0
    let pageCol = 0
    let maxX = config.width, maxY = config.height
    let pageIdx = options.itemIdx ?? 0
    if( !options.utils?.prepareBoards){
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            height: config.height,
            stroke: themeColor(theme, 'border', '#555'),
            fill: themeColor(theme, 'background', 'white'),
            name:"background no_hover"
        })
        g.add(r)
        if( theme.elements ){
            g.add( renderBaseThemeElements(primitive, theme, config, options) )
            g.add( renderTopThemeElements(primitive, theme, config, options) )
        }
        if( primitive.slide_state?.data?.slideSpec && !options.getConfig ){
            renderSlideSuggestions( primitive, g, theme, options)
        }
        g.name("inf_track page")
    }else{
        const subpages = options.utils.prepareBoards( primitive )
        for(const subboards of subpages){
        const subrenders = subboards.map(d=>options.utils.renderBoard(d, {imageCallback: options.imageCallback, amimCallback: options.amimCallback, theme }))
            let i = 0
            const emptyElementIds = subboards
                .map(entry => {
                    const entryId = entry?.primitive?.id
                    const stateEntry = entryId ? entry?.state?.[entryId] : undefined
                    if( !entryId || !stateEntry ){
                        return null
                    }
                    return (stateEntry.empty || stateEntry.noContent) ? entryId : null
                })
                .filter(Boolean)
            const hasEmptyContent = emptyElementIds.length > 0

            const sg = new Konva.Group({
                id: primitive.id,
                x: startX,
                y: startY,
                width: config.width,
                height: config.height,
                onClick: options.onClick,
                minRenderSize : 0,
                pageIdx,
                name: hasEmptyContent ? "inf_track _inf_keep _page empty_content" : "inf_track _inf_keep _page",
                clipX:0,
                clipY:0,
                clipWidth:config.width,
                clipHeight:config.height
                //name:"inf_track action_primitive inf_keep"
            })
            sg.attrs.emptyContent = hasEmptyContent
            sg.attrs.emptyElementIds = emptyElementIds
            const r = new Konva.Rect({
                x: 0,
                y: 0,
                width: config.width,
                height: config.height,
                stroke: themeColor(theme, 'border', '#555'),
                fill: themeColor(theme, 'background', 'white'),
                name:"background"
            })
            sg.add(r)
            g.add(sg)

            if( theme.elements ){
                const elements = renderBaseThemeElements(primitive, theme, config, options) 
                elements.attrs.pageTrack = pageIdx
                sg.add( elements )
            }


            let idx = 0
            if( subrenders.length === 0 ){
                if( primitive.slide_state?.data?.slideSpec && !options.getConfig ){
                    const eg = new Konva.Group({
                        width: config.width,
                        height: config.height,
                        id: "elements",
                        //name:"inf_track primitive shape_element"
                        name:"inf_track primitive shape_element no_hover no_select no_drag"
                    })
                    renderSlideSuggestions( primitive, eg, theme, options)
                    g.add(eg)
                }
            }

            for(const sub of subrenders){
                if( !sub?.rendered ){
                    continue
                }
                idx++
                 const sx = sub.rendered.scaleX() || 1;
                const sy = sub.rendered.scaleY() || 1;

                // base size in local units (unscaled)
                const w = sub.rendered.width();
                const h = sub.rendered.height();

                const x = sub.x;
                const y = sub.y;

                const sw = w * sx;
                const sh = h * sy;

                const overLeft   = Math.max(0, -x);
                const overTop    = Math.max(0, -y);
                const overRight  = Math.max(0,  x + sw - config.width);
                const overBottom = Math.max(0,  y + sh - config.height);

                const visW = Math.max(0, sw - overLeft - overRight);
                const visH = Math.max(0, sh - overTop  - overBottom);

                const clipX = overLeft / sx;
                const clipY = overTop  / sy;
                const clipW = visW     / sx;
                const clipH = visH     / sy ;

                sub.rendered.x(sub.x)
                sub.rendered.y(sub.y)
                sub.rendered.attrs.pageTrack = pageIdx
                sub.rendered.find(d=>d.attrs.name?.includes("inf_track")).forEach(d=>d.attrs.pageTrack = pageIdx)
                if( !sub.rendered.name()){
                    sub.rendered.name('inf_track primitive')
                }
                sub.rendered.clip({ x: clipX, y: clipY, width: clipW, height: clipH });
                sg.add(sub.rendered)
            }
            if( theme.elements ){
                const elements = renderTopThemeElements(primitive, theme, config, options) 
                elements.attrs.pageTrack = pageIdx
                sg.add( elements )
            }
            maxX = Math.max(maxX, startX + config.width)
            maxY = Math.max(maxY, startY + config.height)
            startX += config.width + 20
            pageCol++
            if( pageCol >= config.pageColumns ){
                pageCol = 0
                startX = 0
                startY += config.height + 20
            }
            pageIdx++
        }
        
        g.width(maxX)
        g.height(maxY)
        g.clipWidth(maxX)
        g.clipHeight(maxY)
        if( options.getConfig ){
            config.height = maxY
            config.width = maxX
        }
    }
    config.width = maxX
    config.height = maxY
    if( options.getConfig ){
        return config
    }

    return g
})

registerRenderer( {type: "type", id: "actionrunner", configs: "default"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, typeText: "Action", typeIcon: <HeroIcon icon='FARun'/>}))
registerRenderer( {type: "type", id: "summary", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, width:500, contentAsMarkdown: true,typeText: "Summary", typeIcon: <HeroIcon icon='FARun'/>}))
registerRenderer( {type: "type", id: "flowcontrol", configs: "widget"}, renderFlowControlPrimitive)
registerRenderer( {type: "type", id: "categorizer", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, contentAsMarkdown: true,typeText: "Action", typeIcon: <HeroIcon icon='FARun'/>}))
registerRenderer( {type: "type", id: "query", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, width:500, contentAsMarkdown: true, typeText: "Query", typeIcon: <HeroIcon icon='FARobot'/>}))
registerRenderer( {type: "type", id: "question", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, width:400, contentAsMarkdown: true, typeText: "Question", typeIcon: <HeroIcon icon='QuestionMarkCircleIcon'/>}))
registerRenderer( {type: "type", id: "observation", configs: "observation"}, (primitive, options = {})=>{
    const observationData = options.observationData ?? {}
    const theme = options.theme ?? defaultTheme
    const textColor = themeColor(theme, "primary", "#111827")
    const subtitleColor = themeColor(theme, "secondary", "#475569")
    const fontFamily = theme.fontFamily ?? "Poppins"
    const textWidth = options.tableWidth ?? options.width ?? 420
    const group = new Konva.Group({
        id: options.id,
        name: "observation inf_track",
        x: options.x ?? 0,
        y: options.y ?? 0
    })

    const primaryText = observationData.takeaway
        ?? primitive.referenceParameters?.takeaway
        ?? observationData.description
        ?? primitive.referenceParameters?.description
        ?? "No observation insight available yet."
    const takeawayNode = new CustomText({
        x: 0,
        y: 0,
        width: textWidth,
        text: primaryText,
        wrap: true,
        ellipses: false,
        fontFamily,
        fontSize: 14,
        lineHeight: 1.4,
        fill: textColor,
        listening: false
    })
    group.add(takeawayNode)
    const blockHeight = takeawayNode.textHeight ?? takeawayNode.height() ?? 0
    let cursorY = blockHeight + 12

    const summarySections = []
    const includeSummary = observationData.summaryInclude ?? primitive.referenceParameters?.summaryInclude
    if( includeSummary ){
        summarySections.push({ title: "Group A summary", text: includeSummary })
    }
    const comparisonSummary = observationData.summaryComparison ?? primitive.referenceParameters?.summaryComparison
    if( comparisonSummary ){
        summarySections.push({ title: "Group B summary", text: comparisonSummary })
    }

    summarySections.forEach((section)=>{
        const labelNode = new CustomText({
            x: 0,
            y: cursorY,
            width: textWidth,
            text: section.title,
            wrap: true,
            ellipses: false,
            fontFamily,
            fontSize: 12,
            fontStyle: "600",
            fill: subtitleColor,
            listening: false
        })
        group.add(labelNode)
        const labelHeight = labelNode.textHeight ?? labelNode.height() ?? 0
        cursorY += labelHeight + 4

        const summaryNode = new CustomText({
            x: 0,
            y: cursorY,
            width: textWidth,
            text: section.text,
            wrap: true,
            ellipses: false,
            fontFamily,
            fontSize: 12,
            lineHeight: 1.35,
            fill: textColor,
            listening: false
        })
        group.add(summaryNode)
        const summaryHeight = summaryNode.textHeight ?? summaryNode.height() ?? 0
        cursorY += summaryHeight + 12
    })

    const tableData = observationData.tableData
    const viewConfig = observationData.viewConfig
    if( tableData ){
        const renderOptions = {
            ...options,
            tableWidth: options.tableWidth ?? textWidth,
            tableHeight: options.tableHeight ?? options.height
        }
        const tableGroup = renderDatatable({
            id: `${primitive.id}_observation_table`,
            primitive,
            data: tableData,
            stageOptions: { x: 0, y: cursorY },
            renderOptions,
            viewConfig
        })
        if( tableGroup ){
            group.add(tableGroup)
            cursorY += tableGroup.height()
            const existingWidth = group.width() ?? 0
            group.width(Math.max(existingWidth, textWidth, tableGroup.width()))
        }else{
            group.width(textWidth)
        }
    }else{
        const placeholder = new CustomText({
            x: 0,
            y: cursorY,
            width: textWidth,
            text: "No supporting view data available.",
            wrap: true,
            ellipses: false,
            fontFamily,
            fontSize: 12,
            fill: subtitleColor,
            listening: false
        })
        group.add(placeholder)
        cursorY += placeholder.textHeight ?? placeholder.height() ?? 0
        group.width(textWidth)
    }

    group.height(cursorY)
    return group
})
registerRenderer( {type: "type", id: "action", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, contentAsMarkdown: true, typeText: "Action", typeIcon: <HeroIcon icon='FARobot'/>}))
registerRenderer( {type: "type", id: "external", configs: "widget"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, contentAsMarkdown: true, typeText: "External", gradient: true, color: "#a21caf", typeIcon: <HeroIcon icon='FAPlug'/>, showActiveChip: true}))
registerRenderer( {type: "type", id: "search", configs: "default"}, (primitive,options)=>renderDefaultActionPrimitive(primitive, {...options, gradient: true}))

function renderDefaultActionPrimitive(primitive, options){
        options.data ||= {}
        const config = {width: 400, height: 200, ...options}
        const g = new Konva.Group({
            id: primitive.id,
            x: config.x,
            y: config.y,
            width: config.width,
            height: config.height,
            onClick: options.onClick,
            minRenderSize : 0,
            name:"inf_track action_primitive inf_keep"
        })
        const color = options.color ?? PrimitiveConfig.typeConfig[primitive.type]?.render?.background ?? "white"
        const r = new Konva.Rect({
            x: 0,
            y: 0,
            cornerRadius: 10,
            width: config.width,
            height: config.height,
            fill: color
        })
        if( options.gradient ){
            r.fillPriority("linear-gradient")
            r.fillLinearGradientStartPoint({ x: config.width / 2, y:config.height});
            r.fillLinearGradientEndPoint({ x: config.width / 2.1, y:  0});
            r.fillLinearGradientColorStops([0, mixHexWithWhite(color, 0.9), 0.06, mixHexWithWhite(color, 0.92), 0.15, "white"]);
        }
    
        let lx = 9, ly = 9
        function addWidgetText(text, options = {}){
            if( options.x ){
                lx = options.x
            }
            if( options.y ){
                ly = options.y
            }
            ly += (options.margin?.[0] ?? 0)
            let fontHeight = options.fontSize ?? 14
            let lineHeight = options.lineHeight ?? 1.1
            const t = new CustomText({
                text: text,
                align:"left",
                wrap: false,
                withMarkdown: options.markdown,
                fontStyle: options.bold ? "bold" : undefined,
                fill: options.color,
                verticalAlign:"middle",
                x: lx,
                y: ly,
                wrap: options.wrap,
                lineFill: options.lineFill,
                height: options.maxHeight,
                ellipses: Boolean(options.maxHeight),
                width: config.width - lx,
                lineHeight: lineHeight,
                fontSize: fontHeight,
                refreshCallback: options.imageCallback
            })
            ly += t.height() + (options.margin?.[1] ?? 0)
            return t
        }
    
        g.add(r)
    
        if( options.data.icon ){
            renderReactSVGIcon( options.data.icon, 
                {
                    target: g,
                    x:lx, 
                    y:ly,
                    width: 48,
                    height: 48,
                    imageCallback: options.imageCallback
                })
            lx = 64
            ly = 16
        }else if( options.data.imageUrl){
            g.add( imageHelper( options.data.imageUrl, {
                x: lx,
                y: ly,
                size: 48,
                center: true,
                imageCallback: options.imageCallback,
                placeholder: options.placeholder !== false
            }) )
            lx = 64
            ly = 16
        }

        g.add(addWidgetText(options.data.title, {fontSize: 18, bold:true, lineFill: '#666'}))
        
        if( options.data.count !== undefined){

            const count = addWidgetText(options.data.count + " " + options.data.items, {color: "#eee", fontSize: 14, lineFill: "#3f6212"})
            const pill = new Konva.Rect({
                x: lx,
                y: count.attrs.y,
                cornerRadius: 10,
                fill: PrimitiveConfig.typeConfig[primitive.type]?.render?.accentBackground ?? "#626262",
                width: count.textWidth + 16,
                height: count.textHeight + 8
            })
            count.attrs.x += 8
            count.attrs.y += 5
            ly += 5
            g.add(pill)
            g.add(count)
        }
        
        if( options.data?.descendants ){
            let py = 20
            for(const d of options.data.descendants ){
                g.add(addWidgetText(d.title + " - " + d.count, {x: 20, y: py + 2}))
                if( d.icon ){
                    renderReactSVGIcon( d.icon, 
                            {
                                target: g,
                                x:2, 
                                y:py,
                                width: 16,
                                height: 16,
                                imageCallback: options.imageCallback,
                                target: g
                            })
                }
                py += 20
            }
        }
        if( options.data.content ){
            g.add(addWidgetText(options.data.content, {fontSize: 14, maxHeight: 300, lineHeight: 1.25, color: '#555', wrap: true, markdown: options.contentAsMarkdown, margin: [8,8]}))
        }
        
    
        /*const button1 = new Konva.Group({
            x: config.width - 46,
            y: 5,
            width: 36,
            height:36,
            name:"inf_track widget"
        })
    
        if( true ){
            const progress = 0.3
            button1.add( new Konva.Circle({
                x: 18,
                y: 18,
                radius: 16,
                stroke: '#3f6212',
                strokeWidth: 1,
                name: "hover_target"
            }))
            button1.add( new Konva.Arc({
                x: 18,
                y: 18,
                innerRadius: 14,
                outerRadius: 18,
                angle: 360 * progress,
                rotation: 270,
                stroke: undefined,
                fill: "#3f6212",
                name: "hover_target"
            }))
            button1.add( new Konva.Rect({
                x: 13,
                y: 13,
                width: 10,
                height: 10,
                fill: "#3f6212",
                hoverFill: r.attrs.fill,
                name: "hover_target"
            }))
        }else{
            button1.add( new Konva.Circle({
                x: 18,
                y: 18,
                radius: 18,
                stroke: '#3f6212',
                strokeWidth: 1,
                fill: r.attrs.fill,
                hoverFill: "#3f6212",
                name: "hover_target"
            }))
            button1.add( new Konva.Line({
                points:[20-8.66,8,20+8.66,18,20-8.66,28],
                fill: "#3f6212",
                hoverFill: r.attrs.fill,
                closed: true,
                name: "hover_target"
            }))
        }
        g.add(button1)*/
    
        const finalHeight = Math.max( config.height, ly + 20)
        r.height(finalHeight)
        g.height(finalHeight)

        if( options.getConfig ){
            config.height = finalHeight
            return config
        }
    
        renderReactSVGIcon( options.typeIcon ?? MagnifyingGlassIcon, 
                            {
                                props: {fill: "#555"},
                                target: g,
                                x:5, 
                                y: finalHeight - 20,
                                width: 16,
                                height: 16,
                                imageCallback: options.imageCallback,
                                
                            })
        g.add(addWidgetText(`${options.typeText ?? "Search"} #${primitive.plainId}`, {color:"#555", fontSize: 11, x: 25, y: finalHeight- 18}))

        if( options.data.allowShowItems !== false ){
            const label = new CustomText({text: "Show", color: "#eee", fontSize: 11, lineFill: "#3f6212", x: 4, y: 3})
            const button = new Konva.Group({
                x: config.width - 46,
                y: finalHeight - 18,
                width: label.width() + 8,
                height: label.height() + 4,
                name:"inf_track widget"
            })
            button.add(new Konva.Rect({
                x: 0,
                y: 0,
                cornerRadius: 10,
                fill: '#f2f2f2',
                hoverFill: '#d2d2d2',
                width: button.width(),
                height: button.height(),
                data: {
                    open: options.data.showItems
                },
                name: "hover_target clickable toggle_items"
            }))
            button.add(label)
            g.add(button)
        }

        if( options.showActiveChip && primitiveHasActiveProcessingForCard(primitive) ){
            const chip = createRunningPulseChip({color: options.color ?? "#0ea5e9", dotRadius: 3})
            chip.x(config.width - chip.width() - 10)
            chip.y(10)
            g.add(chip)
        }

        return g
}

const CARD_ACTIVE_PROCESSING_STATUSES = new Set(["running", "pending", "queued"]);
function primitiveHasActiveProcessingForCard(primitive){
    const processing = primitive?.processing;
    if (!processing || typeof processing !== "object") {
        return false;
    }
    for (const value of Object.values(processing)) {
        if (!value || typeof value !== "object") continue;
        const status = String(value.status ?? "").toLowerCase();
        if (CARD_ACTIVE_PROCESSING_STATUSES.has(status)) {
            return true;
        }
    }
    return false;
}

function renderFlowControlPrimitive(primitive, options = {}){
    const flowControlConfig =
        primitive?.referenceParameters?.flowControl ??
        primitive?.configParent?.referenceParameters?.flowControl ??
        primitive?.origin?.referenceParameters?.flowControl ??
        options.data?.flowControl ??
        {}
    const states = Array.isArray(flowControlConfig.states)
        ? flowControlConfig.states
        : Array.isArray(options.data?.states)
        ? options.data.states
        : Array.isArray(options.data?.flowControlStates)
        ? options.data.flowControlStates
        : []
    const evaluationCandidates = [
        options.status?.flowControl,
        options.data?.flowControl,
        options.renderOptions?.status?.flowControl,
        options.renderOptions?.flowControl,
        primitive?.processing?.flowControl,
        primitive?.flowControl
    ]
    const evaluation = evaluationCandidates.find(candidate => candidate && typeof candidate === "object") ?? {}
    const activePins = evaluation.activePins ?? {}
    const stateResults = evaluation.stateResults ?? {}
    const evaluated = evaluation.evaluated === true
    const haltFlow = evaluation.haltFlow === true

    const activeStateNamesSet = new Set()
    const stateLines = []

    states.forEach((state, idx)=>{
        const name = state?.name ?? state?.pinKey ?? `State ${idx + 1}`
        const keyCandidates = []
        if( state?.id ){
            keyCandidates.push(state.id)
        }
        if( state?.pinKey ){
            keyCandidates.push(state.pinKey)
        }
        const fallbackKey = state?.pinKey ?? state?.id ?? `state-${idx}`
        if( !keyCandidates.includes(fallbackKey) ){
            keyCandidates.push(fallbackKey)
        }

        let resolvedKey = fallbackKey
        let result
        for(const key of keyCandidates){
            if( stateResults[key] !== undefined ){
                resolvedKey = key
                result = stateResults[key]
                break
            }
        }

        const activePinKey = state?.pinKey ?? resolvedKey
        const passed = activePins[activePinKey] === true || result?.passed === true
        const pending = result?.pending === true || (!evaluated && !passed)
        const isHaltingActive = passed && state?.haltFlow === true
        const indicator = isHaltingActive ? "⛔" : passed ? "🟢" : pending ? "⏳" : "⚪"

        let statusLabel
        if( isHaltingActive ){
            statusLabel = "Halting"
        }else if( passed ){
            statusLabel = "Active"
        }else if( pending ){
            statusLabel = "Pending"
        }else if( !evaluated ){
            statusLabel = "Not evaluated"
        }else{
            statusLabel = "Inactive"
        }

        if( passed ){
            activeStateNamesSet.add(name)
        }

        const conditions = Array.isArray(state?.conditions) ? state.conditions : []
        const conditionResults = result?.conditions ?? {}
        const condBaseKey = state?.pinKey ?? resolvedKey
        let conditionSummary
        if( conditions.length > 0 ){
            let metCount = 0
            conditions.forEach((cond, cIdx)=>{
                const condKey = cond?.id ?? `${condBaseKey}-cond-${cIdx}`
                if( conditionResults[condKey] === true ){
                    metCount += 1
                }
            })
            conditionSummary = `${metCount}/${conditions.length} met`
        }

        const parts = [`- ${indicator} **${name}**`]
        if( state?.pinKey ){
            parts.push(`_(pin ${state.pinKey})_`)
        }
        if( state?.haltFlow ){
            parts.push("_halts flow_")
        }
        parts.push(`— ${statusLabel}`)
        if( conditionSummary ){
            parts.push(`(${conditionSummary})`)
        }
        if(Array.isArray(state.passThroughPins) && state.passThroughPins.length > 0){
            parts.push(`[pass-through: ${state.passThroughPins.join(", ")}]`)
        }
        stateLines.push(parts.join(" "))
    })

    const activeStateNames = Array.from(activeStateNamesSet).filter(Boolean)
    const pendingSources = Array.isArray(evaluation.pendingSources) ? evaluation.pendingSources : []
    let pendingLabels = []
    if( pendingSources.length > 0 ){
        let mainstore
        try{
            mainstore = MainStore()
        }catch(err){
            mainstore = undefined
        }
        const seenLabels = new Set()
        pendingLabels = pendingSources.map(source=>{
            if( !source ){
                return undefined
            }
            if( typeof source === "object" ){
                const label = source.title ?? source.plainId ?? source.id
                if( label && !seenLabels.has(label) ){
                    seenLabels.add(label)
                    return label
                }
                return undefined
            }
            const sourcePrimitive = mainstore?.primitive?.(source)
            const label = sourcePrimitive?.title ?? sourcePrimitive?.plainId ?? source
            if( label && !seenLabels.has(label) ){
                seenLabels.add(label)
                return label
            }
            return undefined
        }).filter(Boolean)
    }

    const statusSummary = haltFlow ? "Flow halted" : evaluated ? "Evaluated" : "Awaiting inputs"
    const infoLines = [`**Status:** ${statusSummary}`]
    if( haltFlow ){
        infoLines.push("> Downstream execution is paused.")
    }

    if( states.length === 0 ){
        infoLines.push("_No states configured._")
    }else{
        const activeSummary = activeStateNames.length > 0 ? activeStateNames.join(", ") : "_None_"
        infoLines.push(`**Active states:** ${activeSummary}`)
        if( pendingLabels.length > 0 ){
            infoLines.push(`_Waiting on:_ ${pendingLabels.join(", ")}`)
        }else if( !evaluated ){
            infoLines.push("_Waiting on:_ Inputs")
        }
        infoLines.push("")
        infoLines.push("**States**")
        infoLines.push(...stateLines)
    }

    const content = infoLines.join("\n").trim() || "_No states configured._"
    const data = {...(options.data ?? {})}
    data.title = data.title ?? primitive?.title ?? primitive?.metadata?.title ?? "Flow Control"
    data.content = content
    data.allowShowItems = false
    data.icon = data.icon ?? <HeroIcon icon='TreeStruct'/>

    const typeLabel = haltFlow ? "Flow Control (Paused)" : evaluated ? "Flow Control" : "Flow Control (Pending)"

    return renderDefaultActionPrimitive(primitive, {
        ...options,
        width: options.width ?? 500,
        typeText: typeLabel,
        typeIcon: <HeroIcon icon='TreeStruct'/>,
        contentAsMarkdown: true,
        data
    })
}

export function convertIconForKonva( icon, options ){
    if( typeof(icon) === "string"){
    }else if( typeof(icon) === "function"){
        icon = renderToString( icon(options.props))
    }else if( icon.render ){
        icon = renderToString( icon.render(options.props))
    }else if( icon.$$typeof ){
        //icon = renderToString( icon )
        icon = renderToString(cloneElement(icon, options.props));
    }else{
        return undefined
    }
    const dim = options.width && options.height ? undefined : extractSVGDimensions(icon)
    let width = options.width ?? dim.width
    let height = options.height ?? dim.height
    let ox = 0, oy = 0

    if( options.width && options.height && options.center){
        const dim = extractSVGDimensions(icon)
        const scale = Math.min( options.width / dim.width, options.height / dim.height)
        const nWidth = dim.width * scale
        const nHeight = dim.height * scale
        ox = (width - nWidth) / 2
        oy = (height - nHeight) / 2
        width = nWidth
        height = nHeight
    }
    return {icon, ox, oy, width, height}
}

function renderReactSVGIcon( _icon, options = {} ){
    const {icon, ox, oy, width, height} = convertIconForKonva( _icon, options)
    const o = imageHelper( 'svg:' + icon, {
        x: (options.x ?? 0) + ox,
        y: (options.y ?? 0) + oy,
        width,
        height,
        center: true,
        imageCallback: options.imageCallback,
        maxScale: 4,
        scaleRatio: 4
    })
    if( options.target){
        options.target.add(o)
    }
    return o
}

function extractSVGDimensions(svgString) {
    // Regex patterns for width, height, and viewBox
    const widthRegex = /\swidth\s*=\s*["']?(\d+\.?\d*%?)["']?/i;
    const heightRegex = /\sheight\s*=\s*["']?(\d+\.?\d*%?)["']?/i;
    const viewBoxRegex = /\sviewBox\s*=\s*["']?([\d\s.-]+)["']?/i;
  
    // Extract matches for width, height, and viewBox
    const widthMatch = svgString.match(widthRegex);
    const heightMatch = svgString.match(heightRegex);
    const viewBoxMatch = svgString.match(viewBoxRegex);
  
    // Extract width and height values from viewBox if present
    let viewBoxWidth = null, viewBoxHeight = null;
    if (viewBoxMatch) {
      const viewBoxValues = viewBoxMatch[1].split(/\s+/);
      if (viewBoxValues.length === 4) {
        viewBoxWidth = viewBoxValues[2];  // 3rd value in viewBox is width
        viewBoxHeight = viewBoxValues[3]; // 4th value in viewBox is height
      }
    }
  
    // Use width and height if available, otherwise use viewBox dimensions
    const definitiveWidth = parseInt(widthMatch ? widthMatch[1] : viewBoxWidth)
    const definitiveHeight = parseInt(heightMatch ? heightMatch[1] : viewBoxHeight);
  
    return {
      width: definitiveWidth ? definitiveWidth : null,
      height: definitiveHeight ? definitiveHeight : null
    };
  }
  

registerRenderer( {type: "categoryId", id: 29, configs: "default"}, (primitive, options = {})=>{
    const config = {width: 80, padding: [10,10,10,10], ...options}
    config.height = config.width
    if( options.getConfig){
        return config
    }
    

    let availableWidth = config.width - config.padding[1] - config.padding[3]
    let availableHeight = config.height - config.padding[0] - config.padding[2]
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    const backgroundColor = themeColor(options.theme, "background", "#ffffff")


    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        minRenderSize : 0,
        name:"inf_track primitive inf_keep"
    })
    if( g ){
        let showName = options.renderOptions?.showName ?? true

        const logo = imageHelper( `/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`, {
            x: 0,
            y: 0,
            padding: config.padding,
            width: config.width,
            height: config.height - (showName ? 12 : 0),
            center: true,
            alt: primitive.title,
            imageCallback: options.imageCallback,
            linkUrl: primitive.referenceParameters?.url,
            colorKey: options.renderOptions?.colorKey,
            clearImg: options.renderOptions?.clearImg,
            placeholder: options.placeholder !== false
        })
        if( showName ){
                const r = new Konva.Rect({
                    x:0,
                    y: 0,
                    width: config.width,
                    height: config.height,
                    fill: "white"
                })
                const text = new CustomText({
                    fontSize: 8,
                    text: primitive.title,
                    align:"center",
                    wrap: false,
                    ellipsis: true,
                    verticalAlign:"middle",
                    linkUrl: primitive.referenceParameters?.url,
                    bgFill:"#f3f4f6",
                    x: 0,
                    y: config.height - 6,
                    width: config.width,
                    height: 12,
                    refreshCallback: options.imageCallback
                })
                g.add(r)
                g.add(text)
        }
        g.add( logo )

    }
    return g

})

export function finalizeImages( node, options ){
    const list = node.find('.img_ph')
    while(list.length > 0){
        const delay = 1 +(Math.random() * 50)
        const thisSection = list.splice(0,20)
        setTimeout(()=>{
            for(const d of thisSection){
                d.finalize(options)            
            }
        }, delay)

    }
    //for(const d of list){
    //    d.finalize()
   // }

}


function imageHelper(url, options){
        const image = new CustomImage( {
            url: url, 
            ...options,
            x: options.x ?? 0, 
            y: options.y ?? 0, 
            padding: options.padding, 
            width: options.width ?? options.size, 
            height: options.height ?? options.size, 
            placeholder: options.placeholder, 
            name: options.placeholder ? "img_ph" : undefined})

        if(options.imageCallback){
            image.attrs.refreshCallback = ()=>options.imageCallback(image)
        }

    return image

}

    function getRotatedPolygon(node) {
        // Get the local dimensions.
        const rect = node.getClientRect({ skipTransform: true });
        const width = rect.width
        const height = rect.height
        // With the offset set to (width/2, height/2), the local corners are:
        /*const localCorners = [
          { x: -width / 2, y: -height / 2 },
          { x: width / 2, y: -height / 2 },
          { x: width / 2, y: height / 2 },
          { x: -width / 2, y: height / 2 }
        ];*/
        const localCorners = [
          { x: 0, y: 0 },
          { x: width, y: 0 },
          { x: width, y: height },
          { x: 0, y: height  }
        ];
        // Use the node's absolute transform to map local coordinates into the parent coordinate system.
        const transform = node.getAbsoluteTransform();
        return localCorners.map(pt => transform.point(pt));
      }
  
  /**
   * Checks if two convex polygons intersect using the Separating Axis Theorem (SAT).
   *
   * @param {Array} polyA - Array of points [{x, y}, ...] for the first polygon.
   * @param {Array} polyB - Array of points [{x, y}, ...] for the second polygon.
   * @returns {boolean} True if the polygons overlap; false if a separating axis is found.
   */
  function polygonsIntersect(polyA, polyB) {
    // Helper: Compute the normalized axes (perpendiculars to edges) for a polygon.
    function getAxes(polygon) {
      const axes = [];
      for (let i = 0; i < polygon.length; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % polygon.length];
        // The edge from p1 to p2.
        const edge = { x: p2.x - p1.x, y: p2.y - p1.y };
        // The perpendicular (normal) is (edge.y, -edge.x)
        let normal = { x: edge.y, y: -edge.x };
        // Normalize the axis.
        const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
        if (length !== 0) {
          normal.x /= length;
          normal.y /= length;
        }
        axes.push(normal);
      }
      return axes;
    }
  
    // Helper: Projects a polygon onto an axis and returns the min and max values.
    function projectPolygon(axis, polygon) {
      let min = axis.x * polygon[0].x + axis.y * polygon[0].y;
      let max = min;
      for (let i = 1; i < polygon.length; i++) {
        const projection = axis.x * polygon[i].x + axis.y * polygon[i].y;
        if (projection < min) {
          min = projection;
        }
        if (projection > max) {
          max = projection;
        }
      }
      return { min, max };
    }
  
    // Combine the axes from both polygons.
    const axes = getAxes(polyA).concat(getAxes(polyB));
  
    // For each axis, project both polygons. If the projections do not overlap,
    // then a separating axis exists and the polygons do not intersect.
    for (let i = 0; i < axes.length; i++) {
      const axis = axes[i];
      const projA = projectPolygon(axis, polyA);
      const projB = projectPolygon(axis, polyB);
      if (projA.max < projB.min || projB.max < projA.min) {
        // Found a separating axis.
        return false;
      }
    }
  
    // No separating axis found; the polygons intersect.
    return true;
  }

export function plotWordCloud(options = {}){
    const startWords = options.words ?? []
    const sizes = startWords.map(d=>d.size)
    const min = sizes.reduce((a,c)=>a < c ? a : c, Infinity)
    const max = sizes.reduce((a,c)=>a > c ? a : c, -Infinity)
    const range = max - min
    const maxFont = 80
    const minFont = 8
    const fontScale = (maxFont - minFont) / range
    const words = startWords.map(d=>{
        return {
            text: d.text,
            size: ((d.size - min) * fontScale) + minFont
        }
    }).sort((a,b)=>b.size - a.size)

    
    // Center point of the stage
    const cx = options.width / 2;
    const cy = options.height / 2;
    
    // Array to store already placed Konva.Text nodes
    const placedWords = [];
    
    /**
     * Attempts to place a word on the stage by testing five orientations.
     * The candidate rotations range from -30° to 30°.
     * For each candidate spiral position, we try all rotations. If one is
     * collision-free, we place the word; otherwise, we continue moving along the spiral.
     */
    const g = new Konva.Group({
        id: options.id,
        width: options.width,
        height: options.height,
        minRenderSize : 0,
        name:"inf_track primitive inf_keep"
    })
    function placeWord(word) {
        let spiralAngle = 0;
        let spiralRadius = 0;
        let x = cx;
        let y = cy;
    
        // Define the five candidate rotations in degrees.
        const candidateRotations = [-15,0, 15, -30, 30];

        while (true) {
            // Test each candidate rotation for the current (x,y) position.
            for (let rotation of candidateRotations) {
                // Create a temporary text node with the current rotation.
                const textNode = new Konva.Text({
                    text: word.text,
                    fontSize: word.size,
                    fontFamily: 'Arial',
                    fill: 'black',
                    x: x,
                    y: y,
                    rotation: rotation,
                });
                const rect = textNode.getClientRect({ skipTransform: true });
                textNode.offset({x: rect.width / 2, y: rect.height / 2})
        
              // Compute the rotated polygon for this candidate.
                const candidatePoly = getRotatedPolygon(textNode);

                // Check for overlap with each already placed word.
                let collides = placedWords.some(existingWord => {
                    const existingPoly = getRotatedPolygon(existingWord);
                    return polygonsIntersect(candidatePoly, existingPoly);
                });

                if (!collides) {
                    g.add(textNode);
                    placedWords.push(textNode);
                    return; // Exit once the word is placed.
                }
            }
        
            spiralAngle += 0.1;
            spiralRadius += 2;
            x = cx + spiralRadius * Math.cos(spiralAngle);
            y = cy + spiralRadius * Math.sin(spiralAngle);
        }
    }
    
    // Place each word from the list
    words.forEach(placeWord);

    return g

}

export function renderMatrix( primitive, list, options ){
    let columnExtents = options.columnExtents ? options.columnExtents.slice(0, options.max_cols ?? 200) : [{idx:0}]
    //let rowExtents = options.rowExtents ? options.rowExtents.slice(0,options.max_rows ??  200).sort((a,b)=>(`${a.label ?? ""}`).localeCompare(`${b.label ?? ""}`)) : [{idx:0}]
    let rowExtents = options.rowExtents ? options.rowExtents.slice(0,options.max_rows ??  200) : [{idx:0}]
    if( columnExtents.length === 0){
        columnExtents = [{idx:0}]
    }
    if( rowExtents.length === 0){
        rowExtents = [{idx:0}]
    }

    if( options.widgetConfig ){
        const g = new Konva.Group({
            name: "view",
            x:options.x ?? 0,
            y:options.y ?? 0
        })

        let w, h
        const widget = RenderPrimitiveAsKonva( primitive, {config: "widget", data: options.widgetConfig, imageCallback: options.imageCallback})
        widget.name(widget.name() + " item_info")
        g.add( widget )
        w = widget.width()
        h = widget.height()

        if( options.widgetConfig.showItems ){

            const padding = options.x === 0 ? [10,10,10,10] : [0,0,0,0]
            
            const content = renderMatrix(primitive, list, {...options, x: 0, y: 0, padding, widgetConfig: undefined})
            if( content ){

                const contentScale = Math.min(1, w / content.width() )
                content.scale({x:contentScale, y:contentScale})
                content.y( h + (options.x === 0 ? 0 : 20))
                g.add(content)
                
                h = h + (content.height() * contentScale)
            }
        }

        g.width( w )
        g.height( h )
        
        return g
    }

    
    
    const g = new Konva.Group({
        name: "view",
        x:options.x ?? 0,
        y:options.y ?? 0
    })
    let configName = options.viewConfig?.renderType ?? "grid" 

    const asCounts = options.viewConfig?.showAsCounts
    const asChecks = options.viewConfig?.parameters?.showAsCheck ?? configName === "checktable"
    if( asCounts ){
        configName = "heatmap"
    }
    if( asChecks ){
        configName = "checktable"
    }

    const columnSize = new Array(columnExtents.length).fill(0)
    const rowSize = new Array(rowExtents.length).fill(0)
    const cells = []
    
    const referenceIds = list.map(d=>d.primitive.referenceId).filter((d,i,a)=>a.indexOf(d) === i)
    if( referenceIds.length > 1){
        console.log(`Multiple types in list, selecting first`)
    }
    
    const globalData = {}
    let checkMap
    const mainstore = MainStore()
    let cellContentLimit
    if( configName === "grid" ){//&& !asCounts ){
        cellContentLimit = {
            "result": 50,
            "evidence": 150
        }[list[0]?.primitive?.type] ?? 150
        
        cellContentLimit = {
            29: 63
        }[referenceIds[0]] ?? cellContentLimit
    }else if(configName === "checktable" || configName === "dials"){
        const evaluatorMap = (axis)=>{
            if( !axis){return}
            if( axis.type == "category" ){
                const axisPrim = mainstore.primitive(axis.primitiveId)
                if(axisPrim?.referenceId === PrimitiveConfig.Constants.SCORE){
                    return axisPrim.primitives.allItems.reduce((a,d)=>{
                        if( d.title === "high"){
                            a[d.id] = 3
                        }else if( d.title === "medium"){
                            a[d.id] = 2
                        }else if( d.title === "low"){
                            a[d.id] = 1
                        }
                        return a
                    },{})
                }
                if(axisPrim?.referenceId === PrimitiveConfig.Constants.EVALUATOR){
                    return axisPrim.primitives.allItems.reduce((a,d)=>{
                        if( d.title === "clearly"){
                            a[d.id] = 3
                        }else if( d.title === "likely"){
                            a[d.id] = 2
                        }else if( d.title === "possibly"){
                            a[d.id] = 1
                        }
                        return a
                    },{})
                }
            }
            return undefined

        }
        const colEvalMap =  evaluatorMap({type: "category", primitiveId: options.axis?.column?.primitiveId})
        const rowEvalMap =  evaluatorMap({type: "category", primitiveId:options.axis?.row?.primitiveId})
        if( colEvalMap ){
            console.log(`Got evaluator in view ${primitive.plainId}`)
            console.log(colEvalMap)
            list = list.map(d=>({...d, column: 0}))
            columnExtents = [{idx:0}]
        }
        if( colEvalMap || rowEvalMap){
            checkMap ={
                ...(colEvalMap ?? {}),
                ...(rowEvalMap ?? {})
            }
        }

    }

    let itemColsByColumn = new Array(columnExtents.length).fill(0)

    let rIdx = 0
    for(const row of rowExtents){
        let cIdx = 0
        for(const column of columnExtents){

            let subListWithAllocations = list.filter((item)=>(Array.isArray(item.column) ? item.column.includes( column.idx ) : item.column === column.idx) && (Array.isArray(item.row) ? item.row.includes( row.idx ) : item.row === row.idx))
            let subList = subListWithAllocations.map(d=>d.primitive)

            if( asCounts ){
                //console.log(`MAPPING FOR asCounts`)
                //subList = mainstore.uniquePrimitives( subList.flatMap(d=>d.findParentPrimitives({referenceId: [9]})) )
            }

            let cellShowExtra
            if( cellContentLimit ){
                if( subList.length > cellContentLimit){
                    if( options.expand && options.expand.includes([column.idx, row.idx].filter(d=>d).join("-"))){
                        cellShowExtra = -1
                    }else{
                        cellShowExtra = subList.length - cellContentLimit
                        subList = subList.slice(0, cellContentLimit)
                    }
                }
            }

            const itemLength = subList.length 
            const itemCols = Math.floor( Math.sqrt( itemLength) )

            itemColsByColumn[cIdx] = options.data?.columns ?? Math.max(itemColsByColumn[cIdx], itemCols)

            cells.push({
                cIdx, rIdx,
                col: column,
                row: row,
                list: subList,
                listWithAllocations: subListWithAllocations,
                itemLength,
                itemCols,
                showExtra: cellShowExtra
            })
            cIdx++
        }
        rIdx++
    }


    let minHeight = 0
    let minWidth = options.width ?? defaultWidthByCategory[referenceIds[0]] ?? 300
    if( referenceIds[0] === 29){
        minWidth = options.hideColumnHeader ? 60 : 120
    }

    
    if( asCounts ){
        minWidth = 128
    }else if( asChecks || configName === "checktable" || configName === "dials"){
        minWidth = 64
    }
    if( configName === "ranking"){
        minWidth = options.width ? options.width / columnExtents.length : 600 
        minHeight = options.height ? options.height / rowExtents.length : 600 
    }

    

    const baseRenderConfig = {
                config: configName, 
                referenceId: referenceIds[0], 
                placeholder: options.placeholder !== false, 
                imageCallback: options.imageCallback,
                utils: options.utils,
                renderOptions: options.renderOptions,
                theme: options.theme,
                padding: options.padding ?? [0,0,0,0]
    }
    if( asCounts || options?.renderOptions?.calcRange){
        const cellCount = cells.map(d=>d.itemLength)
        const columnRange = columnExtents.map((_,i)=>cells.filter(d=>d.cIdx === i).map(d=>d.itemLength))
        const rowRange = rowExtents.map((_,i)=>cells.filter(d=>d.rIdx === i).map(d=>d.itemLength))

        baseRenderConfig.range = [Math.min(...cellCount), Math.max(...cellCount)]
        baseRenderConfig.totals = cellCount.reduce((a,d)=>a+d,0)
        baseRenderConfig.colTotal = columnRange.map(d=>d.reduce((a,d)=>a+d,0))
        baseRenderConfig.rowTotal = rowRange.map(d=>d.reduce((a,d)=>a+d,0))
        baseRenderConfig.colRange = columnRange.map(d=>[Math.min(0,...d), Math.max(...d)])
        baseRenderConfig.rowRange = rowRange.map(d=>[Math.min(0,...d), Math.max(...d)])
        baseRenderConfig.colTitles = columnExtents.map(d=>d.label)
        baseRenderConfig.rowTitles = rowExtents.map(d=>d.label)
    }

    for(const cell of cells){
        const config = RenderSetAsKonva( primitive, cell.list, 
            {
                ...baseRenderConfig,
                cIdx: cell.cIdx,
                rIdx: cell.rIdx,
                renderConfig:{
                    ...(options.renderConfig ?? {}),
                    asChecks,
                    showExtra: cell.showExtra,
                    columns: itemColsByColumn[cell.cIdx], 
                    minWidth: minWidth,
                    viewConfig: options.viewConfig
                },
                checkMap,
                allocationExtents: options.allocations,
                listWithAllocations: cell.listWithAllocations,
                data: options.data,
                getConfig: true
            } )    
            if(!config){
                console.warn("Did not get a render result")
                return
            }
        
        itemColsByColumn[cell.cIdx] = Math.max(itemColsByColumn[cell.cIdx], config.columns)
        
        columnSize[cell.cIdx] = Math.max( config.width > columnSize[cell.cIdx] ? config.width : columnSize[cell.cIdx], minWidth)
        rowSize[cell.rIdx] = Math.max( config.height > rowSize[cell.rIdx] ? config.height : rowSize[cell.rIdx], 30, minHeight)

        cell.config = config
    }
    const maxRowHeight = Math.max(...rowSize, 0)

    let headerHeight = 0
    let maxFont = maxRowHeight / 4
    let headerScale = Math.max(1, Math.max(columnSize.reduce((a,c)=>a+c, 0) / 2000 , rowSize.reduce((a,c)=>a+c, 0) / 4000 ))
    let headerFontSize = Math.min(12 * headerScale, maxFont, 120)
    let textPadding = new Array(4).fill(headerFontSize * 0.3 )
    let rowLabelAsText = true
    let recalc = false
    let recalcRow = false
    let columnLabelAsText = true
    const iconSize = [128, ...columnSize, ...rowSize].reduce((a,c)=>a < c ? a : c)
    let columnLabels
    let headerTextHeight = 0

    let showColumnHeaders = options.hideColumnHeader !== true && (columnExtents.length > 1)

    if( configName === "timeseries"){
        globalData.maximumValue = cells.map(d=>d.config.data.series).flat().reduce((a,c)=> a > c ? a : c, -Infinity) 
        globalData.minimumValue = cells.map(d=>d.config.data.series).flat().reduce((a,c)=> a < c ? a : c, Infinity) 
    }

    if( showColumnHeaders){

        headerHeight = (headerFontSize * 4)
        headerTextHeight = headerHeight - textPadding[0] - textPadding[2]
        
        
        
        
        rowSize.forEach((d,i)=>{if(d < headerHeight){
            rowSize[i] = headerHeight
        }})
        
        
        columnLabels = columnExtents.map((d,idx)=>{
            const cellConfig = cells.find(d=>d.cIdx === idx)?.config ?? {padding: [5,5,5,5]}
            if( d.imageUrl ){
                columnLabelAsText = false
                const iconSize = 100
                const logo = new Konva.Group({
                    x: 0,
                    y: cellConfig.padding[0] + textPadding[0],
                    width: columnSize[idx],
                    height: iconSize
                })
                logo.add( imageHelper( "/api/remoteImage?url=" + d.imageUrl, {
                    x: (columnSize[idx] - iconSize) /2,
                    y: 0,
                    size: iconSize * 0.8,
                    center: true,
                    imageCallback: options.imageCallback,
                    placeholder: options.placeholder !== false
                }) )
                logo.add( new CustomText({
                    //fontFamily: "system-ui",
                    fontSize: iconSize * 0.12,
                    text: d.label  ?? "",
                    wrap: true,
                    align:"center",
                    bgFill:"transparent",
                   // verticalAlign:"middle",
                    x: 0,
                    y: iconSize * 0.9,
                    width: columnSize[idx],
                    height: iconSize * 0.18,
                    ellipsis: true,
                    refreshCallback: options.imageCallback
                }))
                headerHeight = logo.height()
                return logo
            }else if( options.axis?.column?.type === "icon"){
                columnLabelAsText = false
                const useSize = iconSize < headerHeight ? iconSize : headerHeight
                const logo = imageHelper( `/api/image/${d.idx}`, {
                    x: (columnSize[idx] - useSize) /2,
                    y: cellConfig.padding[0] + textPadding[0],
                    linkUrl: d.referenceParameters?.url,
                    size: useSize, 
                    center: true,
                    imageCallback: options.imageCallback,
                    placeholder: options.placeholder !== false
                })
                return logo
            }else{
                const longestWord = `${(d.label ?? "")}`.split(" ").reduce((a,c)=>a.length > c.length ? a : c, 0)
                const colWidth = columnSize[idx] - textPadding[1] - textPadding[3] - cellConfig.padding[3] - cellConfig.padding[1] 
                
                const text = new CustomText({
                    //fontFamily: "system-ui",
                    fontSize: headerFontSize,
                    text: longestWord,
                    align:"center",
                    wrap: true,
                    verticalAlign:"middle",
                    bgFill:"#f3f4f6",
                    x: cellConfig.padding[3] + textPadding[3],
                    y: cellConfig.padding[0] + textPadding[0],
                    width: colWidth,
                    height: "auto",
                    refreshCallback: options.imageCallback
                })
                
                while( text.measureSize(longestWord).width > colWidth && headerFontSize > 6){
                    headerFontSize = headerFontSize > 50 ? headerFontSize -= (headerFontSize * 0.1) : headerFontSize - 0.25
                    text.fontSize( headerFontSize )
                } 
                text.text( d.label ?? "" )  
                
                return text
            }
        })
        
        
        function isColumnHeaderOverflowing(labels, height){
            return labels.filter(d=>d.height() > height).length >0
        }
        
        if( columnLabelAsText ){
            
            while( isColumnHeaderOverflowing( columnLabels, headerTextHeight) && headerFontSize > 6){
                headerFontSize = headerFontSize > 50 ? headerFontSize -= (headerFontSize * 0.1) : headerFontSize - 0.25
                columnLabels.forEach(d=>d.fontSize(headerFontSize))
                recalc = true
            }
            columnLabels.forEach(d=>d.fontSize(headerFontSize))
        }
        
    }
    let showRowheaders =  (options.hideRowHeaders !== true ) && (rowExtents.length > 1 || rowExtents[0]?.label?.length > 0)
    let headerWidth = 0
    let rowLabels
    
    if( showRowheaders ){
        function isRowHeaderOverflowing(labels, heights){
            return labels.filter((d,i)=>d.height() > heights[i]).length >0
        }
        const longestPairs = rowExtents.map(d=>{
            const words = `${(d.label  ?? "")}`.split(" ")
            const coupleLength = [words, words.map((d,i,a)=> i > 0 ? a[i-1] + " " + d : undefined ).filter(d=>d)].flat()
            return coupleLength.reduce((a,c)=>c.length > a.length ? c : a, "" )
        }).reduce((a,c)=>c.length > a.length ? c : a, "" )
        
        let textWidth 
        let rowHeights = []
        rowLabels = rowExtents.map((d,idx)=>{
            const cellConfig = cells.find(d=>d.rIdx === idx)?.config
            rowHeights[idx] = rowSize[idx] - textPadding[0] - textPadding[2] - cellConfig.padding[0] - cellConfig.padding[2]
            if( d.imageUrl ){
                rowLabelAsText = false
                const iconSize = 100
                headerWidth = iconSize + textPadding[1] + textPadding[3] + cellConfig.padding[3] + cellConfig.padding[1]
                const logo = new Konva.Group({
                    x: cellConfig.padding[3] + textPadding[3],
                    y: cellConfig.padding[0] + textPadding[0],
                    width: iconSize,
                    height: iconSize
                })
                logo.add( imageHelper( "/api/remoteImage?url=" + d.imageUrl, {
                    x: iconSize * 0.1,
                    y: 0,
                    size: iconSize * 0.8,
                    center: true,
                    imageCallback: options.imageCallback,
                    placeholder: options.placeholder !== false
                }) )
                logo.add( new CustomText({
                    //fontFamily: "system-ui",
                    fontSize: iconSize * 0.12,
                    text: d.label  ?? "",
                    wrap: true,
                    align:"center",
                    bgFill:"transparent",
                   // verticalAlign:"middle",
                    x: 0,
                    y: iconSize * 0.9,
                    width: iconSize,
                    height: iconSize * 0.18,
                    ellipsis: true,
                    refreshCallback: options.imageCallback
                }))
                return logo
            }else if( options.axis?.row?.type === "icon"){
                rowLabelAsText = false
                const iconSize = 100
                headerWidth = iconSize + textPadding[1] + textPadding[3] + cellConfig.padding[3] + cellConfig.padding[1]
                const logo = imageHelper( `/api/image/${d.idx}`, {
                    x: cellConfig.padding[3] + textPadding[3],
                    y: cellConfig.padding[0] + textPadding[0],
                    size: iconSize,
                    center: true,
                   // linkUrl: d.referenceParameters?.url,
                    imageCallback: options.imageCallback,
                    placeholder: options.placeholder !== false
                })
                return logo
            }else{

                const text = new CustomText({
                    //fontFamily: "system-ui",
                    fontSize: headerFontSize,
                    text: textWidth ? (d.label  ?? "") : ` ${longestPairs} `,
                    wrap: true,
                    align:"center",
                    bgFill:"#f3f4f6",
                    verticalAlign:"middle",
                    x: cellConfig.padding[3] + textPadding[3],
                    y: cellConfig.padding[0] + textPadding[0],
                    width: textWidth ? textWidth : "auto",
                    refreshCallback: options.imageCallback
                })
                if( !textWidth ){
                    textWidth = text.width()
                    headerWidth = textWidth + textPadding[1] + textPadding[3] + cellConfig.padding[3] + cellConfig.padding[1]
                    text.width(textWidth)
                    text.text( d.label  ?? "")
                }
                return text
            }
        })
        if( rowLabelAsText){

            while( isRowHeaderOverflowing( rowLabels, rowHeights) && headerFontSize > 6){
                headerFontSize = headerFontSize > 50 ? headerFontSize -= (headerFontSize * 0.1) : headerFontSize - 0.25
                rowLabels.forEach(d=>d.fontSize(headerFontSize))
                recalcRow = true
            }
            if( recalcRow && showColumnHeaders ){
                columnLabels.forEach(d=>d.fontSize(headerFontSize))
            }
        }
    }
    if( recalc || recalcRow ){
        headerTextHeight = columnLabels ? columnLabels.reduce((a,c)=>c.height() > a ? c.height() : a, 0) : 0
        headerHeight = headerTextHeight + textPadding[0] + textPadding[2] 
    }
    if( showColumnHeaders){
        columnLabels.forEach(d=>{
            d.y(d.y() + ((headerTextHeight - d.height())/2) )
        })
    }
    let headerPadding = cells[0]?.config.padding[0] ?? 0

    const columnY = rowSize.map((d,i,a)=>a.reduce((t,c,i2)=>t + (i2 < i ? c : 0), showColumnHeaders ?  headerHeight + headerPadding : 0))
    
    if( showRowheaders ){
        rowExtents.forEach((header,idx)=>{
            const cellConfig = cells.find(d=>d.rIdx === idx)?.config ?? {padding:[0,0,0,0]}
            const group = new Konva.Group({
                name: "inf_track row_header",
                id: `${idx}`,
                x: 0,
                y: columnY[idx],
                width: headerWidth,
                height: rowSize[idx]
            }) 
            if( rowLabelAsText ){
                const bg = new Konva.Rect({
                    x: cellConfig.padding[3],
                    y: cellConfig.padding[0],
                    width: headerWidth - cellConfig.padding[3] - cellConfig.padding[1],
                    height: rowSize[idx] - cellConfig.padding[0] - cellConfig.padding[2] ,
                    fill:'#f3f4f6'
                })
                group.add(bg)
            }
            rowLabels[idx].y( cellConfig.padding[0] + textPadding[0] + (((rowSize[idx] - cellConfig.padding[0] - textPadding[0] - cellConfig.padding[2] - textPadding[2]) - rowLabels[idx].height()) / 2))
            group.add(rowLabels[idx])
            g.add(group)
        })
    }

    const columnX = columnSize.map((d,i,a)=>a.reduce((t,c,i2)=>t + (i2 < i ? c : 0), headerWidth ))
    if( showColumnHeaders){
        columnExtents.forEach((header,idx)=>{
            const cellConfig = cells.find(d=>d.cIdx === idx)?.config ?? {padding:[0,0,0,0]}
            const group = new Konva.Group({
                name: "inf_track column_header",
                x: columnX[idx],
                y: 0,
                width: columnSize[idx],
                height: headerHeight
            }) 
            if( columnLabelAsText ){
                const bg = new Konva.Rect({
                    x: cellConfig.padding[3],
                    y: cellConfig.padding[0],
                    width: columnSize[idx] - cellConfig.padding[3] - cellConfig.padding[1] ,
                    height: headerHeight,
                    fill:'#f3f4f6'
                })
                group.add(bg)
            }
            group.add(columnLabels[idx])
            g.add(group)
        })

    }
    for( const cell of cells){

        const c = RenderSetAsKonva( primitive, cell.list, 
            {
                ...baseRenderConfig,
                primitiveClick: options.primitiveClick,
                cIdx: cell.cIdx,
                rIdx: cell.rIdx,
                id: `${cell.cIdx}-${cell.rIdx}`, 
                renderConfig:{
                    ...(options.renderConfig ?? {}),
                    asChecks,
                    showExtra: cell.showExtra,
                    width: columnSize[cell.cIdx], 
                    height: rowSize[cell.rIdx] , 
                    minWidth: minWidth,
                    minHeight: rowSize[cell.rIdx] - cell.config.padding[0] - cell.config.padding[2],
                    maxHeight: options?.renderOptions?.height ? options?.renderOptions?.height - columnY[cell.rIdx] : undefined,
                    columns: itemColsByColumn[cell.cIdx], 
                    rows: cell.itemRows,
                    viewConfig: options.viewConfig
                },
                data: {
                    ...(options.data ?? {}),
                    ...(cell.config.data ?? {})
                },
                globalData,
                allocationExtents: options.allocations,
                listWithAllocations: cell.listWithAllocations,
                checkMap,
                cachedNodes: cell.config.cachedNodes
            })
        
        c.x(columnX[cell.cIdx] )
        c.y(columnY[cell.rIdx] )
        cell.node = c
        g.add(c)
    }

    
    let height = g.find(()=>true).map(d=>d.y() + d.height()).reduce((a,c)=>c > a ? c : a, 0)

    
    if( options.viewConfig?.renderType === "distribution_chart" && options.calcRange){
        columnExtents.forEach((header,idx)=>{
            const group = new Konva.Group({
                name: "inf_track column_header",
                x: columnX[idx],
                y: height + (headerFontSize * 0.2),
                width: columnSize[idx],
                height: headerFontSize * 1.4
            }) 
            group.add(new Konva.CustomText({
                x: 0,
                y: 0,
                width: columnSize[idx], 
                fontSize: headerFontSize,
                align:'center',
                color: '#777',
                text:  baseRenderConfig.colTotal[idx]
            }))
            g.add(group)
        })
        height += headerFontSize * 1.4
    }
    if( height < 40 ){
        height = 200
    }

    g.width( g.find(()=>true).map(d=>d.x() + d.width()).reduce((a,c)=>c > a ? c : a, 0))
    g.height( height )

    return g

}

function generateAxisTicks([minVal, maxVal], tickCount = 5) {
  // Ensure zero baseline for positive-only data
  const dataMin = minVal > 0 ? 0 : minVal;
  const dataMax = maxVal;

  const rawStep = (dataMax - dataMin) / (tickCount - 1);

  // Round step to a "nice" number (1, 2, 5 * power of 10)
  function niceNumber(x, round) {
    const exp = Math.floor(Math.log10(x));
    const f = x / Math.pow(10, exp);
    let nf;
    if (round) {
      if (f < 1.5) nf = 1;
      else if (f < 3) nf = 2;
      else if (f < 7) nf = 5;
      else nf = 10;
    } else {
      if (f <= 1) nf = 1;
      else if (f <= 2) nf = 2;
      else if (f <= 5) nf = 5;
      else nf = 10;
    }
    return nf * Math.pow(10, exp);
  }

  const step = niceNumber(rawStep, true);
  const niceMin = Math.floor(dataMin / step) * step;
  const niceMax = Math.ceil(dataMax / step) * step;

  const ticks = [];
  for (let v = niceMin; v <= niceMax; v += step) {
    ticks.push(v);
  }

  return ticks;
}

function renderBarChart( segments, {
    showValue,
    showLines,
    showAxisValue,
    showXAxis = true,
    showYAxis = true,
    renderContextId,
    allocationField,
    gridStroke = "black",
    subGridStroke = "#d2d2d2",
    ...options
}){
    const config = {size: 20, ...options}
    const width = config.width ?? config.size
    const height = config.height ?? config.barHeght ?? config.size

    let r = config.size / 2
    const g = new Konva.Group({
        x: options.x ?? 0,
        y: options.y ?? 0,
        width,
        height
    })
    const requestedMode = options.mode ?? "interleave"
    let mode
    if( options.stack ){
        mode = "stack"
    }else if( requestedMode === "separate" || requestedMode === "seperate"){
        mode = "separate"
    }else{
        mode = "interleave"
    }
    const fontSize = 4
    const minBarLabelWidth = 10
    let axisFontSize = config.width > 300 ? 20 : 6
    const yAxisVisible = showYAxis !== false
    const xAxisVisible = showXAxis !== false
    const axisValuesVisible = (showAxisValue !== false) && yAxisVisible
    const gridLinesVisible = (showLines !== false) && yAxisVisible

    const asPercent = options.showValue === "percent"
    const segmentCount = segments.length
    const subSegments = options.sublabels?.items?.length ?? 1
    const stackSegments = options.stack && subSegments === 1
    const multiDatasetStack = options.stack && !stackSegments && subSegments > 1
    const positiveStackTotals = new Array(segmentCount).fill(0)
    const negativeStackTotals = new Array(segmentCount).fill(0)
    const stackMagnitudeTotals = new Array(segmentCount).fill(0)
    let stackPositiveOverall = 0
    let stackNegativeOverall = 0
    let hasStackNegatives = false

    // Accumulate series totals and maxima
    const totals = [];
    const perSeriesMax = [];
    const perSegmentTotals = [];
    let globalMaxCount = 0
    let globalMinCount = Infinity
    for (let sIdx = 0; sIdx < segments.length; sIdx++){
        const { count } = segments[sIdx]
        if (Array.isArray(count)) {
            let segSum = 0
            let segmentPositiveTotal = 0
            let segmentNegativeTotal = 0
            for (let i = 0; i < count.length; i++) {
                const v = count[i] ?? 0
                segSum += v
                totals[i] = (totals[i] ?? 0) + v
                perSeriesMax[i] = Math.max(perSeriesMax[i] ?? 0, v)
                if (v > globalMaxCount) globalMaxCount = v
                if (v < globalMinCount) globalMinCount = v
                if( options.stack ){
                    if( v >= 0 ){
                        segmentPositiveTotal += v
                    }else{
                        segmentNegativeTotal += v
                    }
                }
            }
            perSegmentTotals[sIdx] = segSum
            if( options.stack ){
                positiveStackTotals[sIdx] = segmentPositiveTotal
                negativeStackTotals[sIdx] = segmentNegativeTotal
                stackMagnitudeTotals[sIdx] = segmentPositiveTotal + Math.abs(segmentNegativeTotal)
                if( segmentNegativeTotal < 0 ){
                    hasStackNegatives = true
                }
                if( stackSegments ){
                    stackPositiveOverall += segmentPositiveTotal
                    stackNegativeOverall += segmentNegativeTotal
                }
            }
        } else {
            const v = (count ?? 0)
            totals[0] = (totals[0] ?? 0) + v
            perSeriesMax[0] = Math.max(perSeriesMax[0] ?? 0, v)
            perSegmentTotals[sIdx] = v
            if (v > globalMaxCount) globalMaxCount = v
            if (v < globalMinCount) globalMinCount = v
            if( options.stack ){
                if( v >= 0 ){
                    positiveStackTotals[sIdx] = v
                    if( stackSegments ){
                        stackPositiveOverall += v
                    }
                }else{
                    negativeStackTotals[sIdx] = v
                    hasStackNegatives = true
                    if( stackSegments ){
                        stackNegativeOverall += v
                    }
                }
                stackMagnitudeTotals[sIdx] = Math.abs(v)
            }
        }
    }

    if (globalMinCount === Infinity) {
        globalMinCount = 0
    }

    const stackHasMagnitude = stackMagnitudeTotals.some(total => total > 0)
    const shouldNormalizeStacks = (options.normalizeStacked ?? multiDatasetStack) && !hasStackNegatives && stackHasMagnitude

    // Determine scaling range, allowing explicit overrides
    let minValue
    let maxValue
    if (asPercent) {
        minValue = 0
        maxValue = 100
    } else if (options.stack) {
        const stackedMax = stackSegments ? Math.max(0, stackPositiveOverall) : Math.max(0, ...positiveStackTotals)
        const stackedMin = stackSegments ? Math.min(0, stackNegativeOverall) : Math.min(0, ...negativeStackTotals)
        minValue = stackedMin
        maxValue = stackedMax || 1
    } else {
        minValue = Math.min(0, globalMinCount)
        maxValue = globalMaxCount
    }

    if( shouldNormalizeStacks ){
        minValue = 0
        maxValue = 1
    }

    if (options.minY !== undefined) {
        minValue = options.minY
    }
    if (options.maxY !== undefined) {
        maxValue = options.maxY
    }

    if (minValue > maxValue) {
        const tmp = minValue
        minValue = maxValue
        maxValue = tmp
    }

    if (minValue === maxValue) {
        const epsilon = Math.abs(minValue) > 1 ? Math.abs(minValue) * 0.01 : 1
        maxValue = minValue + epsilon
    }
    const positiveBaseline = Math.max(minValue, Math.min(0, maxValue))
    const negativeBaseline = Math.min(maxValue, Math.max(0, minValue))
    let colors = options.colors ?? options.theme?.palette?.categoryColors ?? categoryColors

    const axisLabelEntries = []
    let yAxisList = []
    const axisValueFormatter = (value)=> shouldNormalizeStacks ? `${Math.round(value * 100)}%` : formatNumber(value)

    const barBase = height - (xAxisVisible ? axisFontSize * 3 : 0.2)
    const barSize = barBase
    const valueRange = maxValue - minValue
    const safeRange = valueRange === 0 ? 1 : valueRange
    const scaleY = barSize / safeRange
    const clampValue = (value) => Math.max(minValue, Math.min(maxValue, value))
    const valueToY = (value) => barBase - ((value - minValue) * scaleY)
    let baseLine
    let ox = 0
    let naturalAxisGutter = 0

    const axisAreaHeight = xAxisVisible ? (height - barBase) : 0
    const useSecondaryAxisIcons = Boolean(options.secondaryAxisAsIcon)
    const axisIconPlaceholderAllowed = options.placeholder !== false
    const axisIconMainstore = useSecondaryAxisIcons ? resolveMainstoreInstance(options.mainstore) : undefined
    const axisPrimitiveResolver = useSecondaryAxisIcons && axisIconMainstore ? (options.primitiveResolver ?? createHeaderPrimitiveResolver(axisIconMainstore)) : undefined
    const axisIconScaleValue = typeof options.secondaryAxisIconScale === "string" ? parseFloat(options.secondaryAxisIconScale) : options.secondaryAxisIconScale
    const axisIconScaleFactor = Number.isFinite(axisIconScaleValue) && axisIconScaleValue > 0 ? axisIconScaleValue : 1

    if( yAxisVisible ){
        let rescaleAxis = false
        const yTicks = generateAxisTicks([minValue, maxValue])
        let yAxisGap = 0
        if( axisValuesVisible ){
            const maxAxisValueHeight = barSize / yTicks.length * 0.3
            yTicks.forEach((y, i)=>{
                const yp = valueToY(y)
                if( Number.isFinite(yp) && yp >= 0 && yp <= barBase){
                    const r = prepareAxisText( axisValueFormatter(y), {
                        fontSize: axisFontSize, 
                        maxHeight: maxAxisValueHeight,
                        textPadding: [0,0,0,0],
                        minFontSize: 1,
                        refreshCallback: options.imageCallback,
                        fontFamily: options.fontFamily,
                        fontFill: options.fontFill,
                        placeholderFill: options.placeholderFill,
                    } )
                    r.rendered.x(0)
                    r.rendered.y(yp)
                    g.add( r.rendered)
                    yAxisList.push(r.rendered)
                    if( r.rescaled ){
                        if( !rescaleAxis || r.fontSize < rescaleAxis ){
                            rescaleAxis = r.fontSize
                        }
                    }
                }
            })
            if( rescaleAxis ){
                axisFontSize = rescaleAxis
                for(const d of yAxisList ){
                    d.fontSize( rescaleAxis )
                }
            }
            const yAxisWidth = yAxisList.length ? Math.max( ...yAxisList.map(d=>d.width() ?? 0)) : 0
            if( yAxisWidth > 0 ){
                const ydelta = Math.max(10, yAxisWidth * 0.1)
                yAxisGap = yAxisWidth + ydelta
                ox = yAxisGap + ydelta
            }
            // Natural gutter this cell needs; the table may force a wider, shared gutter so bars align across faceted cells
            naturalAxisGutter = ox
            const forcedAxisGutter = options.forcedAxisGutter
            if( Number.isFinite(forcedAxisGutter) && forcedAxisGutter > ox ){
                ox = forcedAxisGutter
                yAxisGap = ox - Math.max(10, yAxisWidth * 0.1)
            }
            // Right-align labels to the gutter edge so they sit a consistent distance from the bars
            const labelWidth = yAxisWidth > 0 ? Math.max(yAxisWidth, yAxisGap) : yAxisWidth
            for(const d of yAxisList ){
                d.width( labelWidth )
                d.align("right")
                const firstLine = d.textArr?.[0]
                const metricHeight = (firstLine?.ascent ?? 0) + (firstLine?.descent ?? 0)
                const trueHeight = Number.isFinite(metricHeight) && metricHeight > 0 ? metricHeight : d.fontSize()
                d.y( d.y() - trueHeight / 2 )
            }
        }
        if( gridLinesVisible){
            const dash = [5,3]
            yTicks.forEach((y, i)=>{
                const yp = valueToY(y)
                if( Number.isFinite(yp) && yp >= 0 && yp <= barBase){
                    const line = new Konva.Line( {
                        points: [yAxisGap, yp, width, yp],
                        stroke: i === 0 ? gridStroke : subGridStroke,
                        dashEnabled: i > 0,
                        dash: i > 0 ? dash : undefined,
                        strokeWidth: 1
                    })
                    if( i === 0){
                        baseLine = line
                    }else{
                        g.add( line )
                    }
                }
            })
        }
    }
    const plotWidth = width - ox
    const subSegmentGap = (subSegments === 1 || segmentCount === 1) ? 0 : (mode === "interleave" ? 0.2 : 0.05)
    const segmentGap = stackSegments ? 0 : (segmentCount > 1 ? (plotWidth * subSegmentGap) / (segmentCount - 1) : 0)
    const availableWidth = plotWidth - (segmentGap * Math.max(0, segmentCount - 1))
    const safeAvailableWidth = availableWidth > 0 ? availableWidth : plotWidth
    let barWidth
    if( mode === "stack" ){
        if( stackSegments ){
            barWidth = safeAvailableWidth
        }else{
            barWidth = segmentCount ? safeAvailableWidth / segmentCount : safeAvailableWidth
        }
    }else{
        const divisor = segmentCount * (subSegments || 1)
        barWidth = divisor ? safeAvailableWidth / divisor : safeAvailableWidth
    }
    const groupWidth = mode === "interleave" ? barWidth * subSegments : barWidth
    const axisWidth = stackSegments ? barWidth : groupWidth
    if( xAxisVisible ){
        let rescaleAxis = false

        segments.forEach((s, idx)=>{
            if( stackSegments && idx > 0 ){
                return
            }
            [s.count].flat().forEach((ss, iIdx)=>{
                let x
                if( mode === "interleave"){
                    const segmentOffset = idx * (groupWidth + segmentGap)
                    x = ox + segmentOffset + (iIdx * barWidth)
                }else if( mode === "stack"){
                    if( stackSegments ){
                        x = ox
                    }else{
                        x = ox + (idx * (groupWidth + segmentGap))
                    }
                }else{
                    x = ox + (idx * barWidth) + (iIdx * segmentCount * barWidth) +(iIdx * segmentGap * segmentCount)
                }
                const isFirstSubSegment = iIdx === 0
                const shouldRenderAxisLabel = barWidth >= minBarLabelWidth
                    && (mode !== "interleave" || isFirstSubSegment)
                    && (!multiDatasetStack || isFirstSubSegment)
                if( shouldRenderAxisLabel ){
                    const axisLabelSource = stackSegments ? (options.stackLabel ?? segments[0] ?? "") : s
                    let axisNode
                    let axisNodeType = "text"
                    let axisTextResult

                    if( useSecondaryAxisIcons ){
                        let iconSize = computeHeaderIconSize({
                            availableWidth: axisWidth,
                            availableHeight: axisAreaHeight,
                            fontSize: axisFontSize
                        })
                        if( axisIconScaleFactor !== 1 ){
                            iconSize = Math.max(6, iconSize * axisIconScaleFactor)
                        }
                        const iconNode = buildHeaderIconNode({
                            item: axisLabelSource,
                            width: axisWidth,
                            iconSize,
                            refreshCallback: options.imageCallback,
                            placeholder: axisIconPlaceholderAllowed,
                            mainstore: axisIconMainstore,
                            primitiveResolver: axisPrimitiveResolver
                        })
                        if( iconNode ){
                            axisNode = iconNode
                            axisNodeType = "icon"
                        }
                    }

                    if( !axisNode ){
                        axisTextResult = prepareAxisText( axisLabelSource, {
                            fontSize: axisFontSize, 
                            maxWidth: axisWidth,
                            textPadding: [0,0,0,0],
                            minFontSize: 1,
                            fontFamily: options.fontFamily,
                            fontFill: options.fontFill,
                            placeholderFill: options.placeholderFill,
                            refreshCallback: options.imageCallback
                        } )
                        axisNode = axisTextResult.rendered
                    }

                    const nodeWidth = typeof axisNode.width === "function" ? axisNode.width() : axisWidth
                    const alignedX = x + Math.max(0, (axisWidth - nodeWidth) / 2)
                    axisNode.x(alignedX)
                    axisNode.y(0)
                    g.add(axisNode)
                    axisLabelEntries.push({node: axisNode, type: axisNodeType})

                    if( axisNodeType === "text" && axisTextResult?.rescaled ){
                        if( !rescaleAxis || axisTextResult.fontSize < rescaleAxis ){
                            rescaleAxis = axisTextResult.fontSize
                        }
                    }
                }
            })
        })
        if( rescaleAxis ){
            for(const entry of axisLabelEntries ){
                if(entry.type === "text" && typeof entry.node.fontSize === "function"){
                    entry.node.fontSize( rescaleAxis )
                }
            }
        }
    }

    let idx = 0 
    
    if( xAxisVisible ){
        for(const entry of axisLabelEntries ){
            if(entry.type === "icon"){
                const axisNode = entry.node
                const nodeHeight = typeof axisNode.height === "function" ? axisNode.height() : (axisFontSize * 2)
                const available = axisAreaHeight || (axisFontSize * 3)
                axisNode.y( barBase + Math.max(0, (available - nodeHeight) / 2) )
            }else{
                entry.node.y( barBase + (axisFontSize * 0.5) )
            }
        }
    }

    
    const colorBySubSegment = subSegments > 1 //options.colorBySecondary ?? segmentCount  === 1
    const whiteMixFactor = 0.8 / (colorBySubSegment ? segmentCount : subSegments)
    let globalStackPositiveBase = positiveBaseline
    let globalStackNegativeBase = negativeBaseline

    for( const s of  segments){
        // Reset stack position per segment (track positive and negative separately)
        let stackPositiveBase = stackSegments ? globalStackPositiveBase : positiveBaseline
        let stackNegativeBase = stackSegments ? globalStackNegativeBase : negativeBaseline
        ;[s.count].flat().forEach((ss, iIdx)=>{
            let x
            if( mode === "interleave"){
                const segmentOffset = idx * (groupWidth + segmentGap)
                x = ox + segmentOffset + (iIdx * barWidth)
            }else if( mode === "stack"){
                if( stackSegments ){
                    x = ox
                }else{
                    x = ox + (idx * (groupWidth + segmentGap))
                }
            }else{
                x = ox + (idx * barWidth) + (iIdx * segmentCount * barWidth) +(iIdx * segmentGap * segmentCount)
            }
            const denom = asPercent ? (perSegmentTotals[idx] || 0) : 1
            const rawValue = ss ?? 0
            const baseValue = asPercent ? (denom ? (100 * rawValue / denom) : 0) : rawValue
            const stackRange = shouldNormalizeStacks ? (stackMagnitudeTotals[idx] || 0) : 0
            const renderValue = shouldNormalizeStacks ? (stackRange ? rawValue / stackRange : 0) : baseValue
            let startValue = minValue
            let endValue = renderValue
            if( options.stack ){
                const isPositive = renderValue >= 0
                if( isPositive ){
                    startValue = stackPositiveBase
                    endValue = stackPositiveBase + renderValue
                    stackPositiveBase = endValue
                    if( stackSegments ){
                        globalStackPositiveBase = stackPositiveBase
                    }
                }else{
                    startValue = stackNegativeBase
                    endValue = stackNegativeBase + renderValue
                    stackNegativeBase = endValue
                    if( stackSegments ){
                        globalStackNegativeBase = stackNegativeBase
                    }
                }
            }
            const clampedStart = clampValue(startValue)
            const clampedEnd = clampValue(endValue)
            const startY = valueToY(clampedStart)
            const endY = valueToY(clampedEnd)
            const barTopY = Math.min(startY, endY)
            const barBottomY = Math.max(startY, endY)
            const h = barBottomY - barTopY
            if( h > 0){
                const renderIdx = s.renderIdx ?? s.order_idx ?? s.idx ?? idx
                const majorIdx = colorBySubSegment ? iIdx : renderIdx
                const minorIdx = colorBySubSegment ? renderIdx : iIdx
                let color = s.color ?? colors[majorIdx % colors.length]
                if( iIdx > 0 && !colorBySubSegment){
                    color = mixHexWithWhite(color, (minorIdx * whiteMixFactor))
                }
                
                var bar = new Konva.Rect({
                    x: x,
                    y: barTopY,
                    width: barWidth,
                    height: h,
                    fill: color,
                    name: "cell clickable hover",
                    id: `0-${renderIdx}`,
                });
                if (renderContextId) {
                    bar.attrs.renderContextId = renderContextId;
                }
                const dataPayload = {};
                const entryField = s.allocationField ?? allocationField;
                if (entryField !== undefined) {
                    const allocationIdx = s.allocationIdx ?? s.idx;
                    dataPayload.allocationField = entryField;
                    dataPayload.allocationIdx = allocationIdx;
                    dataPayload.allocationPos = renderIdx;
                    if (allocationIdx !== undefined && allocationIdx !== null) {
                        dataPayload.allocationId = `${entryField}:${allocationIdx}`;
                    }
                }
                if (options.cellId) {
                    dataPayload.cellId = options.cellId;
                }
                if (Object.keys(dataPayload).length > 0) {
                    bar.attrs.data = dataPayload;
                }
                g.add(bar)
            }
            const canRenderValue = showValue && h > 0 && barWidth >= minBarLabelWidth
            if( canRenderValue ){
                const isPositive = renderValue >= 0
                const valueY = isPositive ? (barTopY - fontSize * 1.2) : (barBottomY + fontSize * 0.2)
                const t = new CustomText({
                    x: x,
                    y: valueY,
                    fontSize: fontSize,
                    text: asPercent ? `${baseValue.toFixed(0)}%` : baseValue,
                    align:"center",
                    fill: '#334155',
                    bgFill: 'transparent',
                    width: barWidth,
                    align: "center",
                    refreshCallback: options.imageCallback
                })
                g.add(t)
            }
        })
        idx++
    }
    if( baseLine ){
        g.add(baseLine)
    }
    g.attrs.axisGutter = naturalAxisGutter
    return g
}
function renderSubCategoryChart( title, data, options = {}){
    let config ={
        fontSize: 22,
        ...options
    }
    let {
        x = 0,
        y = 0,
        innerPadding = [10,10,10,10],
        itemSize = 200,        
        paletteName ,
        theme,
        secondaryAxisAsIcon = false,
        secondaryAxisIconScale,
        mainstore,
        primitiveResolver,
        showXAxis = true,
        showYAxis = true
    } = options
    
    data = data.map((d,i)=>{
        const renderIdx = d.order_idx === undefined ? i : d.order_idx
        return {
            ...d,
            idx: renderIdx,
            sourceIdx: d.idx,
            allocationField: d.allocationField ?? options.allocationField,
            allocationIdx: options.allocationField ? (d.allocationIdx ?? d.idx) : undefined,
            renderIdx
        }
    })
    
    const width = options.width ?? options.itemSize
    const height = options.height ?? options.itemSize

    if( options.byTag ){
        data = data.sort((a,b)=>a.tag - b.tag)
        const tagColorsArr = Object.values(options.theme?.palette?.tagColors ?? tagColors)
        let lastTag, idx = 0
        data.forEach(d=>{
            if( d.tag !== lastTag){
                idx = 0
            }else{
                idx ++
            }
            lastTag = d.tag
            const colArr = tagColorsArr[d.tag]
            d.color = colArr[idx % colArr.length]
        })
    }else if(options.sort === "labels"){
        data = data.sort((a,b)=>(`${a.label ?? ""}`).localeCompare(`${b.label ?? ""}`))
    }else if(options.sort === "none"){

    }else{
        data = data.sort((a,b)=>b.count - a.count)
    }

    const isPieStyle = options.style !== "bar" && options.style !== "stacked_bar" && options.style !== "weighted"
    const renderData = isPieStyle ? data.filter(entry => (entry?.count ?? 0) > 0) : data
    const chartData = renderData.length > 0 ? renderData : data

    
    const sg = new Konva.Group({
        x,
        y,
        width,
        height
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width,
        height,
        hoverFill: theme?.palette?.hoverFill,
        hoverStroke: theme?.palette?.hoverStroke,
        name:"background"
    })
    sg.add(r)
    const innerSpacing = config.fontSize * 1.2

    const hasSublabels = Boolean(options.sublabels?.items?.length)
    const colorContextItems = hasSublabels ? options.sublabels.items : chartData
    const colorTargetLength = Math.max(1, colorContextItems.length)

    const fallbackPalette = options.theme?.palette?.categoryColors ?? categoryColors
    let colors = options.colors
    if( !colors ){
        colors = fallbackPalette
        if( options.colorMap ){
            colors = data.map(d=>options.colorMap[d.label])
        }else{
            let selectedPalette = heatMapPalette.find(d=>d.name === paletteName)
            if( selectedPalette ){
                if( selectedPalette.dynamic){
                    colors = interpolateHexColors(selectedPalette.colors.at(0), selectedPalette.colors.at(-1), colorTargetLength)
                    if( options.reversePalette ){
                        colors = [...selectedPalette.colors].reverse()
                    }
                }else if( selectedPalette.category_colors ){
                    colors = selectedPalette.category_colors 
                }else{
                    colors = [...selectedPalette.colors]
                }
            }
            if( options.reversePalette === false){
                if( colors.length > colorTargetLength){
                    colors = colors.slice( -colorTargetLength)
                }
            }else{
                colors = [...colors].reverse()
            }
        }
    }

    const normalizeColorList = (source)=>{
        const palette = (Array.isArray(source) && source.length) ? source : fallbackPalette
        if( palette.length >= colorTargetLength ){
            return palette.slice(0, colorTargetLength)
        }
        const extended = []
        for(let i = 0; i < colorTargetLength; i++){
            extended.push(palette[i % palette.length])
        }
        return extended
    }
    const normalizedPalette = normalizeColorList(colors)
    if( hasSublabels ){
        colors = colorContextItems.map((item, idx)=>{
            const mapped = options.colorMap?.[item.label]
            return mapped ?? normalizedPalette[idx]
        })
    }else{
        colors = normalizedPalette
    }
    if (!hasSublabels && options.allocationField && normalizedPalette.length > 1) {
        const keyForEntry = (entry)=>{
            if (!entry) {
                return undefined
            }
            const raw = entry.allocationIdx ?? entry.sourceIdx ?? entry.idx
            if (raw === undefined || raw === null) {
                return undefined
            }
            return `${raw}`
        }
        const chartEntries = chartData.length > 0 ? chartData : data
        const allocationKeys = chartEntries
            .map(keyForEntry)
            .filter(value => value !== undefined)
        const uniqueAllocations = new Set(allocationKeys)
        if (uniqueAllocations.size > 1) {
            const allocationColorIndex = new Map()
            let nextIdx = 0
            for (const key of allocationKeys) {
                if (!allocationColorIndex.has(key)) {
                    allocationColorIndex.set(key, nextIdx)
                    nextIdx += 1
                }
            }
            colors = chartEntries.map((entry, idx) => {
                const key = keyForEntry(entry)
                const paletteIdx = key !== undefined ? allocationColorIndex.get(key) : idx
                const safeIdx = Number.isFinite(paletteIdx) ? paletteIdx : idx
                return normalizedPalette[safeIdx % normalizedPalette.length]
            })
        }
    }
    let showLegend = !options.hideLegend

    let legend
    let minW = innerPadding[1] + innerPadding[3]
    let minH = innerPadding[2] + innerPadding[0]
    let usableWidth = width 
    let usableHeight = height

    const legendData = options.sublabels ? options.sublabels.items.map((d,i)=>({label:d.label,count:d.count, idx:i})) : chartData.slice()

    if( showLegend ){
        const maxLegendWidth = usableWidth * 0.5
        //const legendData = options.colorBySecondary ? (options.sublabels?.items ?? []) : data
        legend = renderLegend( legendData,{
                            ...options,
                            fontSize: options.legendSize ?? (config.fontSize * 0.8),
                            x: 0,
                            y: 0,
                            width: options.legendOnRight ? undefined : width,
                            maxWidth: options.legendOnRight ? maxLegendWidth : undefined,
                            colors,
                            fontFamily: options.legendFont,
                            fontFill: options.legendFontColor,
                            placeholderFill: options.legendFontPlaceholderColor,
                            height: options.legendOnRight ? usableHeight : undefined
        })
        if( options.legendOnRight ){
            usableWidth -= (legend.width() + innerPadding[3])
            if( usableWidth < minW ){usableWidth = minW }
        }else{
            usableHeight -= (legend.height() + (innerSpacing * 1.5))
            if( usableHeight < minH ){usableHeight = minH }
        }
    }
    
    let pieY = 0//innerSpacing
    if( !options.hideTitle){

        const t = new CustomText({
            x: innerPadding[3],
            y: innerPadding[0],
            fontSize: config.fontSize,
            height: config.fontSize * 1.1,
            lineHeight: 1.1,
            fontStyle: "bold",
            text: title,
            align: 'center',
            fill: '#334155',
            wrap: false,
            ellipsis: true,
            width: usableWidth - innerPadding[3] - innerPadding[1]
        })
        sg.add(t)
        pieY = (config.fontSize * 1) + (innerSpacing / 2)
    }
    if( !hasSublabels && data.at(-1)?.label === "Unknown"){
        colors[data.length - 1] = "#d2d2d2"
    }

    let mainChart

    if( options.style ==="bar" || options.style ==="stacked_bar"   ){
        const axisVisibleX = showXAxis !== false
        const axisVisibleY = showYAxis !== false
        const barGraphOptions = {
            width: usableWidth, 
            height: usableHeight, 
            minY: options.min,
            maxY: options.max,
            x: innerPadding[3], 
            barHeght: options.scale ? usableHeight * options.scale : undefined, 
            y: innerPadding[0], 
            colors: colors, 
            colorBySecondary: options.colorBySecondary,
            showValue: options.showValue, 
            showLines: axisVisibleY,
            showAxisValue: axisVisibleY,
            showXAxis: axisVisibleX,
            showYAxis: axisVisibleY,
            stack: options.style === "stacked_bar",
            fontFamily: options.legendFont,
            fontFill: options.legendFontColor,
            placeholderFill: options.legendFontPlaceholderColor,
            sublabels: options.sublabels,
            gridStroke: themeColor(theme, "grid"),
            subGridStroke: themeColor(theme, "subgrid"),
            secondaryAxisAsIcon,
            mainstore,
            primitiveResolver,
            secondaryAxisIconScale,
            placeholder: options.placeholder,
            allocationField: options.allocationField,
            forcedAxisGutter: options.forcedAxisGutter,
        }

    if( options.style ==="bar" ){
        mainChart = renderBarChart(chartData, { ...barGraphOptions, renderContextId: options.renderContextId, cellId: options.cellId })
    }else if( options.style ==="stacked_bar"  ){
        mainChart = renderBarChart(chartData, { ...barGraphOptions, renderContextId: options.renderContextId, cellId: options.cellId })
        }
    }else if( options.style === "weighted"){
        showLegend = false
        const { weightedSum, totalCount } = data.reduce(
            (acc, { label, count }, score) => {
              return {
                weightedSum: acc.weightedSum + score * count,
                totalCount:  acc.totalCount   + count
              };
            },
            { weightedSum: 0, totalCount: 0 }
          );
          
          let avgSentiment, label, color
          if( totalCount > 1){
            avgSentiment = weightedSum / totalCount
            const rounded = Math.round(avgSentiment);
            label = data[rounded].label
            color = colors[rounded]
          }else{
           color = "white"
           label = "None"
        }
        
        mainChart = new Konva.Group({
            x: innerPadding[3],
            y: pieY,
            width: usableWidth - (innerPadding[3] + innerPadding[1]),
            height: usableHeight - (pieY + innerPadding[2]),
        })
        
          mainChart.add(new Konva.Rect({
                x: 0,
                y: 0,
                width: usableWidth - (innerPadding[3] + innerPadding[1]),
                height: usableHeight - (pieY + innerPadding[2]),
                fill:  color
          }))
          const l = new CustomText({
            x: 0,
            y: 0,
            width: usableWidth - (innerPadding[3] + innerPadding[1]),
            text: label,
            fontSize: itemSize / 15,
            align: 'center',
            verticalAlign: 'middle'
        }) 
        l.y( (usableHeight - l.height() ) / 2)
        mainChart.add(l)
    }else{
        let fullPieSize = (Math.min(usableHeight - innerPadding[2] - innerPadding[0], usableWidth - innerPadding[3] - innerPadding[1])) * 0.95
        let pieSize = fullPieSize
        const pieMid = (fullPieSize / 2)
        if( options.scale ){
            pieSize *= options.scale
        }
        const pie = renderPieChart(chartData, {size: pieSize, x: (usableWidth - pieSize) / 2, y: pieY + (usableHeight - pieSize)/ 2, colors: colors, renderContextId: options.renderContextId, allocationField: options.allocationField, cellId: options.cellId})
        mainChart = new Konva.Group({
            x: 0,
            y: 0,
            width: usableWidth,
            height: usableHeight
        })
        mainChart.add(pie)
    }
    sg.add( mainChart )

    let finalWidth = mainChart.width() + innerPadding[1] + innerPadding[3]
    let finalHeight = mainChart.height() + innerPadding[0] + innerPadding[2]
    if( showLegend){
        sg.add( legend)
        if( options.legendOnRight ){
            legend.x( mainChart.x() + mainChart.width() + innerPadding[3])
            legend.y( (mainChart.y() + (mainChart.height() / 2)) - (legend.height() / 2))
            finalWidth = legend.x() + legend.width() + innerPadding[1]
        }else{
            legend.x( innerPadding[3])
            legend.y( mainChart.y() + mainChart.height() + innerPadding[2])
            finalHeight = legend.y() + legend.height() + innerPadding[2]
        }
    }
    sg.width( finalWidth )
    r.width( finalWidth )
    sg.height( finalHeight )
    r.height( finalHeight )
    sg.attrs.legendInfo = {colors, data: legendData}
    sg.attrs.axisGutter = mainChart.attrs?.axisGutter
    return sg
}
function renderLegend( data, {colors, itemSize, height, width, maxWidth, ...options} ){
    if( !width ){
        width = itemSize
    }
    if(!height){
        height = itemSize
    }

    const legendFontSize = options.fontSize ?? 12
    const slx = (legendFontSize * 1.2)
    const rxDelta = legendFontSize * (1.2 - 0.05)
    let lIdx = 0
    let lx = slx
    let ly = 0

    const sg = new Konva.Group({
        x: options.x,
        y: options.y,
        width: 0,
        height: 0
    })

    let maxX = 0, maxY = 0
    
    for( const d of (data ?? []) ){
        if( ly >= height){
            break
        }
        const r = new Konva.Rect({
            //x: innerPadding[3] + (legendFontSize * 0.05),
            x: lx - rxDelta,
            y: ly - (legendFontSize * 0.05),
            width: legendFontSize * 0.9,
            height: legendFontSize * 0.9,
            fill: d?.color ?? colors[ lIdx % colors.length],
            strokeScaleEnabled: false,
            strokeWidth:1,
            stroke: '#555'
        })
        sg.add(r)
        
        const t = new CustomText({
            x: lx,
            y: ly,
            fontSize: legendFontSize,
            text: d.label, //`${d.label} ${(d.count / total * 100).toFixed(2)}%`,
            fill: options.fontFill,
            placeholderFill: options.placeholderFill,
            fontFamily: options.fontFamily,
            ellipsis: true,
            width: options.horizontalLegend ? "auto" : (width ? width - lx : undefined)
        })
        if( maxWidth && t.width() > maxWidth){
            t.width( maxWidth)
        }
        if( options.horizontalLegend ){
            if( lx + t.width() > width){
                lx = slx
                ly += legendFontSize * 1.5
                t.x(slx)
                t.y(ly)
                r.y(ly)
                r.x(slx - rxDelta)
            }
            lx += t.width() + (legendFontSize * 3)
        }else{
            ly += t.height() * 1.5
        }
        sg.add(t)
        const ex = t.x() + t.width()
        if( ex > maxX){maxX = ex}
        const ey = t.y() + t.height()
        if( ey > maxY){maxY = ey}
        lIdx++
    }            
    sg.width(maxX)
    sg.height(maxY)
    return sg

}
registerRenderer( {type: "default", configs: "dial"}, (primitive, options = {})=>{
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 400, padding: [0,0,0,0], ...options, ...(primitive.renderConfig ?? {})}

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    const sets = options.data?.mappedCategories ?? []
    const usableWidth = config.width - config.padding[1] - config.padding[3]
    
    let x = config.padding[3]// + spacing
    let y = config.padding[0] //+ ySpacing

    let innerPadding = [10,10,10,10]

    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: usableWidth,
        onClick: options.onClick,
        name:"inf_track"
    })
    let style = config.style
    
            let colors = [
                "#cd5a50",
                "#e27da7",
                "#f0a9ad",
                "#f7db5a",
                "#c6d866",
                "#83bb57",
                "#52ab59"
            ]

            let aligned = 0, total = 0
            for(const score of options.data ?? []){
                if( score.label === "clearly" || score.label === "likely"){
                    aligned+=score.count
                }
                total+=score.count
            }
            const complete = aligned / total
            const colorIdx = Math.floor(colors.length * complete)
            const needle = 180 * complete
            const ring = 14

            const r = ((usableWidth / 2) - ring) * (options.show_label ? 0.85 : 1)

            var wedge1 = new WedgeRing({
                x: usableWidth / 2,
                y: (usableWidth / 2) - ring,
                outerRadius: r,
                innerRadius: r * 0.65,
                angle: 180,
                fill: "#dadada  ",
                rotation:  180,
            });
            g.add(wedge1)
            var wedge2 = new WedgeRing({
                x: usableWidth / 2,
                y: (usableWidth / 2) - ring,
                outerRadius: r,
                innerRadius: r * 0.65,
                angle: needle,
                fill: config.invert ? colors[colors.length - 1 - colorIdx]  : colors[colorIdx],
                rotation:  180,
            });
            g.add(wedge2)
            g.height(usableWidth / 2)

            g.add( new Konva.Circle({
                x: usableWidth / 2,
                y: (usableWidth / 2) - ring,
                radius: 12,
                fill: "#434343"
            }))
            g.add( new Konva.Line({
                x: usableWidth / 2,
                y: (usableWidth / 2) -ring,
                points:[
                    - 8, - 6,
                    + 8, - 6,
                    0, -r * 0.85,
                ],
                closed: true,
                rotation:  -90 + needle,
                fill: "#434343"
            }))
            if( options.show_label){
                g.add( new CustomText({
                    x: (usableWidth / 2) - (r / 0.92) - 15,
                    y: ((usableWidth / 2) - ring) * 0.8,
                    fontSize: 20,
                    text: "L",
                    fill: '#334155',
                    align:"center",
                    width: 30
                }))
                g.add( new CustomText({
                    x: (usableWidth / 2) - 15,
                    y: ((usableWidth / 2) - ring) - ( r / 0.92) - 8,
                    fontSize: 20,
                    text: "M",
                    align:"center",
                    fill: '#334155',
                    width: 30
                }))
                g.add( new CustomText({
                    x: (usableWidth / 2) + (r / 0.92) - 15,
                    y: ((usableWidth / 2) - ring) * 0.8,
                    fontSize: 20,
                    text: "H",
                    fill: '#334155',
                    align:"center",
                    width: 30
                }))
            }

    return g
})
registerRenderer( {type: "default", configs: "chart"}, (primitive, options = {})=>{
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 400, padding: [0,0,0,0], ...options, ...(primitive.renderConfig ?? {})}

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    const sets = options.data?.mappedCategories ?? []
    const usableWidth = config.width - config.padding[1] - config.padding[3]
    
    let x = config.padding[3]// + spacing
    let y = config.padding[0] //+ ySpacing

    let innerPadding = [10,10,10,10]

    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: usableWidth,
        onClick: options.onClick,
        name:"inf_track"
    })
    let style = config.style
    
    const sg = renderSubCategoryChart("NAME", options.data ?? [], {
        x: x, 
        y: y, 
        itemSize: usableWidth, 
        innerPadding, 
        style, 
        hideTitle: config.show_title === false, 
        hideLegend: config.show_legend === false, 
        byTag: config.by_tag,
        paletteName: config.colors,
        secondaryAxisAsIcon: config.secondary_axis_icons,
        secondaryAxisIconScale: config.secondary_axis_icon_scale,
        mainstore: options.mainstore,
        showXAxis: config.show_x_axis !== false,
        showYAxis: config.show_y_axis !== false,
        renderContextId: options.renderOptions?.renderContextId ?? options.renderContextId
    })
    g.add(sg)
    
    g.width(sg.width())
    g.height(sg.height())
    return g
})

registerRenderer( {type: "default", configs: "word_cloud"}, (primitive, options = {})=>{
    const words = options.data.mappedCategories[0].details.map(d=>({text:d.label, size: d.items.length}))
    
    return plotWordCloud( {id: primitive.id, width: 300, height: 300, ...options, words})
})
registerRenderer( {type: "default", configs: "cat_overview"}, (primitive, options = {})=>{
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, itemSize: 280, width: 1200, padding: [0,0,0,0], ...options, ...(primitive.renderConfig ?? {})}

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    const sets = options.data?.mappedCategories ?? []
    const usableWidth = config.width - config.padding[1] - config.padding[3]
    const setCount = sets.length 
    
    const itemSize = config.itemSize
    const columns = Math.floor(usableWidth / (itemSize + 10))
    const spacing = columns > 1 ? (usableWidth - (columns * itemSize)) / (columns - 1) : 0
    let ySpacing = 30
    const actualColumns = Math.min(columns, setCount)
    
    const actualWidth = ((spacing + itemSize) * actualColumns) - spacing
    let x = config.padding[3]// + spacing
    let y = config.padding[0] //+ ySpacing
    let maxY = 0
    let cIdx = 0

    let rHeight = 0
    let rowCells = []
    let innerPadding = [10,10,10,10]

    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: actualWidth,
        onClick: options.onClick,
        name:"inf_track"
    })

    function updateGraphBackground(){
        for(const d of rowCells){
            d.find('.background')[0]?.height(rHeight)
        }
    }

    for( const item of sets ){
        const sg = renderSubCategoryChart(item.title, item.details, {
                                                x: x, 
                                                y: y, 
                                                itemSize, 
                                                innerPadding, 
                                                style: config.style, 
                                                showValue: config.show_value !== false, 
                                                hideTitle: config.show_title === false, 
                                                hideLegend: config.show_legend === false, 
                                                byTag: config.by_tag,
                                                paletteName: config.colors,
                                                secondaryAxisAsIcon: config.secondary_axis_icons,
                                                secondaryAxisIconScale: config.secondary_axis_icon_scale,
                                                mainstore: options.mainstore,
                                                showXAxis: config.show_x_axis !== false,
                                                showYAxis: config.show_y_axis !== false,
                                                renderContextId: options.renderOptions?.renderContextId ?? options.renderContextId
                                            })
        g.add(sg)
        rowCells.push( sg )
        
        rHeight = sg.attrs.height > rHeight  ? sg.attrs.height : rHeight

        x += itemSize + spacing
        cIdx ++
        maxY = y + rHeight
        if( cIdx === columns ){
            updateGraphBackground()
            x = config.padding[3] //+ spacing
            y += rHeight + ySpacing
            rHeight = 0
            cIdx = 0
            rowCells = []
        }
        
    }
    updateGraphBackground()
    const h = maxY + config.padding[2] //+ ySpacing
    //r.height(h)
    g.height(h)
    return g

})
registerRenderer( {type: "categoryId", id: 128, configs: "set_default"}, function renderFunc(primitive, options = {}){
    return categoryGrid( primitive, options)
})
registerRenderer( {type: "categoryId", id: 113, configs: "set_format_grid"}, function renderFunc(primitive, options = {}){
    return categoryGrid( primitive, {...options, itemSize: 450})
})
registerRenderer( {type: "categoryId", id: 109, configs: "set_format_grid"}, function renderFunc(primitive, options = {}){
    return categoryGrid( primitive, {...options, itemSize: 450})
})
registerRenderer( {type: "default", configs: "set_distribution"}, function renderFunc(primitive, options = {}){
    console.warn("USING OLD RENDER FOR SUB DISTRIBITION")
    const {list, extents, ...forwardOptions} = options
    const viewConfig = {
        renderType: "distribution_chart",
        field: options.renderOptions?.field //?? "is_verified_review"
    }
    return renderMatrix(primitive, list ?? [], {...forwardOptions, rowExtents: extents?.row, columnExtents: extents.column, viewConfig})
})
function resolveAllocationScopeRanges(table, cell, scope){
    const allocationRanges = table?.ranges?.allocations
    if(!allocationRanges){
        return null
    }
    if( scope === "row" ){
        return allocationRanges.rows?.order?.[cell.rIdx] ?? allocationRanges.rows?.idx?.[cell.rowIdx] ?? null
    }
    if( scope === "column" ){
        return allocationRanges.columns?.order?.[cell.cIdx] ?? allocationRanges.columns?.idx?.[cell.columnIdx] ?? null
    }
    return allocationRanges.table
}

function computeAllocationRange(container){
    if(!container){
        return null
    }
    const items = container.items ?? []
    if( items.length === 0 ){
        return {min: 0, max: 0}
    }
    let min = Infinity
    let max = -Infinity
    for( const entry of items ){
        const entryMin = entry?.min ?? entry?.count ?? 0
        const entryMax = entry?.max ?? entry?.count ?? entryMin
        if( entryMin < min ){min = entryMin}
        if( entryMax > max ){max = entryMax}
    }
    if( min === Infinity ){min = 0}
    if( max === -Infinity ){max = 0}
    return {
        min: Math.min(0, min),
        max
    }
}

function collectDeepestAllocationRangeItems(source, fields, level = 0){
    if(!source || !fields?.length){
        return []
    }
    const field = fields[level]
    if(!field){
        return []
    }
    const container = source[field]
    if(!container){
        return []
    }
    if(level === fields.length - 1){
        return container.items ?? []
    }
    const items = []
    for(const entry of container.items ?? []){
        if(entry?.allocations){
            const nested = collectDeepestAllocationRangeItems(entry.allocations, fields, level + 1)
            if(nested.length){
                items.push(...nested)
            }
        }
    }
    return items
}

function collectDeepestAllocationEntries(source, fields, level = 0){
    if(!source || !fields?.length){
        return []
    }
    const field = fields[level]
    if(!field){
        return []
    }
    const entries = source[field] ?? []
    if(level === fields.length - 1){
        return entries
    }
    const items = []
    for(const entry of entries){
        if(entry?.allocations){
            const nested = collectDeepestAllocationEntries(entry.allocations, fields, level + 1)
            if(nested.length){
                items.push(...nested)
            }
        }
    }
    return items
}

function normalizeAllocationFilterKey(value){
    if( value === null || value === undefined || value === "_N_" ){
        return "_N_"
    }
    if( typeof value === "object" ){
        if( value.idx !== undefined ){
            return normalizeAllocationFilterKey(value.idx)
        }
        if( value.value !== undefined && typeof value.value !== "object"){
            return normalizeAllocationFilterKey(value.value)
        }
        return undefined
    }
    return value
}

function extractAllocationFilterSet(filter){
    if( !filter ){
        return null
    }
    const sourceEntries = Array.isArray(filter)
        ? filter
        : (typeof filter === "object" ? Object.values(filter) : [])
    if( !sourceEntries.length ){
        return null
    }
    const resolved = sourceEntries
        .map(normalizeAllocationFilterKey)
        .filter(value=>value !== undefined)
    return resolved.length ? new Set(resolved) : null
}

function buildAllocationFilterMap(allocationDefs){
    const map = new Map()
    for(const def of allocationDefs ?? []){
        if( !def?.field ){
            continue
        }
        const exclusion = extractAllocationFilterSet(def.filter)
        if( exclusion?.size ){
            map.set(def.field, exclusion)
        }
    }
    return map
}

function filterAllocationItems(items, fieldName, exclusionMap){
    if( !Array.isArray(items) ){
        return []
    }
    if( !fieldName ){
        return items.slice()
    }
    const exclusion = exclusionMap?.get(fieldName)
    if( !exclusion?.size ){
        return items.slice()
    }
    return items.filter(item=>!exclusion.has(normalizeAllocationFilterKey(item?.idx)))
}

function resolveAllocationScaleValue(cell, fields, table){
    const fallbackValue = resolveDisplayValue(cell, table)
    const fieldList = Array.isArray(fields) ? fields.filter(Boolean) : [fields].filter(Boolean)
    if(fieldList.length === 0){
        return fallbackValue
    }
    const deepestEntries = collectDeepestAllocationEntries(cell.allocations, fieldList)
    const targetEntries = deepestEntries.length ? deepestEntries : (cell.allocations?.[fieldList[0]] ?? [])
    if(!targetEntries || targetEntries.length === 0){
        return fallbackValue
    }
    return targetEntries.reduce((acc, entry)=>{
        const value = entry?.count ?? 0
        return value > acc ? value : acc
    }, 0)
}

registerRenderer( {type: "default", configs: "datatable_distribution"}, function renderFunc({table, cell, renderOptions, ...options}){
    const config = {itemSize: 280, padding: [10,10,10,10], ...options}
    let scale = 1
    let max, min
    const width = renderOptions.width ?? config.itemSize
    const height = renderOptions.height ?? config.itemSize

    const resolvedFont = options.resolvedFontFamily
    const resolvedFontColor = options.resolvedFontColor
    const placeholderFill = options.resovledPlaceholderFill
    const allocationDefs = table.defs?.allocations ?? []
    const allocationFields = allocationDefs.map(d=>d?.field).filter(Boolean)
    const allocationFilterMap = buildAllocationFilterMap(allocationDefs)

    function sortByOrder(values){
        values.forEach((d,i)=>d.order_idx = i)
        if( renderOptions.order === "high_to_low"){
            return values.sort((a,b)=>b.count - a.count)
        }else if( renderOptions.order === "low_to_high"){
            return values.sort((a,b)=>a.count - b.count)
        }
        return values
    }

    const count = resolveDisplayValue(cell, table)
    const primaryAllocationField = allocationFields[0]
    let values
    if( primaryAllocationField ){
        values = filterAllocationItems(cell.allocations?.[primaryAllocationField] ?? [], primaryAllocationField, allocationFilterMap)
    }else{
        values = [{count, label: renderOptions?.value_label ?? "Value"}]
    }
    let sublabels
    if( allocationFields.length > 1 ){
        const subField = allocationFields[1]
        const subAlloc = table.allocations?.[subField]
        if( subAlloc ){
            sublabels = {
                ...subAlloc,
                items: filterAllocationItems(subAlloc.items ?? [], subField, allocationFilterMap)
            }
        }
        values = sortByOrder(values)
        values = values.map(entry=>{
            const entrySubList = filterAllocationItems(entry.allocations?.[subField] ?? [], subField, allocationFilterMap)
            return {
                label: entry.label,
                count: entrySubList.map(item=>item.count)
            }
        })
    }else{
        values = sortByOrder(Array.isArray(values) ? values : [values])
    }
    
    const useAllocationRanges = allocationDefs.length > 0 && (renderOptions.style === "bar" || renderOptions.style === "stacked_bar")
    let scaleReferenceValue = count
    if( renderOptions.calcRange ){
        let resolvedRange
        if( useAllocationRanges ){
            const allocationFields = allocationDefs.map(d=>d?.field).filter(Boolean)
            const scopeRanges = resolveAllocationScopeRanges(table, cell, renderOptions.calcRange)
            let rangeContainer
            if( allocationFields.length ){
                const deepestRangeItems = collectDeepestAllocationRangeItems(scopeRanges, allocationFields)
                if( deepestRangeItems.length ){
                    rangeContainer = {items: deepestRangeItems}
                }else{
                    rangeContainer = scopeRanges?.[allocationFields[0]]
                }
            }
            const allocRange = computeAllocationRange(rangeContainer)
                if( allocRange ){
                    resolvedRange = allocRange
                    scaleReferenceValue = resolveAllocationScaleValue(cell, allocationFields, table)
                }
        }
        if( !resolvedRange && table.ranges ){
            if( renderOptions.calcRange === "row"){
                resolvedRange = table.ranges.rows?.order?.[cell.rIdx]
            }else if( renderOptions.calcRange === "column"){
                resolvedRange = table.ranges.columns?.order?.[cell.cIdx]
            }else{
                resolvedRange = table.ranges.table
            }
        }
        if( resolvedRange ){
            ({min, max} = resolvedRange)
        }
        const fallbackMax = resolvedRange?.max ?? count ?? 1
        const safeMax = max && max !== 0 ? max : (fallbackMax !== 0 ? fallbackMax : 1)
        scale = ((scaleReferenceValue / safeMax) * 0.8) + 0.2
    }

    
    const allocValues = Object.values(table.defs?.allocations ?? {})
    const colorBySecondary = allocValues[0]?.type !== "category" && allocValues[1]?.type === "category"
    
    const title = [table.columns[cell.cIdx].label,table.rows[cell.rIdx].label].filter(Boolean).join(" - ")

    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width,
        height
    })
    if (renderOptions?.renderContextId) {
        g.attrs.renderContextId = renderOptions.renderContextId;
    }
        const sg = renderSubCategoryChart(title, values, {
            x: 0,//config.itemSize,// * (renderOptions.show_legend ? 0.1 : 0), 
            y: 0, 
            width: width - config.padding[1] - config.padding[3],
            height: height - config.padding[0] - config.padding[2],
            innerPadding: config.padding, 
        style: renderOptions.style, 
        hideTitle: renderOptions.show_title === false, 
        paletteName: renderOptions.colors,
        colorBySecondary,
        sublabels,
        scale,
        max,
        min,
        count,
        legendFont: resolvedFont,
        legendFontColor: resolvedFontColor,
        legendFontPlaceholderColor: placeholderFill,
        colors: renderOptions.colorMap ? Object.values(renderOptions.colorMap) : undefined,
        colorMap: renderOptions.colorMap,
        showValue: renderOptions.show_value ?? false, 
        legendSize: renderOptions.legend_size,
        legendOnRight: renderOptions.show_legend === "right",
        horizontalLegend: renderOptions.show_legend !== "right",
        reversePalette: renderOptions.reverse_palette,
        hideLegend: !renderOptions.show_legend,
        theme: options.theme,
        sort: renderOptions.order ?? "none",
        secondaryAxisAsIcon: renderOptions.secondary_axis_icons,
        secondaryAxisIconScale: renderOptions.secondary_axis_icon_scale,
        mainstore: options.mainstore,
            showXAxis: renderOptions.show_x_axis !== false,
            showYAxis: renderOptions.show_y_axis !== false,
            allocationField: primaryAllocationField,
            cellId: cell?.id,
            forcedAxisGutter: options.axisGutter,
            renderContextId: renderOptions?.renderContextId ?? options.renderContextId
        })
    g.add(sg)
    g.width(sg.width())
    g.height(sg.height())
    if( options.getConfig){
        return {
            width: sg.width(),
            height: sg.height(),
            axisGutter: sg.attrs?.axisGutter
        }
    }
    return g
})
registerRenderer( {type: "default", configs: "datatable_heatmap"}, ({table, cell, renderOptions, ...options})=>{
    const config = {width: 128, height: 128, padding: [5,5,5,5], ...(options.renderConfig ?? {})}
    if( renderOptions.width ){
        config.width = renderOptions.width
    }
    if( renderOptions.height ){
        config.height = renderOptions.height
    }
    let range = table.ranges.table
    let totals = table.totals.table
    let title = ""
    let fontSize = 16 / 128 * config.width

    if( renderOptions?.group_by === "row"){
        totals = table.totals.rows?.order?.[cell.rIdx]
        range = table.ranges.rows?.order?.[cell.rIdx]
        //title = options.colTitles?.[options.cIdx]
    }else if( renderOptions?.group_by === "col"){
        totals = table.totals.columns?.order?.[cell.cIdx]
        range = table.ranges.columns?.order?.[cell.cIdx]
        //title = options.rowTitles?.[options.rIdx]
    }

    const colors = heatMapPalette.find(d=>d.name === (renderOptions?.colors ?? "default"))?.colors ?? heatMapPalette[0].colors
    const textColors = heatMapPalette.find(d=>d.name === (renderOptions?.colors ?? "default"))?.text_colors ?? colors.map(d=>"black")
    range ||= {min: 0, max: 0}
    totals ||= 0
    const spread = range.max - range.min + 1
    
    
    const g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height,
    })

    const cellValue = resolveDisplayValue(cell, table)
    const idx = Math.floor((cellValue - range.min) / spread * colors.length) 

    if( renderOptions.bubble){
        const b = new Konva.Rect({
            x: config.padding[3],
            y: config.padding[0],
            width: config.width - config.padding[3] - config.padding[1],
            height: config.height - config.padding[0] - config.padding[2],
            fill: "transparent",
            name: "background"
        })
        g.add(b)
        const midX = (config.width - config.padding[3] - config.padding[1]) / 2
        const midY = (config.height - config.padding[2] - config.padding[0]) / 2
        const maxR = Math.min(midX, midY)
        const s = cellValue
        let r = s ===0 ? (!renderOptions.counts ? 1 : 0) : maxR / spread * (1+(s- range.min))
        let color = s === 0 && !renderOptions.counts ? "#555" : s === 0 ? "white" : colors[idx]
        if( r > 0){
            const r2 = new Konva.Circle({
                x: config.padding[3] + midX,
                y: config.padding[0] + midY,
                radius: r,
                fill: color
            })
            g.add(r2)
        }
    }else{
        const r2 = new Konva.Rect({
            x: config.padding[3],
            y: config.padding[0],
            width: config.width - config.padding[3] - config.padding[1],
            height: config.height - config.padding[0] - config.padding[2],
                fill: cellValue === 0 ? "white" : colors[idx]
        })
        g.add(r2)
    }
    if( renderOptions?.counts){
        const t = new Konva.CustomText({
            x: config.padding[3],
            y: (config.height) / 2,
            text: renderOptions?.counts === "percentage" ? `${(cellValue / totals * 100).toFixed(0)}% ` : formatNumber( cellValue ?? 0), 
            fontSize: fontSize * (renderOptions.bubble ? 0.5  : 1),
            fontStyle: renderOptions.bubble ? "bold" : undefined,
            fill: textColors[idx],
            width: config.width - config.padding[3] - config.padding[1],
            align:'center',
            height:20,
            bgFill: 'transparent',
            refreshCallback: options.imageCallback
        })
        g.add(t)
        t.y((config.height - t.textHeight ) /2)

    }else if( renderOptions?.titles){
        const t = new Konva.CustomText({
            x: config.padding[3],
            y: (config.height - 20) / 2,
            text: title,
            fontSize: 16,
            width: config.width - config.padding[3] - config.padding[1],
            fill: textColors[idx],
            wrap: true,
            ellipses: true,
            align:'center',
            //height:20,
            bgFill: 'transparent',
            refreshCallback: options.imageCallback
        })
        g.add(t)
        t.y((config.height - t.height() ) /2)

    }

    if( options.getConfig){
        return config
    }

    return g
})
registerRenderer( {type: "default", configs: "datatable_xy_cluster"}, function renderFunc({table, cell, renderOptions = {}, stageOptions = {}, ...options}){
    const basePadding = Array.isArray(renderOptions.padding) ? renderOptions.padding : [12,12,12,12]
    const padding = [0,1,2,3].map((idx)=>Number.isFinite(basePadding[idx]) ? basePadding[idx] : (basePadding.length ? basePadding[basePadding.length - 1] : 12))
    const resolvedWidth = renderOptions.width ?? options.cellWidth ?? options.width ?? 320
    const resolvedHeight = renderOptions.height ?? options.cellHeight ?? options.height ?? 240
    const config = {
        width: resolvedWidth,
        height: resolvedHeight,
        padding,
        circleMin: renderOptions.circle_min ?? 12,
        circleMax: renderOptions.circle_max ?? 36
    }
    if( options.getConfig ){
        return config
    }
    const theme = options.theme ?? defaultTheme
    const resolvedFont = options.resolvedFontFamily ?? theme.fontFamily ?? "Poppins"
    const resolvedFontColor = options.resolvedFontColor ?? themeColor(theme, 'primary', '#111111', options.text_color)
    const fallbackPalette = options.theme?.palette?.categoryColors ?? categoryColors

    function resolveColorPalette(option, targetLength){
        const ensureList = (source)=>{
            if(!Array.isArray(source)){
                return null
            }
            const filtered = source.filter((value)=>typeof value === "string" && value.trim().length > 0)
            return filtered.length ? filtered : null
        }
        let palette = null
        if(Array.isArray(option) || (typeof option === "object" && option !== null)){
            palette = ensureList(Array.isArray(option) ? option : Object.values(option))
        }else if(typeof option === "string"){
            const heatPalette = heatMapPalette.find((entry)=>entry.name === option)
            if(heatPalette){
                if(heatPalette.dynamic && heatPalette.colors?.length >= 2){
                    palette = interpolateHexColors(heatPalette.colors[0], heatPalette.colors.at(-1), Math.max(targetLength, 2))
                }else if(Array.isArray(heatPalette.category_colors) && heatPalette.category_colors.length){
                    palette = heatPalette.category_colors.slice()
                }else if(Array.isArray(heatPalette.colors) && heatPalette.colors.length){
                    palette = heatPalette.colors.slice()
                }
            }
            if(!palette){
                const themeCollections = options.theme?.palette?.collections
                const fromTheme = options.theme?.palette?.[option] ?? themeCollections?.[option]
                palette = ensureList(Array.isArray(fromTheme) ? fromTheme : null)
            }
            if(!palette){
                const builtin = {
                    default: categoryColors,
                    pastel: categoryColors_pastel,
                    muted: categoryColors_MUTED,
                    std: categoryColors_STD,
                    classic: categoryColors_old,
                    vibrant: categoryColors2
                }
                if(builtin[option]){
                    palette = builtin[option].slice()
                }
            }
        }
        if(!palette || palette.length === 0){
            palette = fallbackPalette.slice()
        }
        if(renderOptions.reverse_palette){
            palette = [...palette].reverse()
        }
        if(targetLength > 0){
            if(palette.length < targetLength){
                const extended = []
                for(let i = 0; i < targetLength; i++){
                    extended.push(palette[i % palette.length])
                }
                palette = extended
            }else if(palette.length > targetLength){
                palette = palette.slice(0, targetLength)
            }
        }
        return palette
    }
    const axisFontSize = renderOptions.axis_font_size ?? 12
    const axisLabelColor = themeColor(theme, "muted", "#6b7280")
    const axisGap = renderOptions.axis_gap ?? 8

    const group = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: options.x ?? 0,
        y: options.y ?? 0,
        width: config.width,
        height: config.height
    })
    const discreteColumns = renderOptions.column_is_discrete === true || renderOptions.column_is_discrete === "true"
    const discreteRows = renderOptions.row_is_discrete === true || renderOptions.row_is_discrete === "true"
    const axisScales = {
        y: null,
        x: null
    }
    const stageImageCallback = stageOptions?.imageCallback ?? options.imageCallback
    const allocationDefs = table.defs?.allocations ?? []
    const allocationFilterMap = buildAllocationFilterMap(allocationDefs)
    const clusterField = allocationDefs[0]?.field
    const yField = allocationDefs[1]?.field
    const xField = allocationDefs[2]?.field

    function estimateAxisLabelWidth(items){
        if( !items?.length ){
            return 0
        }
        const longest = items.reduce((acc, item)=>{
            const length = `${item.label ?? item.idx ?? ""}`.trim().length
            return length > acc ? length : acc
        }, 0)
        const estimated = Math.max(axisFontSize * 3, longest * axisFontSize * 0.65)
        return Math.min(renderOptions.axis_max_width ?? 160, estimated)
    }

    function buildAxisScale(field){
        if( !field ){
            return null
        }
        const source = filterAllocationItems(table.allocations?.[field]?.items ?? [], field, allocationFilterMap)
        if( !source.length ){
            return null
        }
        const denominator = Math.max(source.length - 1, 1)
        const positions = new Map()
        source.forEach((item, idx)=>{
            const pos = source.length === 1 ? 0.5 : idx / denominator
            positions.set(item.idx, pos)
        })
        const indexMap = new Map()
        source.forEach((item, idx)=>{
            indexMap.set(item.idx, idx)
        })
        return {positions, items: source, indexMap}
    }

    axisScales.y = buildAxisScale(yField)
    axisScales.x = buildAxisScale(xField)

    const axisLeftSpace = axisScales.y ? estimateAxisLabelWidth(axisScales.y.items) + axisGap : 0
    const axisTopSpace = axisScales.x ? (renderOptions.axis_top_space ?? (axisFontSize * 2.4)) : 0

    const plotOriginX = padding[3] + axisLeftSpace
    const plotOriginY = padding[0] + axisTopSpace
    const drawWidth = Math.max(0, config.width - plotOriginX - padding[1])
    const drawHeight = Math.max(0, config.height - plotOriginY - padding[2])
    const background = new Konva.Rect({
        x: plotOriginX,
        y: plotOriginY,
        width: drawWidth,
        height: drawHeight,
        cornerRadius: renderOptions.cornerRadius ?? 12,
        fill: options.includeBackground === false ? "transparent" : themeColor(theme, "cell", themeColor(theme, "surface", "#ffffff")),
        stroke: renderOptions.showBorder === false ? undefined : themeColor(theme, "border", "#e5e7eb"),
        strokeWidth: renderOptions.showBorder === false ? 0 : 1,
        name: "background"
    })
    group.add(background)
    const discreteGridLayer = new Konva.Group({name: "xy_cluster_grid", listening: false})
    if((discreteColumns && axisScales.x?.items?.length > 0) || (discreteRows && axisScales.y?.items?.length > 0)){
        const colCount = Math.max(axisScales.x?.items?.length ?? 1, 1)
        const rowCount = Math.max(axisScales.y?.items?.length ?? 1, 1)
        const cellWidth = drawWidth / colCount
        const cellHeight = drawHeight / rowCount
        for(let cx = 0; cx < colCount; cx++){
            for(let cy = 0; cy < rowCount; cy++){
                const shading = ((cx + cy) % 2 === 0) ? 0 : 0.02
                if(shading > 0){
                    discreteGridLayer.add(new Konva.Rect({
                        x: plotOriginX + cx * cellWidth,
                        y: plotOriginY + cy * cellHeight,
                        width: cellWidth,
                        height: cellHeight,
                        fill: mixHexWithWhite(themeColor(theme, "surface", "#ffffff"), 0.9),
                        opacity: shading
                    }))
                }
            }
        }
    }
    if(discreteColumns && axisScales.x?.items?.length > 0){
        const panelWidth = drawWidth / Math.max(axisScales.x.items.length, 1)
        for(let i = 1; i < axisScales.x.items.length; i++){
            const lineX = plotOriginX + panelWidth * i
            discreteGridLayer.add(new Konva.Line({
                points: [lineX, plotOriginY, lineX, plotOriginY + drawHeight],
                stroke: themeColor(theme, "border", "#e5e7eb"),
                strokeWidth: 1,
                dash: [4, 3]
            }))
        }
    }
    if(discreteRows && axisScales.y?.items?.length > 0){
        const panelHeight = drawHeight / Math.max(axisScales.y.items.length, 1)
        for(let i = 1; i < axisScales.y.items.length; i++){
            const lineY = plotOriginY + panelHeight * i
            discreteGridLayer.add(new Konva.Line({
                points: [plotOriginX, lineY, plotOriginX + drawWidth, lineY],
                stroke: themeColor(theme, "border", "#e5e7eb"),
                strokeWidth: 1,
                dash: [4, 3]
            }))
        }
    }
    if(discreteGridLayer.children.length){
        group.add(discreteGridLayer)
    }

    function addPlaceholder(text){
        const node = new CustomText({
            x: plotOriginX,
            y: plotOriginY,
            width: drawWidth,
            fontSize: 14,
            lineHeight: 1.3,
            text,
            align: "center",
            wrap: "word",
            ellipses: true,
            fontFamily: resolvedFont,
            fill: resolvedFontColor,
            refreshCallback: stageImageCallback,
            listening: false
        })
        const messageHeight = node.textHeight ?? node.height() ?? 0
        node.y(plotOriginY + Math.max(0, (drawHeight - messageHeight) / 2))
        group.add(node)
    }

    if( !clusterField ){
        addPlaceholder(renderOptions.emptyMessage ?? "Add an allocation to visualize clusters.")
        return group
    }

    const clusterLabelMap = new Map((table.allocations?.[clusterField]?.items ?? []).map(item=>[item.idx, item.label]))
    const clusterEntries = filterAllocationItems(cell.allocations?.[clusterField] ?? [], clusterField, allocationFilterMap)
    const clusters = clusterEntries.filter(entry=>(entry?.count ?? 0) > 0)

    if( !clusters.length ){
        addPlaceholder(renderOptions.emptyMessage ?? "No allocation data to visualize.")
        return group
    }

    const clusterCount = clusters.length
    const paletteOption = renderOptions.colors ?? renderOptions.renderConfig?.colors
    const colorPalette = resolveColorPalette(paletteOption, Math.max(clusterCount, 1))
    const legendColorMap = typeof renderOptions.colorMap === "object" ? renderOptions.colorMap : null
    const useIconMarkers = Boolean(renderOptions.label_icons)
    const markerMainstore = useIconMarkers ? resolveMainstoreInstance(options.mainstore) : null
    const markerPrimitiveResolver = useIconMarkers ? createHeaderPrimitiveResolver(markerMainstore) : null
    const markerPlaceholderAllowed = renderOptions.placeholder !== false

    function aggregateFieldAllocations(entry, field){
        if( !field || !entry?.allocations ){
            return []
        }
        const accumulator = new Map()
        const visit = (source)=>{
            if( !source || typeof source !== "object" ){
                return
            }
            const direct = source[field]
            if( Array.isArray(direct) ){
                for( const part of direct ){
                    if( !part ){
                        continue
                    }
                    const normalizedIdx = normalizeAllocationFilterKey(part.idx)
                    if( normalizedIdx === undefined ){
                        continue
                    }
                    if( accumulator.has(normalizedIdx) ){
                        const current = accumulator.get(normalizedIdx)
                        current.count += part.count ?? 0
                    }else{
                        accumulator.set(normalizedIdx, {
                            idx: normalizedIdx,
                            label: part.label,
                            count: part.count ?? 0
                        })
                    }
                }
            }
            for( const value of Object.values(source) ){
                if( Array.isArray(value) ){
                    for( const child of value ){
                        if( child?.allocations ){
                            visit(child.allocations)
                        }
                    }
                }
            }
        }
        visit(entry.allocations)
        return Array.from(accumulator.values())
    }

function computeAxisPosition(entry, field, axisScale, fallback = 0.5){
        if( !field || !axisScale ){
            return fallback
        }
        const aggregated = aggregateFieldAllocations(entry, field)
        if( !aggregated.length ){
            return fallback
        }
        const parts = filterAllocationItems(aggregated, field, allocationFilterMap)
        let numerator = 0
        let denom = 0
        const sampledPositions = []
        for( const part of parts ){
            const pos = axisScale.positions.get(part?.idx)
            if( typeof pos !== "number" ){
                continue
            }
            sampledPositions.push(pos)
            const weight = part?.count ?? 0
            if( !weight ){
                continue
            }
            numerator += pos * weight
            denom += weight
        }
        if( denom === 0 ){
            if( sampledPositions.length ){
                return Math.min(...sampledPositions)
            }
            const firstIdx = axisScale.items?.[0]?.idx
            const firstPos = typeof firstIdx !== "undefined" ? axisScale.positions.get(firstIdx) : undefined
            if( typeof firstPos === "number" ){
                return firstPos
            }
            return fallback
        }
        return numerator / denom
    }


    const circleBase = Math.min(drawWidth, drawHeight)
    const clamp01 = (value)=>Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0))
    const percentMin = Math.max(0.05, renderOptions.circle_min_percent ?? 0.05)
    const percentMax = Math.max(percentMin, renderOptions.circle_max_percent ?? 0.10)
    const derivedCircleMin = Math.max(4, circleBase * percentMin)
    const derivedCircleMax = Math.max(derivedCircleMin + 2, circleBase * percentMax)
    const circleMin = Math.max(4, renderOptions.circle_min ?? derivedCircleMin)
    const circleMax = Math.max(circleMin + 2, renderOptions.circle_max ?? derivedCircleMax)

    const useDiscreteX = discreteColumns && axisScales.x?.items?.length > 0
    const useDiscreteY = discreteRows && axisScales.y?.items?.length > 0
    const discreteXSlots = useDiscreteX ? Math.max(axisScales.x.items.length, 1) : 1
    const discreteYSlots = useDiscreteY ? Math.max(axisScales.y.items.length, 1) : 1
    const panelWidth = drawWidth / discreteXSlots
    const panelHeight = drawHeight / discreteYSlots

    const clampSlot = (slot, max)=>Number.isFinite(slot) ? Math.min(Math.max(0, slot), max - 1) : 0
    const clampValue = (value, min, max)=>{
        if(!Number.isFinite(value)){
            return Number.isFinite(min) ? min : 0
        }
        if(!Number.isFinite(min) || !Number.isFinite(max)){
            return value
        }
        if(max < min){
            return (max + min) / 2
        }
        return Math.min(Math.max(value, min), max)
    }
    const allocationFieldOrder = allocationDefs.map(def=>def?.field).filter(Boolean)
    const clusterFieldOrderIndex = allocationFieldOrder.indexOf(clusterField)

    function collectDiscreteCombinationStats(entry){
        if( !(useDiscreteX || useDiscreteY) ){
            return [{
                entry,
                rawXSlot: 0,
                rawYSlot: 0,
                count: entry.count ?? 0,
                xAxisCounts: new Map(),
                yAxisCounts: new Map(),
                xEntry: null,
                yEntry: null
            }]
        }
        const stats = new Map()
        const startIndex = clusterFieldOrderIndex >= 0 ? clusterFieldOrderIndex + 1 : 0
        const totalFields = allocationFieldOrder.length
        const resolveSlotIndex = (axisScale, axisEntry)=>{
            if( !axisScale ){
                return 0
            }
            const indexMap = axisScale.indexMap
            if( !indexMap ){
                return 0
            }
            if( axisEntry?.idx !== undefined ){
                const slotIdx = indexMap.get(axisEntry.idx)
                if( Number.isFinite(slotIdx) ){
                    return slotIdx
                }
            }
            const fallbackSlot = indexMap.get("_N_")
            if( Number.isFinite(fallbackSlot) ){
                return fallbackSlot
            }
            return 0
        }
        function record(context, rawCount){
            const weight = Number.isFinite(rawCount) ? rawCount : 0
            if( weight <= 0 ){
                return
            }
            const rawXSlot = useDiscreteX ? resolveSlotIndex(axisScales.x, context.xEntry) : 0
            const rawYSlot = useDiscreteY ? resolveSlotIndex(axisScales.y, context.yEntry) : 0
            const keyParts = []
            if( useDiscreteX ){
                keyParts.push(rawXSlot)
            }
            if( useDiscreteY ){
                keyParts.push(rawYSlot)
            }
            const key = keyParts.length ? keyParts.join("|") : "default"
            let stat = stats.get(key)
            if( !stat ){
                stat = {
                    entry,
                    rawXSlot,
                    rawYSlot,
                    count: 0,
                    xAxisCounts: new Map(),
                    yAxisCounts: new Map(),
                    xEntry: context.xEntry ?? null,
                    yEntry: context.yEntry ?? null
                }
                stats.set(key, stat)
            }
            stat.count += weight
            if( !stat.xEntry && context.xEntry ){
                stat.xEntry = context.xEntry
            }
            if( !stat.yEntry && context.yEntry ){
                stat.yEntry = context.yEntry
            }
            if( !useDiscreteX && context.xEntry?.idx !== undefined ){
                const prev = stat.xAxisCounts.get(context.xEntry.idx) ?? 0
                stat.xAxisCounts.set(context.xEntry.idx, prev + weight)
            }
            if( !useDiscreteY && context.yEntry?.idx !== undefined ){
                const prev = stat.yAxisCounts.get(context.yEntry.idx) ?? 0
                stat.yAxisCounts.set(context.yEntry.idx, prev + weight)
            }
        }
        function traverse(node, level, context, currentCount){
            if( level >= totalFields ){
                record(context, currentCount)
                return
            }
            const field = allocationFieldOrder[level]
            if( !field ){
                traverse(node, level + 1, context, currentCount)
                return
            }
            const entries = node.allocations?.[field]
            if( Array.isArray(entries) && entries.length ){
                for( const child of entries ){
                    const nextContext = {
                        ...context,
                        xEntry: field === xField ? child : context.xEntry,
                        yEntry: field === yField ? child : context.yEntry
                    }
                    traverse(child, level + 1, nextContext, child.count ?? currentCount)
                }
                return
            }
            traverse(node, level + 1, context, currentCount)
        }
        traverse(entry, Math.max(0, startIndex), {xEntry: null, yEntry: null}, entry.count ?? 0)
        if( stats.size === 0 ){
            record({xEntry: null, yEntry: null}, entry.count ?? 0)
        }
        return Array.from(stats.values())
    }

    function resolveAxisNormFromStat(stat, axisKey, entry){
        const axisScale = axisKey === "x" ? axisScales.x : axisScales.y
        const field = axisKey === "x" ? xField : yField
        if( !axisScale ){
            return 0.5
        }
        if( stat ){
            const contributions = axisKey === "x" ? stat.xAxisCounts : stat.yAxisCounts
            if( contributions?.size ){
                let numerator = 0
                let denom = 0
                contributions.forEach((value, idx)=>{
                    if( !Number.isFinite(value) || value === 0 ){
                        return
                    }
                    const pos = axisScale.positions.get(idx)
                    if( typeof pos === "number" ){
                        numerator += pos * value
                        denom += value
                    }
                })
                if( denom !== 0 ){
                    return numerator / denom
                }
            }
            const axisEntry = axisKey === "x" ? stat.xEntry : stat.yEntry
            if( axisEntry ){
                const pos = axisScale.positions.get(axisEntry.idx)
                if( typeof pos === "number" ){
                    return pos
                }
            }
        }
        return computeAxisPosition(entry, field, axisScale, 0.5)
    }

    const labelRadiusFactor = renderOptions.label_collision_radius_factor ?? 0.7
    const clusterNodeDescriptors = (useDiscreteX || useDiscreteY)
        ? clusters.flatMap((entry, idx)=>{
            const combos = collectDiscreteCombinationStats(entry)
            return combos.map(stat=>({
                entry,
                idx,
                weight: stat.count ?? 0,
                rawXSlot: stat.rawXSlot ?? 0,
                rawYSlot: stat.rawYSlot ?? 0,
                normX: useDiscreteX ? 0.5 : clamp01(resolveAxisNormFromStat(stat, "x", entry)),
                normY: useDiscreteY ? 0.5 : clamp01(resolveAxisNormFromStat(stat, "y", entry))
            }))
        }).filter(node=>node.weight > 0)
        : clusters.map((entry, idx)=>({
            entry,
            idx,
            weight: entry.count ?? 0,
            rawXSlot: 0,
            rawYSlot: 0,
            normX: clamp01(resolveAxisNormFromStat(null, "x", entry)),
            normY: clamp01(resolveAxisNormFromStat(null, "y", entry))
        }))

    if( !clusterNodeDescriptors.length ){
        addPlaceholder(renderOptions.emptyMessage ?? "No allocation data to visualize.")
        return group
    }

    const maxClusterCount = clusterNodeDescriptors.reduce((acc, node)=>Math.max(acc, node.weight ?? 0), 0) || 1
    const clusterNodes = clusterNodeDescriptors.map(descriptor=>{
        const weight = descriptor.weight ?? 0
        const discreteXSlot = useDiscreteX ? clampSlot(descriptor.rawXSlot ?? 0, discreteXSlots) : 0
        const discreteYSlot = useDiscreteY ? clampSlot(descriptor.rawYSlot ?? 0, discreteYSlots) : 0
        const slotOffsetX = plotOriginX + (useDiscreteX ? discreteXSlot * panelWidth : 0)
        const slotOffsetY = plotOriginY + (useDiscreteY ? discreteYSlot * panelHeight : 0)
        const innerWidth = useDiscreteX ? panelWidth : drawWidth
        const innerHeight = useDiscreteY ? panelHeight : drawHeight
        const px = slotOffsetX + descriptor.normX * innerWidth
        const py = slotOffsetY + (1 - descriptor.normY) * innerHeight
        const radius = circleMin + (circleMax - circleMin) * Math.sqrt(Math.max(0, weight / maxClusterCount))
        const paletteColor = colorPalette[descriptor.idx % colorPalette.length] ?? fallbackPalette[descriptor.idx % fallbackPalette.length] ?? "#2563eb"
        return {
            entry: descriptor.entry,
            idx: descriptor.idx,
            weight,
            radius,
            x: px,
            y: py,
            labelOffsetY: 0,
            labelCollisionRadius: Math.max(4, radius * labelRadiusFactor),
            color: paletteColor,
            label: descriptor.entry.label ?? clusterLabelMap.get(descriptor.entry.idx) ?? "Untitled",
            discreteXSlot,
            discreteYSlot
        }
    })

    const showLabels = renderOptions.show_labels !== false
    const showLabelConnectors = showLabels && renderOptions.label_connectors !== false

    if(useDiscreteX && clusterNodes.length){
        const padding = Math.max(4, renderOptions.discrete_x_padding ?? 8)
        const columnBuckets = new Map()
        clusterNodes.forEach(node=>{
            const slotKey = Number.isFinite(node.discreteXSlot) ? node.discreteXSlot : 0
            if(!columnBuckets.has(slotKey)){
                columnBuckets.set(slotKey, [])
            }
            columnBuckets.get(slotKey).push(node)
        })
        columnBuckets.forEach((nodes, slot)=>{
            if(nodes.length <= 1){
                return
            }
            nodes.sort((a,b)=>a.y - b.y)
            const slotOriginX = plotOriginX + slot * panelWidth
            const slotEndX = slotOriginX + panelWidth
            const maxRadius = nodes.reduce((acc, entry)=>Math.max(acc, entry.radius), 0)
            const halfWidth = panelWidth / 2
            const budget = Math.max(0, halfWidth - maxRadius - padding)
            if(budget <= 0){
                return
            }
            const pairCount = Math.max(1, Math.ceil(nodes.length / 2))
            const baseStep = renderOptions.discrete_x_nudge_step ?? 14
            const step = Math.min(baseStep, budget / pairCount)
            nodes.forEach((node, idx)=>{
                const minX = slotOriginX + node.radius + padding / 2
                const maxX = slotEndX - node.radius - padding / 2
                if(step === 0 || idx === 0){
                    node.x = clampValue(node.x, minX, maxX)
                    return
                }
                const direction = idx % 2 === 1 ? 1 : -1
                const magnitude = Math.ceil(idx / 2)
                const offset = direction * step * magnitude
                node.x = clampValue(node.x + offset, minX, maxX)
            })
        })
    }

    function clampLabelOffset(node){
        const limitFactor = renderOptions.label_offset_limit_factor ?? 0.8
        const limit = Math.max(6, node.radius * limitFactor)
        node.labelOffsetY = Math.max(-limit, Math.min(limit, node.labelOffsetY))
        const centerY = node.y + node.labelOffsetY
        const minY = plotOriginY + 4
        const maxY = plotOriginY + drawHeight - 4
        if( centerY < minY ){
            node.labelOffsetY += minY - centerY
        }else if( centerY > maxY ){
            node.labelOffsetY -= centerY - maxY
        }
    }

    function resolveLabelOffsets(nodes){
        if( nodes.length <= 1 ){
            return
        }
        const paddingBetween = renderOptions.collision_padding ?? 6
        const iterations = renderOptions.collision_iterations ?? 30
        for(let iter = 0; iter < iterations; iter++){
            let moved = false
            for(let i = 0; i < nodes.length; i++){
                for(let j = i + 1; j < nodes.length; j++){
                    const a = nodes[i]
                    const b = nodes[j]
                    const ay = a.y + a.labelOffsetY
                    const by = b.y + b.labelOffsetY
                    const gap = Math.abs(by - ay)
                    const minGap = a.labelCollisionRadius + b.labelCollisionRadius + paddingBetween
                    if( gap < minGap ){
                        const overlap = (minGap - gap) / 2
                        if( by >= ay ){
                            b.labelOffsetY += overlap
                            a.labelOffsetY -= overlap
                        }else{
                            b.labelOffsetY -= overlap
                            a.labelOffsetY += overlap
                        }
                        clampLabelOffset(a)
                        clampLabelOffset(b)
                        moved = true
                    }
                }
            }
            if( !moved ){
                break
            }
        }
    }

    if( showLabels ){
        resolveLabelOffsets(clusterNodes)
    }else{
        clusterNodes.forEach(node=>{node.labelOffsetY = 0})
    }

    const circleLayer = new Konva.Group({
        name: "xy_cluster_circles",
        clip: {
            x: plotOriginX,
            y: plotOriginY,
            width: drawWidth,
            height: drawHeight
        }
    })
    const labelLayer = showLabels ? new Konva.Group({name: "xy_cluster_labels"}) : null
    const connectorLayer = showLabelConnectors ? new Konva.Group({name: "xy_cluster_connectors"}) : null

    const labelGap = renderOptions.label_gap ?? 12
    const minLabelWidth = renderOptions.label_min_width ?? 60
    const preferredLabelPosition = renderOptions.label_position ?? "auto"

    function resolveLabelPlacement(node){
        const leftAvailable = Math.max(0, node.x - node.radius - labelGap - plotOriginX)
        const rightAvailable = Math.max(0, (plotOriginX + drawWidth) - (node.x + node.radius + labelGap))
        const chooseSide = (side)=>{
            if(side === "right"){
                const x = node.x + node.radius + labelGap
                const width = Math.max(0, (plotOriginX + drawWidth) - x)
                return {side: "right", x, width, align: "left"}
            }
            const width = Math.max(0, leftAvailable)
            return {side: "left", x: plotOriginX, width, align: "right"}
        }
        let side
        if(preferredLabelPosition === "left" || preferredLabelPosition === "right"){
            side = preferredLabelPosition
        }else{
            side = leftAvailable >= rightAvailable ? "left" : "right"
        }
        let placement = chooseSide(side)
        if(placement.width < minLabelWidth){
            const alternateSide = side === "left" ? "right" : "left"
            const alternate = chooseSide(alternateSide)
            if(alternate.width > placement.width){
                placement = alternate
            }
        }
        const maxWidth = placement.side === "right"
            ? Math.max(0, (plotOriginX + drawWidth) - placement.x)
            : Math.max(0, node.x - node.radius - labelGap - plotOriginX)
        placement.width = Math.min(placement.width, maxWidth)
        return placement
    }

    const connectorEntries = []
    clusterNodes.forEach(node=>{
        const mappedColor = typeof legendColorMap?.[node.label] === "string" ? legendColorMap[node.label] : undefined
        const resolvedColor = mappedColor && mappedColor.trim().length ? mappedColor : node.color
        node.color = resolvedColor

        let markerNode
        if(useIconMarkers){
            const iconSize = Math.max(16, node.radius * 2)
            const iconSource = ""//node.entry?.items?.[0]?.primitive ?? node.entry?.items?.[0] ?? node.entry?.primitive ?? node.entry
            const icon = buildHeaderIconNode({
                item: iconSource || { label: node.label },
                width: iconSize,
                iconSize,
                refreshCallback: stageImageCallback,
                placeholder: markerPlaceholderAllowed,
                mainstore: markerMainstore,
                primitiveResolver: markerPrimitiveResolver
            })
            if(icon){
                icon.x(0)
                icon.y(0)
                const outlineWidth = Math.max(1.5, iconSize * 0.08)
                const iconGroup = new Konva.Group({
                    x: node.x - iconSize / 2,
                    y: node.y - iconSize / 2,
                    width: iconSize,
                    height: iconSize,
                    clipFunc: function(ctx){
                        ctx.beginPath()
                        ctx.arc(iconSize / 2, iconSize / 2, iconSize / 2, 0, Math.PI * 2, false)
                        ctx.closePath()
                    }
                })
                /*const baseCircle = new Konva.Circle({
                    x: iconSize / 2,
                    y: iconSize / 2,
                    radius: iconSize / 2,
                    fill: mixHexWithWhite(resolvedColor, 0.85),
                    listening: false
                })
                const outline = new Konva.Circle({
                    x: iconSize / 2,
                    y: iconSize / 2,
                    radius: Math.max(2, iconSize / 2 - outlineWidth / 2),
                    stroke: mixHexWithWhite(resolvedColor, 0.3),
                    strokeWidth: outlineWidth,
                    listening: false
                })*/
                //iconGroup.add(baseCircle)
                iconGroup.add(icon)
                //iconGroup.add(outline)
                node.labelCollisionRadius = Math.max(node.labelCollisionRadius, iconSize * 0.5)
                markerNode = iconGroup
            }
        }
        if(!markerNode){
            markerNode = new Konva.Circle({
                x: node.x,
                y: node.y,
                radius: node.radius,
                fill: mixHexWithWhite(resolvedColor, 0.8),
                stroke: resolvedColor,
                strokeWidth: 2,
                shadowColor: resolvedColor,
                shadowBlur: 8,
                shadowOpacity: 0.2
            })
        }

        circleLayer.add(markerNode)

        if( showLabels ){
            const placement = resolveLabelPlacement(node)
            const labelWidth = Math.max(0, Math.min(drawWidth, placement.width))
            const labelCenterY = node.y + node.labelOffsetY
            const fontSize = Math.max(2, Math.min(node.radius * 0.28, drawWidth * 0.022))
            node.labelCollisionRadius = Math.max(node.labelCollisionRadius, fontSize * 0.6)
            const textNode = new CustomText({
                x: placement.x,
                y: labelCenterY,
                width: labelWidth,
                text: node.label,
                fontSize,
                fontFamily: resolvedFont,
                fill: resolvedFontColor,
                align: placement.align,
                wrap: "word",
                ellipses: true,
                lineHeight: 1.15,
                listening: false,
                refreshCallback: stageImageCallback
            })
            const measuredHeight = typeof textNode.textHeight === "number" && textNode.textHeight > 0
                ? textNode.textHeight
                : (typeof textNode.height === "function" ? textNode.height() : undefined)
            const textHeight = Number.isFinite(measuredHeight) && measuredHeight > 0 ? measuredHeight : fontSize
            textNode.y(labelCenterY - textHeight / 2)

            labelLayer.add(textNode)
            if(showLabelConnectors && labelWidth > 0){
                connectorEntries.push({node, placement, labelNode: textNode})
            }
        }
    })

    if(showLabelConnectors && connectorEntries.length){
        connectorEntries.forEach(({node, placement, labelNode})=>{
            const startX = placement.side === "right" ? (node.x + node.radius) : (node.x - node.radius)
            const startY = node.y
            const endX = placement.side === "right" ? placement.x : (placement.x + placement.width)
            const endHeight = typeof labelNode.height === "function" ? labelNode.height() : (labelNode.textHeight ?? 0)
            const endY = labelNode.y() + (Number.isFinite(endHeight) && endHeight > 0 ? endHeight / 2 : 0)
            const connector = new Konva.Line({
                points: [
                    startX,
                    startY,
                    startX + (placement.side === "right" ? labelGap / 2 : -labelGap / 2),
                    startY,
                    endX,
                    endY
                ],
                stroke: mixHexWithWhite(node.color, 0.7),
                strokeWidth: 1.5,
                lineCap: "round",
                lineJoin: "round",
                dash: placement.side === "right" ? [] : [4, 3]
            })
            connectorLayer.add(connector)
        })
        group.add(connectorLayer)
    }

    if( showLabels ){
        group.add(labelLayer)
    }
    group.add(circleLayer)

    function renderAxisLabels(){
        if( axisScales.y ){
            const yGroup = new Konva.Group({name: "axis_y"})
            const labelWidth = axisLeftSpace - axisGap
            axisScales.y.items.forEach(item=>{
                const pos = axisScales.y.positions.get(item.idx)
                if( pos === undefined ){
                    return
                }
                let y
                if(useDiscreteY){
                    const slotIdx = clampSlot(axisScales.y.indexMap?.get(item.idx), discreteYSlots)
                    y = plotOriginY + panelHeight * (discreteYSlots - slotIdx - 0.5)
                }else{
                    y = plotOriginY + (1 - pos) * drawHeight
                }
                const labelNode = new CustomText({
                    x: padding[3],
                    y,
                    width: Math.max(0, labelWidth),
                    text: item.label ?? item.idx ?? "",
                    fontSize: axisFontSize,
                    fontFamily: resolvedFont,
                    fill: axisLabelColor,
                    align: "right",
                    wrap: "word",
                    ellipses: true,
                    lineHeight: 1.2,
                    listening: false,
                    refreshCallback: stageImageCallback
                })
                const textHeight = labelNode.textHeight ?? labelNode.height() ?? axisFontSize
                labelNode.y(y - textHeight / 2)
                yGroup.add(labelNode)
            })
            group.add(yGroup)
        }
        if( axisScales.x ){
            const xGroup = new Konva.Group({name: "axis_x"})
            const labelHeight = axisTopSpace - axisGap
            axisScales.x.items.forEach(item=>{
                const pos = axisScales.x.positions.get(item.idx)
                if( pos === undefined ){
                    return
                }
                let x
                if(useDiscreteX){
                    const slotIdx = clampSlot(axisScales.x.indexMap?.get(item.idx), discreteXSlots)
                    x = plotOriginX + panelWidth * (slotIdx + 0.5)
                }else{
                    x = plotOriginX + pos * drawWidth
                }
                const width = Math.min(120, Math.max(40, renderOptions.axis_x_width ?? axisFontSize * 5))
                const labelNode = new CustomText({
                    x: x - width / 2,
                    y: padding[0],
                    width,
                    text: item.label ?? item.idx ?? "",
                    fontSize: axisFontSize,
                    fontFamily: resolvedFont,
                    fill: axisLabelColor,
                    align: "center",
                    wrap: "word",
                    ellipses: true,
                    lineHeight: 1.2,
                    listening: false,
                    refreshCallback: stageImageCallback
                })
                const textHeight = labelNode.textHeight ?? labelNode.height() ?? axisFontSize
                const maxY = padding[0] + Math.max(0, labelHeight - textHeight)
                labelNode.y(Math.min(maxY, padding[0] + labelHeight / 2 - textHeight / 2))
                xGroup.add(labelNode)
            })
            group.add(xGroup)
        }
    }

    renderAxisLabels()

    return group
})
registerRenderer( {type: "default", configs: "datatable_series"}, function renderSeriesChart({table, cell, renderOptions = {}, stageOptions = {}, ...options}){
    const basePadding = Array.isArray(renderOptions.padding) ? renderOptions.padding : [12,12,16,12]
    const padding = [0,1,2,3].map((idx)=>Number.isFinite(basePadding[idx]) ? basePadding[idx] : (basePadding.length ? basePadding[basePadding.length - 1] : 12))
    const resolvedWidth = renderOptions.width ?? options.cellWidth ?? options.width ?? 640
    const resolvedHeight = renderOptions.height ?? options.cellHeight ?? options.height ?? 480
    const config = {
        width: resolvedWidth,
        height: resolvedHeight,
        padding
    }
    if( options.getConfig ){
        return config
    }
    const theme = options.theme ?? defaultTheme
    const resolvedFontFamily = options.resolvedFontFamily ?? theme.fontFamily ?? "Poppins"
    const resolvedFontColor = options.resolvedFontColor ?? themeColor(theme, 'primary', '#111827', options.text_color)
    const backgroundFill = options.includeBackground === false ? "transparent" : themeColor(theme, 'cell', themeColor(theme, 'surface', '#ffffff'))
    const borderColor = themeColor(theme, 'border', '#e5e7eb')
    const placeholderFill = options.resovledPlaceholderFill ?? mixHex(resolvedFontColor, backgroundFill, 0.6)
    const refreshCallback = stageOptions?.imageCallback ?? options.imageCallback
    const allocationDefs = table.defs?.allocations ?? []
    const allocationFilterMap = buildAllocationFilterMap(allocationDefs)

    function renderPlaceholder(text = "Not enough data"){
        const group = new Konva.Group({
            id: options.id,
            name:"cell inf_track",
            x: options.x ?? 0,
            y: options.y ?? 0,
            width: config.width,
            height: config.height
        })
        const background = new Konva.Rect({
            x: 0,
            y: 0,
            width: config.width,
            height: config.height,
            fill: backgroundFill,
            stroke: borderColor,
            strokeWidth: options.includeBackground === false ? 0 : 1
        })
        group.add(background)
        const label = new CustomText({
            x: padding[3],
            y: padding[0],
            width: config.width - padding[3] - padding[1],
            text,
            align: "center",
            fontSize: 12,
            fontFamily: resolvedFontFamily,
            fill: placeholderFill,
            wrap: "word",
            ellipses: true,
            listening: false,
            refreshCallback
        })
        label.y((config.height - label.textHeight) / 2)
        group.add(label)
        return group
    }

    if( allocationDefs.length === 0 ){
        return renderPlaceholder("Series view needs at least one allocation")
    }
    const axisField = allocationDefs[0]?.field
    if( !axisField ){
        return renderPlaceholder("Missing allocation axis definition")
    }
    const seriesAllocationDefs = allocationDefs.slice(1)
    const seriesFields = seriesAllocationDefs.map((entry)=>entry?.field).filter(Boolean)

    const axisItems = filterAllocationItems(table.allocations?.[axisField]?.items ?? [], axisField, allocationFilterMap)
    if( axisItems.length === 0 ){
        return renderPlaceholder("No axis values to chart")
    }

    const axisMeta = new Map()
    axisItems.forEach((item, idx)=>{
        axisMeta.set(item.idx, {
            ...item,
            order: idx
        })
    })

    const axisEntries = filterAllocationItems(cell.allocations?.[axisField] ?? [], axisField, allocationFilterMap)
    if( axisEntries.length === 0 ){
        return renderPlaceholder("Cell is missing allocation data")
    }
    const axisEntryMap = new Map(axisEntries.map((entry)=>[entry.idx, entry]))

    const toNumeric = (value)=>{
        if( value === null || value === undefined ){
            return undefined
        }
        if( typeof value === "number" && Number.isFinite(value) ){
            return value
        }
        if( typeof value === "string" ){
            const parsed = Number.parseFloat(value)
            return Number.isFinite(parsed) ? parsed : undefined
        }
        return undefined
    }

    const showAxisLabels = renderOptions.show_axis_labels !== false

    const axisFontSizeSetting =
        toNumeric(renderOptions.axis_text_size)
        ?? toNumeric(renderOptions.renderConfig?.axis_text_size)
        ?? toNumeric(renderOptions.axis_text)
        ?? toNumeric(renderOptions.renderConfig?.axis_text)
        ?? 10
    const axisValueFontSize = Math.max(6, axisFontSizeSetting)
    const valueFontSizeSource =
        toNumeric(renderOptions.value_text_size)
        ?? toNumeric(renderOptions.renderConfig?.value_text_size)
        ?? toNumeric(renderOptions.axis_text)
        ?? toNumeric(renderOptions.renderConfig?.axis_text)
        ?? 9
    const valueFontSize = Math.max(6, valueFontSizeSource)
    let axisLabelHeightBase = showAxisLabels ? Math.max(24, axisValueFontSize * 2) : 0
    const axisLabelWidth = showAxisLabels ? Math.max(45, axisValueFontSize * 2.5) : 0
    let plotOriginX = padding[3] + axisLabelWidth
    let plotOriginY = padding[0]
    let axisLabelHeight = axisLabelHeightBase
    let plotWidth = Math.max(10, config.width - plotOriginX - padding[1])
    let plotHeight = Math.max(10, config.height - plotOriginY - padding[2] - axisLabelHeight)
    let axisBaselineY = plotOriginY + plotHeight
    const perSeriesAxisConfig = {
        enabled: false,
        axisWidth: Math.max(40, axisLabelWidth),
        axisMargin: 10,
        leftCount: 0,
        rightCount: 0,
        topMargin: Math.max(axisValueFontSize * 2, 20)
    }

    const group = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: options.x ?? 0,
        y: options.y ?? 0,
        width: config.width,
        height: config.height
    })

    const background = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: backgroundFill,
        stroke: options.includeBackground === false ? undefined : borderColor,
        strokeWidth: options.includeBackground === false ? 0 : 1,
        name: "background"
    })
    group.add(background)

    const defaultSeriesName = renderOptions.series_label ?? "Series"
    const seriesGroupMode = renderOptions.series_group_mode ?? "per_series"
    const showValues = renderOptions.show_values === true || renderOptions.show_values === "true"
    const seriesData = new Map()

    function findAllocationBuckets(entry, fieldName){
        if( !entry || typeof entry !== "object" ){
            return []
        }
        const collected = []
        function traverse(node){
            if( !node || typeof node !== "object" ){
                return
            }
            const directArray = node[fieldName]
            if( Array.isArray(directArray) && directArray.length ){
                collected.push(...directArray)
            }
            const allocationArray = node.allocations?.[fieldName]
            if( Array.isArray(allocationArray) && allocationArray.length ){
                collected.push(...allocationArray)
            }
            if( node.allocations ){
                for( const bucketList of Object.values(node.allocations) ){
                    if( !Array.isArray(bucketList) ){
                        continue
                    }
                    bucketList.forEach(traverse)
                }
            }
        }
        traverse(entry)
        return collected
    }

    function resolveBucketFallbackValue(entry, fallbackIndex = 0){
        if( typeof entry?.value === "number" ){
            return entry.value
        }
        if( typeof entry?.bucket_min === "number" || typeof entry?.bucket_max === "number" ){
            const min = typeof entry.bucket_min === "number" ? entry.bucket_min : entry.bucket_max
            const max = typeof entry.bucket_max === "number" ? entry.bucket_max : entry.bucket_min
            if( typeof min === "number" && typeof max === "number" ){
                return (min + max) / 2
            }
            return min ?? max ?? fallbackIndex
        }
        const idxNumber = Number.parseFloat(entry?.idx)
        if( Number.isFinite(idxNumber) ){
            return idxNumber
        }
        const labelNumber = Number.parseFloat(entry?.label)
        if( Number.isFinite(labelNumber) ){
            return labelNumber
        }
        return fallbackIndex
    }

    function addSeriesPoint(seriesKey, seriesLabel, axisNode, value){
        if( !axisNode ){
            return
        }
        const meta = axisMeta.get(axisNode.idx)
        if( !meta ){
            return
        }
        const safeValue = Number.isFinite(value) ? value : 0
        let seriesEntry = seriesData.get(seriesKey)
        if( !seriesEntry ){
            seriesEntry = {
                label: seriesLabel ?? defaultSeriesName,
                buckets: new Map()
            }
            seriesData.set(seriesKey, seriesEntry)
        }
        const axisKey = meta.idx ?? meta.order
        const bucket = seriesEntry.buckets
        const existing = bucket.get(axisKey)
        if( existing ){
            existing.sumY += safeValue
            existing.totalWeight += 1
            if( safeValue < existing.minY ){existing.minY = safeValue}
            if( safeValue > existing.maxY ){existing.maxY = safeValue}
        }else{
            bucket.set(axisKey, {
                xMeta: meta,
                sumY: safeValue,
                totalWeight: 1,
                minY: safeValue,
                maxY: safeValue
            })
        }
    }

    function traverseSeries(node, depth, keyParts, axisNode){
        if( depth >= seriesFields.length ){
            const keyPath = keyParts.length ? keyParts.map((part)=>part.key).join("|") : "__default_series__"
            const labelPath = keyParts.length ? keyParts.map((part)=>part.label).join(" / ") : defaultSeriesName
            addSeriesPoint(keyPath, labelPath, axisNode, node.count ?? axisNode.count ?? 0)
            return
        }
        const field = seriesFields[depth]
        if( !field ){
            traverseSeries(node, depth + 1, keyParts, axisNode)
            return
        }
        const nested = filterAllocationItems(node.allocations?.[field] ?? [], field, allocationFilterMap)
        if( nested.length === 0 ){
            traverseSeries(node, depth + 1, keyParts, axisNode)
            return
        }
        nested.forEach((entry)=>{
            const partKey = entry.idx ?? entry.value ?? `field_${field}_${depth}_${keyParts.length}`
            const partLabel = entry.label ?? entry.idx ?? `Series ${depth + 1}`
            traverseSeries(entry, depth + 1, [...keyParts, { key: `${field}:${partKey}`, label: partLabel }], axisNode)
        })
    }

    axisEntries.forEach((axisEntry)=>{
        if( seriesFields.length === 0 ){
            addSeriesPoint("__default_series__", defaultSeriesName, axisEntry, axisEntry.count ?? 0)
            return
        }
        traverseSeries(axisEntry, 0, [], axisEntry)
    })

    if( seriesData.size === 0 ){
        return renderPlaceholder("No series data to display")
    }

    function resolveSeriesPalette(targetLength){
        if( renderOptions.colorMap ){
            const colorEntries = Array.isArray(renderOptions.colorMap)
                ? renderOptions.colorMap
                : Object.values(renderOptions.colorMap)
            if( colorEntries.length ){
                return colorEntries.slice()
            }
        }
        const fallbackPalette = options.theme?.palette?.categoryColors ?? categoryColors
        let resolved = null
        const option = renderOptions.colors
        if( Array.isArray(option) && option.length ){
            resolved = option.slice()
        }else if( typeof option === "string" && option.trim().length ){
            const heatEntry = heatMapPalette.find((entry)=>entry.name === option)
            if( heatEntry ){
                if( Array.isArray(heatEntry.category_colors) && heatEntry.category_colors.length ){
                    resolved = heatEntry.category_colors.slice()
                }else if( Array.isArray(heatEntry.colors) && heatEntry.colors.length ){
                    resolved = heatEntry.colors.slice()
                }
            }
            if( !resolved ){
                const themeCollection = options.theme?.palette?.collections?.[option]
                if( Array.isArray(themeCollection) && themeCollection.length ){
                    resolved = themeCollection.slice()
                }else if( Array.isArray(options.theme?.palette?.[option]) && options.theme.palette[option].length ){
                    resolved = options.theme.palette[option].slice()
                }
            }
            const builtin = {
                default: categoryColors,
                pastel: categoryColors_pastel,
                muted: categoryColors_MUTED,
                std: categoryColors_STD,
                classic: categoryColors_old,
                vibrant: categoryColors2
            }
            if( !resolved && builtin[option] ){
                resolved = builtin[option].slice()
            }
        }
        if( !resolved ){
            resolved = fallbackPalette.slice()
        }
        if( renderOptions.reverse_palette ){
            resolved = resolved.slice().reverse()
        }
        if( targetLength > 0 ){
            if( resolved.length < targetLength ){
                const extended = []
                for( let i = 0; i < targetLength; i++ ){
                    extended.push(resolved[i % resolved.length])
                }
                resolved = extended
            }else if( resolved.length > targetLength ){
                resolved = resolved.slice(0, targetLength)
            }
        }
        return resolved
    }

    let seriesList = Array.from(seriesData.entries()).map(([key, seriesEntry])=>{
        const points = Array.from(seriesEntry.buckets.values())
            .sort((a,b)=>a.xMeta.order - b.xMeta.order)
            .map((entry)=>({
                xMeta: entry.xMeta,
                yValue: entry.totalWeight > 0 ? entry.sumY / entry.totalWeight : entry.sumY,
                minY: entry.minY,
                maxY: entry.maxY
            }))
        const min = points.reduce((acc, point)=>point.yValue < acc ? point.yValue : acc, Infinity)
        const max = points.reduce((acc, point)=>point.yValue > acc ? point.yValue : acc, -Infinity)
        return {
            key,
            label: seriesEntry.label ?? defaultSeriesName,
            points,
            range: {min, max},
            bucketMap: seriesEntry.buckets
        }
    }).filter((entry)=>entry.points.length > 0)

    if( seriesGroupMode === "average" && seriesFields.length > 0 ){
        const allocationMetaMap = new Map((table.allocations ? Object.entries(table.allocations) : []).map(([field, meta])=>[field, meta]))
        const averagedSeries = seriesFields.map((field, idx)=>{
            const label = allocationMetaMap.get(field)?.title ?? renderOptions.series_labels?.[field] ?? field ?? `Series ${idx + 1}`
            const points = axisItems.map((axisItem)=>{
                const meta = axisMeta.get(axisItem.idx)
                if( !meta ){
                    return null
                }
                const axisEntry = axisEntryMap.get(axisItem.idx)
                if( !axisEntry ){
                    return null
                }
                const buckets = findAllocationBuckets(axisEntry, field)
                if( !buckets.length ){
                    return null
                }
                let totalWeight = 0
                let weightedSum = 0
                let minValue = Infinity
                let maxValue = -Infinity
                buckets.forEach((bucket, bucketIdx)=>{
                    let numericWeight = Number.isFinite(bucket?.valueWeight) ? bucket.valueWeight : 0
                    let bucketAvg = null
                    if( numericWeight > 0 && typeof bucket?.valueSum === "number" && bucket.valueWeight > 0 ){
                        bucketAvg = bucket.valueSum / bucket.valueWeight
                    }else if( typeof bucket?.valueAvg === "number" ){
                        bucketAvg = bucket.valueAvg
                    }
                    if( bucketAvg === null || !Number.isFinite(bucketAvg) ){
                        bucketAvg = resolveBucketFallbackValue(bucket, bucketIdx)
                        numericWeight = bucket?.count ?? 0
                    }
                    if( numericWeight <= 0 || !Number.isFinite(bucketAvg) ){
                        return
                    }
                    totalWeight += numericWeight
                    weightedSum += bucketAvg * numericWeight
                    if( bucketAvg < minValue ){minValue = bucketAvg}
                    if( bucketAvg > maxValue ){maxValue = bucketAvg}
                })
                if( totalWeight === 0 ){
                    return null
                }
                minValue = Number.isFinite(minValue) ? minValue : weightedSum / totalWeight
                maxValue = Number.isFinite(maxValue) ? maxValue : weightedSum / totalWeight
                return {
                    xMeta: meta,
                    yValue: weightedSum / totalWeight,
                    minY: minValue,
                    maxY: maxValue
                }
            }).filter(Boolean)
            if( !points.length ){
                return null
            }
            const min = points.reduce((acc, point)=>point.yValue < acc ? point.yValue : acc, Infinity)
            const max = points.reduce((acc, point)=>point.yValue > acc ? point.yValue : acc, -Infinity)
            return {
                key: field ?? `average_series_${idx}`,
                label,
                points,
                range: {min, max}
            }
        }).filter(Boolean)
        seriesList = averagedSeries
    }else{
        seriesList = seriesList.map(({bucketMap, ...rest})=>rest)
    }

    if( seriesList.length === 0 ){
        return renderPlaceholder("Series data could not be resolved")
    }

    const palette = resolveSeriesPalette(seriesList.length)
    seriesList = seriesList.map((series, idx)=>({
        ...series,
        color: palette[idx % palette.length]
    }))
    let extraLeftAxisSpace = 0
    let extraRightAxisSpace = 0
    if( showAxisLabels && renderOptions.series_scale === "per_series" && seriesList.length > 1 ){
        perSeriesAxisConfig.enabled = true
        perSeriesAxisConfig.leftCount = Math.ceil(seriesList.length / 2)
        perSeriesAxisConfig.rightCount = seriesList.length - perSeriesAxisConfig.leftCount
        if( perSeriesAxisConfig.leftCount > 0 ){
            extraLeftAxisSpace = perSeriesAxisConfig.leftCount * (perSeriesAxisConfig.axisWidth + perSeriesAxisConfig.axisMargin)
        }
        if( perSeriesAxisConfig.rightCount > 0 ){
            extraRightAxisSpace = perSeriesAxisConfig.rightCount * (perSeriesAxisConfig.axisWidth + perSeriesAxisConfig.axisMargin)
        }
        plotOriginX += extraLeftAxisSpace
        plotOriginY += perSeriesAxisConfig.topMargin
        axisLabelHeight += perSeriesAxisConfig.topMargin
        plotWidth = Math.max(10, plotWidth - extraLeftAxisSpace - extraRightAxisSpace)
    }
    plotHeight = Math.max(10, config.height - plotOriginY - padding[2] - axisLabelHeight)
    axisBaselineY = plotOriginY + plotHeight

    function normalizeRange(range, fallbackMin = 0, fallbackMax = 1){
        let min = Number.isFinite(range?.min) ? range.min : fallbackMin
        let max = Number.isFinite(range?.max) ? range.max : fallbackMax
        min = Math.min(0, min)
        if( !Number.isFinite(max) || max === -Infinity ){
            max = fallbackMax
        }
        if( max <= min ){
            max = min + 1
        }
        return {min, max}
    }

    let globalRange = seriesList.reduce((acc, series)=>{
        if( series.range.min < acc.min ){acc.min = series.range.min}
        if( series.range.max > acc.max ){acc.max = series.range.max}
        return acc
    }, {min: Infinity, max: -Infinity})
    globalRange = normalizeRange(globalRange)

    function scaleForSeries(series){
        if( renderOptions.series_scale === "per_series" || renderOptions.series_scale === "individual" || renderOptions.series_scale === false ){
            const min = series.range.min
            const max = series.range.max
            if( Number.isFinite(min) && Number.isFinite(max) && max !== min ){
                return normalizeRange(series.range, globalRange.min, globalRange.max)
            }
        }
        return globalRange
    }

    function yValueToPixel(value, range){
        let min = range.min
        let max = range.max
        if( !Number.isFinite(min) || !Number.isFinite(max) || min === max ){
            min = globalRange.min
            max = globalRange.max
        }
        if( min === max ){
            max = min + 1
        }
        const clamped = Math.max(min, Math.min(max, value))
        const scale = (clamped - min) / (max - min)
        return axisBaselineY - scale * plotHeight
    }

    const xCount = axisItems.length
    function xOrderToPixel(order){
        if( xCount <= 1 ){
            return plotOriginX + plotWidth / 2
        }
        const categoryWidth = plotWidth / Math.max(xCount, 1)
        return plotOriginX + order * categoryWidth + categoryWidth / 2
    }

    const showXAxisMarkers = (
        renderOptions.show_axis_markers === undefined ||
        renderOptions.show_axis_markers === true ||
        renderOptions.show_axis_markers === "x" ||
        renderOptions.show_axis_markers === "both"
    )
    const showYAxisMarkers = (
        renderOptions.show_axis_markers === undefined ||
        renderOptions.show_axis_markers === true ||
        renderOptions.show_axis_markers === "y" ||
        renderOptions.show_axis_markers === "both"
    )

    if( showXAxisMarkers || showYAxisMarkers ){
        const axisLines = new Konva.Group({listening: false, name: "series_axes"})
        if( showXAxisMarkers ){
            axisLines.add(new Konva.Line({
                points: [plotOriginX, axisBaselineY, plotOriginX + plotWidth, axisBaselineY],
                stroke: borderColor,
                strokeWidth: 1
            }))
        }
        if( showYAxisMarkers ){
            axisLines.add(new Konva.Line({
                points: [plotOriginX, plotOriginY, plotOriginX, axisBaselineY],
                stroke: borderColor,
                strokeWidth: 1
            }))
            const tickCount = 4
            for(let i = 1; i < tickCount; i++){
                const ratio = i / tickCount
                const y = axisBaselineY - ratio * plotHeight
                axisLines.add(new Konva.Line({
                    points: [plotOriginX, y, plotOriginX + plotWidth, y],
                    stroke: mixHexWithWhite(borderColor, 0.2),
                    strokeWidth: 1,
                    dash: [4, 4]
                }))
            }
        }
        group.add(axisLines)
    }

    const columnAsIcon = Boolean(renderOptions.columnAsIcon)
    const axisIconStore = columnAsIcon ? resolveMainstoreInstance(options.mainstore) : null
    const axisPrimitiveResolver = axisIconStore ? (options.primitiveResolver ?? createHeaderPrimitiveResolver(axisIconStore)) : undefined

    function renderAxisLabels(){
        if( !showAxisLabels ){
            return
        }
        if( axisItems.length ){
            const xLabels = new Konva.Group({name: "series_axis_x_labels", listening: false})
            axisItems.forEach((item, idx)=>{
                const centerX = xOrderToPixel(idx)
                if( columnAsIcon ){
                    const iconSize = Math.min(48, Math.max(24, axisLabelHeight * 0.8))
                    const icon = buildHeaderIconNode({
                        item,
                        width: iconSize,
                        iconSize,
                        refreshCallback,
                        placeholder: renderOptions.placeholder !== false,
                        mainstore: axisIconStore,
                        primitiveResolver: axisPrimitiveResolver
                    })
                    if( icon ){
                        icon.x(centerX - iconSize / 2)
                        icon.y(axisBaselineY + 4)
                        xLabels.add(icon)
                        return
                    }
                }
                const label = new CustomText({
                    x: centerX - (axisLabelWidth / 2),
                    y: axisBaselineY + 4,
                    width: axisLabelWidth,
                    text: item.label ?? `${item.idx}`,
                    align: "center",
                    fontSize: axisValueFontSize,
                    fontFamily: resolvedFontFamily,
                    fill: resolvedFontColor,
                    listening: false,
                    wrap: "word",
                    ellipses: true,
                    refreshCallback
                })
                xLabels.add(label)
            })
            group.add(xLabels)
        }
        if( perSeriesAxisConfig.enabled ){
            return
        }
        const yLabels = new Konva.Group({name: "series_axis_y_labels", listening: false})
        const tickCount = Math.max(2, renderOptions.axis_ticks ?? 4)
        for(let i = 0; i <= tickCount; i++){
            const ratio = i / tickCount
            const value = globalRange.min + (globalRange.max - globalRange.min) * ratio
            const y = axisBaselineY - ratio * plotHeight
            const label = new CustomText({
                x: padding[3],
                y: y - 6,
                width: axisLabelWidth - 6,
                text: formatNumber(value),
                align: "right",
                fontSize: axisValueFontSize,
                fontFamily: resolvedFontFamily,
                fill: resolvedFontColor,
                listening: false,
                refreshCallback
            })
            yLabels.add(label)
        }
        group.add(yLabels)
    }

    renderAxisLabels()
    function renderPerSeriesAxes(){
        if( !perSeriesAxisConfig.enabled ){
            return
        }
        const axisGroup = new Konva.Group({name: "series_individual_axes", listening: false})
        const tickCount = Math.max(2, renderOptions.axis_ticks ?? 4)
        const axisMargin = perSeriesAxisConfig.axisMargin
        const axisWidth = perSeriesAxisConfig.axisWidth
        const reservedTopY = plotOriginY - perSeriesAxisConfig.topMargin
        const axisTitleY = reservedTopY + axisValueFontSize * 0.2
        let leftCount = 0
        let rightCount = 0
        seriesList.forEach((series, idx)=>{
            const useLeft = idx % 2 === 0
            const seriesIdx = useLeft ? leftCount++ : rightCount++
            const scale = normalizeRange(series.range, globalRange.min, globalRange.max)
            const axisColor = series.color ?? resolvedFontColor
            const baseX = useLeft
                ? plotOriginX - axisMargin - axisWidth - seriesIdx * (axisWidth + axisMargin)
                : plotOriginX + plotWidth + axisMargin + seriesIdx * (axisWidth + axisMargin)
            const lineX = useLeft ? baseX + axisWidth : baseX
            axisGroup.add(new Konva.Line({
                points: [lineX, plotOriginY, lineX, axisBaselineY],
                stroke: axisColor,
                strokeWidth: 1,
                dash: [4, 3]
            }))
            for(let i = 0; i <= tickCount; i++){
                const ratio = i / tickCount
                const value = scale.min + (scale.max - scale.min) * ratio
                const y = axisBaselineY - ratio * plotHeight
                axisGroup.add(new CustomText({
                    x: useLeft ? baseX : lineX + axisMargin,
                    y: y - 6,
                    width: axisWidth,
                    text: formatNumber(value),
                    align: useLeft ? "right" : "left",
                    fontSize: Math.max(7, axisValueFontSize - 1),
                    fontFamily: resolvedFontFamily,
                    fill: axisColor,
                    listening: false,
                    refreshCallback
                }))
            }
            axisGroup.add(new CustomText({
                x: useLeft ? baseX : lineX + axisMargin,
                y: axisTitleY,
                width: axisWidth,
                text: series.label ?? `Series ${idx + 1}`,
                align: useLeft ? "right" : "left",
                fontSize: axisValueFontSize,
                fontFamily: resolvedFontFamily,
                fill: axisColor,
                wrap: "word",
                ellipses: true,
                listening: false,
                refreshCallback
            }))
        })
        group.add(axisGroup)
    }

    renderPerSeriesAxes()
    const style = renderOptions.series_style ?? "line"
    const barBand = style === "bar" ? 32 : 0

    const valueLabelBounds = []

    seriesList.forEach((series, seriesIdx)=>{
        const scale = scaleForSeries(series)
        const color = series.color ?? palette[seriesIdx % palette.length]
        if( style === "bar" ){
            const categoryWidth = plotWidth / Math.max(xCount, 1)
            const clusterPadding = Math.min(categoryWidth * 0.15, 12)
            const clusterWidth = Math.max(6, categoryWidth - clusterPadding * 2)
            const bandWidth = Math.max(3, Math.min(clusterWidth / Math.max(seriesList.length, 1), 36))
            series.points.forEach((point)=>{
                const order = point.xMeta.order ?? 0
                const categoryStart = plotOriginX + order * categoryWidth
                const clusterStart = Math.min(
                    Math.max(categoryStart + clusterPadding, plotOriginX),
                    plotOriginX + plotWidth - (bandWidth * seriesList.length)
                )
                const x = clusterStart + seriesIdx * bandWidth
                const y = yValueToPixel(point.yValue, scale)
                const height = axisBaselineY - y
                const bar = new Konva.Rect({
                    x,
                    y,
                    width: Math.max(2, bandWidth * 0.9),
                    height: Math.max(1, height),
                    cornerRadius: 3,
                    fill: color,
                    opacity: 0.85,
                    listening: false
                })
                group.add(bar)
                if( showValues ){
                    const label = new CustomText({
                        x: x,
                        y: Math.min(y, axisBaselineY) - 18,
                        width: Math.max(40, bandWidth),
                        text: formatNumber(point.yValue),
                        align: "center",
                        fontSize: valueFontSize,
                        fontFamily: resolvedFontFamily,
                        fill: color,
                        listening: false,
                        refreshCallback
                    })
                    label.x(x + (Math.max(2, bandWidth * 0.9) - label.width()) / 2)
                    const bounds = {x: label.x(), y: label.y(), width: label.width(), height: Math.max(label.textHeight ?? valueFontSize, valueFontSize), node: label}
                    valueLabelBounds.push(bounds)
                    group.add(label)
                }
            })
        }else{
            const points = []
            series.points.forEach((point)=>{
                const x = xOrderToPixel(point.xMeta.order)
                const y = yValueToPixel(point.yValue, scale)
                points.push(x, y)
                const marker = new Konva.Circle({
                    x,
                    y,
                    radius: 3,
                    fill: backgroundFill,
                    stroke: color,
                    strokeWidth: 1.5,
                    listening: false
                })
                group.add(marker)
                if( showValues ){
                    const label = new CustomText({
                        x: x - 28,
                        y: y - 18,
                        width: 56,
                        text: formatNumber(point.yValue),
                        align: "center",
                        fontSize: valueFontSize,
                        fontFamily: resolvedFontFamily,
                        fill: color,
                        listening: false,
                        refreshCallback
                    })
                    const bounds = {x: label.x(), y: label.y(), width: label.width(), height: label.textHeight ?? valueFontSize, node: label}
                    valueLabelBounds.push(bounds)
                    group.add(label)
                }
            })
            if( points.length >= 2 ){
                const line = new Konva.Line({
                    points,
                    stroke: color,
                    strokeWidth: 2,
                    tension: style === "curved" ? 0.4 : 0,
                    lineCap: "round",
                    lineJoin: "round",
                    listening: false
                })
                group.add(line)
            }else if( points.length === 1 ){
                const dot = new Konva.Circle({
                    x: points[0],
                    y: points[1],
                    radius: 3.5,
                    fill: color,
                    listening: false
                })
                group.add(dot)
            }
        }
    })

    
    function resolveLabelCollisions(){
        if( !showValues || valueLabelBounds.length === 0 ){
            return
        }
        const sorted = valueLabelBounds.slice().sort((a,b)=>a.y - b.y)
        const margin = 4
        const minLabelY = padding[0] + 2
        for( let i = 0; i < sorted.length; i++ ){
            const current = sorted[i]
            for( let j = 0; j < i; j++ ){
                const prev = sorted[j]
                const horizontalOverlap = Math.min(current.x + current.width, prev.x + prev.width) - Math.max(current.x, prev.x)
                if( horizontalOverlap <= 0 ){
                    continue
                }
                const overlap = (prev.y + prev.height + margin) - current.y
                if( overlap > 0 ){
                    current.y += overlap
                }
            }
            current.y = Math.max(minLabelY, current.y)
            current.node.y(current.y)
        }
    }

    resolveLabelCollisions()

    group.attrs.legendInfo = {
        data: seriesList.map((series, idx)=>({idx, label: series.label ?? series.key})),
        colors: seriesList.map((series)=>series.color)
    }

    return group
})
const TIMESERIES_RESOLUTION_IN_MS = Object.freeze({
    second: 1000,
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: (365 / 12) * 24 * 60 * 60 * 1000,
    year: 365 * 24 * 60 * 60 * 1000
})
registerRenderer( {type: "default", configs: "datatable_timeseries"}, function renderFunc({table, cell, renderOptions = {}, ...options}){
    const config = {width: 280, height: 140, padding: [10,10,10,10], ...options}
    if( renderOptions.width ){
        config.width = renderOptions.width
    }
    if( renderOptions.height ){
        config.height = renderOptions.height
    }

    const normalizedStyle = typeof renderOptions.timeseries_style === "string"
        ? renderOptions.timeseries_style.toLowerCase()
        : "line"
    const chartStyle = normalizedStyle === "bar" ? "bar" : "line"
    const resolutionKey = typeof renderOptions.resolution === "string" ? renderOptions.resolution : "day"
    const resolutionMs = TIMESERIES_RESOLUTION_IN_MS[resolutionKey] ?? TIMESERIES_RESOLUTION_IN_MS.day
    const timeRangeMode = typeof renderOptions.timeRange === "string" ? renderOptions.timeRange : null
    const showingDeltas = timeRangeMode ? timeRangeMode.startsWith("delta_") : false
    const isAlignedAbsolute = timeRangeMode === "row" || timeRangeMode === "column" || timeRangeMode === "delta_row" || timeRangeMode === "delta_column"
    const timeseries = cell.timeseries ?? []
    const paletteName = renderOptions.timeseries_palette ?? renderOptions.colors ?? "default"
    const paletteEntry = heatMapPalette.find(d=>d.name === paletteName)
    const builtinPalettes = {
        default: categoryColors,
        pastel: categoryColors_pastel,
        muted: categoryColors_MUTED,
        std: categoryColors_STD,
        classic: categoryColors_old,
        vibrant: categoryColors2
    }
    const builtinPalette = builtinPalettes[paletteName]
    const sequentialPalette = paletteEntry?.colors?.length
        ? paletteEntry.colors
        : (builtinPalette?.length ? builtinPalette : heatMapPalette[0].colors)
    const categoricalPalette = paletteEntry?.category_colors?.length
        ? paletteEntry.category_colors
        : (builtinPalette?.length ? builtinPalette : sequentialPalette)
    const colorMode = renderOptions.timeseries_color_by ?? "row"
    const allocationDefs = Array.isArray(table.defs?.allocations) ? table.defs.allocations : []
    const rowColorMap = buildAxisColorMap(table.rows, categoricalPalette)
    const columnColorMap = buildAxisColorMap(table.columns, categoricalPalette)
    const allocationColorMaps = buildAllocationColorMaps(allocationDefs, table.allocations, categoricalPalette)
    const sequentialAssignments = new Map()
    let sequentialCursor = 0

    const resolveLegendItems = () => {
        const metricFilter = Array.isArray(table.defs?.viewFilters)
            ? table.defs.viewFilters.find(entry=>entry?.type === "metric_fields")
            : null
        const metricFields = Array.isArray(metricFilter?.fields) ? metricFilter.fields : null
        if( metricFields?.length ){
            return metricFields.map((field, idx)=>({
                idx,
                label: field?.label ?? field?.parameter ?? `Series ${idx + 1}`
            }))
        }
        const allocItems = table.allocations?.filterGroup0?.items
        if( Array.isArray(allocItems) && allocItems.length ){
            return allocItems.map((item, idx)=>({
                idx,
                label: item?.label ?? item?.idx ?? `Series ${idx + 1}`
            }))
        }
        return (timeseries ?? []).map((_, idx)=>({idx, label: `Series ${idx + 1}`}))
    }
    const showValueLabel = renderOptions.timeseries_show_value_label !== false
    const showXAxis = renderOptions.timeseries_show_x_axis !== false
    const showYAxis = renderOptions.timeseries_show_y_axis === true
    const showTrend = renderOptions.timeseries_show_trend === true
    const showAverage = renderOptions.timeseries_show_average === true
    const linkedScale = renderOptions.timeseries_linked_scale !== false
    const xAxisLabel = renderOptions.timeseries_x_axis_label
    const yAxisLabel = renderOptions.timeseries_y_axis_label
    const xLabelFormat = typeof renderOptions.timeseries_x_label_format === "string"
        ? renderOptions.timeseries_x_label_format
        : "month_short"
    const legendSetting = renderOptions.show_legend
    const legendPosition = !legendSetting
        ? null
        : (typeof legendSetting === "string" && legendSetting.includes("right") ? "right" : "below")
    const legendFontSize = renderOptions.legend_size ?? 10
    const legendGap = 6

    const hasTimeseriesData = Array.isArray(timeseries) && timeseries.some((series)=>{
        if( !series ){
            return false
        }
        return Object.values(series).some((entry)=>{
            const count = entry?.count
            return Number.isFinite(count)
        })
    })
    if( !hasTimeseriesData ){
        return new Konva.Group({
            id: options.id,
            name:"cell inf_track empty",
            x: (options.x ?? 0),
            y: (options.y ?? 0),
            width: config.width,
            height: config.height,
            listening: false
        })
    }

    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.width,
        height: config.height
    })
    g.add(new Konva.Rect({
        width: config.width,
        height: config.height,
        fill: "white",
        listening: false
    }))
    const shadowGroup = new Konva.Group({listening: false})
    const axisLinesGroup = new Konva.Group({listening: false})
    const axisLabelsGroup = new Konva.Group({listening: false})
    const seriesGroup = new Konva.Group({listening: false})
    const dataLabelGroup = new Konva.Group({listening: false})
    g.add(shadowGroup)
    g.add(axisLinesGroup)
    g.add(axisLabelsGroup)
    g.add(seriesGroup)
    g.add(dataLabelGroup)

    let axisBottomPadding = showXAxis ? 12 : 6
    const yAxisCurrency = resolveYAxisCurrency()
    const axisTopPadding = showYAxis ? (yAxisCurrency ? 18 : 12) : 4
    const valueFontBase = 11
    const valueFontMin = 5
    const valueLabelFormatter = (value)=>formatCompactNumber(value)
    const yAxisLabelFormatter = (value)=>formatCompactNumber(value)
    const yAxisLabelSamples = showYAxis
        ? resolveYAxisLabelSamples(yAxisLabelFormatter)
        : []
    const axisLeftPadding = showYAxis
        ? Math.max(32, computeAxisPaddingFromLabels(yAxisLabelSamples, 10) + 8)
        : 6
    const showSecondaryAxis = showYAxis && !linkedScale && timeseries.length > 1
    let axisRightPadding = showSecondaryAxis ? 32 : (chartStyle === "line" ? 16 : 6)
    const legendItemsRaw = legendPosition ? resolveLegendItems() : []
    const seriesCount = timeseries.length || legendItemsRaw.length
    const legendItems = legendItemsRaw.slice(0, seriesCount)
    let legend = null
    let legendColors = []
    if( legendPosition === "right" && legendItems.length ){
        const provisionalHeight = Math.max(10, config.height - axisTopPadding - axisBottomPadding)
        const maxLegendWidth = Math.min(160, config.width * 0.35)
        legendColors = legendItems.map((_, idx)=>resolveSeriesColor(cell, idx))
        legend = renderLegend(legendItems, {
            colors: legendColors,
            fontSize: legendFontSize,
            horizontalLegend: false,
            width: maxLegendWidth,
            maxWidth: maxLegendWidth,
            height: provisionalHeight,
            fontFamily: renderOptions.legendFont,
            fontFill: renderOptions.legendFontColor,
            placeholderFill: renderOptions.legendFontPlaceholderColor
        })
        axisRightPadding += legend.width() + legendGap
    }
    const plotWidth = Math.max(10, config.width - axisLeftPadding - axisRightPadding)
    let labelTicks = []
    const globalTime = resolveGlobalTimeRange(timeseries, chartStyle === "bar")
    if( showXAxis ){
        labelTicks = resolveXAxisTicks(timeseries, resolutionMs, plotWidth, axisLeftPadding, undefined, chartStyle === "bar")
        const xFont = labelTicks[0]?.fontSize ?? 10
        const paddingTarget = xFont + (xAxisLabel ? 4 : 2)
        axisBottomPadding = Math.max(axisBottomPadding, paddingTarget)
    }
    const axisBottomPaddingBase = axisBottomPadding
    if( legendPosition === "below" && legendItems.length ){
        if( legendColors.length === 0 ){
            legendColors = legendItems.map((_, idx)=>resolveSeriesColor(cell, idx))
        }
        legend = renderLegend(legendItems, {
            colors: legendColors,
            fontSize: legendFontSize,
            horizontalLegend: true,
            width: plotWidth,
            height: config.height,
            fontFamily: renderOptions.legendFont,
            fontFill: renderOptions.legendFontColor,
            placeholderFill: renderOptions.legendFontPlaceholderColor
        })
        axisBottomPadding += legend.height() + (legendFontSize * 0.6) + legendGap
    }
    const plotHeight = Math.max(10, config.height - axisBottomPadding - axisTopPadding)
    const valueLabelSamples = []
    if( showValueLabel ){
        for( const series of (cell.timeseries ?? []) ){
            if( !series ){
                continue
            }
            for( const entry of Object.values(series) ){
                const label = valueLabelFormatter(entry?.count ?? 0)
                if( label ){
                    valueLabelSamples.push(label)
                }
            }
        }
    }
    const valueLabelFontSize = showValueLabel
        ? computeValueLabelFontSize(valueLabelSamples, plotWidth, valueFontBase, valueFontMin)
        : valueFontBase

    const resolveGlobalScale = () => {
        let min = Infinity
        let max = -Infinity
        timeseries.forEach((series, idx)=>{
            if( !series ){
                return
            }
            let seriesMin = Infinity
            let seriesMax = -Infinity
            Object.values(series).forEach((entry)=>{
                const value = entry?.count ?? 0
                if( value < seriesMin ){
                    seriesMin = value
                }
                if( value > seriesMax ){
                    seriesMax = value
                }
            })
            if( renderOptions.calcRange ){
                const totals = table.ranges?.timeseries?.values
                if( renderOptions.calcRange === "row" ){
                    const rangeTarget = totals?.rows?.order?.[cell.rIdx]?.[idx]
                    if( rangeTarget?.min !== undefined ){
                        seriesMin = rangeTarget.min
                    }
                    if( rangeTarget?.max !== undefined ){
                        seriesMax = rangeTarget.max
                    }
                }else if( renderOptions.calcRange === "column" ){
                    const rangeTarget = totals?.columns?.order?.[cell.cIdx]?.[idx]
                    if( rangeTarget?.min !== undefined ){
                        seriesMin = rangeTarget.min
                    }
                    if( rangeTarget?.max !== undefined ){
                        seriesMax = rangeTarget.max
                    }
                }
            }
            if( Number.isFinite(seriesMin) && seriesMin < min ){
                min = seriesMin
            }
            if( Number.isFinite(seriesMax) && seriesMax > max ){
                max = seriesMax
            }
        })
        if( !Number.isFinite(min) || !Number.isFinite(max) ){
            return null
        }
        return {min, max}
    }

    const resolveBaseRange = () => {
        const range = table.ranges?.timeseries?.values
        const rowRange = range?.rows?.order?.[cell.rIdx]?.[0]
        const columnRange = range?.columns?.order?.[cell.cIdx]?.[0]
        let min = rowRange?.min ?? columnRange?.min ?? 0
        let max = rowRange?.max ?? columnRange?.max ?? 0
        if( !Number.isFinite(min) ){
            min = 0
        }
        if( !Number.isFinite(max) ){
            max = 0
        }
        if( min > 0 ){
            min = 0
        }
        if( max < 0 ){
            max = 0
        }
        return {min, max}
    }

    const resolveSeriesRange = (seriesIdx)=>{
        const series = timeseries[seriesIdx]
        if( !series ){
            return baseRange
        }
        let min = Infinity
        let max = -Infinity
        Object.values(series).forEach((entry)=>{
            const value = entry?.count ?? 0
            if( value < min ){
                min = value
            }
            if( value > max ){
                max = value
            }
        })
        if( !Number.isFinite(min) ){
            min = 0
        }
        if( !Number.isFinite(max) ){
            max = 0
        }
        if( min > 0 ){
            min = 0
        }
        if( max < 0 ){
            max = 0
        }
        return {min, max}
    }

    const baseRange = resolveBaseRange()
    const globalScaleAll = resolveGlobalScale()
    const clampRangeToZero = (range)=>{
        if( !range ){
            return range
        }
        return {
            min: Math.min(range.min, 0),
            max: Math.max(range.max, 0)
        }
    }
    const axisRange = linkedScale && globalScaleAll ? clampRangeToZero(globalScaleAll) : baseRange
    const zeroRange = clampRangeToZero(globalScaleAll ?? baseRange)
    const zeroBaselineY = axisTopPadding + plotHeight - ((0 - zeroRange.min) * (plotHeight / (zeroRange.max - zeroRange.min || 1)))

    let seriesIdx = -1
    let placeholderOverlayPlaced = false
    let zeroLineDrawn = false
    const labelCollisionCache = new Set()
    for( const series of timeseries ){
        seriesIdx += 1
        if( !series ){
            continue
        }
        const {msList: globalMsList, min, max, bucketDuration} = globalTime
        if( !Number.isFinite(min) || !Number.isFinite(max) ){
            continue
        }
        const msList = Object.keys(series ?? {})
            .map((d)=>Number(d))
            .filter((value)=>Number.isFinite(value))
            .sort((a,b)=>a-b)
        if(msList.length === 0){
            continue
        }
        const axisMin = Number.isFinite(min) ? min : 0
        const rawAxisMax = Number.isFinite(max) ? max : axisMin + bucketDuration
        const axisMax = rawAxisMax === axisMin ? axisMin + bucketDuration : rawAxisMax
        const spanMs = Math.max(bucketDuration, axisMax - axisMin, 1)
        const dx = plotWidth / spanMs
        const inFrame = msList.filter((value)=>Number.isFinite(value) && value >= axisMin && value <= axisMax)
        if( inFrame.length === 0 ){
            continue
        }

        let minValue = Infinity
        let maxValue = -Infinity
        Object.values(series).forEach((entry)=>{
            const value = entry?.count ?? 0
            if( value < minValue ){
                minValue = value
            }
            if( value > maxValue ){
                maxValue = value
            }
        })

        if( renderOptions.calcRange ){
            const totals = table.ranges?.timeseries?.values
            let rangeTarget
            if( renderOptions.calcRange === "row"){
                rangeTarget = totals?.rows?.order?.[cell.rIdx]?.[seriesIdx]
            }else if( renderOptions.calcRange === "column"){
                rangeTarget = totals?.columns?.order?.[cell.cIdx]?.[seriesIdx]
            }
            if( rangeTarget?.max !== undefined ){
                maxValue = rangeTarget.max
            }
            if( rangeTarget?.min !== undefined ){
                minValue = rangeTarget.min
            }
        }

        const scaleTarget = linkedScale ? axisRange : resolveSeriesRange(seriesIdx)
        if( scaleTarget ){
            minValue = scaleTarget.min
            maxValue = scaleTarget.max
        }
        if( !Number.isFinite(maxValue) ){
            maxValue = 0
        }
        if( !Number.isFinite(minValue) ){
            minValue = 0
        }

        const valueSpan = maxValue - minValue
        const safeSpan = valueSpan === 0 ? 1 : valueSpan
        const dy = plotHeight / safeSpan
        const zeroY = zeroBaselineY
        const posHeight = Math.max(1, zeroY - axisTopPadding)
        const negHeight = Math.max(1, (axisTopPadding + plotHeight) - zeroY)
        const posSpan = maxValue > 0 ? maxValue : 0
        const negSpan = minValue < 0 ? Math.abs(minValue) : 0
        const mapValueToY = (value)=>{
            if( linkedScale ){
                return axisTopPadding + plotHeight - ((value - minValue) * dy)
            }
            if( value >= 0 ){
                if( posSpan === 0 ){
                    return zeroY
                }
                return zeroY - ((value / posSpan) * posHeight)
            }
            if( negSpan === 0 ){
                return zeroY
            }
            return zeroY + ((Math.abs(value) / negSpan) * negHeight)
        }

        const plotPoints = inFrame.map((ms)=>{
            const value = series[ms]?.count ?? 0
            return {
                ms,
                value,
                x: axisLeftPadding + (ms - axisMin) * dx,
                y: mapValueToY(value)
            }
        })
        if( plotPoints.length === 0 ){
            continue
        }
        const flatPoints = plotPoints.flatMap(point=>[point.x, point.y])
        const color = resolveSeriesColor(cell, seriesIdx)

        if( !placeholderOverlayPlaced ){
            const placeholderRanges = resolvePlaceholderRanges(globalMsList, series, bucketDuration)
            if( placeholderRanges.length ){
                placeholderRanges.forEach((range)=>{
                    addPlaceholderOverlay({
                        start: range.start,
                        end: range.end,
                        axisMin,
                        axisMax,
                        dx,
                        offsetX: axisLeftPadding
                    })
                })
                placeholderOverlayPlaced = true
            }
        }

        if( chartStyle === "bar" ){
            const seriesCount = timeseries.length || 1
            const bucketWidth = Math.max(1, bucketDuration * dx)
            const groupWidth = Math.max(2, bucketWidth * 0.9)
            const seriesWidth = Math.max(1, groupWidth / seriesCount)
            if( !zeroLineDrawn && minValue < 0 && maxValue > 0 ){
                axisLinesGroup.add(new Konva.Line({
                    points: [axisLeftPadding, zeroY, axisLeftPadding + plotWidth, zeroY],
                    stroke: "#e5e7eb",
                    strokeWidth: 1,
                    listening: false
                }))
                zeroLineDrawn = true
            }
            for( const point of plotPoints ){
                const barBase = Math.min(axisTopPadding + plotHeight, Math.max(axisTopPadding, zeroY))
                const barTop = point.y
                const barHeight = Math.abs(barBase - barTop)
                if( barHeight <= 0 ){
                    continue
                }
                const barY = Math.min(barBase, barTop)
                const baseX = point.x + (bucketWidth - groupWidth) / 2
                const barX = baseX + (seriesIdx * seriesWidth)
                const width = Math.min(seriesWidth, Math.max(0, config.width - barX))
                seriesGroup.add(new Konva.Rect({
                    x: barX,
                    y: barY,
                    width,
                    height: barHeight,
                    fill: color,
                    opacity: 0.9,
                    cornerRadius: Math.min(3, seriesWidth / 2),
                    listening: false
                }))
                if( showValueLabel ){
                    const maxLabelWidth = width * 0.8
                    const barLabelFontSize = computeValueLabelFontSize(valueLabelSamples, maxLabelWidth, valueFontBase, valueFontMin)
                    if( barLabelFontSize >= valueFontMin ){
                    let labelY = point.value < 0
                        ? (barY + barHeight + 2)
                        : Math.max(2, barY - (barLabelFontSize + 4))
                    const bottomLimit = axisTopPadding + plotHeight - 2
                    if( point.value < 0 && (labelY + barLabelFontSize) > bottomLimit ){
                        labelY = Math.max(axisTopPadding + 2, barY + barHeight - (barLabelFontSize + 2))
                    }
                    const label = new Konva.Text({
                        x: barX,
                        y: labelY,
                        text: valueLabelFormatter(point.value),
                            fontSize: barLabelFontSize,
                            fill: "#374151",
                            width,
                            align: "center",
                            wrap: "none"
                        })
                        dataLabelGroup.add(label)
                    }
                }
            }
        }else{
            const lastSeriesKey = msList.at(-1)
            const lastPointInterpolated = lastSeriesKey !== undefined && series[lastSeriesKey]?.interpolated
            const mainPoints = lastPointInterpolated && flatPoints.length >= 2 ? flatPoints.slice(0, -2) : flatPoints.slice()

            if( mainPoints.length >= 4 ){
                seriesGroup.add(new Konva.Line({
                    points: mainPoints,
                    stroke: color,
                    strokeWidth: 1,
                    listening: false
                }))
            }else if( mainPoints.length === 2 ){
                seriesGroup.add(new Konva.Circle({
                    x: mainPoints[0],
                    y: mainPoints[1],
                    radius: 2,
                    fill: color,
                    listening: false
                }))
            }

            if( lastPointInterpolated && flatPoints.length >= 4 ){
                const tail = flatPoints.slice(-4)
                seriesGroup.add(new Konva.Line({
                    points: tail,
                    stroke: color,
                    strokeWidth: 1,
                    dashEnabled: true,
                    dash: [6, 5],
                    listening: false
                }))
            }

            for( const point of plotPoints ){
                seriesGroup.add(new Konva.Circle({
                    x: point.x,
                    y: point.y,
                    radius: 2,
                    fill: color,
                    listening: false
                }))
                if( showValueLabel ){
                    const lineLabelFontSize = Math.max(5, Math.floor(valueLabelFontSize * 0.8))
                    const labelText = valueLabelFormatter(point.value)
                    const labelWidth = Math.max(20, (lineLabelFontSize * (labelText?.length ?? 0) * 0.62) + 6)
                    const overlapKey = `${seriesIdx}-${Math.round(point.x / 10)}`
                    if( labelText && !labelCollisionCache.has(overlapKey) ){
                        labelCollisionCache.add(overlapKey)
                        const label = new Konva.Text({
                            x: point.x - (labelWidth / 2),
                            y: Math.max(axisTopPadding + 2, point.y - (lineLabelFontSize + 4)),
                            text: labelText,
                            fontSize: lineLabelFontSize,
                            fill: "#374151",
                            wrap: "none",
                            width: labelWidth,
                            align: "center"
                        })
                        dataLabelGroup.add(label)
                    }
                }
            }
        }

        if( showAverage ){
            const avg = plotPoints.reduce((acc, point)=>acc + (point.value ?? 0), 0) / plotPoints.length
            const avgY = mapValueToY(avg)
            seriesGroup.add(new Konva.Line({
                points: [axisLeftPadding, avgY, axisLeftPadding + plotWidth, avgY],
                stroke: mixHexWithWhite(color, 0.35),
                strokeWidth: 1,
                dash: [4, 4],
                listening: false
            }))
        }

        if( showTrend && plotPoints.length >= 2 ){
            const regression = computeLinearRegression(plotPoints)
            if( regression ){
                const {slope, intercept} = regression
                const barOffset = chartStyle === "bar" ? (Math.max(1, bucketDuration * dx) / 2) : 0
                const x1 = plotPoints[0].x + barOffset
                const x2 = plotPoints.at(-1).x + barOffset
                const v1 = slope * plotPoints[0].ms + intercept
                const v2 = slope * plotPoints.at(-1).ms + intercept
                const y1 = mapValueToY(v1)
                const y2 = mapValueToY(v2)
                seriesGroup.add(new Konva.Line({
                    points: [x1, y1, x2, y2],
                    stroke: mixHexWithWhite(color, 0.15),
                    strokeWidth: 1,
                    dash: [6, 3],
                    listening: false
                }))
            }
        }
    }

    const leftAxisRange = linkedScale ? axisRange : resolveSeriesRange(0)
    let yTicks = []
    if( showYAxis ){
        yTicks = resolveYAxisTicks(undefined, leftAxisRange.min, leftAxisRange.max, zeroRange.min, zeroRange.max, yAxisLabelFormatter)
    }
    if( showXAxis || showYAxis ){
        const xFont = labelTicks[0]?.fontSize ?? 10
        const yFont = yTicks[0]?.fontSize ?? 10
        const axisFontSize = Math.min(xFont, yFont)
        if( showXAxis && axisFontSize !== xFont ){
            labelTicks = resolveXAxisTicks(timeseries, resolutionMs, plotWidth, axisLeftPadding, axisFontSize, chartStyle === "bar")
        }
        if( showYAxis && axisFontSize !== yFont ){
            yTicks = resolveYAxisTicks(axisFontSize, leftAxisRange.min, leftAxisRange.max, zeroRange.min, zeroRange.max, yAxisLabelFormatter)
        }
    }

    if( showXAxis ){
        labelTicks.forEach((tick)=>{
            const labelX = chartStyle === "bar"
                ? clamp(tick.x, axisLeftPadding, axisLeftPadding + plotWidth - tick.width)
                : tick.x
            const label = new Konva.Text({
                x: labelX,
                y: axisTopPadding + plotHeight + 4,
                text: tick.label,
                fontSize: tick.fontSize,
                fill: "#6b7280",
                width: tick.width,
                align: "center"
            })
            axisLabelsGroup.add(label)
        })
        const axisBaselineY = zeroBaselineY
        axisLinesGroup.add(new Konva.Line({
            points: [axisLeftPadding, axisBaselineY, axisLeftPadding + plotWidth, axisBaselineY],
            stroke: "#e5e7eb",
            strokeWidth: 1,
            listening: false
        }))
        if( xAxisLabel ){
            const axisLabel = new Konva.Text({
                x: axisLeftPadding,
                y: axisTopPadding + plotHeight + 14,
                text: xAxisLabel,
                fontSize: 10,
                fill: "#9ca3af",
                width: plotWidth,
                align: "center"
            })
            axisLabelsGroup.add(axisLabel)
        }
    }

    if( showYAxis ){
        yTicks.forEach((tick)=>{
            axisLinesGroup.add(new Konva.Line({
                points: [axisLeftPadding - 3, tick.y, axisLeftPadding, tick.y],
                stroke: "#e5e7eb",
                strokeWidth: 1,
                listening: false
            }))
            const label = new Konva.Text({
                x: Math.max(2, axisLeftPadding - tick.width - 4),
                y: tick.y - (tick.fontSize ?? 10) / 2,
                text: yAxisLabelFormatter(tick.value),
                fontSize: tick.fontSize ?? 10,
                fill: "#6b7280"
            })
            axisLabelsGroup.add(label)
        })
        if( yAxisCurrency ){
            const baseFont = yTicks[0]?.fontSize ?? 10
            const labelFont = Math.max(5, baseFont - 1)
            const safeY = 2
            const currencyLabel = new Konva.Text({
                x: 2,
                y: safeY,
                text: yAxisCurrency,
                fontSize: labelFont,
                fill: "#9ca3af"
            })
            axisLabelsGroup.add(currencyLabel)
        }
        if( yAxisLabel ){
            const axisLabel = new Konva.Text({
                x: 2,
                y: 2,
                text: yAxisLabel,
                fontSize: 10,
                fill: "#9ca3af"
            })
            axisLabelsGroup.add(axisLabel)
        }
        axisLinesGroup.add(new Konva.Line({
            points: [axisLeftPadding, axisTopPadding, axisLeftPadding, axisTopPadding + plotHeight],
            stroke: "#e5e7eb",
            strokeWidth: 1,
            listening: false
        }))
    }

    if( showSecondaryAxis ){
        const rightRange = resolveSeriesRange(1)
        const rightTicks = resolveYAxisTicks(labelTicks[0]?.fontSize ?? 10, rightRange.min, rightRange.max, zeroRange.min, zeroRange.max, yAxisLabelFormatter)
        rightTicks.forEach((tick)=>{
            axisLinesGroup.add(new Konva.Line({
                points: [axisLeftPadding + plotWidth, tick.y, axisLeftPadding + plotWidth + 3, tick.y],
                stroke: "#e5e7eb",
                strokeWidth: 1,
                listening: false
            }))
            const label = new Konva.Text({
                x: axisLeftPadding + plotWidth + 4,
                y: tick.y - (tick.fontSize ?? 10) / 2,
                text: yAxisLabelFormatter(tick.value),
                fontSize: tick.fontSize ?? 10,
                fill: "#6b7280"
            })
            axisLabelsGroup.add(label)
        })
        axisLinesGroup.add(new Konva.Line({
            points: [axisLeftPadding + plotWidth, axisTopPadding, axisLeftPadding + plotWidth, axisTopPadding + plotHeight],
            stroke: "#e5e7eb",
            strokeWidth: 1,
            listening: false
        }))
    }

    if( legend && legendItems.length ){
        if( legendPosition === "right" ){
            legend.x(axisLeftPadding + plotWidth + legendGap)
            legend.y(axisTopPadding + (plotHeight - legend.height()) / 2)
        }else{
            legend.x(axisLeftPadding + Math.max(0, (plotWidth - legend.width()) / 2))
            legend.y(axisTopPadding + plotHeight + axisBottomPaddingBase + legendGap + (legendFontSize * 0.4))
        }
        g.add(legend)
    }

    if( options.getConfig){
        return {
            width: g.width(),
            height: g.height()
        }
    }
    return g

    function resolveSeriesColor(cell, seriesIndex){
        if( colorMode === "series" || timeseries.length > 1 ){
            return assignSequentialColor(`series-${seriesIndex}`)
        }
        switch(colorMode){
            case "column": {
                const columnKey = table.columns?.[cell.cIdx]?.idx ?? cell.cIdx
                const color = getColorFromMap(columnColorMap, columnKey)
                if( color ){
                    return color
                }
                break
            }
            case "allocation": {
                const allocationColor = resolveAllocationColor(cell, seriesIndex)
                if( allocationColor ){
                    return allocationColor
                }
                break
            }
            case "cell": {
                return assignSequentialColor(`cell-${cell.id}-${seriesIndex}`)
            }
            case "row":
            default: {
                const rowKey = table.rows?.[cell.rIdx]?.idx ?? cell.rIdx
                const color = getColorFromMap(rowColorMap, rowKey)
                if( color ){
                    return color
                }
                break
            }
        }
        return assignSequentialColor(`${colorMode}-${cell.id}-${seriesIndex}`)
    }

    function computeValueLabelFontSize(labels = [], availableWidth, baseSize, minSize){
        if( labels.length === 0 ){
            return baseSize
        }
        const longest = labels.reduce((a,c)=> (c?.length ?? 0) > (a?.length ?? 0) ? c : a, "")
        const targetWidth = Math.max(20, availableWidth)
        const estimated = longest.length > 0 ? Math.floor(targetWidth / (longest.length * 0.62)) : baseSize
        return Math.min(baseSize, Math.max(minSize, estimated))
    }

    function resolveXAxisTicks(seriesList, resolutionMs, width, offsetX = 0, forceFontSize, useBucketCenter = false){
        if( !seriesList || seriesList.length === 0 ){
            return []
        }
        const msList = resolveTimeseriesMsList(seriesList)
        if( msList.length === 0 ){
            return []
        }
        const min = msList[0]
        const max = msList.at(-1)
        const deltas = msList.slice(1)
            .map((value, idx)=>value - msList[idx])
            .filter((value)=>Number.isFinite(value) && value > 0)
        const bucketDuration = deltas.length ? Math.min(...deltas) : resolutionMs
        const axisMax = useBucketCenter ? (max + bucketDuration) : max
        const span = Math.max(axisMax - min, resolutionMs)
        const dx = width / span
        const baseFontSize = 10
        const minFontSize = 5
        const minGap = 6
        const bucketWidth = Math.max(1, bucketDuration * dx)
        const groupWidth = Math.max(2, bucketWidth * 0.9)
        const maxLabelWidth = Math.max(12, groupWidth * 0.8)
        const labels = msList.map(ms=>formatDateLabel(ms))
        const maxLen = labels.reduce((acc, label)=>Math.max(acc, label.length), 1)
        const neighborWidths = deltas.length
            ? deltas.map(delta=>Math.max(12, Math.min(maxLabelWidth, (delta * dx) - minGap)))
            : [maxLabelWidth]
        const allowedWidth = Math.max(12, Math.min(maxLabelWidth, Math.min(...neighborWidths)))
        const estimated = Math.floor(allowedWidth / (maxLen * 0.62))
        const fontSize = forceFontSize
            ? Math.min(baseFontSize, Math.max(minFontSize, forceFontSize))
            : Math.min(baseFontSize, Math.max(minFontSize, estimated))

        const buildTicks = (size)=>{
            const ticks = []
            let lastEnd = -Infinity
            for( let i = 0; i < msList.length; i += 1 ){
                const ms = msList[i]
                const label = labels[i]
                const labelWidth = Math.max(12, Math.min(maxLabelWidth, size * (label.length * 0.62)))
                const xCenter = offsetX + (ms - min) * dx + (useBucketCenter ? (bucketWidth / 2) : 0)
                const left = xCenter - (labelWidth / 2)
                const right = left + labelWidth
                const overlaps = left < (lastEnd + minGap)
                ticks.push({
                    ms,
                    x: left,
                    label,
                    width: labelWidth,
                    fontSize: size,
                    overlaps
                })
                if( !overlaps ){
                    lastEnd = right
                }
            }
            return ticks
        }

        let ticks = buildTicks(fontSize)
        if( ticks.some(tick=>tick.overlaps) && fontSize > minFontSize ){
            ticks = buildTicks(minFontSize)
        }
        if( ticks.some(tick=>tick.overlaps) ){
            let lastEnd = -Infinity
            ticks = ticks.filter((tick, idx)=>{
                const left = tick.x
                const right = tick.x + tick.width
                if( idx === 0 || left >= lastEnd + minGap ){
                    lastEnd = right
                    return true
                }
                return false
            })
        }else{
            ticks = ticks.map(({overlaps, ...tick})=>tick)
        }
        return ticks.map(({overlaps, ...tick})=>tick)
    }

    function resolveYAxisTicks(forceFontSize, rangeMin, rangeMax, zeroMin, zeroMax, formatter){
        const min = rangeMin
        const max = rangeMax
        const safeSpan = max - min || 1
        const zeroSpan = zeroMax - zeroMin || 1
        const zeroY = axisTopPadding + plotHeight - ((0 - zeroMin) / zeroSpan * plotHeight)
        const posHeight = Math.max(1, zeroY - axisTopPadding)
        const negHeight = Math.max(1, (axisTopPadding + plotHeight) - zeroY)
        const posSpan = max > 0 ? max : 0
        const negSpan = min < 0 ? Math.abs(min) : 0
        const mapValueToTickY = (value)=>{
            if( linkedScale ){
                return axisTopPadding + plotHeight - ((value - min) / safeSpan * plotHeight)
            }
            if( value >= 0 ){
                if( posSpan === 0 ){
                    return zeroY
                }
                return zeroY - ((value / posSpan) * posHeight)
            }
            if( negSpan === 0 ){
                return zeroY
            }
            return zeroY + ((Math.abs(value) / negSpan) * negHeight)
        }
        const ticks = [0, 0.5, 1].map((ratio)=>{
            const value = min + (safeSpan * ratio)
            const y = mapValueToTickY(value)
            return {value, y}
        })
        ticks.push({value: 0, y: zeroY})
        const deduped = new Map()
        ticks.forEach((tick)=>{
            const key = `${tick.value}-${Math.round(tick.y)}`
            if( !deduped.has(key) ){
                deduped.set(key, tick)
            }
        })
        const ordered = Array.from(deduped.values()).sort((a,b)=>b.y - a.y)
        const formatValue = typeof formatter === "function" ? formatter : (value)=>String(value ?? "")
        const labels = ordered.map(tick=>formatValue(tick.value)).filter(Boolean)
        const computedFont = computeValueLabelFontSize(labels, axisLeftPadding - 6, 10, 5)
        const fontSize = forceFontSize
            ? Math.min(computedFont, Math.max(5, forceFontSize))
            : computedFont
        return ordered.map((tick)=>({
            ...tick,
            width: Math.max(20, fontSize * ((formatValue(tick.value)?.length ?? 0) * 0.62)),
            fontSize
        }))
    }

    function formatDateLabel(ms){
        const date = moment(ms)
        switch(xLabelFormat){
            case "full":
                return date.format("YYYY-MM-DD")
            case "month_long":
                return date.format("MMMM YYYY")
            case "month_short":
                return date.format("MMM YYYY")
            case "year_long":
                return date.format("YYYY")
            case "year_short":
                return date.format("YY")
            default:
                return date.format("MMM YYYY")
        }
    }

    function formatCompactNumber(value, currency, forceSuffix){
        if( value === undefined || value === null || Number.isNaN(value) ){
            return ""
        }
        const numeric = Number(value)
        const abs = Math.abs(numeric)
        const suffixes = [
            {limit: 1e12, value: 1e12, suffix: "T"},
            {limit: 1e9, value: 1e9, suffix: "B"},
            {limit: 1e6, value: 1e6, suffix: "M"},
            {limit: 1e3, value: 1e3, suffix: "K"}
        ]
        const forced = forceSuffix ? suffixes.find(d=>d.suffix === forceSuffix) : undefined
        let entry = forced ?? suffixes.find(d=>abs >= d.limit)
        if( forced && abs < forced.value ){
            entry = suffixes.find(d=>abs >= d.limit)
        }
        if( entry ){
            const scaled = numeric / entry.value
            const decimals = Math.abs(scaled) >= 100 ? 0 : Math.abs(scaled) >= 10 ? 1 : 2
            return `${scaled.toFixed(decimals)}${entry.suffix}${currency ? ` ${currency}` : ""}`
        }
        const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2
        return `${numeric.toFixed(decimals)}${currency ? ` ${currency}` : ""}`
    }

    function resolveYAxisLabelSamples(formatter){
        const range = table.ranges?.timeseries?.values
        const rowRange = range?.rows?.order?.[cell.rIdx]?.[0]
        const columnRange = range?.columns?.order?.[cell.cIdx]?.[0]
        const min = rowRange?.min ?? columnRange?.min ?? 0
        const max = rowRange?.max ?? columnRange?.max ?? 0
        const format = typeof formatter === "function" ? formatter : (value)=>String(value ?? "")
        return [min, (min + max) / 2, max].map(value=>format(value)).filter(Boolean)
    }

    function resolveYAxisCurrency(){
        const defs = Array.isArray(table.defs?.allocations) ? table.defs.allocations : []
        const metricDefs = defs
            .filter(def=>def?.type === "metric_fields")
            .flatMap(def=>Array.isArray(def?.fields) ? def.fields : [])
        const currencies = metricDefs.map(field=>field?.currency).filter(Boolean)
        const unique = Array.from(new Set(currencies))
        return unique.length === 1 ? unique[0] : null
    }

    function resolveTimeseriesMsList(seriesList){
        const seen = new Set()
        const out = []
        for( const series of seriesList ){
            if( !series ){
                continue
            }
            for( const raw of Object.keys(series) ){
                const ms = Number(raw)
                if( !Number.isFinite(ms) || seen.has(ms) ){
                    continue
                }
                seen.add(ms)
                out.push(ms)
            }
        }
        out.sort((a,b)=>a-b)
        return out
    }

    function resolveGlobalTimeRange(seriesList, includeTail = true){
        const msList = resolveTimeseriesMsList(seriesList)
        let min = msList.at(0)
        let max = msList.at(-1)
        let usingBounds = false
        if( timeRangeMode === "row" || timeRangeMode === "delta_row"){
            const bounds = table.ranges?.timeseries?.time?.rows?.order?.[cell.rIdx]
            if( bounds ){
                min = bounds.min
                max = bounds.max
                usingBounds = true
            }
        }else if( timeRangeMode === "column" || timeRangeMode === "delta_column"){
            const bounds = table.ranges?.timeseries?.time?.columns?.order?.[cell.cIdx]
            if( bounds ){
                min = bounds.min
                max = bounds.max
                usingBounds = true
            }
        }
        const defaultBucket = TIMESERIES_RESOLUTION_IN_MS[resolutionKey] ?? TIMESERIES_RESOLUTION_IN_MS.day
        const bucketDuration = msList.length > 1 ? Math.max(defaultBucket, msList[1] - msList[0]) : defaultBucket
        if( !Number.isFinite(min) ){
            min = 0
        }
        if( !Number.isFinite(max) ){
            max = min + bucketDuration
        }
        const adjustedMax = usingBounds
            ? max
            : (Number.isFinite(max) ? (includeTail ? max + bucketDuration : max) : (Number.isFinite(min) ? min + bucketDuration : bucketDuration))
        return {msList, min, max: adjustedMax, bucketDuration}
    }

    function computeAxisPaddingFromLabels(labels = [], baseFont){
        if( labels.length === 0 ){
            return 0
        }
        const longest = labels.reduce((a,c)=> (c?.length ?? 0) > (a?.length ?? 0) ? c : a, "")
        return Math.max(20, baseFont * (longest.length * 0.62))
    }

    function clamp(value, min, max){
        if( Number.isNaN(value) ){
            return min
        }
        return Math.min(Math.max(value, min), max)
    }

    function computeLinearRegression(points = []){
        const n = points.length
        if( n < 2 ){
            return null
        }
        let sumX = 0
        let sumY = 0
        let sumXY = 0
        let sumX2 = 0
        points.forEach((point)=>{
            const x = point.ms
            const y = point.value ?? 0
            sumX += x
            sumY += y
            sumXY += x * y
            sumX2 += x * x
        })
        const denominator = (n * sumX2) - (sumX * sumX)
        if( denominator === 0 ){
            return null
        }
        const slope = ((n * sumXY) - (sumX * sumY)) / denominator
        const intercept = (sumY - (slope * sumX)) / n
        return {slope, intercept}
    }

    function addPlaceholderOverlay({start, end, axisMin, axisMax, dx, offsetX = 0}){
        if(!Number.isFinite(start) || !Number.isFinite(end) || end <= start){
            return
        }
        const safeStart = Math.max(start, axisMin)
        const safeEnd = Math.min(Math.max(end, safeStart + 1), axisMax)
        const width = Math.max(0, (safeEnd - safeStart) * dx)
        if(width <= 0){
            return
        }
        const x = offsetX + (safeStart - axisMin) * dx
        const overlay = new Konva.Group({
            listening: false,
            clip: {x, y: 0, width, height: config.height}
        })
        overlay.add(new Konva.Rect({
            x,
            y: 0,
            width,
            height: config.height,
            fill: "rgba(148, 163, 184, 0.18)",
            listening: false
        }))
        const spacing = 8
        for( let offset = -config.height; offset < width + config.height; offset += spacing ){
            overlay.add(new Konva.Line({
                points: [x + offset, 0, x + offset + config.height, config.height],
                stroke: "rgba(71, 85, 105, 0.35)",
                strokeWidth: 1,
                listening: false
            }))
        }
        seriesGroup.add(overlay)
    }

    function resolvePlaceholderRanges(msList, series, bucketDuration){
        if(!Array.isArray(msList) || msList.length === 0){
            return []
        }
        const results = []
        const firstRealIndex = msList.findIndex(ms=>!series[ms]?.placeholderHead)
        const lastRealIndex = findLastIndex(msList, (ms)=>!series[ms]?.placeholderTail)

        if(firstRealIndex === -1 || lastRealIndex === -1){
            const start = msList[0]
            const last = msList.at(-1) ?? start
            if(Number.isFinite(start) && Number.isFinite(last)){
                results.push({start, end: last + bucketDuration})
            }
            return results
        }

        if(firstRealIndex > 0){
            results.push({start: msList[0], end: msList[firstRealIndex]})
        }
        if(lastRealIndex < msList.length - 1){
            const start = msList[lastRealIndex + 1] ?? (msList[lastRealIndex] + bucketDuration)
            const end = (msList.at(-1) ?? start) + bucketDuration
            results.push({start, end})
        }
        return results
    }

    function findLastIndex(list, predicate){
        for(let idx = list.length - 1; idx >= 0; idx--){
            if(predicate(list[idx], idx)){
                return idx
            }
        }
        return -1
    }

    function assignSequentialColor(key){
        if(!sequentialPalette.length){
            return "#0ea5e9"
        }
        if(!sequentialAssignments.has(key)){
            sequentialAssignments.set(key, sequentialPalette[sequentialCursor % sequentialPalette.length])
            sequentialCursor += 1
        }
        return sequentialAssignments.get(key)
    }

    function getColorFromMap(map, key){
        if(!map || key === undefined || key === null){
            return undefined
        }
        return map.get(key)
    }

    function resolveAllocationColor(cell, seriesIndex){
        const meta = allocationColorMaps[seriesIndex] ?? allocationColorMaps[0]
        if(!meta){
            return undefined
        }
        const field = meta.field
        const entries = normalizeAllocationEntries(cell.allocations?.[field])
        if(entries.length === 0){
            return undefined
        }
        const primary = entries.find(entry=>entry?.idx !== undefined && entry?.idx !== null && entry?.idx !== "_N_") ?? entries[0]
        const key = primary?.idx ?? primary?.label
        if(key === undefined || key === null){
            return undefined
        }
        return meta.map.get(key)
    }

    function normalizeAllocationEntries(entry){
        if(!entry){
            return []
        }
        if(Array.isArray(entry)){
            return entry
        }
        if(typeof entry === "object"){
            const values = Object.values(entry)
            if(values.every(value=>Array.isArray(value))){
                return values.flat()
            }
            return values
        }
        return []
    }

    function buildAxisColorMap(axisEntries = [], palette = []){
        const map = new Map()
        if(!Array.isArray(axisEntries) || !palette.length){
            return map
        }
        axisEntries.forEach((entry, idx)=>{
            const key = entry?.idx ?? idx
            if(key === undefined || key === null){
                return
            }
            const color = palette[idx % palette.length]
            map.set(key, color)
        })
        return map
    }

    function buildAllocationColorMaps(defs = [], allocationData = {}, palette = []){
        if(!Array.isArray(defs) || defs.length === 0 || !palette.length){
            return []
        }
        return defs.map((def)=>{
            const items = allocationData?.[def.field]?.items ?? []
            const map = new Map()
            items.forEach((item, idx)=>{
                const key = item?.idx ?? item?.label ?? idx
                if(key !== undefined && key !== null){
                    map.set(key, palette[idx % palette.length])
                }
            })
            return {field: def.field, map}
        })
    }

    function minMaxTimeForCell(series, includeTail = true){
        const msList = Object.keys(series ?? {})
            .map((d)=>Number(d))
            .filter((value)=>Number.isFinite(value))
            .sort((a,b)=>a-b)
        let min = msList.at(0)
        let max = msList.at(-1)

        let usingBounds = false
        if( timeRangeMode === "row" || timeRangeMode === "delta_row"){
            const bounds = table.ranges?.timeseries?.time?.rows?.order?.[cell.rIdx]
            if( bounds ){
                min = bounds.min
                max = bounds.max
                usingBounds = true
            }
        }else if( timeRangeMode === "column" || timeRangeMode === "delta_column"){
            const bounds = table.ranges?.timeseries?.time?.columns?.order?.[cell.cIdx]
            if( bounds ){
                min = bounds.min
                max = bounds.max
                usingBounds = true
            }
        }
        const defaultBucket = TIMESERIES_RESOLUTION_IN_MS[resolutionKey] ?? TIMESERIES_RESOLUTION_IN_MS.day
        const bucketDuration = msList.length > 1 ? Math.max(defaultBucket, msList[1] - msList[0]) : defaultBucket
        const adjustedMax = usingBounds
            ? max
            : (Number.isFinite(max) ? (includeTail ? max + bucketDuration : max) : (Number.isFinite(min) ? min + bucketDuration : bucketDuration))
        return {msList, min, max: adjustedMax, bucketDuration}
    }
})
registerRenderer( {type: "default", configs: "datatable_overunder"}, ({table, cell, renderOptions, ...options})=>{
     const config = {itemSize: 350, padding: [10,2,10,2], ...options}
    const fontSize = 16
    const barHeight = 20
    const list = cell.items ?? []

    const _allocations = table.defs?.allocations ?? []
    const [scoreName, axisName, compsName] = _allocations.map(d=>d.field)

    const allocationFilterMap = buildAllocationFilterMap(_allocations)

    let scores = scoreName ? (table.allocations?.[scoreName]?.items ?? []).filter(d=>d.idx !== undefined && d.idx !== null && d.idx !== "_N_")  : []
    scores = filterAllocationItems(scores, scoreName, allocationFilterMap)

    let axis = axisName ? (table.allocations?.[axisName]?.items ?? []) : []
    axis = filterAllocationItems(axis, axisName, allocationFilterMap)

    let comps = compsName ? (table.allocations?.[compsName]?.items ?? []) : [undefined]
    if( compsName ){
        comps = filterAllocationItems(comps, compsName, allocationFilterMap)
    }

    const splitMiddleScores = renderOptions.split_middle !== false
    const colorBarByScore = renderOptions.bar_color_by_score === true
    const positiveBarFill = renderOptions.positive_color ?? "#16a34a"
    const negativeBarFill = renderOptions.negative_color ?? "#dc2626"
    let groupColors = options.theme?.palette?.categoryColors ?? categoryColors
    const sentimentPalette = heatMapPalette.find(d=>d.name === "sentiment_5")?.colors ?? []
    const baseScorePalette = (colorBarByScore && scores.length === 5 && sentimentPalette.length >= 5)
        ? sentimentPalette
        : (options.theme?.palette?.categoryColors ?? categoryColors)
    const scoreColorMap = new Map()
    if( colorBarByScore ){
        scores.forEach((score, idx)=>{
            scoreColorMap.set(score.idx, baseScorePalette[idx % baseScorePalette.length])
        })
    }

    let g = new Konva.Group({
        id: options.id,
        name:"cell inf_track",
        x: (options.x ?? 0),
        y: (options.y ?? 0),
        width: config.itemSize,
        height: config.itemSize
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.itemSize,
        height: config.itemSize,
        fill:"white"
    })
    g.add(r)

    const scoreIds = scores.map(d=>d.idx)
    const midIndex = Math.floor(scoreIds.length / 2)
    const isOddScoreCount = scoreIds.length % 2 === 1
    const middleScoreEntry = splitMiddleScores && isOddScoreCount ? scores[midIndex] : null
    const middleScoreId = middleScoreEntry?.idx
    const negativeIds = scoreIds.slice(0, midIndex)
    const positiveIds = scoreIds.slice(isOddScoreCount ? midIndex + 1 : midIndex)
    const negatives = new Set(negativeIds)
    const positives = new Set(positiveIds)
    const negativeBaseCounts = {}
    const positiveBaseCounts = {}
    for(const id of negatives){
        if( id !== undefined ){
            negativeBaseCounts[id] = 0
        }
    }
    for(const id of positives){
        if( id !== undefined ){
            positiveBaseCounts[id] = 0
        }
    }
    if( middleScoreEntry && middleScoreId !== undefined ){
        negativeBaseCounts[middleScoreId] = 0
        positiveBaseCounts[middleScoreId] = 0
    }
    const scoreAllocations = cell.allocations?.[scoreName] ?? []
    const negativeSegmentOrder = colorBarByScore
        ? [
            ...(middleScoreEntry ? [middleScoreId] : []),
            ...negativeIds.slice().reverse()
        ].filter(id=>id !== undefined)
        : []
    const positiveSegmentOrder = colorBarByScore
        ? [
            ...(middleScoreEntry ? [middleScoreId] : []),
            ...positiveIds
        ].filter(id=>id !== undefined)
        : []

    const renderData = {}
    const maximums = {positives: 0, negatives: 0}
    const middleSlotBonus = middleScoreEntry ? 1 : 0
    const heatPalette = heatMapPalette.find(d=>d.name === "heat")?.colors ?? []
    const greenPalette = heatMapPalette.find(d=>d.name === "green")?.colors ?? []
    const heatColors = (negatives.size + middleSlotBonus) > 0 ? [...heatPalette].slice(-(negatives.size + middleSlotBonus)).reverse() : []
    const greenColors = (positives.size + middleSlotBonus) > 0 ? [...greenPalette].slice(-(positives.size + middleSlotBonus)) : []

    for(const comp of comps ){
        for(const ax of axis ){
            const key = `${ax.idx}-${comp?.idx}`
            renderData[key] = {
                negativeScoreCounts: {...negativeBaseCounts},
                positiveScoreCounts: {...positiveBaseCounts},
                global: {
                    positives: 0,
                    negatives: 0,
                    total: 0
                },
            }
            for(const score of scores){
                const scoreNode = scoreAllocations.find(d=>d.idx === score.idx)
                if( !scoreNode ){
                    continue
                }
                const axisEntries = scoreNode.allocations?.[axisName]
                if( !Array.isArray(axisEntries) ){
                    continue
                }
                const axisNode = axisEntries.find(d=>d.idx === ax.idx)
                if( !axisNode ){
                    continue
                }
                let leafNode = axisNode
                if( compsName ){
                    const compEntries = axisNode.allocations?.[compsName]
                    if( !Array.isArray(compEntries) ){
                        continue
                    }
                    leafNode = compEntries.find(d=>d.idx === comp?.idx)
                    if( !leafNode ){
                        continue
                    }
                }

                const count = leafNode?.count ?? 0
                if( count === 0 ){
                    continue
                }

                const isMiddleScore = Boolean(middleScoreEntry && score.idx === middleScoreEntry.idx)

                if( isMiddleScore ){
                    const negativePortion = count / 2
                    const positivePortion = count - negativePortion
                    renderData[key].negativeScoreCounts[score.idx] = (renderData[key].negativeScoreCounts[score.idx]  || 0) + negativePortion
                    renderData[key].global.negatives += negativePortion
                    renderData[key].positiveScoreCounts[score.idx] = (renderData[key].positiveScoreCounts[score.idx]  || 0) + positivePortion
                    renderData[key].global.positives += positivePortion
                }else if( negatives.has(score.idx)){
                    renderData[key].negativeScoreCounts[score.idx] = (renderData[key].negativeScoreCounts[score.idx]  || 0) + count
                    renderData[key].global.negatives += count
                }else if( positives.has(score.idx)){
                    renderData[key].positiveScoreCounts[score.idx] = (renderData[key].positiveScoreCounts[score.idx]  || 0) + count
                    renderData[key].global.positives += count
                }
                renderData[key].global.total += count

                if( renderData[key].global.positives > maximums.positives){
                    maximums.positives = renderData[key].global.positives
                }
                if( renderData[key].global.negatives > maximums.negatives){
                    maximums.negatives = renderData[key].global.negatives
                }

            }
        }
    }

    const usableWidth = (config.itemSize * 0.9) - (renderOptions.show_breakdown ? barHeight * 4 : 0)
    const scale = usableWidth / Math.max(1, (renderOptions?.center ? Math.max(maximums.negatives, maximums.positives) * 2 : (maximums.negatives + maximums.positives)))
    const axisOrigin = (renderOptions.show_breakdown ? barHeight * 2 : 0) + (renderOptions?.center ? (Math.max(maximums.negatives, maximums.positives) * scale) : (maximums.negatives * scale))

    let x = config.padding[3], y = config.padding[0]
    let maxW = 0
    let rIdx = -1
    const includeNeutralInPercent = true
    for(const ax of axis ){
        x = config.padding[3] 
        rIdx ++
        let cIdx = -1
        let sg = new Konva.Group({
            x,
            y,
            width: config.itemSize,
        })
        let ly = 0
        let idx = -1
        for(const comp of comps ){
            idx++
            const key = `${ax.idx}-${comp?.idx}`
            cIdx++
            const thisData = renderData[key]            
            if( !thisData ){
                continue
            }

            if( cIdx === 0){
                sg.add(new CustomText({
                    x: axisOrigin + 4,
                    text: axis[rIdx].label,
                    fontSize
                }))
                ly += fontSize * 1.2
            }
                if( renderOptions.show_breakdown){
                    sg.add(renderPieChart( Object.keys(thisData.negativeScoreCounts).map(d=>({label: scores.find(d2=>d2.idx === d)?.label, count: thisData.negativeScoreCounts[d] })),
                    {
                        x: 0,
                        y: ly + barHeight * 0.1,
                        size: barHeight * 0.8,
                        colors: heatColors
                    }))
                    sg.add(new CustomText({
                        x: (barHeight),
                        y: ly + barHeight * 0.1,                        
                        fontSize: fontSize * 0.7,
                        text: `${((includeNeutralInPercent ? thisData.global.negatives / thisData.global.total : thisData.global.negatives  / (thisData.global.positives + thisData.global.negatives ) )* 100).toFixed(0)}%`
                    }))
                }
                if( colorBarByScore ){
                    let negCursor = axisOrigin
                    for(const scoreId of negativeSegmentOrder ){
                        const count = thisData.negativeScoreCounts[scoreId] ?? 0
                        if( count <= 0 ){continue}
                        const width = scale * count
                        negCursor -= width
                        sg.add(new Konva.Rect({
                            x: negCursor,
                            y: ly,
                            width,
                            height: barHeight,
                            fill: scoreColorMap.get(scoreId) ?? negativeBarFill
                        }))
                    }
                    let posCursor = axisOrigin
                    for(const scoreId of positiveSegmentOrder ){
                        const count = thisData.positiveScoreCounts[scoreId] ?? 0
                        if( count <= 0 ){continue}
                        const width = scale * count
                        sg.add(new Konva.Rect({
                            x: posCursor,
                            y: ly,
                            width,
                            height: barHeight,
                            fill: scoreColorMap.get(scoreId) ?? positiveBarFill
                        }))
                        posCursor += width
                    }
                }else{
                    const negativeWidth = scale * thisData.global.negatives
                    if( negativeWidth > 0 ){
                        sg.add(new Konva.Rect({
                            x: axisOrigin - negativeWidth,
                            y: ly,
                            width: negativeWidth,
                            height: barHeight,
                            fill: negativeBarFill
                        }))
                    }
                    const positiveWidth = scale * thisData.global.positives
                    if( positiveWidth > 0 ){
                        sg.add(new Konva.Rect({
                            x: axisOrigin,
                            y: ly,
                            width: positiveWidth,
                            height: barHeight,
                            fill: positiveBarFill
                        }))
                    }
                }
                if( renderOptions.show_breakdown){
                    sg.add(renderPieChart( Object.keys(thisData.positiveScoreCounts).map(d=>({label: scores.find(d2=>d2.idx === d)?.label, count: thisData.positiveScoreCounts[d] })),
                    {
                        x: config.itemSize - barHeight,
                        y: ly + barHeight * 0.1,
                        size: barHeight * 0.8,
                        colors: greenColors
                    }))
                    sg.add(new CustomText({
                        x: config.itemSize - (barHeight * 2),
                        fontSize: fontSize * 0.7,
                        y: ly + barHeight * 0.1,                        
                        text: `${((includeNeutralInPercent ? thisData.global.positives / thisData.global.total : thisData.global.positives / (thisData.global.positives + thisData.global.negatives ))* 100).toFixed(0)}%`
                    }))
                }
                ly += barHeight * 0.5
            }
            sg.height(ly)
            g.add(sg)
            y += sg.height() + (fontSize * 1.75)

            x += config.itemSize
            maxW = x > maxW ? x : maxW
    }
    g.width(maxW)

    g.add(new Konva.Line({
        points: [ config.padding[3] + axisOrigin, config.padding[0], config.padding[3] + axisOrigin, y],
        strokeWidth: 1 ,
        stroke: "#828282"
    }))
    if( comps.length > 1){
        if( renderOptions.show_legend){
            y += fontSize * 1.2
            const legend = renderLegend( comps,{
                                ...options,
                                fontSize: fontSize * 0.8,
                                x: config.padding[3],
                                y: y,
                                itemSize: config.itemSize,
                                colors: groupColors
            })
            g.add( legend)
            y += legend.height()

        }

    }

    r.height(y)
    g.height(y)

    if( options.getConfig){
        return {
            ...config,
            width: g.width(),
            height: g.height()
        }
    }

    /*list.forEach(d=>{
        const v = Array.isArray(d.filterGroup0) ? d.filterGroup0[0] : d.filterGroup0
        values[v] ||= {label: v, count: 0}
        values[v].count++
    })*/
    return g
})

function categoryGrid(primitive, options = {}){
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 1200, height: 1200, itemSize: 280, padding: [10,10,10,10], ...options, ...(primitive.renderConfig ?? {})}
    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 
    
    if( !options.extents?.column ||  options.extents?.row){
        return new Konva.Group({
            x: ox,
            y: oy,
            width: 100,
            height: 100,
            onClick: options.onClick,
            name:"inf_track primitive"
        })
    }

    const setCount = options.extents.column.length * options.extents.row.length
    const usableWidth = config.width - config.padding[1] - config.padding[3]
    const itemSize = config.itemSize
    const columns = Math.floor(usableWidth / (itemSize + 10))
    const spacing = (usableWidth - (columns * itemSize)) / (columns - 1)
    let ySpacing = spacing
    const actualColumns = Math.min(columns, setCount)
    let colors = options.theme?.palette?.categoryColors ?? categoryColors
    
    const actualWidth = ((spacing + itemSize) * actualColumns) - spacing
    let x = config.padding[3]// + spacing
    let y = config.padding[0] //+ ySpacing
    let maxY = 0
    let cIdx = 0
    let idx = 0

    let rHeight = 0
    let rowCells = []
    let innerPadding = [10,10,10,10]

    const g = new Konva.Group({
        id: primitive.id,
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track"
    })

    function updateBackground(){
        for(const d of rowCells){
            d.find('.background_rect')[0]?.height(rHeight)
        }
    }

    let sortedRows = options.extents.row
    let sortedCols = options.extents.column
    
    if(  config.by_tag ){
        [sortedRows, sortedCols].flat().forEach(d=>{
            d.tag = d.primitive?.filterTargets?.[0]?.referenceParameters?.tag
        })

        sortedCols = sortedCols.sort((a,b)=>a.tag - b.tag)
        sortedRows = sortedRows.sort((a,b)=>a.tag - b.tag)

        let lastTag, tagIdx = 0;

        ([sortedRows, sortedCols]).flat().forEach(d=>{
            if( d.tag !== lastTag){
                tagIdx = 0
            }
            const scol = Object.values(options.theme?.palette?.tagColors ?? tagColors)[d.tag]
            d.color = scol ? scol[tagIdx % scol.length] : undefined
            tagIdx ++
            lastTag = d.tag
        })
        
    }

    for(const row of sortedRows){
        for(const column of sortedCols){
            let color = column.color ?? row.color ?? colors[idx % colors.length] 
            const tg = new Konva.Group({
                x: x,
                y: y,
                width: itemSize,
                height: itemSize,
                onClick: options.onClick,
                name:"inf_track primitive"
            })
            const r = new Konva.Rect({
                x: 0,
                y: 0,
                width: itemSize,
                height: itemSize,
                stroke: color,
                strokeWidth: 1,
                name:"background_rect"
            })
            tg.add(r)

            const titleText = [row.label, column.label].filter(d=>d).join(" - ")

            const header = new CustomText({
                        fontSize: 16,
                        text: titleText,
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        verticalAlign:"top",
                        fontStyle: "bold",
                        fontFamily: "Poppins",
                        fill: config.by_tag ? getTextColor(color) : "white",
                        x: innerPadding[3],
                        y: innerPadding[0],
                        width: itemSize - innerPadding[3] - innerPadding[1],
                        refreshCallback: options.imageCallback
                    })

            const r2 = new Konva.Rect({
                x: 0,
                y: 0,
                width: itemSize,
                height: header.height() + innerPadding[3] + innerPadding[1],
                fill: color,
            })
            tg.add(r2)
            tg.add(header)

            
            const thisSet = options.list.filter(d=>(!d.column || d.column.includes(column.idx)) && (!d.row || d.row.includes(row.idx)))
            let ty = r2.height() + innerPadding[0]
            let mainText
            if( thisSet.length > 0){
                tg.attrs.id = thisSet[0].primitive.id
            }
            for(const item of thisSet){
                mainText = new CustomText({
                    fontSize: 11,
                    fontFamily: "Poppins",
                    text: item.primitive.referenceParameters.summary,
                    align:"left",
                    wrap: false,
                    ellipsis: true,
                    withMarkdown: true,
                    lineHeight: 1.3,
                    verticalAlign:"top",
                    fill:"#334155",
                    x: 4,
                    y: ty,
                    width: itemSize - 8,
                    refreshCallback: options.imageCallback
                })
                ty += mainText.height() + innerPadding[2]
                tg.add( mainText )
            }
            g.add(tg)

            rHeight = ty > rHeight  ? ty : rHeight

            tg.height( rHeight)
            r.height( rHeight)

            rowCells.push(tg)
            
            x += itemSize + spacing
            cIdx ++
            idx++
            maxY = y + rHeight
            
            if( cIdx === columns ){
                updateBackground()
                x = config.padding[3] 
                y += rHeight + innerPadding[2] + innerPadding[0]
                rHeight = 0
                cIdx = 0
                rowCells = []
            }
        }
    }
    updateBackground()

    const h = maxY + config.padding[2] //+ ySpacing
    g.height(h)
    
    return g
}
registerRenderer( {type: "categoryId", id: 29, configs: "export_finances"}, function renderFunc(primitive, options = {}){
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 492, itemSize: 280, padding: [10,10,10,10], ...options}

    let lineColor
    let withBackground = false
    let bgColor = "white"
    let textColor = "black"
    let padding = [0,0,0,0]
    let columnWidths = []

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    if( options.renderConfig?.layout === "narrow"){
        config.width = 156
        bgColor = "#1C258C"
        lineColor = "white"
        withBackground = true
        textColor = "white"
        padding = [16,8,8,8]
    }else{
        columnWidths = [140]
        config.width = 492
    }

    const g = new Konva.Group({
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill: bgColor,
        name: withBackground ? "" : "background"
    })
    g.add(r)
    let text = convertOrganizationFinancialData( primitive, primitive.metadata?.actions?.find(d=>d.key === "convert_financials")?.transform ?? {}, options.renderConfig?.section ?? "financial_highlights")
    if( text ){
        const index = text.indexOf('|');
        if( index >-1 ){
            text = text.substring(index)
        }
        const table = convertMarkupToTable(text, {
            x: padding[3],
            y: padding[0],
            cellPadding: [7,6,7,0],
            columnWidths,
            textColor,
            columnLines: false, 
            borderColor: lineColor, 
            width: config.width - padding[1] - padding[3]
        })
        if( table ){
            g.add(table)
            g.height(table.height() + padding[0] + padding[2])
            r.height(table.height() + padding[0] + padding[2])
        }
    }

    if( options.getConfig){
        return {...config, height: g.height()}
    }
    return g
})
registerRenderer( {type: "categoryId", id: 109, configs: "export"}, function renderFunc(primitive, options = {}){
    const config = {field: "summary", showId: true, idSize: 14, fontSize: 16, width: 612, itemSize: 280, padding: [10,10,10,10], ...options}

    let calloutColor = "#1C258C"
    let secondaryColor = "#F5F5F5"

    primitive= options.list[0]

    let ox = (options.x ?? 0) 
    let oy = (options.y ?? 0) 

    let innerPadding = [5,5,5,5]
    let minorWidth = 156
    let minHeight = 0

    let majorSpacing = [12,12]
    let majorPadding = [2,2,2,2]
    let minorSpacing = [8,8]
    let minorPadding = [16,2,16,2]

    const id = parseInt(options.exportOptions?.section ?? "0")
    let layout = options.exportOptions?.layout ?? "bullets_on_left_full"
    let bulletColor = "black"
    innerPadding = [32, 60, 20, 60]


    if( layout === "bullets_on_left_full"){
        minHeight = 525
        layout = "bullets_on_left"
    }else if( layout === "bullets_above_full"){
        minHeight = 525
        layout = "bullets_above"
    }

    if( layout === "bullets_on_left"){
        bulletColor = "white"
    }else if( layout === "bullets_on_right"){
        bulletColor = "white"
        minorWidth = 216
        innerPadding = [32, 0, 20, 60]
    }else if( layout === "plain" || layout === "text_table"){
        minorWidth = undefined

    }else if( layout === "bullets_above"){
        minorWidth = undefined
        majorSpacing = [12,170]
    }
    
    const g = new Konva.Group({
        x: ox,
        y: oy,
        width: config.width,
        height: config.height,
        onClick: options.onClick,
        name:"inf_track primitive"
    })
    const r = new Konva.Rect({
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        fill:"white",
        shadowColor: 'black',
        name:"background"
    })
    g.add(r)

    
        
        
    let section
    
    if( primitive.referenceParameters?.structured_summary ){
        section = primitive.referenceParameters.structured_summary[id]
    //text = flattenStructuredResponse( struct, struct)
    }
    if( section ){
        const heading = section.heading

        let subsections = section.subsections
        if( subsections?.length === 1 && !subsections[0].content){
            subsections = subsections[0].subsections
        }

        const tables = subsections ? subsections.filter(d=>d.content?.startsWith("|")) : []
        const bullets = subsections ? subsections.filter(d=>d.content?.startsWith("- ")) : []
        const main = subsections ? subsections.filter(d=>!d.content?.startsWith("- ") && !d.content?.startsWith("|")) : [section]
        let bulletGroup 
        let mainGroup 
        let majorWidth = (config.width - innerPadding[1] - innerPadding[3]) - (minorWidth ?? 0) - majorSpacing[1]
        
        const sectionTitle = new CustomText({
                    fontSize: 20,
                    text: heading.toUpperCase(),
                    align:"left",
                    wrap: false,
                    ellipsis: true,
                    lineHeight:1.3,
                    verticalAlign:"top",
                    fontFamily: "Poppins",
                    fill: calloutColor,
                    fontStyle: "bold",
                    x: innerPadding[3],
                    y: innerPadding[0],
                    width: config.width - innerPadding[1] - innerPadding[3],
                    refreshCallback: options.imageCallback
                })

        g.add(sectionTitle)

        let y = innerPadding[0] + sectionTitle.height() + 44
        let targetHeight

        if( layout === "section_grid"){
            const sections = section.subsections.slice(0,4)
            const rows = Math.ceil(sections / 2)
            const sectionHeight = rows === 1 ? 502 : (502 - 18)
            let sectionWidth = 240
            
            sections.forEach((section, idx)=>{
                let sg = new Konva.Group({
                    x: innerPadding[3] + ((idx % 2) * 252),
                    y: y + (Math.floor( idx / 2)  * (sectionWidth + 18)),
                    width: sectionWidth,
                    height: sectionHeight,
                })
                g.add(sg)
                
                let textIndent = 0

                if( sections.length > 2){
                    const r = new Konva.Rect({
                        x: 0,
                        y: 0,
                        width: sectionWidth,
                        height: sectionHeight,
                        fill: secondaryColor
                    })
                    sg.add(r)
                    textIndent = 16
                }
                const subHeader = new CustomText({
                        fontSize: 16,
                        text: (section.heading ?? ""),
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        lineHeight:1.3,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        fontStyle:"bold",
                        withMarkdown: true,
                        fill: bulletColor,
                        x: textIndent,
                        y: textIndent,
                        width: sectionWidth - (textIndent * 2),
                        refreshCallback: options.imageCallback
                    })
                const text = new CustomText({
                        fontSize: 10,
                        text: section.content ?? "",
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        lineHeight:1.6,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        withMarkdown: true,
                        fill: bulletColor,
                        x: textIndent,
                        y: textIndent + subHeader.height() + 12,
                        width: sectionWidth - (textIndent * 2),
                        refreshCallback: options.imageCallback
                    })
                    sg.add(subHeader)
                    sg.add(text)

            })
        }else{

            const bulletText = new CustomText({
                        fontSize: 10,
                        text: bullets.map(d=>d.content).join("\n"),
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        lineHeight:1.3,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        withMarkdown: true,
                        fill: bulletColor,
                        x: minorPadding[1],
                        y: minorPadding[0],
                        width: (minorWidth ?? majorWidth)  - minorPadding[1] - minorPadding[3],
                        refreshCallback: options.imageCallback
                    })

            const mainText = new CustomText({
                        fontSize: 10,
                        text: main.map(d=>d.content).join("\n"),
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        withMarkdown: true,
                        lineHeight: 1.6,
                        fill:"black",
                        x: majorPadding[1],
                        y: majorPadding[0],
                        width: majorWidth - minorPadding[1] - minorPadding[3],
                        refreshCallback: options.imageCallback
                    })

            
            if( layout === "bullets_on_left"){
                bulletGroup = new Konva.Group({
                    x: innerPadding[3],
                    y: y,
                    width: minorWidth,
                    height: config.height,
                })
                g.add(bulletGroup)
                const br = new Konva.Rect({
                    x: 0,
                    y: 0,
                    width: minorWidth,
                    height: config.height,
                    fill:calloutColor,
                    name:"background_rect"
                })
                bulletGroup.add(br)

                mainGroup = new Konva.Group({
                    x: innerPadding[3] + minorWidth + majorSpacing[1],
                    y: y,
                    width: majorWidth,
                    height: config.height,
                })
                g.add(mainGroup)

                targetHeight = Math.max(minHeight, mainText.height(), bulletText.height()  )
                mainGroup.height(targetHeight)
                bulletGroup.height(targetHeight)
                br.height(targetHeight)
                r.height(targetHeight + bulletGroup.y()+ innerPadding[2])
                g.height(targetHeight+ bulletGroup.y()+ innerPadding[2])

            }else if( layout === "text_table"){
                mainGroup = new Konva.Group({
                    x: innerPadding[3],
                    y: y,
                    width: majorWidth,
                    height: config.height,
                })
                g.add(mainGroup)

                let tableY = mainGroup.y() + mainText.height() + 44

                let tableHeaderText = tables.map(d=>d.heading ?? "").filter(d=>d).join("\n").toUpperCase()
                if( tableHeaderText){

                    let tableHeader  = new CustomText({
                        fontSize: 10,
                        text: tableHeaderText,
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        fontStyle:"bold",
                        withMarkdown: true,
                        lineHeight: 1.6,
                        fill:"black",
                        x: innerPadding[3],
                        y: tableY,
                        width: majorWidth - minorPadding[1] - minorPadding[3],
                        refreshCallback: options.imageCallback
                    })
                    g.add(tableHeader)
                    tableY += tableHeader.height() + 8
                }

                for(const d of tables){
                    const table = convertMarkupToTable( d.content, {
                        cellPadding: [7,6,7,0],
                        columnLines: false,
                        width: majorWidth, 
                        refreshCallback: options.imageCallback
                    } )
                    if( table ){
                        table.x(innerPadding[3])
                        table.y(tableY)

                        g.add(table)
                        tableY += table.height() + 12
                    }
                }

                targetHeight = Math.max(minHeight, mainText.height() )
                mainGroup.height(targetHeight)

                let overallHeight = tableY

                r.height( overallHeight + innerPadding[2])
                g.height( overallHeight + innerPadding[2])
            }else if( layout === "plain"){
                mainGroup = new Konva.Group({
                    x: innerPadding[3],
                    y: y,
                    width: majorWidth,
                    height: config.height,
                })
                g.add(mainGroup)

                targetHeight = Math.max(minHeight, mainText.height() )
                mainGroup.height(targetHeight)
                r.height(targetHeight + mainGroup.y()+ innerPadding[2])
                g.height(targetHeight + mainGroup.y()+ innerPadding[2])
            }else if( layout === "bullets_on_right"){
                bulletGroup = new Konva.Group({
                    x: innerPadding[3] + majorWidth + majorSpacing[1],
                    y: y,
                    width: minorWidth,
                    height: config.height,
                })
                g.add(bulletGroup)
                const br = new Konva.Rect({
                    x: 0,
                    y: 0,
                    width: minorWidth,
                    height: config.height,
                    fill:calloutColor,
                    name:"background_rect"
                })
                bulletGroup.add(br)

                mainGroup = new Konva.Group({
                    x: innerPadding[3],
                    y: y,
                    width: majorWidth,
                    height: config.height,
                })
                g.add(mainGroup)

                targetHeight = Math.max(minHeight, mainText.height(), bulletText.height()  )
                mainGroup.height(targetHeight)
                bulletGroup.height(targetHeight)
                br.height(targetHeight)
                r.height(targetHeight + bulletGroup.y()+ innerPadding[2])
                g.height(targetHeight+ bulletGroup.y()+ innerPadding[2])

            }else if( layout === "bullets_image_full"){
                let br
                let offsetForBullet = 0
                if( bullets.length > 0){
                    offsetForBullet = minorWidth + majorSpacing[1]
                    bulletGroup = new Konva.Group({
                        x: innerPadding[3],
                        y: y,
                        width: minorWidth,
                        height: config.height,
                    })
                    g.add(bulletGroup)
                    br = new Konva.Rect({
                        x: 0,
                        y: 0,
                        width: minorWidth,
                        height: config.height,
                        fill:secondaryColor,
                        name:"background_rect"
                    })
                    bulletGroup.add(br)
                }else{
                    majorWidth = (config.width - innerPadding[1] - innerPadding[3])
                    mainText.width(majorWidth)
                }

                mainGroup = new Konva.Group({
                    x: innerPadding[3] + offsetForBullet,
                    y: y,
                    width: majorWidth ,
                    height: config.height,
                })
                g.add(mainGroup)

                
                let totalHeight = 647
                targetHeight = Math.max(minHeight, mainText.height(), bulletText.height()  )
                let imagePos =  Math.max( 329, mainGroup.y() + targetHeight + 27)
                let imageHeight = totalHeight - imagePos - 19 - 15

                const placeHolderBg = new Konva.Rect({
                    x: 0,
                    y: imagePos + 60,
                    width: config.width,
                    height: totalHeight - (imagePos + 60),
                    fill: calloutColor
                })
                g.add( placeHolderBg )
                const placeHolder = new Konva.Rect({
                    x: innerPadding[3],
                    y: imagePos,
                    width: config.width - innerPadding[3] - innerPadding[1],
                    height: imageHeight,
                    fill: "#dadada"
                })
                g.add( placeHolder )

                const captionText = new CustomText({
                        fontSize: 8,
                        text: "CAPTION FOR IMAGE",
                        align:"left",
                        wrap: false,
                        ellipsis: true,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        withMarkdown: true,
                        lineHeight: 1.6,
                        fill: "white",
                        x: innerPadding[3],
                        y: imagePos + imageHeight + 4,
                        width: config.width - innerPadding[3] - innerPadding[1],
                        refreshCallback: options.imageCallback
                    })
                g.add(captionText)

                mainGroup.height(targetHeight)
                if( br ){
                    bulletGroup.height(targetHeight)
                    br.height(targetHeight)
                }
                r.height( totalHeight )
                g.height( totalHeight )
            }else {
                minorWidth = majorWidth
                
                bulletGroup = new Konva.Group({
                    x: innerPadding[3] + majorSpacing[1],
                    y: y,
                    width: minorWidth,
                    height: config.height,
                })
                g.add(bulletGroup)
                const br = new Konva.Rect({
                    x: 0,
                    y: 0,
                    width: minorWidth ,
                    height: bulletText.height() + minorPadding[0] + minorPadding[2],
                    fill: secondaryColor,
                    name:"background_rect"
                })
                bulletText.fontStyle("500")
                bulletGroup.add(br)
                bulletGroup.height(br.height())

                mainGroup = new Konva.Group({
                    x: innerPadding[3] + majorSpacing[1],
                    y: y + br.height() + 24,
                    width: majorWidth,
                    height: config.height,
                })
                g.add(mainGroup)
                targetHeight = Math.max(minHeight , (mainText.height() + mainGroup.y())  ) + innerPadding[2]
                r.height(targetHeight)
                g.height(targetHeight)
            }
            if( bulletGroup && bulletText ){
                bulletGroup.add(bulletText)
            }
            if( mainGroup && mainText ){
                mainGroup.add(mainText)
            }
        }
    }
    
    
    g.height(r.height())
    
    if( options.getConfig ){
        return {...config, height: g.height()}
    }

    return g
})
registerRenderer( {type: "default", configs: "set_export"}, (primitive, options = {})=>{
    return RenderPrimitiveAsKonva(options.list[0], {...options, exportOptions: primitive.renderConfig})
})

function convertMarkupToTable( markup, options = {} ){
    let config = {width: 612, fontSize: 12, cellPadding: [4,4,4,4], rowLines: true, columnLines: true, borderColor: '#D4D4D4', textColor: 'black',...options}
    if( typeof(markup) !== "string"){
        return
    }
    const textRows = markup.trim().split("\n")
    if( textRows.length > 0){
        let firstRow = textRows[0]
        if( firstRow[0] === "|" && firstRow[firstRow.length - 1] === "|"){
            
            let rows = textRows.map(d=>d.slice(1, d.length - 1).split("|"))//.map(d=>d.trim()))
            const columns = rows[0]
            let columnWidths = options.columnWidths ?? []
            const definedWidth = columnWidths.reduce((a,c)=>a + (c ?? 0), 0)
            const calcCols = columns.length - columnWidths.filter(d=>d !== undefined).length
            const calcWidth = (config.width - definedWidth) / calcCols
            columnWidths = columns.map((_,i)=>columnWidths[i] == undefined ? calcWidth : columnWidths[i] )

            let g = new Konva.Group({
                x: options.x ?? 0,
                y: options.y ?? 0,
                name: "table"
            })
            let x = 0, y = 0, rIdx = 0

            let headerRows = 0
            rows = rows.filter((d,i)=>{
                const isHeaderSeparator = d.every((col) => /^-+$/.test(col));
                if( isHeaderSeparator ){
                    headerRows = i
                    return false
                }
                return true
            })

            let rowHeight = 0
            let toUpdate = []
            function updateRowHeight(){
                for(const d of toUpdate){
                    d.header( rowHeight)
                }
            }

            for(const row of rows){
                rowHeight = 0
                for(let cIdx = 0; cIdx < columns.length; cIdx++){
                    let gc = new Konva.Group({
                        name: "cell",
                        id: `${cIdx}-${rIdx}`,
                        x,
                        y
                    })
                    let cellText = row[cIdx] ?? ""
                    let align = ((cellText[0] === " ") && (cellText.match(/^\s-?\$?\d+(\.\d+)?[KMB]?$/))) ? "right" : "left"
                    const t = new CustomText({
                        fontSize: 8,
                        text: cellText.trim(),
                        align: align,
                        wrap: true,
                        fontStyle: rIdx < headerRows ? "bold" : undefined,
                        verticalAlign:"top",
                        fontFamily: "Poppins",
                        withMarkdown: false,//rIdx >= headerRows,
                        lineHeight: 1.6,
                        fill: config.textColor,
                        x: config.cellPadding[3],
                        y: config.cellPadding[0],
                        width: columnWidths[cIdx] - config.cellPadding[3] - config.cellPadding[1],
                        refreshCallback: options.refreshCallback
                    })
                    gc.add(t)
                    rowHeight = Math.max(rowHeight, t.height())

                    g.add(gc)
                    x += columnWidths[cIdx]
                }
                updateRowHeight()
                x = 0
                y += rowHeight + config.cellPadding[0] + config.cellPadding[2]
                toUpdate = []
                rowHeight = 0
                rIdx++
                if( config.rowLines ){
                    g.add( new Konva.Line({
                        points:[0,y,config.width,y],
                        strokeWidth: rIdx === headerRows ? 2 : 1,
                        stroke: rIdx === headerRows ? config.textColor : config.borderColor
                    }))

                }
            }
            if( config.columnLines ){
                let x =0
                for(let cIdx = 0; cIdx < (columns.length - 1); cIdx++){
                    x += columnWidths[cIdx]
                    g.add( new Konva.Line({
                        points:[x,0,x,y],
                        strokeWidth: 1,
                        stroke: config.borderColor
                    }))
                }
            }
            g.width(config.width)
            g.height(y)

            return g
        }
    }
}

// Creates a small Konva.Group of pulsing dots used to signal "active" / "running".
// The group is named "running" so useRunningAnimation will tick it; the
// runStyle attr tells the hook to modulate child opacity (instead of dashOffset).
//
// Options:
//   dotCount  — number of dots (default 3)
//   dotRadius — radius of each dot in px (default 2.5)
//   gap       — gap between dot centres in px (default dotRadius * 2.6)
//   color     — fill colour of the dots (default "#0ea5e9")
//   name      — extra Konva name (the "running" name is always added)
export function createRunningPulseChip(options = {}){
    const dotCount = Math.max(1, options.dotCount ?? 3);
    const dotRadius = options.dotRadius ?? 2.5;
    const gap = options.gap ?? (dotRadius * 2.6);
    const color = options.color ?? "#0ea5e9";
    const extraName = options.name ? ` ${options.name}` : "";

    const width = (dotCount * dotRadius * 2) + ((dotCount - 1) * (gap - dotRadius * 2));
    const height = dotRadius * 2;

    const group = new Konva.Group({
        name: `running running_chip${extraName}`,
        listening: false,
        width,
        height,
    });
    group.attrs.runStyle = "pulse";

    for (let i = 0; i < dotCount; i++) {
        const dot = new Konva.Circle({
            x: dotRadius + i * (dotRadius * 2 + (gap - dotRadius * 2)),
            y: dotRadius,
            radius: dotRadius,
            fill: color,
            opacity: 0.5,
            listening: false,
        });
        group.add(dot);
    }
    return group;
}

export function renderIndicators(indicatorData, options = {}){
    const { label } = options;

    if (!label) {
        return undefined;
    }

    if (!indicatorData || !indicatorData.count) {
        if (label.indicatorGroup) {
            label.indicatorGroup.destroy();
            label.indicatorGroup = undefined;
        }
        label.indicatorData = undefined;
        return undefined;
    }

    const titleHeight = options.titleHeight ?? (label.indicatorBase?.titleHeight ?? 16);
    const baseBarWidth = options.baseBarWidth ?? (label.indicatorBase?.barWidth ?? 60);
    const baseInsideGap = options.baseInsideGap ?? (label.indicatorBase?.insideGap ?? 6);
    const baseOuterGap = options.baseOuterGap ?? (label.indicatorBase?.outerGap ?? 10);
    const baseFontSize = options.baseFontSize ?? (label.indicatorBase?.countFontSize ?? 12);
    const scaleFactor = options.scaleFactor ?? 1;

    const barWidth = baseBarWidth * scaleFactor;
    const insideGap = baseInsideGap * scaleFactor;
    const countFontSize = baseFontSize * scaleFactor;
    const progressValue = Math.max(0, Math.min(1, indicatorData.progress ?? 0));

    let indicatorGroup = label.indicatorGroup;
    if (!indicatorGroup) {
        indicatorGroup = new Konva.Group({ name: "frame_indicator" });
        label.add(indicatorGroup);
        label.indicatorGroup = indicatorGroup;
    } else {
        indicatorGroup.destroyChildren();
    }

    const track = new Konva.Rect({
        x: 0,
        y: 0,
        width: barWidth,
        height: titleHeight,
        fill: "#e5e7eb",
        cornerRadius: titleHeight / 2,
        listening: false,
    });
    indicatorGroup.add(track);

    const fill = new Konva.Rect({
        x: 0,
        y: 0,
        width: barWidth * progressValue,
        height: titleHeight,
        fill: "#34d399",
        cornerRadius: titleHeight / 2,
        listening: false,
    });
    indicatorGroup.add(fill);

    const countText = new Konva.Text({
        text: `x${indicatorData.count}`,
        fontSize: countFontSize,
        fontFamily: "Poppins",
        fill: "#0f172a",
        listening: false,
    });
    indicatorGroup.add(countText);

    const textMetrics = countText.getSelfRect();
    const textY = Math.max(0, (titleHeight - textMetrics.height) / 2);
    countText.y(textY);
    countText.x(barWidth + insideGap);

    const indicatorWidth = barWidth + insideGap + textMetrics.width;
    indicatorGroup.width(indicatorWidth);
    indicatorGroup.height(titleHeight);
    indicatorGroup.x(0);
    indicatorGroup.y(0);
    indicatorGroup.attrs.insideGap = insideGap;
    indicatorGroup.attrs.outerGap = baseOuterGap;
    indicatorGroup.attrs.scaleFactor = scaleFactor;

    label.indicatorData = indicatorData;
    label.indicatorBase = {
        barWidth: baseBarWidth,
        barHeight: titleHeight,
        insideGap: baseInsideGap,
        outerGap: baseOuterGap,
        countFontSize: baseFontSize,
        titleHeight,
        textWidth: textMetrics.width / Math.max(scaleFactor, 0.0001)
    };

    return indicatorGroup;
}


export function renderDatatable({id, primitive, data, stageOptions, renderOptions, viewConfig, ...options}){
    const { 
        width, 
        height
    } = { 
        ...renderOptions 
    };
    const {
        x = 0,
        y = 0,
        imageCallback
    } = stageOptions

    const theme = options.theme ?? renderOptions.theme ?? defaultTheme;
    options.theme = theme;
    const explicitWidth = Number.isFinite(Number(width)) ? Number(width) : undefined
    const explicitHeight = Number.isFinite(Number(height)) ? Number(height) : undefined
    const hasSingleCell = Array.isArray(data?.cells) && data.cells.length === 1
    const shouldApplyExplicitCellBounds = hasSingleCell && (explicitWidth !== undefined || explicitHeight !== undefined)
    if( shouldApplyExplicitCellBounds ){
        if( explicitWidth !== undefined && renderOptions.tableWidth === undefined ){
            renderOptions.tableWidth = explicitWidth
        }
        if( explicitHeight !== undefined && renderOptions.tableHeight === undefined ){
            renderOptions.tableHeight = explicitHeight
        }
    }
    if( isHeightDebugEnabled() || renderOptions?.debugHeight === true ){
        console.debug("[height-debug][RenderHelpers][datatable:entry]", {
            id,
            primitiveId: primitive?.id,
            matrixType: viewConfig?.matrixType,
            explicitWidth,
            explicitHeight,
            tableWidth: renderOptions.tableWidth,
            tableHeight: renderOptions.tableHeight,
            hasSingleCell,
            shouldApplyExplicitCellBounds,
            cellCount: Array.isArray(data?.cells) ? data.cells.length : 0
        })
    }

    if( renderOptions.widgetConfig && primitive){
        const g = new Konva.Group({
            name: "view",
            x,
            y
        })

        let w, h
        const widget = RenderPrimitiveAsKonva( primitive, {config: "widget", data: renderOptions.widgetConfig, imageCallback: options.imageCallback, theme: renderOptions.theme ?? options.theme})
        widget.name(widget.name() + " item_info")
        g.add( widget )
        w = widget.width()
        h = widget.height()

        if( renderOptions.widgetConfig.showItems ){

            const padding = options.x ? [0,0,0,0] : [10,10,10,10]
            
            const content = renderDatatable({id, data, stageOptions, renderOptions, viewConfig, ...options})
            if( content ){
                const targetWidth = (w - padding[1] - padding[3])

                const contentScale = Math.min(1, targetWidth / content.width() )
                content.x( Math.min(padding[3], (w - targetWidth) / 2))
                content.scale({x:contentScale, y:contentScale})
                content.y( h + padding[0])
                g.add(content)
                
                h = h + (content.height() * contentScale) + padding[0] + padding[2]
            }
        }

        g.width( w  )
        g.height( h )
        
        return g
    }
    const resolvedFontFamily = theme.fontFamily ?? "Poppins";
    const resolvedFontColor = themeColor(theme, 'primary', '#000000', options.text_color)
    const resovledBackground = themeColor(theme, 'background', '#ffffff')
    const resovledPlaceholderFill = mixHex( resolvedFontColor, resovledBackground, 0.7)
    const allocationDefs = data.defs?.allocations ?? []
    const allocationFilterMap = buildAllocationFilterMap(allocationDefs)

    let showColumnheaders =  (renderOptions.show_column_headers !== false ) && (data.columns.length > 1)
    let showRowheaders =  (renderOptions.show_row_headers !== false ) && (data.rows.length > 1)

    const columns = data.columns.slice(0, renderOptions.max_cols ?? 200)
    const rows = data.rows.slice(0, renderOptions.max_rows ?? 200)

    const g = new Konva.Group({
        id: id,
        x,
        y,
        name: primitive?.type === "element" ? "primitive inf_track" : "view"
        //name:"primitive inf_track"
    })
    if (renderOptions?.renderContextId) {
        g.attrs.renderContextId = renderOptions.renderContextId;
    }


    const asTable = columns.length > 1 || columns.length > 1 || showColumnheaders || showRowheaders 

    let rowHeights = rows.map(d=>0)
    let columnWidths = columns.map(d=>0)

    let config = viewConfig?.matrixType ?? "grid"

    let renderer = typeMaps[ "default" ][`datatable_${config}`]

    const maxColIdx = columns.length - 1
    const maxRowIdx = rows.length - 1

    let showSingleLegend = !(typeof(renderOptions.show_legend) === "string" && renderOptions.show_legend.startsWith("each-"))
    const legendPosition = !renderOptions.show_legend ? false : typeof(renderOptions.show_legend) === "string" && renderOptions.show_legend.includes("right") ? "right" : "below"

    let legend
    let legendInfo
    let colors
    let legendDeltaX, legendDeltaY
    const legendFontSize = renderOptions.legend_size ? renderOptions.legend_size : 12

    function buildLegend(){
         const showOnRight = legendPosition === "right"
        
        if( !legendInfo && data.allocations){
            const allocs = Object.values(data.allocations)
            const legendSource = allocs.at(-1)
            if( legendSource ){
                const legendField = allocationDefs.at(-1)?.field
                let valuesForLegend = legendSource.items ?? []
                if( legendField ){
                    valuesForLegend = filterAllocationItems(valuesForLegend, legendField, allocationFilterMap)
                }else{
                    valuesForLegend = valuesForLegend.slice()
                }
                let palette = options.theme?.palette?.categoryColors ?? categoryColors
                colors = valuesForLegend.map((_,i)=>palette[i % palette.length])
    
                legendInfo = {
                    colors,
                    data: valuesForLegend,
                    colorMap: valuesForLegend.reduce((a,d, i)=>{
                        a[d.label] = colors[i]
                        return a
                    }, {})
                }
            }
        }
        if( legendInfo){

            legend = renderLegend( legendInfo.data,{
                ...options,
                fontSize: legendFontSize,
                horizontalLegend: !showOnRight,
                width: showOnRight ? 200 : renderOptions.tableWidth,
                height: showOnRight ? renderOptions.tableHeight : 300,
                colors: legendInfo?.colors,
                fontFamily: resolvedFontFamily,
                fontFill: resolvedFontColor,
                placeholderFill: resovledPlaceholderFill
            })
            legend.name("inf_track row_header")
            g.add( legend)
            return legend
        }
    }
    
    let baseCellConfig = {
        width,
        //height
    }
    let {show_legend, ...relayConfig} = renderOptions
    if(data.cells.length === 1 ){
        relayConfig = {...renderOptions}
        showSingleLegend = false
        if( height !== undefined ){
            baseCellConfig.height = height
        }
    }
    const buildLegendFirst = (renderOptions.tableHeight || renderOptions.tableWidth) && showSingleLegend

    // Build legendInfo from table-level allocation data before any cell
    // renders, so all cells get the same colorMap (not just the first cell).
    if( !legendInfo && data.allocations ){
        const allocs = Object.values(data.allocations)
        const legendSource = allocs.at(-1)
        if( legendSource ){
            const legendField = allocationDefs.at(-1)?.field
            let valuesForLegend = legendSource.items ?? []
            if( legendField ){
                valuesForLegend = filterAllocationItems(valuesForLegend, legendField, allocationFilterMap)
            }else{
                valuesForLegend = valuesForLegend.slice()
            }
            // Resolve palette the same way renderSubCategoryChart does,
            // respecting per-primitive palette configuration (paletteName / heatMapPalette).
            const fallbackPalette = options.theme?.palette?.categoryColors ?? categoryColors
            const paletteName = renderOptions.colors
            const targetLen = valuesForLegend.length
            let palette = null

            if (paletteName) {
              const selected = heatMapPalette.find(d => d.name === paletteName)
              if (selected) {
                if (selected.dynamic) {
                  palette = interpolateHexColors(selected.colors[0], selected.colors.at(-1), Math.max(targetLen, 2))
                  if (renderOptions.reverse_palette) {
                    palette = [...selected.colors].reverse()
                  }
                } else if (selected.category_colors) {
                  palette = selected.category_colors.slice()
                } else {
                  palette = [...selected.colors]
                }
              }
              // Match renderSubCategoryChart: when reverse_palette is false take last N
              if (palette && !renderOptions.reverse_palette && palette.length > targetLen) {
                palette = palette.slice(-targetLen)
              }
            }

            if (!palette || palette.length === 0) {
              palette = fallbackPalette.slice()
            }
            if (renderOptions.reverse_palette && !paletteName) {
              palette = [...palette].reverse()
            }

            // Safety: ensure palette is never empty (modulo below would divide by zero)
            if (palette.length === 0) {
              palette = fallbackPalette.slice()
            }

            const legendColors = valuesForLegend.map((_, i) => palette[i % palette.length])
            legendInfo = {
                colors: legendColors,
                data: valuesForLegend,
                colorMap: valuesForLegend.reduce((a, d, i)=>{
                    a[d.label] = legendColors[i]
                    return a
                }, {})
            }
        }
    }

    if( buildLegendFirst ){
        legend = buildLegend()
    }
    if( renderOptions.tableWidth ){
        baseCellConfig.width = renderOptions.tableWidth / columns.length
    }
    if( renderOptions.tableHeight ){
        let chartHeight = renderOptions.tableHeight

        if( legend && showSingleLegend  && legendPosition === "below"){
            chartHeight -= legend.height()
            chartHeight -= legendFontSize * 1.5
        }
        if( showColumnheaders ){
            chartHeight -= (renderOptions.headerFontSize ?? 10) * 1.5
        }
        
        baseCellConfig.height = chartHeight / rows.length
    }


    relayConfig.width = baseCellConfig.width
    relayConfig.height = baseCellConfig.height

    relayConfig.show_legend = showSingleLegend ? false : legendPosition

    let configName = viewConfig?.renderType ?? "grid" 

    const childRenderOptions = {
        ...renderOptions,
        themeStyle: renderOptions.themeStyle
    }
    Object.entries(viewConfig?.config ?? {}).forEach(([k, v])=>{
        if( v?.relay ){
            childRenderOptions[k] = renderOptions[k]
        }
    })
    if(data.cells.length === 1 ){
        childRenderOptions.width = renderOptions.width ?? childRenderOptions.width
        childRenderOptions.height = renderOptions.height ?? childRenderOptions.height
        childRenderOptions.columns = renderOptions.columns ?? childRenderOptions.columns
    }

    const commonOptions = {
        stageOptions, 
        renderOptions: relayConfig, 
        expand: options.expand,
        config: configName, 
        maxHeight: renderOptions.height,
        sectionConfig: primitive?.getConfig.sections,
        resolvedFontColor,
        resovledBackground,
        resolvedFontFamily,
        resovledPlaceholderFill,
        utils: options.utils,
        includeBackground: asTable,
        childRenderOptions,
        colors,
        theme
    }

    let sharedAxisGutter = 0
    let configForCells = data.cells.reduce((a,cell)=>{
        if( cell.cIdx > maxColIdx || cell.rId > maxRowIdx){
            return a
        }
        const cellConfig = renderer({
            id: cell.id,
            ...commonOptions,
            table:data,
            getConfig: true,
            cell
        })
        a[cell.id] = cellConfig
        if( cellConfig.height > rowHeights[cell.rIdx]){
            rowHeights[cell.rIdx] = cellConfig.height
        }
        if( cellConfig.width > columnWidths[cell.cIdx]){
            columnWidths[cell.cIdx] = cellConfig.width
        }
        if( Number.isFinite(cellConfig.axisGutter) && cellConfig.axisGutter > sharedAxisGutter){
            sharedAxisGutter = cellConfig.axisGutter
        }
        return a
    },{})
    let maxDim = Math.max(...columnWidths, ...rowHeights)
    const spacing = 10//Math.round( maxDim * 0.05)

    const columnX = columnWidths.reduce((acc, w, i) => (acc.push(i ? acc[i - 1] + columnWidths[i - 1] + spacing : 0), acc), []);
    const rowY = rowHeights.reduce((acc, w, i) => (acc.push(i ? acc[i - 1] + rowHeights[i - 1] + spacing : 0), acc), []);

    const headerBackground = renderOptions.headerBackground
    const columnAsIcon = Boolean(renderOptions?.renderConfig?.columnAsIcon ?? renderOptions?.columnAsIcon);
    const rowAsIcon = Boolean(renderOptions?.renderConfig?.rowAsIcon ?? renderOptions?.rowAsIcon);
    const placeholderSetting = options.placeholder ?? stageOptions.placeholder ?? renderOptions?.placeholder;
    const mainstoreForHeaders = resolveMainstoreInstance(options.mainstore);
    const primitiveResolver = createHeaderPrimitiveResolver(mainstoreForHeaders);

    const headers = prepareHeaders({
        columns: showColumnheaders ? columns : [],
        rows: showRowheaders ? rows : [],
        columnWidths,
        rowHeights,
        columnX,
        rowY,
        spacing,
        background: headerBackground,
        renderOptions,
        theme,
        refreshCallback: imageCallback,
        columnAsIcon,
        rowAsIcon,
        mainstore: mainstoreForHeaders,
        primitiveResolver,
        placeholder: placeholderSetting,
    })
    let footers
    if( (data.totals.columns && renderOptions.show_column_totals) || (data.totals.rows  && renderOptions.show_row_totals)){
        footers = prepareHeaders({
            columns: data.totals.columns && data.totals.columns.order.map(d=>({label: d})),
            rows: data.totals.rows && data.totals.rows.order.map(d=>({label: d})),
            theme,
            background: headerBackground, 
            fontSize: headers.fontSize,
            includeColumns: renderOptions.show_column_totals,
            includeRows: renderOptions.show_row_totals,
            renderOptions,
            columnWidths,
            rowHeights,
            columnX,
            rowY,
            spacing,
            refreshCallback: imageCallback,
            columnAsIcon,
            rowAsIcon,
            allowColumnIcons: false,
            allowRowIcons: false,
            placeholder: placeholderSetting,
            mainstore: mainstoreForHeaders,
            primitiveResolver,
        })
    }

    let maxX = 50, maxY = 50

    let ox = 0, oy = 0
    if( headers.columns ){
        g.add( headers.columns)
        oy += headers.columns.height() + spacing
    }
    if( headers.rows ){
        g.add( headers.rows)
        ox += headers.rows.width() + spacing
    }

    const subColumnTracker = new Array(maxColIdx + 1).fill(undefined)

    for(const cell of data.cells){
        if( cell.cIdx > maxColIdx || cell.rIdx > maxRowIdx){
            continue
        }
        const x = ox + columnX[cell.cIdx]
        const y =  oy + rowY[cell.rIdx]
        const width = columnWidths[cell.cIdx]
        const height = rowHeights[cell.rIdx]
        const rendered = renderer({
            id: cell.id,
            x, 
            y, 
            table: data, 
            ...commonOptions,
            cellWidth: width,
            cellHeight: height,
            axisGutter: sharedAxisGutter,
            renderOptions: {...relayConfig, colorMap: legendInfo?.colorMap},
            cell})
        if (data.cells.length === 1 && rendered?.attrs?.name?.includes("cell")) {
            const name = rendered.attrs.name
            if (!name.includes("clickable")) {
                rendered.attrs.name = `${name} clickable`.trim()
            }
        }

        if( x + width > maxX){ maxX = x + width}
        if( y + height > maxY){ maxY = y + height}
        g.add(rendered)
        if( rendered.attrs.resizeInfo ){
            if( !subColumnTracker[ cell.cIdx ] || rendered.attrs.resizeInfo.columns > subColumnTracker[ cell.cIdx ].columns ){
                subColumnTracker[ cell.cIdx ] = rendered.attrs.resizeInfo
            }
        }
    }
    if( footers?.columns){
        footers?.columns.y(maxY)
        if( headers?.columns){
            footers.columns.x( headers.columns.x() )
        }
        maxY += footers?.columns.height()
        g.add( footers.columns)

    }
    if( footers?.rows){
        footers?.rows.x(maxX)
        if( headers?.rows){
            footers.rows.y( headers.rows.y() )
        }
        maxX += footers?.rows.width()
        g.add( footers.rows)
    }
    if( legendPosition && showSingleLegend ){
        legend = legend ?? buildLegend()
         const showOnRight = legendPosition === "right"
         if( legend ){

             
             if( showOnRight ){
                 const ox = maxX
                 maxX = legend.x() + legend.width()
                 legend.y( (maxY - legend.height()) / 2)
                 legendDeltaX = maxX - ox
                }else{
                    const oy = maxY
                    legend.x( (maxX - legend.width()) /2 )
                    legend.y( maxY + legendFontSize * 1.25)
                    maxY = legend.y() + legend.height()
                    legendDeltaY = maxY - oy
                }
            }

    }
    const finalHeight = (shouldApplyExplicitCellBounds && explicitHeight !== undefined) ? explicitHeight : maxY
    if( isHeightDebugEnabled() || renderOptions?.debugHeight === true ){
        console.debug("[height-debug][RenderHelpers][datatable:final]", {
            id,
            computedWidth: maxX,
            computedHeight: maxY,
            finalHeight,
            hasSingleCell,
            shouldApplyExplicitCellBounds,
            explicitHeight
        })
    }
    g.width(maxX)
    g.height(finalHeight)
    if( shouldApplyExplicitCellBounds && explicitHeight !== undefined && maxY > explicitHeight ){
        g.clip({
            x: 0,
            y: 0,
            width: maxX,
            height: explicitHeight
        })
    }
    if( options.getConfig ){
        return {
            width: maxX,
            height: finalHeight
        }
    }

    const subColumns = subColumnTracker.reduce((a,c)=>a + (c?.columns ?? 1), 0)
    const widthPadding = (maxColIdx  * spacing) + subColumnTracker.reduce((a,c)=>a + (c?.widthPadding ?? 0), 0)

    g.attrs.resizeInfo = {
        //spacing: [spacing, spacing],
        columns: subColumns, //columns.length,
        rows: rows.length,
        widthPadding
    }

    return g
}
function prepareAxisText(header, {maxWidth, maxHeight, textPadding, fontSize, refreshCallback, longestPair, minFontSize = 6, fontFamily, fontFill, placeholderFill}){
    const d = header.label ?? header ?? ""
    let longestFrag
    const words = `${d}`.split(" ")
    if( longestPair && words.length > 5){
        const coupleLength = [words, words.map((d,i,a)=> i > 0 ? a[i-1] + " " + d : undefined ).filter(d=>d)].flat()
        longestFrag = coupleLength.reduce((a,c)=>c.length > a.length ? c : a, "" )
    }else{
        longestFrag = words.reduce((a,c)=>a.length > c.length ? a : c, 0)
    }

    
    const text = new CustomText({
        fontSize,
        text: longestFrag,
        align:"center",
        wrap: true,
        verticalAlign:"middle",
        //bgFill:"#f3f4f6",
        x: textPadding[3],
        y: textPadding[0],
        withMarkdown: false,
        width: maxWidth ?? "auto",
        height: "auto",
        fontFamily,
        fill: fontFill,
        placeholderFill,
        refreshCallback
    })
    let rescaled = false
    
    if( maxWidth ){
        while( text.measureSize(longestFrag).width > maxWidth && fontSize > minFontSize){
            fontSize = fontSize > 50 ? fontSize -= (fontSize * 0.1) : fontSize - 0.25
            text.fontSize( fontSize )
            rescaled = true
        } 
    }
    text.text( d )  
    if( maxHeight ){
        while( text.height() > maxHeight && fontSize > minFontSize){
            fontSize = fontSize > 50 ? fontSize -= (fontSize * 0.1) : fontSize - 0.25
            text.fontSize( fontSize )
            rescaled = true
        } 
    }

    return {rendered: text, fontSize, rescaled}
}

function resolveMainstoreInstance(candidate){
    if(candidate){
        return candidate;
    }
    try{
        return MainStore();
    }catch(err){
        return undefined;
    }
}

function isLikelyMongoId(value){
    if(typeof value !== "string"){
        return false;
    }
    return MONGO_ID_PATTERN.test(value);
}

function extractCandidateId(value){
    if(value === undefined || value === null){
        return undefined;
    }
    if(typeof value === "string" || typeof value === "number"){
        return `${value}`;
    }
    if(typeof value === "object"){
        if(typeof value._id === "string"){
            return value._id;
        }
        if(typeof value.id === "string"){
            return value.id;
        }
    }
    return undefined;
}

function collectHeaderCandidateValues(item){
    const candidates = [];
    if(item === undefined || item === null){
        return candidates;
    }
    if(Array.isArray(item?.idx)){
        candidates.push(...item.idx);
    }else if(item?.idx !== undefined && item?.idx !== null){
        candidates.push(item.idx);
    }
    if(item?.primitiveId !== undefined && item?.primitiveId !== null){
        candidates.push(item.primitiveId);
    }
    if(item?.id !== undefined && item?.id !== null){
        candidates.push(item.id);
    }
    if(typeof item === "string" || typeof item === "number"){
        candidates.push(item);
    }
    if(typeof item?.label === "string" && item.label.trim().length > 0){
        candidates.push(item.label.trim());
    }
    if(typeof item?.title === "string" && item.title.trim().length > 0){
        candidates.push(item.title.trim());
    }
    return candidates;
}

function resolvePrimitiveForHeader(item, store, context = {}){
    if(!store || typeof store.primitive !== "function"){
        return undefined;
    }
    const idCache = context.idCache ?? (context.idCache = new Map());
    const candidates = collectHeaderCandidateValues(item);
    for(const candidate of candidates){
        const candidateId = extractCandidateId(candidate);
        if(isLikelyMongoId(candidateId)){
            if(!idCache.has(candidateId)){
                let primitive;
                try{
                    primitive = store.primitive(candidateId);
                }catch(err){
                    primitive = undefined;
                }
                idCache.set(candidateId, primitive);
            }
            const primitive = idCache.get(candidateId);
            if(primitive){
                return primitive;
            }
        }
        const label = typeof candidate === "string" ? candidate.trim() : undefined;
        if(label){
            if(typeof context.getPrimitiveByTitle === "function"){
                const primitive = context.getPrimitiveByTitle(label);
                if(primitive){
                    return primitive;
                }
            }else if(typeof context.ensureCandidateList === "function"){
                const list = context.ensureCandidateList();
                if(Array.isArray(list)){
                    const match = list.find(d=>d?.title === label);
                    if(match){
                        return match;
                    }
                }
            }
        }
    }
    return undefined;
}

const COMMON_HEADER_ENTITY_STEMS = [
    "holdings",
    "holding",
    "technologies",
    "technology",
    "tehcnologues",
    "limited",
    "ltd",
    "inc",
    "inc.",
    "incorporated",
    "corp",
    "corporation",
    "company",
    "co",
    "group",
    "partners",
    "labs",
    "solutions"
];
const COMMON_HEADER_ENTITY_STEM_SET = new Set(COMMON_HEADER_ENTITY_STEMS.map(stem=>stem.toLowerCase()));

function stripTrailingEntityStems(label){
    if(typeof label !== "string"){
        return undefined;
    }
    const normalized = label.replace(/[,/&]+/g, " ").trim();
    if(!normalized){
        return undefined;
    }
    const tokens = normalized.split(/\s+/).map(token=>{
        return token.replace(/^[^0-9a-zA-Z]+/, "").replace(/[^0-9a-zA-Z]+$/, "");
    }).filter(Boolean);
    if(tokens.length === 0){
        return undefined;
    }
    let end = tokens.length;
    while(end > 0){
        const token = tokens[end - 1].toLowerCase();
        if(COMMON_HEADER_ENTITY_STEM_SET.has(token)){
            end -= 1;
            continue;
        }
        break;
    }
    if(end === tokens.length){
        return undefined;
    }
    const stripped = tokens.slice(0, end).join(" ");
    return stripped.length > 0 ? stripped : undefined;
}

function createHeaderPrimitiveResolver(store){
    if(!store || typeof store.primitive !== "function"){
        return undefined;
    }
    const context = {
        idCache: new Map(),
    };
    let titleMap;
    const ensureTitleMap = ()=>{
        if(titleMap){
            return titleMap;
        }
        titleMap = new Map();
        if(typeof store.primitives === "function"){
            const primitives = store.primitives();
            if(Array.isArray(primitives)){
                const entityPrimitives = primitives.filter(primitive=>primitive?.type === "entity");
                const addTitleKey = (key, primitive)=>{
                    if(!key || titleMap.has(key)){
                        return;
                    }
                    titleMap.set(key, primitive);
                };
                for(const primitive of entityPrimitives){
                    const title = primitive?.title;
                    if(typeof title !== "string" || title.trim().length === 0){
                        continue;
                    }
                    const trimmed = title.trim();
                    addTitleKey(trimmed, primitive);
                    addTitleKey(trimmed.toLowerCase(), primitive);
                    const stripped = stripTrailingEntityStems(trimmed);
                    if(stripped){
                        addTitleKey(stripped, primitive);
                        addTitleKey(stripped.toLowerCase(), primitive);
                    }
                }
            }
        }
        return titleMap;
    };
    context.getPrimitiveByTitle = (label)=>{
        const map = ensureTitleMap();
        if(!map){
            return undefined;
        }
        if(typeof label !== "string"){
            return undefined;
        }
        const trimmed = label.trim();
        if(!trimmed){
            return undefined;
        }
        const lower = trimmed.toLowerCase();
        let primitive = map.get(trimmed) ?? map.get(lower);
        if(primitive){
            return primitive;
        }
        const stripped = stripTrailingEntityStems(trimmed);
        if(!stripped){
            return undefined;
        }
        return map.get(stripped) ?? map.get(stripped.toLowerCase());
    };
    return (item)=>resolvePrimitiveForHeader(item, store, context);
}

let cachedPlainObjectPrimitiveResolver;
function ensurePlainObjectPrimitiveResolver(){
    if(cachedPlainObjectPrimitiveResolver === undefined){
        const store = resolveMainstoreInstance();
        cachedPlainObjectPrimitiveResolver = store ? createHeaderPrimitiveResolver(store) : null;
    }
    return cachedPlainObjectPrimitiveResolver ?? undefined;
}

export const renderPlainObject = (options = {})=>{
    const resolver = ensurePlainObjectPrimitiveResolver();
    const payload = {
        ...options,
    };
    if(!payload.imageHelper){
        payload.imageHelper = imageHelper;
    }
    if(resolver && !payload.resolvePrimitiveForHeader){
        payload.resolvePrimitiveForHeader = resolver;
    }
    if(!payload.resolvePrimitiveImageUrl){
        payload.resolvePrimitiveImageUrl = resolvePrimitiveImageUrl;
    }
    const rendered = renderPlainObjectUtil(payload);
    const renderContextId = payload.renderOptions?.renderContextId ?? payload.renderContextId;
    if (renderContextId && rendered?.attrs) {
        rendered.attrs.renderContextId = renderContextId;
    }
    return rendered;
};

function extractAxisImageUrl(item){
    if(!item || typeof item !== "object"){
        return undefined;
    }
    const candidates = [item.imageUrl, item.image_url, item.logoUrl, item.logo_url, item.logo, item.iconUrl, item.icon_url, item.icon];
    for(const value of candidates){
        if(typeof value === "string" && value.trim().length > 0){
            return value.trim();
        }
    }
    return undefined;
}

export function renderLogo(label, options = {}){
    const resolvedLabel = typeof label === "string" ? label.trim() : "";
    if(!resolvedLabel){
        return undefined;
    }
    const { size = 48 } = options;
    const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    let normalizedDomain;
    if(DOMAIN_PATTERN.test(resolvedLabel.replace(/\s+/g, ""))){
        normalizedDomain = getRegisteredDomain(resolvedLabel) ?? resolvedLabel.replace(/\s+/g, "").toLowerCase();
    }
    const endpoint = normalizedDomain
        ? `/api/companyLogo?domain=${encodeURIComponent(normalizedDomain)}`
        : `/api/companyLogo?name=${encodeURIComponent(resolvedLabel)}`;
    return imageHelper(endpoint, {
        x: options.x ?? 0,
        y: options.y ?? 0,
        size,
        center: options.center ?? true,
        fit: options.fit ?? "contain",
        imageCallback: options.imageCallback,
        placeholder: options.placeholder !== false,
        padding: options.padding,
        clearImg: options.clearImg,
    });
}

function computeHeaderIconSize({availableWidth, availableHeight, fontSize, defaultSize = 48} = {}){
    const candidates = [];
    if(Number.isFinite(availableWidth) && availableWidth > 0){
        candidates.push(availableWidth * 0.7);
    }
    if(Number.isFinite(availableHeight) && availableHeight > 0){
        candidates.push(availableHeight * 0.8);
    }
    if(Number.isFinite(fontSize) && fontSize > 0){
        candidates.push(fontSize * 2.5);
    }
    candidates.push(defaultSize);
    const filtered = candidates.filter((value)=>Number.isFinite(value) && value > 0);
    const upperBound = filtered.length > 0 ? Math.min(...filtered) : defaultSize;
    const baseline = Number.isFinite(fontSize) && fontSize > 0 ? Math.max(fontSize * 1.25, 24) : 24;
    return Math.max(baseline, Math.min(upperBound, 128));
}

function buildHeaderIconNode({item, width = 0, iconSize = 48, refreshCallback, placeholder = true, mainstore, primitiveResolver} = {}){
    let primitive;
    if(typeof primitiveResolver === "function"){
        primitive = primitiveResolver(item);
    }else{
        primitive = resolvePrimitiveForHeader(item, mainstore);
    }
    const label = typeof item?.label === "string" && item.label.trim().length > 0 ? item.label.trim() : (typeof item === "string" ? item : undefined);
    const explicitUrl = extractAxisImageUrl(item);
    const iconOptions = {
        x: Math.max(0, (width - iconSize) / 2),
        y: 0,
        size: iconSize,
        imageCallback: refreshCallback,
        placeholder,
        fit: 'contain',
        center: true,
    };

    const primitiveUrl = resolvePrimitiveImageUrl(primitive, label);
    if(primitiveUrl){
        return imageHelper(primitiveUrl, iconOptions);
    }

    if(explicitUrl){
        const useUrl = explicitUrl.startsWith('/api/') ? explicitUrl : `/api/remoteImage?url=${encodeURIComponent(explicitUrl)}`;
        return imageHelper(useUrl, iconOptions);
    }

    if(label){
        return renderLogo(label, iconOptions);
    }

    return undefined;
}
function prepareHeaders({columns, rows, columnWidths, rowHeights, columnX, rowY, renderOptions, baseFontSize = 12, spacing, textPadding = [5,5,5,5], refreshCallback, includeColumns = true, includeRows = true, background = true, theme = defaultTheme,...options}){
    const resolvedFontFamily = theme.fontFamily ?? "Poppins";
    const resolvedFontColor = themeColor(theme, 'headerText', '#000000', options.text_color)
    const resovledBackground = themeColor(theme, 'header', '#ffffff')
    const resovledFontPlaceholderFill = mixHex( resolvedFontColor, resovledBackground, 0.7)
    const userScaleHeaders = typeof renderOptions.axis_text === "number" && Number.isFinite(renderOptions.axis_text) && renderOptions.axis_text > 0 ? renderOptions.axis_text : 1
    const resolveAxisScale = (value, fallback) => (typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback)
    const columnScale = resolveAxisScale(renderOptions.axis_text_columns, userScaleHeaders)
    const rowScale = resolveAxisScale(renderOptions.axis_text_rows, userScaleHeaders)

    const safeColumnWidths = columnWidths.length > 0 ? columnWidths : [200];
    const safeRowHeights = rowHeights.length > 0 ? rowHeights : [40];

    const maxColumnWidth = Math.max(...safeColumnWidths)
    const rawMaxRowHeight = Math.max(...safeRowHeights)
    const maxRowHeight = Math.min(Math.max(rawMaxRowHeight, 0), 200)
    let maxFont =  Math.max(6, (maxRowHeight || 60) / 4)
    let headerScale = Math.max(1, Math.max((maxColumnWidth || 200) / 200 , (maxRowHeight || 45) / 45 ))
    let headerFontSize = renderOptions.headerFontSize ?? options.fontSize ?? Math.min(baseFontSize * headerScale, maxFont, baseFontSize * 10)
    const padding = textPadding.map(d=>d / baseFontSize * headerFontSize)

    let columnLabelAsText = true
    let rowLabelAsText = true

    const columnAsIcon = Boolean(options.columnAsIcon)
    const rowAsIcon = Boolean(options.rowAsIcon)
    const allowColumnIcons = options.allowColumnIcons !== false
    const allowRowIcons = options.allowRowIcons !== false
    const mainstore = options.mainstore ?? resolveMainstoreInstance()
    let placeholderAllowed = true
    if(options.placeholder !== undefined){
        placeholderAllowed = options.placeholder !== false
    }else if(renderOptions?.placeholder !== undefined){
        placeholderAllowed = renderOptions.placeholder !== false
    }

    const getNodeHeight = (node)=>typeof node?.height === "function" ? node.height() : 0

    if( columns.length === 0 || (columns.length === 1 && !columns[0].label)  ){includeColumns = false}
    if( rows.length === 0 || (rows.length === 1 && !rows[0].label)){includeRows = false}
    const renderedColumns = includeColumns && new Konva.Group({x: 0, y: 0})
    const renderedRows = includeRows && new Konva.Group({x: 0, y: 0})

    let needColumnFontRescale = false
    let needRowFontRescale = false
    
    const idealWidth = Math.min(200, ...safeColumnWidths)

    const columnContent = includeColumns ? columns.map((d,i)=>{
        if( allowColumnIcons && columnAsIcon){
            const rawIconSize = computeHeaderIconSize({
                availableWidth: columnWidths[i],
                availableHeight: maxRowHeight,
                fontSize: headerFontSize
            })
            const iconSizeLimit = columnWidths[i]
            const targetIconSize = (() => {
                if(columnScale === 1){
                    return rawIconSize
                }
                let next = rawIconSize * columnScale
                if(Number.isFinite(iconSizeLimit) && iconSizeLimit > 0){
                    next = Math.min(next, iconSizeLimit)
                }
                if(Number.isFinite(maxRowHeight) && maxRowHeight > 0){
                    next = Math.min(next, maxRowHeight)
                }
                return next
            })()
            const icon = buildHeaderIconNode({
                item: d,
                width: columnWidths[i],
                iconSize: targetIconSize,
                refreshCallback,
                placeholder: placeholderAllowed,
                mainstore,
                primitiveResolver: options.primitiveResolver
            })
            if( icon ){
                columnLabelAsText = false
                return icon
            }
        }
        if( renderOptions?.columnAsCompany){
            columnLabelAsText = false
            const maxWidth = Number.isFinite(columnWidths[i]) && columnWidths[i] > 0 ? columnWidths[i] : idealWidth
            const iconSize = Math.min(idealWidth * columnScale, maxWidth)
            const logo = imageHelper( "/api/companyLogo?name=" + d.label, {
                x: (columnWidths[i] - (iconSize * 0.8)) /2,
                y: 0,
                size: iconSize * 0.8,
                imageCallback: refreshCallback
            }) 
            return logo

        }
        if( d.imageUrl ){
            columnLabelAsText = false
            const maxWidth = Number.isFinite(columnWidths[i]) && columnWidths[i] > 0 ? columnWidths[i] : idealWidth
            const iconSize = Math.min(idealWidth * columnScale, maxWidth)
            const resolvedUrl = d.imageUrl.startsWith('/api/') ? d.imageUrl : `/api/remoteImage?url=${encodeURIComponent(d.imageUrl)}`
            const logo = imageHelper( resolvedUrl, {
                x: (columnWidths[i] - (iconSize * 0.8)) /2,
                y: 0,
                size: iconSize * 0.8,
                imageCallback: refreshCallback
            }) 
            return logo
        }else{
            const r = prepareAxisText( d, {
                fontSize: headerFontSize, 
                maxWidth: columnWidths[i]  - padding[1] - padding[3],
                textPadding: padding,
                fontFamily: resolvedFontFamily,
                fontFill: resolvedFontColor,
                placeholderFill: resovledFontPlaceholderFill,
                refreshCallback
            } )
            if( r.fontSize !== headerFontSize){
                headerFontSize = r.fontSize
                needColumnFontRescale = true
            }
            return r.rendered
        }
    }) : []
    const rowContent = includeRows ? rows.map((d,i)=>{
        if( allowRowIcons && rowAsIcon){
            const rawIconSize = computeHeaderIconSize({
                availableWidth: idealWidth,
                availableHeight: rowHeights[i],
                fontSize: headerFontSize
            })
            const iconSizeLimit = idealWidth
            const targetIconSize = (() => {
                if(rowScale === 1){
                    return rawIconSize
                }
                let next = rawIconSize * rowScale
                if(Number.isFinite(iconSizeLimit) && iconSizeLimit > 0){
                    next = Math.min(next, iconSizeLimit)
                }
                if(Number.isFinite(rowHeights[i]) && rowHeights[i] > 0){
                    next = Math.min(next, rowHeights[i])
                }
                return next
            })()
            const icon = buildHeaderIconNode({
                item: d,
                width: idealWidth,
                iconSize: targetIconSize,
                refreshCallback,
                placeholder: placeholderAllowed,
                mainstore,
                primitiveResolver: options.primitiveResolver
            })
            if( icon ){
                rowLabelAsText = false
                return icon
            }
        }
        const r = prepareAxisText( d, {
            fontSize: headerFontSize, 
            longestPair: true,
            maxWidth: idealWidth - padding[1] - padding[3],
            maxHeight: rowHeights[i],
            textPadding: padding,
            fontFamily: resolvedFontFamily,
            fontFill: resolvedFontColor,
            placeholderFill: resovledFontPlaceholderFill,
            refreshCallback
        } )
        if( r.fontSize !== headerFontSize){
            headerFontSize = r.fontSize
            needRowFontRescale = true
        }
        return r.rendered
    }) : []

    if( headerScale !== 1){
        needColumnFontRescale = true        
        needRowFontRescale = true        
    }

    const scaledColumnFontSize = headerFontSize * columnScale
    const scaledRowFontSize = headerFontSize * rowScale
    needColumnFontRescale = needColumnFontRescale || columnScale !== 1
    needRowFontRescale = needRowFontRescale || rowScale !== 1

    if( needColumnFontRescale ){
        columnContent.forEach(d=>{
            if(typeof d?.fontSize === "function"){
                d.fontSize(scaledColumnFontSize)
            }
        })
    }
    if( needRowFontRescale ){
        rowContent.forEach(d=>{
            if(typeof d?.fontSize === "function"){
                d.fontSize(scaledRowFontSize)
            }
        })
    }

    const maxColumnContentHeight = columnContent.reduce((a,d)=>{
        const h = getNodeHeight(d)
        return h > a ? h : a
    }, 0)
    const height = maxColumnContentHeight + padding[0] + padding[2]
    if( includeColumns ){

        columnContent.forEach((d,i)=>{
            const h = new Konva.Group({
                id: `column_${i}`,
                name: "inf_track column_header",
                x: columnX[i], y: 0,
                width: columnWidths[i],
                height
            })
            if( columnLabelAsText && background ){
                const r = new Konva.Rect({
                    x: 0, y: 0,
                    width: columnWidths[i],
                    height,
                    fill: resovledBackground
                })
                h.add(r)
            }
            const nodeHeight = getNodeHeight(d)
            d.y( (height - nodeHeight) / 2)
            h.add(d)
            renderedColumns.add(h)
        })
        renderedColumns.width(columnX.at(-1) + columnWidths.at(-1))
        renderedColumns.height(height)

        if( renderedRows ){
            renderedRows.y(height + spacing)
        }
    }

    if( includeRows){

        rowContent.forEach((d,i)=>{
            const h = new Konva.Group({
                id: `row_${i}`,
                name: "inf_track row_header",
                x: 0, y: rowY[i],
                width: idealWidth,
                height: rowHeights[i]
            })
            if( background ){
                const r = new Konva.Rect({
                    x: 0, y: 0,
                    width: idealWidth,
                    height: rowHeights[i],
                    fill: resovledBackground
                })
                h.add(r)
            }
            const nodeHeight = getNodeHeight(d)
            d.y( (rowHeights[i] - nodeHeight) / 2)
            h.add(d)
            renderedRows.add(h)
        })
        if( renderedColumns ){
            renderedColumns.x(idealWidth + spacing)
        }
        renderedRows.width(idealWidth)
        renderedRows.height(rowY.at(-1) + rowHeights.at(-1))
    }

    const effectiveFontSize = Math.max(scaledColumnFontSize, scaledRowFontSize)
    return {columns: renderedColumns, rows: renderedRows, fontSize: effectiveFontSize, columnFontSize: scaledColumnFontSize, rowFontSize: scaledRowFontSize}
}
